import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useAdminPanel } from '../../contexts/AdminPanelContext';
import { formatUserDateTime } from '../../utils/dateFormat';
import { downloadCsv } from '../../utils/csv';
import { useGeneralSettings } from '../../hooks/useGeneralSettings';
import { formatDT, formatDateOnly, formatTimeOnly, formatSpeed, getFileTimestamp } from '../../utils/timezone';
import { IO_VALUE_COLOR_OPTIONS } from '../../utils/ioValueColors';
import { DeviceDeactivationWizard, DeviceReactivationWizard } from '../../components/wizards/DeactivationWizards';
import DeviceImportWizard, { downloadDeviceImportTemplate, downloadDeviceImportTemplateXlsx } from '../../components/wizards/DeviceImportWizard';
import * as XLSX from 'xlsx';
import { RawPacketViewer } from '../../components/devices/RawPacketViewer';
import { RssiSparkline } from '../../components/shared/RssiSparkline';
import { PaginationFooter, usePersistedPageSize } from '../../components/shared/PaginationFooter';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';
import { useUrlPage, useUrlPageSize, useSkipFirstEffect, useDebouncedValue, useSessionSearch } from '../../components/shared/useUrlPagination';
import { DateRangePresetFilter } from '../../components/shared/DateRangePresetFilter';
import { MobileTabPicker } from '../../components/shared/MobileTabPicker';
import { IdentityLogbookSettingsSection, normalizeIdentityLogbook, DEFAULT_IDENTITY_LOGBOOK } from '../../components/admin/IdentityLogbookSettingsSection';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { NumberInput } from '../../components/ui/number-input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '../../components/ui/dropdown-menu';
import { newLandscapeDoc, addBrandHeader, addFooters, loadLogoPng, drawPdfTable, exportTableHtml } from '../../utils/tripExport';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  HardDrive,
  Video,
  Loader2,
  RefreshCw,
  Undo2,
  Truck,
  PackageCheck,
  ListChecks,
  Users,
  Command as CommandIcon,
  Activity,
  Image as ImageIcon,
  History,
  AlertTriangle,
  X,
  Check,
  Circle,
  Wifi,
  WifiOff,
  Server,
  Plug,
  StickyNote,
  Gauge,
  Radio,
  Bluetooth,
  Lock,
  CalendarDays,
  Signal,
  TrendingUp,
  ExternalLink,
  Link2,
  Wrench,
  Clock,
  Smartphone,
  Copy,
  Upload,
  Download,
  Send,
  GripVertical,
  Save,
  KeyRound,
  ChevronsUpDown,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Sparkles,
  Power,
  PowerOff,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { DeviceIconDisplay, DeviceIconPicker, isCustomIcon, IconColorPicker, IconSearchInput, assetTypeIcon, formatIconLabel } from '../../components/icons/DeviceIcons';
import { TimezoneSelector } from '../../components/TimezoneSelector';
import { SortableTableHead } from '../../components/SortableTableHead';
import { ComputedLogicBuilder } from '../../components/admin/ComputedLogicBuilder';
import { IOMappingHelpPanel, IOHelpTip } from '../../components/admin/IOMappingHelp';
import { CommandsHelpPanel, CmdHelpTip } from '../../components/admin/CommandsHelp';
import { SyncedTableScroll } from '../../components/admin/SyncedTableScroll';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const protocols = ['Teltonika', 'Meitrack', 'Digital Matter', 'Jimi Concox', 'CalAmp', 'BLE Beacon'];

const deviceTypes = [
  { value: 'gps_tracker', label: 'GPS Tracker (Hardwired)' },
  { value: 'obd_tracker', label: 'OBD-II Tracker' },
  { value: 'personal_tracker', label: 'Personal Tracker' },
  { value: 'asset_tracker', label: 'Asset Tracker (Battery/Solar)' },
  { value: 'dashcam', label: 'Dashcam Video Tracker' },
  { value: 'beacon', label: 'BLE Beacon' },
];

const installationTypes = [
  { value: 'hardwired', label: 'Hardwired' },
  { value: 'obd', label: 'OBD-II Plug-in' },
  { value: 'magnetic', label: 'Magnetic Mount' },
  { value: 'portable', label: 'Portable' },
  { value: 'fixed', label: 'Fixed / Bolted' },
];

const fuelTypes = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'lpg', label: 'LPG' },
  { value: 'na', label: 'N/A' },
];

const peripheralTypes = [
  { value: 'can_module', label: 'CAN Module' },
  { value: 'can_control', label: 'CAN Control' },
  { value: 'temperature_sensor', label: 'Temperature Sensor' },
  { value: 'humidity_sensor', label: 'Humidity Sensor' },
  { value: 'temp_humidity_sensor', label: 'Temperature & Humidity Sensor' },
  { value: 'switch_beacon', label: 'Switch Beacon' },
  { value: 'fuel_sensor', label: 'Fuel Sensor' },
  { value: 'door_sensor', label: 'Door Sensor' },
  { value: 'other', label: 'Other' },
];

const peripheralConnections = [
  { value: '1-wire', label: '1-Wire' },
  { value: 'ble', label: 'BLE (Bluetooth)' },
  { value: 'rs232_rs485', label: 'RS232 / RS485' },
  { value: 'analog_input', label: 'Analog Input' },
  { value: 'digital_input', label: 'Digital Input' },
  { value: 'can_bus', label: 'CAN Bus' },
  { value: 'other', label: 'Other' },
];

const assetTypes = [
  { value: 'car', label: 'Car' },
  { value: 'suv', label: 'SUV' },
  { value: 'van', label: 'Van' },
  { value: 'ute', label: 'Ute' },
  { value: 'truck', label: 'Truck' },
  { value: 'bus', label: 'Bus' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'trailer', label: 'Trailer' },
  { value: 'machine', label: 'Machine' },
  { value: 'excavator', label: 'Excavator' },
  { value: 'ebike', label: 'eBike' },
  { value: 'scooter', label: 'Scooter' },
  { value: 'caravan', label: 'Caravan' },
  { value: 'boat', label: 'Boat' },
  { value: 'jetski', label: 'JetSki' },
  { value: 'container', label: 'Container' },
  { value: 'building', label: 'Building / Property' },
  { value: 'tool', label: 'Tool' },
  { value: 'personnel', label: 'Personnel' },
  { value: 'device', label: 'Device' },
  { value: 'other', label: 'Other' },
];

const beaconTypes = [
  { value: 'ibeacon', label: 'iBeacon' },
  { value: 'eddystone', label: 'Eddystone' },
  { value: 'vendor_specific', label: 'Vendor Specific' },
];

// Mirrors backend _compute_beacon_identifier. Validates type-specific fields and returns the
// canonical identifier preview + per-field errors so a malformed beacon can never be saved.
const _onlyHex = (s) => (s || '').replace(/[^0-9a-fA-F]/g, '').toUpperCase();
// Accepts plain hex (7CD9F408E3EB) OR colon/dash format — QR scanners emit plain hex.
const _isValidMac = (s) => _onlyHex(s).length === 12;
// Normalizes any accepted MAC input to the colon format the backend/TCP parser expects.
const _normalizeMacToColon = (s) => {
  const hex = _onlyHex(s);
  if (hex.length !== 12) return (s || '').trim().toUpperCase().replace(/-/g, ':');
  return hex.match(/.{2}/g).join(':');
};

const computeBeaconValidation = (fd) => {
  const errors = {};
  let identifier = null;
  const bt = fd.beacon_type;

  // MAC is optional reference on iBeacon/Eddystone, but if present its format is validated.
  const macRaw = (fd.mac_address || '').trim();
  if (macRaw && !_isValidMac(macRaw)) {
    errors.mac = 'Enter 12 hex characters, e.g. 7CD9F408E3EB or 7C:D9:F4:08:E3:EB.';
  }

  if (bt === 'ibeacon') {
    const uuidHex = _onlyHex(fd.beacon_uuid);
    if (!(fd.beacon_uuid || '').trim()) errors.uuid = 'UUID is required for an iBeacon.';
    else if (uuidHex.length !== 32) errors.uuid = `UUID must be 32 hex characters (16 bytes); you have ${uuidHex.length}.`;

    const majorStr = String(fd.beacon_major ?? '').trim();
    const minorStr = String(fd.beacon_minor ?? '').trim();
    const major = majorStr === '' ? 0 : Number(majorStr);
    const minor = minorStr === '' ? 0 : Number(minorStr);
    if (majorStr !== '' && (!Number.isInteger(major) || major < 0 || major > 65535)) errors.major = 'Major must be a whole number 0–65535.';
    if (minorStr !== '' && (!Number.isInteger(minor) || minor < 0 || minor > 65535)) errors.minor = 'Minor must be a whole number 0–65535.';
    if (uuidHex.length === 32 && !errors.major && !errors.minor) identifier = `${uuidHex}:${major || 0}:${minor || 0}`;
  } else if (bt === 'eddystone') {
    const nsHex = _onlyHex(fd.beacon_namespace);
    const instHex = _onlyHex(fd.beacon_instance_id);
    if (!(fd.beacon_namespace || '').trim()) errors.namespace = 'Namespace is required for an Eddystone beacon.';
    else if (nsHex.length !== 20) errors.namespace = `Namespace must be 20 hex characters (10 bytes); you have ${nsHex.length}.`;
    if (!(fd.beacon_instance_id || '').trim()) errors.instance = 'Instance ID is required for an Eddystone beacon.';
    else if (instHex.length !== 12) errors.instance = `Instance ID must be 12 hex characters (6 bytes); you have ${instHex.length}.`;
    if (nsHex.length === 20 && instHex.length === 12) identifier = `${nsHex}:${instHex}`;
  } else if (bt === 'vendor_specific') {
    const serial = (fd.serial_number || '').trim();
    if (!macRaw && !serial) errors.mac = 'Enter a MAC Address (or Serial Number) — it is the beacon identifier.';
    else if (macRaw && _isValidMac(macRaw)) identifier = _normalizeMacToColon(macRaw);
    else if (!macRaw && serial) identifier = serial;
  }

  return { errors, identifier, isValid: Object.keys(errors).length === 0 };
};


const beaconPurposes = [
  { value: 'asset', label: 'Asset Beacon' },
  { value: 'personal', label: 'Personal ID Beacon' },
];

const beaconAssetTypes = [
  { value: 'people', label: 'People' },
  { value: 'trailer', label: 'Trailer' },
  { value: 'container', label: 'Container' },
  { value: 'tool', label: 'Tool' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'pallet', label: 'Pallet' },
  { value: 'cargo', label: 'Cargo' },
  { value: 'temperature_zone', label: 'Temperature Zone' },
  { value: 'door', label: 'Door / Gate' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other', label: 'Other' },
];



const ioTypes = [
  'Battery', 'External Voltage', 'Ignition', 'Immobilizer', 'Movement', 'Speed', 'Odometer',
  'Engine Hours', 'Fuel Level', 'Fuel Consumption', 'Fuel Range',
  'EV Battery Level', 'EV Energy Consumption', 'EV Range', 'EV Charging Status', 'EV Battery Health', 'EV Charging Power',
  'Temperature', 'Humidity',
  'Digital Input', 'Digital Output', 'Relay/Output', 'Door Sensor',
  'GSM Signal', 'Mobile Network', 'Satellites', 'HDOP', 'Altitude', 'Heading',
  'Accelerometer', 'CAN-BUS', 'BLE Beacon', 'Pressure', 'Vehicle',
  'RFID / iButton',
  'Custom'
];

const valueTypes = ['Value', 'Logic', 'Percentage', 'String', 'Multi-Parameter'];

const unitOptions = [
  'None', 'V', 'mV', 'km/h', 'mph', 'km', 'm', '°C', '°F', '%', 'L', 'gal',
  'hours', 'min', 'sec', 'dBm', 'RPM', 'bar', 'psi', 'kPa', 'g', 'mAh', 'W', 'kW', 'kWh', 'Custom',
];

// Palette is now shared (utils/ioValueColors) so the picker here can never
// drift out of sync with the Live Tracking / Public Tracking render surfaces.
const valueColorOptions = IO_VALUE_COLOR_OPTIONS;

const getValueColorClass = (value, valueColors) => {
  if (!valueColors) return '';
  const color = valueColors[String(value)] || valueColors['_default'] || '';
  const opt = valueColorOptions.find(o => o.value === color);
  return opt?.class || '';
};

// SVG icon paths for IO types
const ioTypeIcons = {
  'Battery': 'M17 6h-1V4H8v2H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V8a2 2 0 00-2-2zm-3 10h-4v-2h4v2zm0-4h-4v-2h4v2z',
  'External Voltage': 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  'Ignition': 'M12 2a5 5 0 00-5 5v4H5v10h14V11h-2V7a5 5 0 00-5-5zm0 2a3 3 0 013 3v4H9V7a3 3 0 013-3z',
  'Immobilizer': 'M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zM9 8V6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9z',
  'Movement': 'M13.49 5.48c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm-3.6 13.9l1-4.4 2.1 2v6h2v-7.5l-2.1-2 .6-3A7.03 7.03 0 0018 12.5V10a4.97 4.97 0 00-4.1-1.8l-1.2.2-2.7 1.4-3 1.5.8 1.6 2.5-1.3-.7 3.5-2 8.1h2.1z',
  'Speed': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5z',
  'Odometer': 'M12 2C6.48 2 2 6.48 2 12h4c0-3.31 2.69-6 6-6s6 2.69 6 6h4c0-5.52-4.48-10-10-10zm0 4c-3.31 0-6 2.69-6 6h12c0-3.31-2.69-6-6-6z',
  'Engine Hours': 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  'Temperature': 'M15 13V5c0-1.66-1.34-3-3-3S9 3.34 9 5v8c-1.21.91-2 2.37-2 4 0 2.76 2.24 5 5 5s5-2.24 5-5c0-1.63-.79-3.09-2-4zm-4-8c0-.55.45-1 1-1s1 .45 1 1h-1v1h1v2h-1v1h1v2h-2V5z',
  'Fuel Level': 'M19.77 7.23l.01-.01-3.72-3.72L15 4.56l2.11 2.11c-.94.36-1.61 1.26-1.61 2.33a2.5 2.5 0 002.5 2.5c.36 0 .69-.08 1-.21v7.21c0 .55-.45 1-1 1s-1-.45-1-1V14c0-1.1-.9-2-2-2h-1V5c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2v16h10v-7.5h1.5v5a2.5 2.5 0 005 0V9c0-.69-.28-1.32-.73-1.77z',
  'EV Battery Level': 'M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM11 20v-5.5H9L13 7v5.5h2L11 20z',
  'EV Energy Consumption': 'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  'EV Range': 'M19 15.18V7c0-2.21-1.79-4-4-4s-4 1.79-4 4v10c0 1.1-.9 2-2 2s-2-.9-2-2V8.82C8.16 8.4 9 7.3 9 6c0-1.66-1.34-3-3-3S3 4.34 3 6c0 1.3.84 2.4 2 2.82V17c0 2.21 1.79 4 4 4s4-1.79 4-4V7c0-1.1.9-2 2-2s2 .9 2 2v8.18c-1.16.41-2 1.51-2 2.82 0 1.66 1.34 3 3 3s3-1.34 3-3c0-1.3-.84-2.4-2-2.82zM6 7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 11c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z',
  'EV Charging Status': 'M16.01 7L16 3h-2v4h-4V3H8v4h-.01C7 6.99 6 7.99 6 8.99v5.49L9.5 18v3h5v-3l3.5-3.51v-5.5c0-1-1-2-1.99-1.99z',
  'EV Battery Health': 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
  'EV Charging Power': 'M7 2v11h3v9l7-12h-4l4-8z',
  'GSM Signal': 'M2 22h20V2L2 22zm18-2h-3V9.97l3-3V20z',
  'Mobile Network': 'M2 22h20V2L2 22zm18-2h-3V9.97l3-3V20zM15 20h-3v-7.03l3-3V20zM10 20H7v-4.03l3-3V20z',
  'Satellites': 'M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z',
  'Digital Input': 'M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z',
  'Digital Output': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
  'Door Sensor': 'M19 19V5c0-1.1-.9-2-2-2H7c-1.1 0-2 .9-2 2v14H3v2h18v-2h-2zm-8-6H9v-2h2v2z',
  'Altitude': 'M14 6l-3.75 5 2.85 3.8-1.6 1.2C9.81 13.75 7 10 7 10l-6 8h22L14 6z',
  'Heading': 'M12 2L4.5 20.29l.71.71L12 18l6.79 3 .71-.71z',
  'Custom': 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z',
};

// Sortable table row component for drag-and-drop reordering
const SortableTableRow = ({ id, children, className = '' }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 10 : 'auto',
  };
  return (
    <TableRow ref={setNodeRef} style={style} className={className}>
      <TableCell className="px-1 w-6 cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
      </TableCell>
      {children}
    </TableRow>
  );
};


const DeviceManagement = () => {
  const { api, user: currentUser } = useAuth();
  const isManager = currentUser?.role === 'manager';
  const { isSubpageEnabled } = useFeatureFlags();
  const { pendingDeviceAction, setPendingDeviceAction } = useAdminPanel();
  // Device Management LIST privileges (independent of operational allocation).
  // Super Admin gets these as true from /features; admins via granted privileges.
  const canAddDevices = isSubpageEnabled('admin_device_management_add_devices');
  // Management List (pick which Admins manage a device) is gated to View/Manage All only.
  // 'Add Devices' admins are auto-added to devices they create but can't manage the list.
  const canManageDeviceList = isSubpageEnabled('admin_device_management_view_manage_all_devices');
  const [manageableAdmins, setManageableAdmins] = useState([]);
  const [managementListSearch, setManagementListSearch] = useState('');
  const fetchManageableAdmins = useCallback(async () => {
    try {
      const resp = await api.get('/admin/manageable-admins');
      setManageableAdmins(resp.data || []);
    } catch (e) {
      setManageableAdmins([]);
    }
  }, [api]);
  const handleToggleManagementAdmin = (adminId) => {
    const cur = formData.management_admin_ids || [];
    const next = cur.includes(adminId) ? cur.filter((x) => x !== adminId) : [...cur, adminId];
    handleInputChange('management_admin_ids', next);
  };
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  // Sticks to `true` after the first fetch — prevents the table-swap loader
  // from re-appearing on subsequent refetches (which otherwise causes a flash
  // whenever the current filter yields 0 rows and the next keystroke fires).
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useSessionSearch('device-mgmt');
  const [protocolFilter, setProtocolFilter] = useState('__all__');
  const [modelFilter, setModelFilter] = useState('__all__');
  const [deviceModels, setDeviceModels] = useState([]);
  const [ioDupAudit, setIoDupAudit] = useState([]);
  const [simDupAudit, setSimDupAudit] = useState([]); // groups of devices sharing an ICCID
  const [dupAuditOpen, setDupAuditOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [isActiveFilter, setIsActiveFilter] = useState('all');
  const [managementFilter, setManagementFilter] = useState('all');  // Device Management list: 'all' | 'unassigned' | 'assigned'
  const [simHealthFilter, setSimHealthFilter] = useState('all');  // all | dead_sim | healthy_sim | no_sim
  // Server-side pagination — future-proof for tenants with 1000+ devices. Filters (search /
  // protocol / status / active) are applied server-side; page resets to 0 on any filter change.
  const [pageIndex, setPageIndex] = useUrlPage('page', { zeroIndexed: true });
  const [pageSize, setPageSize] = useUrlPageSize('device-mgmt', 25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAllAcrossLoading, setSelectAllAcrossLoading] = useState(false);
  // Bulk "Assign to Admin(s)" (management list)
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [bulkAssignMode, setBulkAssignMode] = useState('add');
  const [bulkAssignSelectedAdmins, setBulkAssignSelectedAdmins] = useState([]);
  const [bulkAssignSearch, setBulkAssignSearch] = useState('');
  const [bulkAssignSubmitting, setBulkAssignSubmitting] = useState(false);
  // Bulk "Allocate to User(s)" (operational access — silent, audit log only)
  const [isBulkAllocOpen, setIsBulkAllocOpen] = useState(false);
  const [bulkAllocMode, setBulkAllocMode] = useState('add');
  const [bulkAllocSelectedUsers, setBulkAllocSelectedUsers] = useState([]);
  const [bulkAllocSearch, setBulkAllocSearch] = useState('');
  const [bulkAllocSubmitting, setBulkAllocSubmitting] = useState(false);
  const [deviceSortField, setDeviceSortField] = useState('name');
  const [deviceSortDir, setDeviceSortDir] = useState('asc');
  const { dateFormat: userDateFmt, timeFormat: userTimeFmt, timezone: userTimezone } = useGeneralSettings();
  
  // SIM Providers
  const [simProviders, setSimProviders] = useState([]);
  
  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [savedFormSnapshot, setSavedFormSnapshot] = useState(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showAddUnsavedWarning, setShowAddUnsavedWarning] = useState(false);
  const [formDirty, setFormDirty] = useState(false);  // Tracks ANY edit across all tabs
  const [imeiLocked, setImeiLocked] = useState(true);
  const [macLocked, setMacLocked] = useState(true);
  const [beaconIdLocked, setBeaconIdLocked] = useState(true);  // locks iBeacon/Eddystone identity fields on edit
  const [showBeaconDiscover, setShowBeaconDiscover] = useState(false);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverCandidates, setDiscoverCandidates] = useState([]);
  const [discoverError, setDiscoverError] = useState('');
  const [discoverSearch, setDiscoverSearch] = useState('');
  const [discoverTypeFilter, setDiscoverTypeFilter] = useState('all');
  const [discoverDetectorFilter, setDiscoverDetectorFilter] = useState('all');
  const [discoverMinRssi, setDiscoverMinRssi] = useState('any');
  const [discoverMinScans, setDiscoverMinScans] = useState('');
  const [discoverSort, setDiscoverSort] = useState('recent');
  const [pendingDelete, setPendingDelete] = useState(null); // { type: 'command'|'mapping', id, label }
  const [pendingImport, setPendingImport] = useState(null); // { type: 'commands'|'mappings', items: [], count: number }
  const [pendingAction, setPendingAction] = useState(null); // { type: 'delete_device'|'clear_history', id?, count?, action: async fn, requireTyped? }
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  
  // Bulk selection
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);
  const isMobileOrWebview = useIsMobileOrWebview();
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteText, setBulkDeleteText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkPurgeHistory, setBulkPurgeHistory] = useState(true);
  const [bulkImpact, setBulkImpact] = useState(null);
  
  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsLoading, setAuditLogsLoading] = useState(false);
  const [auditSearch, setAuditSearch] = useState('');
  const [auditDateRange, setAuditDateRange] = useState({ from: undefined, to: undefined });
  const [auditDateResetKey, setAuditDateResetKey] = useState(0);
  
  // Allocation state
  const [allocatableUsers, setAllocatableUsers] = useState([]);
  const [allocatedUserIds, setAllocatedUserIds] = useState([]);
  const [originalAllocatedUserIds, setOriginalAllocatedUserIds] = useState([]);
  const [primaryOperationalAccountId, setPrimaryOperationalAccountId] = useState('');
  const [originalPrimaryOperationalAccountId, setOriginalPrimaryOperationalAccountId] = useState('');
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [allocationSaving, setAllocationSaving] = useState(false);
  const [allocationSearch, setAllocationSearch] = useState('');
  const [showRemoveAllUsersConfirm, setShowRemoveAllUsersConfirm] = useState(false);
  const [removeAllUsersContext, setRemoveAllUsersContext] = useState(null); // 'allocation' | 'save'
  const [removedUserCount, setRemovedUserCount] = useState(0); // # of users a save would de-allocate

  useEffect(() => {
    if (!allocatableUsers.length) return;
    const allocatedAccountIds = allocatedUserIds.filter(
      id => allocatableUsers.some(user => user.id === id)
    );
    if (allocatedAccountIds.length === 1 && primaryOperationalAccountId !== allocatedAccountIds[0]) {
      setPrimaryOperationalAccountId(allocatedAccountIds[0]);
    } else if (primaryOperationalAccountId && !allocatedAccountIds.includes(primaryOperationalAccountId)) {
      setPrimaryOperationalAccountId('');
    }
  }, [allocatedUserIds, allocatableUsers, primaryOperationalAccountId]);

  // IO Mapping selective export
  const [selectedMappingIds, setSelectedMappingIds] = useState([]);

  // Command send confirmation dialog
  const [sendConfirmCmd, setSendConfirmCmd] = useState(null);
  const [sendingCommand, setSendingCommand] = useState(false);

  // Command selective export
  const [selectedCommandIds, setSelectedCommandIds] = useState([]);

  // Bulk Command Import modal
  const [isBulkCmdImportOpen, setIsBulkCmdImportOpen] = useState(false);
  const [bulkCmdImportData, setBulkCmdImportData] = useState([]);
  const [bulkCmdImportProtocol, setBulkCmdImportProtocol] = useState('');
  const [bulkCmdImportMode, setBulkCmdImportMode] = useState('merge');
  const [bulkCmdImportTargetIds, setBulkCmdImportTargetIds] = useState([]);
  const [bulkCmdImportSearch, setBulkCmdImportSearch] = useState('');
  const [bulkCmdImporting, setBulkCmdImporting] = useState(false);

  // Bulk IO Import modal
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [bulkImportMappings, setBulkImportMappings] = useState([]);
  const [bulkImportProtocol, setBulkImportProtocol] = useState('');
  const [bulkImportMode, setBulkImportMode] = useState('merge');
  const [bulkImportTargetIds, setBulkImportTargetIds] = useState([]);
  const [bulkImportSearch, setBulkImportSearch] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);

  // Device Import Wizard (CSV → guided create)
  const [isDeviceImportWizardOpen, setIsDeviceImportWizardOpen] = useState(false);
  const [lastImportRun, setLastImportRun] = useState(null);
  const [undoingImport, setUndoingImport] = useState(false);
  const [ioLogOpen, setIoLogOpen] = useState(false);
  const [ioLogs, setIoLogs] = useState([]);
  const [ioLogsLoading, setIoLogsLoading] = useState(false);
  const [ioLogTypeFilter, setIoLogTypeFilter] = useState('all'); // all | import | export
  const [ioLogPersonFilter, setIoLogPersonFilter] = useState('all');
  const [ioLogDateRange, setIoLogDateRange] = useState({ from: undefined, to: undefined });
  const [ioLogDateResetKey, setIoLogDateResetKey] = useState(0);
  const [ioLogPage, setIoLogPage] = useState(1);
  const [ioLogPageSize, setIoLogPageSize] = usePersistedPageSize('device-io-log', 25);
  const [ioLogTotal, setIoLogTotal] = useState(0);
  const [ioLogGrandTotal, setIoLogGrandTotal] = useState(0);
  const [ioLogPersons, setIoLogPersons] = useState([]);
  // Command Delivery Receipts — per-device "View all" command log modal
  const [cmdLogOpen, setCmdLogOpen] = useState(false);
  const [cmdLogs, setCmdLogs] = useState([]);
  const [cmdLogsLoading, setCmdLogsLoading] = useState(false);
  const [cmdLogStatusFilter, setCmdLogStatusFilter] = useState('all');
  const [cmdLogMethodFilter, setCmdLogMethodFilter] = useState('all');
  const [cmdLogDateRange, setCmdLogDateRange] = useState({ from: undefined, to: undefined });
  const [cmdLogDateResetKey, setCmdLogDateResetKey] = useState(0);
  const [cmdLogPage, setCmdLogPage] = useState(1);
  const [cmdLogPageSize, setCmdLogPageSize] = usePersistedPageSize('device-cmd-log', 25);
  const [cmdLogTotal, setCmdLogTotal] = useState(0);
  const [cmdNowTick, setCmdNowTick] = useState(Date.now());
  // SIM Health Report modal (server-paginated per section)
  const [simReportOpen, setSimReportOpen] = useState(false);
  const [simReportCounts, setSimReportCounts] = useState(null);
  const [simReportRows, setSimReportRows] = useState([]);
  const [simReportTotal, setSimReportTotal] = useState(0);
  const [simReportLoading, setSimReportLoading] = useState(false);
  const [simReportDays, setSimReportDays] = useState('1');
  const [simReportTab, setSimReportTab] = useState('offline'); // offline | dead | orphaned
  const [simReportPage, setSimReportPage] = useState(1);
  const [simReportPageSize, setSimReportPageSize] = usePersistedPageSize('sim-health-report', 25);

  // Device Status tab state
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [latestIoLoading, setLatestIoLoading] = useState(false);
  const [ioViewMode, setIoViewMode] = useState('raw');
  // Persistent RFID / iButton scan history (server-paginated)
  const [rfidEvents, setRfidEvents] = useState([]);
  const [rfidEventsTotal, setRfidEventsTotal] = useState(0);
  const [rfidEventsPage, setRfidEventsPage] = useState(1);
  const [rfidEventsPageSize, setRfidEventsPageSize] = useState(10);
  const [rfidEventsStart, setRfidEventsStart] = useState('');
  const [rfidEventsEnd, setRfidEventsEnd] = useState('');
  const [rfidEventsSearch, setRfidEventsSearch] = useState('');
  const rfidEventsSearchDebounced = useDebouncedValue(rfidEventsSearch, 400);
  const [rfidEventsMatch, setRfidEventsMatch] = useState('all');
  const [rfidBackfillInfo, setRfidBackfillInfo] = useState(null);
  const [rfidEventsLoading, setRfidEventsLoading] = useState(false);
  const [rfidBackfillRunning, setRfidBackfillRunning] = useState(false);
  const [rfidBackfillConfirmOpen, setRfidBackfillConfirmOpen] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [commandHistoryLoading, setCommandHistoryLoading] = useState(false);
  const [historyFromDate, setHistoryFromDate] = useState('');
  const [historyToDate, setHistoryToDate] = useState('');
  const [simDetailOpen, setSimDetailOpen] = useState(false);
  const [simDetailData, setSimDetailData] = useState(null);
  const [simDetailLoading, setSimDetailLoading] = useState(false);
  const [simSmsMessage, setSimSmsMessage] = useState('');
  const [simSmsSending, setSimSmsSending] = useState(false);
  const [simSmsHistory, setSimSmsHistory] = useState([]);
  const [showReplaceDevice, setShowReplaceDevice] = useState(false);
  const [replaceImei, setReplaceImei] = useState('');
  const [replaceReason, setReplaceReason] = useState('');
  const [replaceTransferHistory, setReplaceTransferHistory] = useState(true);
  const [replacing, setReplacing] = useState(false);
  const [userMap, setUserMap] = useState({});
  const [deviceLimitError, setDeviceLimitError] = useState(null);
  const [deactivateWizardDevice, setDeactivateWizardDevice] = useState(null);
  const [reactivateWizardDevice, setReactivateWizardDevice] = useState(null);  // {userId: {name, email}} for allocated users column
  const [statusTogglePrompt, setStatusTogglePrompt] = useState(null); // { next: bool } — wizard-or-toggle prompt from the Device Status card switch
  const [linkableDevices, setLinkableDevices] = useState([]);
  const [linkedDeviceCache, setLinkedDeviceCache] = useState({});  // {device_id: {name, imei, protocol}} for linked devices not in filtered list
  const [linkedDeviceSearchOpen, setLinkedDeviceSearchOpen] = useState(false);
  const [linkedDeviceSearchTerm, setLinkedDeviceSearchTerm] = useState('');
  // IO Mapping → "IO Parameter" searchable dropdown (Add IO element)
  const [ioParamSearchOpen, setIoParamSearchOpen] = useState(false);
  const [ioParamSearchTerm, setIoParamSearchTerm] = useState('');
  // IO Mapping → "Secondary IO Parameter" searchable dropdown (multi-param mode)
  const [secondaryIoParamSearchOpen, setSecondaryIoParamSearchOpen] = useState(false);
  const [secondaryIoParamSearchTerm, setSecondaryIoParamSearchTerm] = useState('');

  // Custom Icons state
  const [customIcons, setCustomIcons] = useState([]);
  const [customIconsLoading, setCustomIconsLoading] = useState(false);
  const [showIconUpload, setShowIconUpload] = useState(false);
  const [iconUploadName, setIconUploadName] = useState('');
  const [iconUploadFile, setIconUploadFile] = useState(null);
  const [iconUploading, setIconUploading] = useState(false);
  const [showIconLibrary, setShowIconLibrary] = useState(false);
  const [iconDeleting, setIconDeleting] = useState(null);
  const [iconLibrarySearch, setIconLibrarySearch] = useState('');
  const [iconPickerSearch, setIconPickerSearch] = useState('');
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [iconEditingId, setIconEditingId] = useState(null);
  const [iconEditingName, setIconEditingName] = useState('');

  // DnD sensors for drag-and-drop reordering
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleCommandDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.custom_commands.findIndex(c => c.id === active.id);
        const newIndex = prev.custom_commands.findIndex(c => c.id === over.id);
        return { ...prev, custom_commands: arrayMove(prev.custom_commands, oldIndex, newIndex) };
      });
      setFormDirty(true);
    }
  };

  const handleMappingDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setFormData(prev => {
        const oldIndex = prev.io_mappings.findIndex(m => m.id === active.id);
        const newIndex = prev.io_mappings.findIndex(m => m.id === over.id);
        return { ...prev, io_mappings: arrayMove(prev.io_mappings, oldIndex, newIndex) };
      });
      setFormDirty(true);
    }
  };

  // BLE Beacon Status state
  const [beaconStatus, setBeaconStatus] = useState(null);
  const [beaconStatusLoading, setBeaconStatusLoading] = useState(false);
  const [beaconTelemetry, setBeaconTelemetry] = useState(null);
  const [beaconTelemetryLoading, setBeaconTelemetryLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    // Tab 1: Device Details
    name: '',
    device_type: 'gps_tracker',
    imei: '',
    serial_number: '',
    mac_address: '',
    protocol: '',
    model: '',
    firmware_version: '',
    hardware_version: '',
    device_password: '',
    has_video: false,
    num_cameras: 0,
    channel_names: {},
    always_on_streaming: false,
    sim_iccid: '',
    sim_number: '',
    sim_provider: '',
    installation_date: '',
    installation_date_display: '',
    installation_type: '',
    odometer_source: 'gps',
    engine_hours_source: 'off',
    speed_source: 'gps',
    fuel_source: 'off',
    max_speed_kmh: 200,
    server_ip_1: '',
    server_port_1: '',
    server_ip_2: '',
    server_port_2: '',
    show_server_2: false,
    reporting_interval_moving: '',
    reporting_interval_stopped: '',
    apn: '',
    beacon_type: '',
    beacon_purpose: 'asset',
    beacon_uuid: '',
    beacon_major: '',
    beacon_minor: '',
    beacon_eid: '',
    beacon_namespace: '',
    beacon_instance_id: '',
    advertising_interval: '',
    tx_power: '',
    connected_peripherals: [],
    backup_device_ids: [],
    linked_devices: [],
    timezone_override_enabled: true,
    timezone_override: 'auto',
    offline_threshold_secs: 600,
    notes: '',
    
    // Tab 2: Asset Type
    asset_type: 'car',
    asset_name: '',
    asset_make: '',
    asset_model: '',
    asset_year: '',
    registration: '',
    vin_number: '',
    rfid_ibutton_number: '',
    identity_method: 'ble',
    fuel_type: '',
    serial_no: '',
    container_number: '',
    container_size: '',
    container_type: '',
    asset_description: '',
    odometer_reading: 0,
    engine_hours: 0,
    tags: [],
    asset_editing_enabled: true,
    
    // Tab 3: Allocation
    allocated_tenant_ids: [],
    // Device Management LIST (which Admins manage/see this device in the Admin Panel)
    management_admin_ids: [],
    
    // Tab 4: Custom Commands
    custom_commands: [],
    
    // Tab 5: IO Mapping
    io_mappings: [],
    
    // Identity & Logbooks (per-device opt-in)
    identity_logbook: { ...DEFAULT_IDENTITY_LOGBOOK },
    
    // Tab 6: Device Icon
    device_icon: 'car',
    device_icon_color: null,
    map_marker_mode: 'arrow',
    icon_editing_enabled: true,
    
    // Status
    is_active: true,
  });

  // RFID/iButton-only trailer: no beacon hardware — skip BLE identity validation entirely.
  const rfidOnlyTrailer = formData.device_type === 'beacon' && formData.asset_type === 'trailer' && formData.identity_method === 'rfid';
  const beaconValidation = formData.device_type === 'beacon' && !rfidOnlyTrailer
    ? computeBeaconValidation(formData)
    : { errors: {}, identifier: null, isValid: true };
  
  // Temporary states for adding commands/mappings
  const [newCommand, setNewCommand] = useState({
    protocol: '',
    command_text: '',
    button_label: '',
    button_color: 'blue',
    description: '',
    requires_confirmation: false,
    show_telemetry: true,
    show_fleet_card: true,
  });
  const [editingCommandId, setEditingCommandId] = useState(null);
  
  const [newIOMapping, setNewIOMapping] = useState({
    io_type: 'Battery',
    io_id: '',
    display_name: '',
    value_type: 'Value',
    formula: '',
    units: '',
    decimal_places: 2,
    logic_mapping: { "1": "ON", "0": "OFF" },
    dictionary: {},
    calibration: [],
    data_source: 'device_io',
    value_colors: {},
    show_telemetry: true,
    show_fleet_card: true,
    show_map_popup: false,
    show_icon: true,
    ignore_ignition_off: false,
    secondary_io_id: null,
    multi_param_mode: 'lookup',
    multi_param_separator: null,
    multi_param_dictionary: {},
    multi_param_template: '',
  });
  
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [pendingFormGuard, setPendingFormGuard] = useState(null);
  const [saveAfterApply, setSaveAfterApply] = useState(null);
  const [protocolIODefaults, setProtocolIODefaults] = useState([]);
  const [currentIOValues, setCurrentIOValues] = useState({});
  // Safe render for IO values — some protocols store nested objects (e.g., DM analog data)
  const safeIOVal = (val) => {
    if (val === null || val === undefined) return undefined;
    if (typeof val === 'object') return JSON.stringify(val);
    return val;
  };
  const [newDictKey, setNewDictKey] = useState('');
  const [newDictValue, setNewDictValue] = useState('');
  const [newMultiDictDisplay, setNewMultiDictDisplay] = useState('');
  
  const [customIOType, setCustomIOType] = useState('');
  
  // Connected peripheral temp state
  const [newPeripheral, setNewPeripheral] = useState({
    peripheral_type: 'can_module',
    name: '',
    serial_number: '',
    connection_type: 'can_bus',
    program_id: '',
  });
  
  // Tag input
  const [tagInput, setTagInput] = useState('');
  
  // Peripheral form visibility
  const [showPeripheralForm, setShowPeripheralForm] = useState(false);
  const [editingPeripheralId, setEditingPeripheralId] = useState(null);
  
  // SIM auto-populate
  const [simAutoPopulate, setSimAutoPopulate] = useState(true);
  const [simLookupLoading, setSimLookupLoading] = useState(false);
  const [simImeiSyncLoading, setSimImeiSyncLoading] = useState(false);
  const [simAddToProviderLoading, setSimAddToProviderLoading] = useState(false);
  
  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const skip = pageIndex * pageSize;
      let url = `/admin/devices?limit=${pageSize}&skip=${skip}`;
      if (searchTerm) url += `&search=${searchTerm}`;
      if (protocolFilter && protocolFilter !== '__all__') url += `&protocol=${protocolFilter}`;
      if (modelFilter && modelFilter !== '__all__') url += `&model=${encodeURIComponent(modelFilter)}`;
      if (statusFilter && statusFilter !== '__all__') url += `&status_filter=${statusFilter}`;
      if (isActiveFilter !== 'all') url += `&is_active=${isActiveFilter === 'active'}`;
      if (simHealthFilter !== 'all') url += `&sim_health=${simHealthFilter}`;
      if (deviceSortField) url += `&sort=${deviceSortField}&sort_dir=${deviceSortDir || 'asc'}`;

      const response = await api.get(url);
      setDevices(response.data);
      // Total row count from X-Total-Count header (backend sets this for pagination).
      const total = parseInt(response.headers?.['x-total-count'], 10);
      setTotalCount(Number.isFinite(total) ? total : (response.data?.length || 0));
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [api, searchTerm, protocolFilter, modelFilter, statusFilter, isActiveFilter, simHealthFilter, pageIndex, pageSize, deviceSortField, deviceSortDir]);

  // Distinct model list for the Model filter dropdown (tenant-scoped)
  useEffect(() => {
    api.get('/admin/device-models').then(r => setDeviceModels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/admin/device-io-duplicates').then(r => setIoDupAudit(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/admin/device-sim-duplicates').then(r => setSimDupAudit(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [api]);

  const openDupAudit = async () => {
    try {
      const r = await api.get('/admin/device-io-duplicates');
      setIoDupAudit(Array.isArray(r.data) ? r.data : []);
    } catch { /* keep last known list */ }
    setDupAuditOpen(true);
  };

  /* ---- SIM Health Report (server-paginated) ---- */
  const fetchSimReport = useCallback(async () => {
    setSimReportLoading(true);
    try {
      const skip = (simReportPage - 1) * simReportPageSize;
      const r = await api.get(`/admin/reports/sim-health?offline_days=${simReportDays}&section=${simReportTab}&skip=${skip}&limit=${simReportPageSize}`);
      setSimReportCounts(r.data.counts || null);
      setSimReportRows(r.data.rows || []);
      setSimReportTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'You do not have permission to view the SIM Health report' : 'Failed to load SIM Health report');
    } finally {
      setSimReportLoading(false);
    }
  }, [api, simReportTab, simReportDays, simReportPage, simReportPageSize]);

  // Refetch whenever the modal is open and tab / days / page / page-size change
  useEffect(() => {
    if (simReportOpen) fetchSimReport();
  }, [simReportOpen, fetchSimReport]);

  const openSimReport = useCallback(() => setSimReportOpen(true), []);

  // Auto-open the SIM Health Report when arriving from the dashboard card (?sim_report=1).
  // Consume-once: the param is removed so a refresh doesn't reopen the report.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('sim_report') === '1') {
      openSimReport();
      p.delete('sim_report');
      window.history.replaceState(null, '', `${window.location.pathname}${p.toString() ? `?${p}` : ''}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportSimReportCsv = async () => {
    let allRows = [];
    try {
      const r = await api.get(`/admin/reports/sim-health?offline_days=${simReportDays}&section=${simReportTab}&skip=0&limit=10000`);
      allRows = r.data.rows || [];
    } catch {
      toast.error('Failed to export SIM Health report');
      return;
    }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    let headers, rows, name;
    if (simReportTab === 'offline') {
      headers = ['Device', 'IMEI', 'Protocol', 'Last Connection', 'Offline Days', 'ICCID', 'Provider', 'Mobile', 'Plan', 'SIM Status', 'Session'];
      rows = allRows.map(r => [r.device_name, r.imei, r.protocol, r.last_connection || 'Never connected', r.offline_days ?? 'Never connected', r.iccid, r.provider, r.mobile_number, r.plan_name, r.sim_status, r.session_status]);
      name = 'active-sim-offline-trackers';
    } else if (simReportTab === 'dead') {
      headers = ['Device', 'IMEI', 'Protocol', 'Device Status', 'ICCID', 'Provider', 'Mobile', 'Plan', 'SIM Status'];
      rows = allRows.map(r => [r.device_name, r.imei, r.protocol, r.device_status, r.iccid, r.provider, r.mobile_number, r.plan_name, r.sim_status]);
      name = 'dead-sim-active-devices';
    } else if (simReportTab === 'duplicates') {
      headers = ['Device', 'IMEI', 'Protocol', 'Device Status', 'ICCID', 'Provider', 'SIM Status', 'Shared With'];
      rows = allRows.map(r => [r.device_name, r.imei, r.protocol, r.device_status, r.iccid, r.provider, r.sim_status, (r.shared_with || []).join('; ')]);
      name = 'duplicate-sims';
    } else {
      headers = ['ICCID', 'Provider', 'Mobile', 'Plan', 'SIM Status', 'Session', 'Last Activity', 'Cost'];
      rows = allRows.map(r => [r.iccid, r.provider, r.mobile_number, r.plan_name, r.sim_status, r.session_status, r.sim_last_activity, r.cost]);
      name = 'orphaned-active-sims';
    }
    const csv = [headers.map(esc).join(','), ...rows.map(row => row.map(esc).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `sim-health-${name}-${getFileTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fetchAllSimReportRows = async () => {
    try {
      const r = await api.get(`/admin/reports/sim-health?offline_days=${simReportDays}&section=${simReportTab}&skip=0&limit=10000`);
      return r.data.rows || [];
    } catch {
      toast.error('Failed to export SIM Health report');
      return null;
    }
  };

  const SIM_REPORT_TITLES = { offline: 'Active SIM · Offline Tracker', dead: 'Dead SIM · Active Device', orphaned: 'Orphaned Active SIMs', duplicates: 'Duplicate SIMs' };

  const buildSimReportTable = (allRows) => {
    let columns, rows;
    if (simReportTab === 'offline') {
      columns = [
        { label: 'Device', weight: 16 }, { label: 'IMEI', weight: 13 }, { label: 'Protocol', weight: 9 },
        { label: 'Last Connection', weight: 14 }, { label: 'Offline For', weight: 10 },
        { label: 'ICCID', weight: 17 }, { label: 'Provider', weight: 10 }, { label: 'Session', weight: 9 },
      ];
      rows = allRows.map(r => [
        r.device_name, r.imei, r.protocol,
        r.last_connection ? formatDT(r.last_connection) : 'Never connected',
        r.offline_days === null ? 'Never connected' : `${r.offline_days} days`,
        r.iccid, r.provider, (r.session_status || '').replace('_', ' '),
      ]);
    } else if (simReportTab === 'dead') {
      columns = [
        { label: 'Device', weight: 17 }, { label: 'IMEI', weight: 13 }, { label: 'Protocol', weight: 10 },
        { label: 'Device Status', weight: 10 }, { label: 'ICCID', weight: 18 },
        { label: 'Provider', weight: 11 }, { label: 'SIM Status', weight: 11 },
      ];
      rows = allRows.map(r => [r.device_name, r.imei, r.protocol, r.device_status, r.iccid, r.provider, (r.sim_status || '').replace(/_/g, ' ')]);
    } else if (simReportTab === 'duplicates') {
      columns = [
        { label: 'Device', weight: 16 }, { label: 'IMEI', weight: 13 }, { label: 'Protocol', weight: 9 },
        { label: 'Device Status', weight: 9 }, { label: 'ICCID', weight: 17 },
        { label: 'Provider', weight: 10 }, { label: 'Shared With', weight: 18 },
      ];
      rows = allRows.map(r => [r.device_name, r.imei, r.protocol, r.device_status, r.iccid, r.provider, (r.shared_with || []).join(', ')]);
    } else {
      columns = [
        { label: 'ICCID', weight: 18 }, { label: 'Provider', weight: 11 }, { label: 'Mobile', weight: 11 },
        { label: 'Plan', weight: 16 }, { label: 'SIM Status', weight: 9 }, { label: 'Session', weight: 9 },
        { label: 'Last Activity', weight: 13 }, { label: 'Cost', weight: 7 },
      ];
      rows = allRows.map(r => [
        r.iccid, r.provider, r.mobile_number, r.plan_name, (r.sim_status || '').replace(/_/g, ' '),
        (r.session_status || '').replace('_', ' '),
        r.sim_last_activity ? formatDT(r.sim_last_activity) : '', r.cost != null ? `$${Number(r.cost).toFixed(2)}` : '',
      ]);
    }
    return { columns, rows };
  };

  const exportSimReportPdf = async () => {
    const allRows = await fetchAllSimReportRows();
    if (!allRows) return;
    try {
      const { doc, layout } = newLandscapeDoc();
      const logo = await loadLogoPng();
      const y = addBrandHeader(doc, layout, logo, `SIM Health Report — ${SIM_REPORT_TITLES[simReportTab]}`,
        `${allRows.length} row(s)  ·  ${new Date().toLocaleString()}`);
      const { columns, rows } = buildSimReportTable(allRows);
      drawPdfTable(doc, layout, y, columns, rows);
      addFooters(doc, layout);
      doc.save(`sim-health-${simReportTab}-${getFileTimestamp()}.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const exportSimReportHtml = async () => {
    const allRows = await fetchAllSimReportRows();
    if (!allRows) return;
    const { columns, rows } = buildSimReportTable(allRows);
    await exportTableHtml(`SIM Health Report — ${SIM_REPORT_TITLES[simReportTab]}`,
      `${allRows.length} row(s)`,
      columns.map(c => c.label), rows, `sim-health-${simReportTab}-${getFileTimestamp()}.html`);
    toast.success('HTML downloaded');
  };

  // Server-side type-ahead for the "Linked Devices" picker in the Edit Device dialog. Instead
  // of pre-loading ALL devices (previous behavior: /admin/devices?limit=1000 on every dialog
  // open — kilobytes and a full page of overhead at 1000+ device tenants), we now fetch a
  // small result set (limit=25) based on the user's search term. Called on-demand from the
  // Popover as they type. Empty term returns the first 25 devices A→Z so the popover shows
  // something immediately.
  const [linkableDevicesLoading, setLinkableDevicesLoading] = useState(false);
  const linkableSearchAbortRef = React.useRef(null);
  const searchLinkableDevices = useCallback(async (term = '') => {
    if (linkableSearchAbortRef.current) {
      try { linkableSearchAbortRef.current.abort(); } catch { /* ignore */ }
    }
    const controller = new AbortController();
    linkableSearchAbortRef.current = controller;
    setLinkableDevicesLoading(true);
    try {
      const trimmed = String(term || '').trim();
      const url = trimmed
        ? `/admin/devices?search=${encodeURIComponent(trimmed)}&limit=25&sort=name&sort_dir=asc`
        : `/admin/devices?limit=25&sort=name&sort_dir=asc`;
      const response = await api.get(url, { signal: controller.signal });
      const results = response.data || [];
      setLinkableDevices(results);
      return results;
    } catch (error) {
      // Ignore aborts from newer keystrokes overriding an in-flight request.
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return [];
      console.error('Failed to search linkable devices:', error);
      return [];
    } finally {
      setLinkableDevicesLoading(false);
    }
  }, [api]);

  // Backwards-compat shim: some callers still expect a Promise<Device[]>. Return the fresh
  // result of a top-25 search so ``openEditDialog`` can compare its linked_devices against it.
  const fetchLinkableDevices = useCallback(async () => {
    return await searchLinkableDevices('');
  }, [searchLinkableDevices]);
  
  const fetchSimProviders = useCallback(async () => {
    try {
      const response = await api.get('/admin/sim-providers');
      setSimProviders(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Failed to fetch SIM providers:', error);
    }
  }, [api]);
  
  const fetchUserMap = useCallback(async () => {
    try {
      const res = await api.get('/users/names');
      const map = {};
      (res.data || []).forEach(u => { map[u.id] = { name: u.full_name, email: u.email, role: u.role }; });
      setUserMap(map);
    } catch {}
  }, [api]);

  // Custom Icons fetch
  const fetchCustomIcons = useCallback(async () => {
    setCustomIconsLoading(true);
    try {
      const res = await api.get('/custom-icons');
      setCustomIcons(res.data || []);
    } catch (err) {
      console.error('Failed to fetch custom icons:', err);
    } finally {
      setCustomIconsLoading(false);
    }
  }, [api]);

  const handleIconUpload = async () => {
    if (!iconUploadFile || !iconUploadName.trim()) {
      toast.error('Please provide a name and select a file');
      return;
    }
    setIconUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', iconUploadFile);
      const res = await api.post(`/custom-icons/upload?name=${encodeURIComponent(iconUploadName.trim())}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Icon "${res.data.name}" uploaded`);
      setIconUploadFile(null);
      setIconUploadName('');
      setShowIconUpload(false);
      fetchCustomIcons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setIconUploading(false);
    }
  };

  const handleIconDelete = async (iconId, iconName) => {
    setIconDeleting(iconId);
    try {
      const res = await api.delete(`/custom-icons/${iconId}`);
      toast.success(`Icon "${iconName}" deleted${res.data.devices_reset > 0 ? ` (${res.data.devices_reset} device${res.data.devices_reset > 1 ? 's' : ''} reset to default)` : ''}`);
      fetchCustomIcons();
      fetchDevices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setIconDeleting(null);
    }
  };

  const handleIconRename = async (iconId) => {
    const trimmed = iconEditingName.trim();
    if (!trimmed) return;
    try {
      await api.patch(`/custom-icons/${iconId}`, { name: trimmed });
      toast.success('Icon renamed');
      setIconEditingId(null);
      fetchCustomIcons();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Rename failed');
    }
  };

  const handleBulkFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const items = files.filter(f => f.type === 'image/png' || f.type === 'image/svg+xml').map(f => ({
      file: f,
      name: f.name.replace(/\.(png|svg)$/i, '').replace(/[_-]/g, ' ').trim(),
      preview: URL.createObjectURL(f),
    }));
    if (items.length === 0) { toast.error('No valid PNG or SVG files selected'); return; }
    setBulkUploadFiles(items);
    setShowBulkUpload(true);
    e.target.value = '';
  };

  const handleBulkUpload = async () => {
    const valid = bulkUploadFiles.filter(f => f.name.trim());
    if (valid.length === 0) return;
    setBulkUploading(true);
    setBulkUploadProgress(0);
    let uploaded = 0;
    let failed = 0;
    for (let i = 0; i < valid.length; i++) {
      try {
        const fd = new FormData();
        fd.append('file', valid[i].file);
        await api.post(`/custom-icons/upload?name=${encodeURIComponent(valid[i].name.trim())}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploaded++;
      } catch (err) {
        failed++;
        console.error(`Failed to upload ${valid[i].name}:`, err?.response?.data?.detail || err);
      }
      setBulkUploadProgress(i + 1);
    }
    setBulkUploading(false);
    setBulkUploadFiles([]);
    setShowBulkUpload(false);
    fetchCustomIcons();
    if (failed > 0) {
      toast.warning(`Uploaded ${uploaded} icon${uploaded !== 1 ? 's' : ''}, ${failed} failed`);
    } else {
      toast.success(`Uploaded ${uploaded} icon${uploaded !== 1 ? 's' : ''}`);
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchSimProviders();
    fetchUserMap();
    fetchCustomIcons();
  }, [api, fetchDevices, fetchSimProviders, fetchUserMap, fetchCustomIcons]);

  // Latest undoable device import run (30-minute window) for the current admin
  useEffect(() => {
    let cancelled = false;
    api.get('/admin/devices/import-runs/latest')
      .then(res => { if (!cancelled && res.data?.run) setLastImportRun(res.data.run); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  const handleUndoDeviceImport = async () => {
    if (!lastImportRun || undoingImport) return;
    setUndoingImport(true);
    try {
      const res = await api.post(`/admin/devices/import-runs/${lastImportRun.id}/undo`);
      const { deleted = 0, errors: undoErrors = [] } = res.data || {};
      if (undoErrors.length > 0) {
        toast.warning(`Import undone with issues — ${deleted} device(s) removed; ${undoErrors.length} issue(s): ${undoErrors.map(e => e.message).slice(0, 2).join('; ')}`);
      } else {
        toast.success(`Import undone — ${deleted} device(s) removed`);
      }
      setLastImportRun(null);
      fetchDevices();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to undo import');
      // Expired or already undone — the banner is stale, drop it.
      if (error?.response?.status === 400) setLastImportRun(null);
    } finally {
      setUndoingImport(false);
    }
  };

  // Reset to page 0 whenever any server-side filter/pageSize changes (otherwise the user
  // might land on a page that no longer exists after narrowing the result set). Sort changes
  // batch pageIndex reset directly inside handleDeviceSort so both state updates commit in
  // one render — see above. Skips mount so ?page= deep-links survive.
  useSkipFirstEffect(() => {
    setPageIndex(0);
  }, [searchTerm, protocolFilter, modelFilter, statusFilter, isActiveFilter, simHealthFilter, pageSize]);

  // Debounced server-side type-ahead for the "Linked Devices" picker. Fires only while the
  // popover is open — no wasted requests when the dialog isn't visible. When the popover
  // just opened, the debounce is short (30 ms) so the initial results appear immediately;
  // subsequent keystrokes use the normal 220 ms debounce.
  useEffect(() => {
    if (!linkedDeviceSearchOpen) return;
    const delay = linkedDeviceSearchTerm ? 220 : 30;
    const handle = setTimeout(() => {
      searchLinkableDevices(linkedDeviceSearchTerm || '');
    }, delay);
    return () => clearTimeout(handle);
  }, [linkedDeviceSearchTerm, linkedDeviceSearchOpen, searchLinkableDevices]);

  // iter151 follow-up — Deep-link support for `?device_id=<id>` so the
  // Device Health card (and any future link source) can navigate
  // straight into a device's edit dialog. Runs once after the device
  // list loads.  Self-clears the URL param so a subsequent in-page
  // navigation doesn't re-open the dialog.
  //
  // Note: `openEditDialog` is intentionally NOT in the deps array — it
  // is declared further down in this file (after this hook) and adding
  // it would trigger a temporal-dead-zone "Cannot access X before
  // initialization" crash during the first render.  Closures still
  // capture it correctly at call-time.
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);
  useEffect(() => {
    if (deepLinkHandled || !devices || devices.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('device_id');
    if (!targetId) { setDeepLinkHandled(true); return; }
    const target = devices.find(d => d.id === targetId);
    if (target) {
      setIsEditDialogOpen(true);
      openEditDialog(target);
      params.delete('device_id');
      const newQs = params.toString();
      const newUrl = `${window.location.pathname}${newQs ? `?${newQs}` : ''}${window.location.hash}`;
      window.history.replaceState(null, '', newUrl);
    }
    setDeepLinkHandled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, deepLinkHandled]);
  
  const fetchAuditLogs = useCallback(async (deviceId, search, dateRange) => {
    if (!deviceId) return;
    setAuditLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (search) params.set('search', search);
      if (dateRange?.from) params.set('from_date', dateRange.from.toISOString());
      if (dateRange?.to) {
                params.set('to_date', dateRange.to.toISOString());
      }
      const response = await api.get(`/admin/devices/${deviceId}/audit?${params}`);
      setAuditLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      setAuditLogs([]);
    } finally {
      setAuditLogsLoading(false);
    }
  }, [api]);

  // Export device audit logs to CSV
  const handleExportDeviceAudit = () => {
    if (auditLogs.length === 0) { toast.info('No entries to export'); return; }
    const fmtTs = (t) => formatUserDateTime(t, userDateFmt, userTimeFmt, userTimezone);
    const headers = ['Time', 'Action', 'Actor', 'IP Address', 'Details', 'Changes'];
    const rows = auditLogs.map(l => [
      fmtTs(l.timestamp || l.created_at),
      l.action?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '',
      l.actor_email || '',
      l.ip_address || '',
      l.details || '',
      l.changes ? Object.entries(l.changes).filter(([k]) => k !== 'updated_at').map(([k, v]) => {
        if (typeof v === 'object' && v !== null && ('old' in v || 'new' in v)) return `${k}: ${v.old ?? ''} → ${v.new ?? ''}`;
        return `${k}: ${v}`;
      }).join('; ') : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const ts = getFileTimestamp();
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `device_audit_${selectedDevice?.name || 'unknown'}_${ts}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${auditLogs.length} entry(ies)`);
  };
  
  // Fetch available users for allocation
  const fetchAllocatableUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/allocatable-users');
      setAllocatableUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch allocatable users:', error);
      setAllocatableUsers([]);
    }
  }, [api]);
  
  // Fetch current allocation for a device
  const fetchDeviceAllocation = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setAllocationLoading(true);
    try {
      const response = await api.get(`/admin/devices/${deviceId}/allocation`);
      const ids = (response.data.allocated_users || []).map(u => u.id);
      setAllocatedUserIds(ids);
      setOriginalAllocatedUserIds(ids);
      const primaryId = response.data.primary_operational_account_id || '';
      setPrimaryOperationalAccountId(primaryId);
      setOriginalPrimaryOperationalAccountId(primaryId);
    } catch (error) {
      console.error('Failed to fetch device allocation:', error);
      setAllocatedUserIds([]);
      setOriginalAllocatedUserIds([]);
      setPrimaryOperationalAccountId('');
      setOriginalPrimaryOperationalAccountId('');
    } finally {
      setAllocationLoading(false);
    }
  }, [api]);
  
  // Save allocation changes
  const handleSaveAllocation = async (deviceId, confirmRemoveAll = false) => {
    if (!deviceId) return;
    // If the save would REMOVE any allocated user(s) and not yet confirmed, ask first.
    const removedUsers = originalAllocatedUserIds.filter(id => !allocatedUserIds.includes(id));
    if (removedUsers.length > 0 && !confirmRemoveAll) {
      setRemovedUserCount(removedUsers.length);
      setRemoveAllUsersContext('allocation');
      setShowRemoveAllUsersConfirm(true);
      return;
    }
    setAllocationSaving(true);
    try {
      const response = await api.put(`/admin/devices/${deviceId}/allocation`, {
        allocated_user_ids: allocatedUserIds,
        primary_operational_account_id: primaryOperationalAccountId || null,
        confirm_remove_all: allocatedUserIds.length === 0,
      });
      toast.success('Allocation saved successfully');
      setOriginalAllocatedUserIds([...allocatedUserIds]);
      const savedPrimaryId = response.data.primary_operational_account_id || '';
      setPrimaryOperationalAccountId(savedPrimaryId);
      setOriginalPrimaryOperationalAccountId(savedPrimaryId);
      fetchAllocatableUsers();
      if (selectedDevice?.id) fetchAuditLogs(selectedDevice.id);
    } catch (error) {
      console.error('Failed to save allocation:', error);
      toast.error(error.response?.data?.detail || 'Failed to save allocation');
    } finally {
      setAllocationSaving(false);
    }
  };

  // Confirm a de-allocation (partial or full) for either the dedicated Save Allocation
  // button ('allocation') or the main Save Changes flow ('save').
  const handleConfirmRemoveAllUsers = async () => {
    setShowRemoveAllUsersConfirm(false);
    const ctx = removeAllUsersContext;
    setRemoveAllUsersContext(null);
    if (ctx === 'allocation') {
      await handleSaveAllocation(selectedDevice?.id, true);
    } else if (ctx === 'save') {
      // Complete the main "Save Changes" allocation sync (the device fields were already
      // saved before the prompt). Send the ACTUAL selected users — NOT a hardcoded empty
      // list — so a partial removal removes only the de-selected users.
      try {
        await api.put(`/admin/devices/${selectedDevice.id}/allocation`, {
          allocated_user_ids: allocatedUserIds,
          primary_operational_account_id: primaryOperationalAccountId || null,
          confirm_remove_all: allocatedUserIds.length === 0,
        });
        setOriginalAllocatedUserIds([...allocatedUserIds]);
        setOriginalPrimaryOperationalAccountId(primaryOperationalAccountId);
        toast.success('Device updated successfully');
        fetchDevices();
        setSavedFormSnapshot(null);
        setShowUnsavedWarning(false);
        setIsEditDialogOpen(false);
        resetForm();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to update allocation');
      }
    }
  };

  // Fetch device live status from parsing server
  const fetchDeviceStatus = useCallback(async (imei) => {
    if (!imei) return;
    setStatusLoading(true);
    try {
      const response = await api.get(`/parsing/devices/${imei}/status`);
      setDeviceStatus(response.data);
      // Sync fresh status back to device list to prevent stale display
      const freshStatus = response.data?.device?.status;
      if (freshStatus) {
        setDevices(prev => prev.map(d => d.imei === imei ? { ...d, status: freshStatus } : d));
      }
    } catch (error) {
      console.error('Failed to fetch device status:', error);
      setDeviceStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [api]);

  const fetchLatestIoElements = useCallback(async (imei) => {
    if (!imei) return;
    setLatestIoLoading(true);
    try {
      const response = await api.get(`/parsing/devices/${imei}/status`);
      setDeviceStatus(prev => {
        if (!prev) return response.data;
        return {
          ...prev,
          io_elements: response.data?.io_elements || prev.io_elements,
        };
      });
    } catch (error) {
      console.error('Failed to refresh latest IO elements:', error);
      toast.error('Failed to refresh latest IO elements');
    } finally {
      setLatestIoLoading(false);
    }
  }, [api]);

  const fetchRfidScanEvents = useCallback(async (imei, { page, pageSize, start, end } = {}) => {
    if (!imei) return;
    setRfidEventsLoading(true);
    try {
      const p = new URLSearchParams();
      const size = pageSize ?? rfidEventsPageSize;
      p.set('skip', String(((page ?? rfidEventsPage) - 1) * size));
      p.set('limit', String(size));
      const s = start ?? rfidEventsStart; const e = end ?? rfidEventsEnd;
      if (s) p.set('start', s);
      if (e) p.set('end', e);
      if (rfidEventsSearchDebounced.trim()) p.set('search', rfidEventsSearchDebounced.trim());
      if (rfidEventsMatch !== 'all') p.set('matched', rfidEventsMatch);
      const r = await api.get(`/parsing/devices/${imei}/rfid-scan-events?${p.toString()}`);
      setRfidEvents(r.data || []);
      setRfidEventsTotal(parseInt(r.headers['x-total-count'] || '0', 10));
    } catch (error) {
      console.error('Failed to fetch RFID scan events:', error);
    } finally {
      setRfidEventsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, rfidEventsPage, rfidEventsPageSize, rfidEventsStart, rfidEventsEnd, rfidEventsSearchDebounced, rfidEventsMatch]);

  const startRfidBackfill = useCallback(async (imei) => {
    try {
      await api.post(`/parsing/devices/${imei}/rfid-scan-events/backfill`);
      setRfidBackfillRunning(true);
      toast.success('History rebuild started — scanning stored packets…');
      const poll = async () => {
        try {
          const r = await api.get(`/parsing/devices/${imei}/rfid-scan-events/backfill-status`);
          if (r.data?.status === 'running') { setTimeout(poll, 2000); return; }
          setRfidBackfillRunning(false);
          if (r.data?.status === 'done') {
            toast.success(`History rebuilt — ${r.data.events_built} scans from ${r.data.packets_scanned} packets`);
            fetchRfidScanEvents(imei, { page: 1 });
            setRfidEventsPage(1);
          } else if (r.data?.status === 'failed') {
            toast.error(`History rebuild failed: ${r.data.error || 'unknown error'}`);
          }
        } catch { setRfidBackfillRunning(false); }
      };
      setTimeout(poll, 2000);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to start history rebuild');
    }
  }, [api, fetchRfidScanEvents]);

  useEffect(() => {
    if (selectedDevice?.imei && deviceStatus?.identity_scan?.configured) {
      fetchRfidScanEvents(selectedDevice.imei);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.imei, deviceStatus?.identity_scan?.configured, rfidEventsPage, rfidEventsPageSize, rfidEventsStart, rfidEventsEnd, rfidEventsSearchDebounced, rfidEventsMatch]);

  // Backfill coverage info — what date range the last rebuild actually covered.
  useEffect(() => {
    if (!(selectedDevice?.imei && deviceStatus?.identity_scan?.configured)) { setRfidBackfillInfo(null); return; }
    let cancelled = false;
    api.get(`/parsing/devices/${selectedDevice.imei}/rfid-scan-events/backfill-status`)
      .then((r) => { if (!cancelled) setRfidBackfillInfo(r.data?.status === 'done' ? r.data : null); })
      .catch(() => { if (!cancelled) setRfidBackfillInfo(null); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice?.imei, deviceStatus?.identity_scan?.configured, rfidBackfillRunning]);

  const RFID_EXPORT_HEADERS = ['Time', 'IO ID', 'IO Name', 'Parsed Credential', 'Status', 'Resolved Type', 'Resolved Name', 'Packets', 'Last Seen', 'Source'];

  const fetchRfidExportRows = useCallback(async () => {
    const p = new URLSearchParams();
    p.set('skip', '0');
    p.set('limit', '5000');
    if (rfidEventsStart) p.set('start', rfidEventsStart);
    if (rfidEventsEnd) p.set('end', rfidEventsEnd);
    if (rfidEventsSearchDebounced.trim()) p.set('search', rfidEventsSearchDebounced.trim());
    if (rfidEventsMatch !== 'all') p.set('matched', rfidEventsMatch);
    const r = await api.get(`/parsing/devices/${selectedDevice?.imei}/rfid-scan-events?${p.toString()}`);
    const events = r.data || [];
    if (!events.length) { toast.error('No saved scans to export'); return null; }
    const fmt = (t) => formatUserDateTime(t, userDateFmt, userTimeFmt, userTimezone, { showSeconds: true });
    return events.map((e) => [
      fmt(e.timestamp), e.io_id, e.display_name || '', e.credential_identifier || '',
      e.matched ? 'Matched' : 'Unmatched', e.resolved_entity?.person_type || '',
      e.resolved_entity?.person_name || '', e.repeat_count || 1, fmt(e.last_seen_at), e.source || 'live',
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, selectedDevice?.imei, rfidEventsStart, rfidEventsEnd, rfidEventsSearchDebounced, rfidEventsMatch, userDateFmt, userTimeFmt, userTimezone]);

  const handleExportRfidCsv = useCallback(async () => {
    const rows = await fetchRfidExportRows();
    if (!rows) return;
    downloadCsv(`rfid_scans_${selectedDevice?.imei}_${getFileTimestamp()}.csv`, RFID_EXPORT_HEADERS, rows);
    toast.success(`Exported ${rows.length} scan(s)`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRfidExportRows, selectedDevice?.imei]);

  const handleExportRfidXlsx = useCallback(async () => {
    const rows = await fetchRfidExportRows();
    if (!rows) return;
    const aoa = [RFID_EXPORT_HEADERS, ...rows.map((r) => r.map((v) => `${v ?? ''}`))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = RFID_EXPORT_HEADERS.map((h) => ({ wch: Math.min(Math.max(h.length + 2, 12), 30) }));
    // Force credential column to Text so Excel never mangles long hex/numeric IDs.
    const credIdx = RFID_EXPORT_HEADERS.indexOf('Parsed Credential');
    for (let r = 1; r < aoa.length; r++) {
      const addr = XLSX.utils.encode_cell({ r, c: credIdx });
      if (ws[addr]) { ws[addr].t = 's'; ws[addr].z = '@'; ws[addr].v = String(ws[addr].v ?? ''); }
    }
    ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RFID Scans');
    XLSX.writeFile(wb, `rfid_scans_${selectedDevice?.imei}_${getFileTimestamp()}.xlsx`);
    toast.success(`Exported ${rows.length} scan(s)`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRfidExportRows, selectedDevice?.imei]);

  const fetchCommandHistory = useCallback(async (imei) => {
    if (!imei) return;
    setCommandHistoryLoading(true);
    try {
      const response = await api.get(`/parsing/commands/${imei}?limit=20`);
      setCommandHistory(response.data.commands || []);
    } catch (error) {
      console.error('Failed to fetch command history:', error);
      setCommandHistory([]);
    } finally {
      setCommandHistoryLoading(false);
    }
  }, [api]);

  // Live 1s tick for queued-command expiry countdowns in the Command History card
  useEffect(() => {
    if (!commandHistory.some(c => c.status === 'queued')) return;
    const t = setInterval(() => setCmdNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [commandHistory]);

  const fetchBeaconStatus = useCallback(async (macOrImei) => {
    if (!macOrImei) return;
    setBeaconStatusLoading(true);
    try {
      const response = await api.get(`/parsing/beacons/${encodeURIComponent(macOrImei)}/status`);
      setBeaconStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch beacon status:', error);
      setBeaconStatus(null);
    } finally {
      setBeaconStatusLoading(false);
    }
  }, [api]);

  const fetchBeaconTelemetry = useCallback(async (deviceId) => {
    if (!deviceId) return;
    setBeaconTelemetryLoading(true);
    try {
      const response = await api.get(`/parsing/beacons/${encodeURIComponent(deviceId)}/telemetry`);
      setBeaconTelemetry(response.data);
    } catch (error) {
      console.error('Failed to fetch beacon telemetry:', error);
      setBeaconTelemetry(null);
    } finally {
      setBeaconTelemetryLoading(false);
    }
  }, [api]);

  const refreshSelectedBeaconStatus = useCallback((device = selectedDevice) => {
    if (!device?.id) return;
    fetchBeaconTelemetry(device.id);
    fetchBeaconStatus(device.id);
  }, [fetchBeaconStatus, fetchBeaconTelemetry, selectedDevice]);

  const fetchProtocolIODefaults = useCallback(async (protocol, imei) => {
    if (!protocol) return;
    try {
      const url = imei 
        ? `/parsing/protocol-io-defaults/${encodeURIComponent(protocol)}?imei=${encodeURIComponent(imei)}`
        : `/parsing/protocol-io-defaults/${encodeURIComponent(protocol)}`;
      const response = await api.get(url);
      setProtocolIODefaults(response.data.io_elements || []);
    } catch (error) {
      setProtocolIODefaults([]);
    }
  }, [api]);

  const fetchCurrentIOValues = useCallback(async (imei) => {
    if (!imei) return;
    try {
      const response = await api.get(`/parsing/devices/${imei}/current-io`);
      const raw = response.data.io_elements || {};
      // Backend already flattens with dot notation (e.g. analog.internal_battery_mv)
      setCurrentIOValues(raw);
    } catch (error) {
      setCurrentIOValues({});
    }
  }, [api]);
  
  // Toggle user in allocation
  const handleToggleUserAllocation = (userId) => {
    setFormDirty(true);
    setAllocatedUserIds(prev => {
      const next = prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId];
      setPrimaryOperationalAccountId(current => {
        if (next.length === 1) return next[0];
        return next.includes(current) ? current : '';
      });
      return next;
    });
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      device_type: 'gps_tracker',
      imei: '',
      serial_number: '',
      mac_address: '',
      protocol: '',
      model: '',
      firmware_version: '',
      hardware_version: '',
      device_password: '',
      has_video: false,
      num_cameras: 0,
      channel_names: {},
      always_on_streaming: false,
      sim_iccid: '',
      sim_number: '',
      sim_provider: '',
      installation_date: '',
      installation_date_display: '',
      installation_type: '',
      odometer_source: 'gps',
      engine_hours_source: 'off',
      fuel_source: 'off',
      max_speed_kmh: 200,
      server_ip_1: '',
      server_port_1: '',
      server_ip_2: '',
      server_port_2: '',
      show_server_2: false,
      reporting_interval_moving: '',
      reporting_interval_stopped: '',
      apn: '',
      beacon_type: '',
      beacon_purpose: 'asset',
      beacon_uuid: '',
      beacon_major: '',
      beacon_minor: '',
      beacon_eid: '',
      beacon_namespace: '',
      beacon_instance_id: '',
      advertising_interval: '',
      tx_power: '',
      connected_peripherals: [],
      backup_device_ids: [],
    linked_devices: [],
    timezone_override_enabled: true,
    timezone_override: 'auto',
    offline_threshold_secs: 600,
      notes: '',
      asset_type: 'car',
      asset_name: '',
      asset_make: '',
      asset_model: '',
      asset_year: '',
      registration: '',
      vin_number: '',
      rfid_ibutton_number: '',
      identity_method: 'ble',
      fuel_type: '',
      odometer_reading: 0,
      engine_hours: 0,
      tags: [],
      asset_editing_enabled: true,
      allocated_tenant_ids: [],
      management_admin_ids: [],
      custom_commands: [],
      io_mappings: [],
      identity_logbook: { ...DEFAULT_IDENTITY_LOGBOOK },
      device_icon: 'car',
      device_icon_color: null,
      map_marker_mode: 'arrow',
      icon_editing_enabled: true,
      is_active: true,
    });
    setActiveTab('details');
    setAuditLogs([]);
    setAuditSearch('');
    setAuditDateRange({ from: undefined, to: undefined });
    setSelectedDevice(null);
    setPrimaryOperationalAccountId('');
    setOriginalPrimaryOperationalAccountId('');
    setTagInput('');
    setShowPeripheralForm(false);
    setSimAutoPopulate(true);
    setNewCommand(getDefaultCommand());
    setEditingCommandId(null);
    setNewIOMapping({
      io_type: 'Battery', io_id: '', display_name: '', value_type: 'Value',
      formula: '', units: '', decimal_places: 2, logic_mapping: { "1": "ON", "0": "OFF" },
      dictionary: {}, calibration: [], data_source: 'device_io', show_telemetry: true, show_fleet_card: true,
      show_map_popup: false, show_icon: true, ignore_ignition_off: false,
      secondary_io_id: null, multi_param_mode: 'lookup', multi_param_separator: null, multi_param_dictionary: {}, multi_param_template: '',
    });
    setEditingMappingId(null);
    setHistoryFromDate('');
    setHistoryToDate('');
    setFormDirty(false);
  };

  const hasUnsavedChanges = () => {
    return formDirty;
  };

  const handleEditDialogClose = (forceClose = false) => {
    if (!forceClose && hasUnsavedChanges()) {
      setShowUnsavedWarning(true);
      return;
    }
    setIsEditDialogOpen(false);
    resetForm();
    setSavedFormSnapshot(null);
    setShowUnsavedWarning(false);
    setFormDirty(false);
  };

  const handleAddDialogClose = (forceClose = false) => {
    if (!forceClose && formDirty) {
      setShowAddUnsavedWarning(true);
      return;
    }
    setIsAddDialogOpen(false);
    resetForm();
    setShowAddUnsavedWarning(false);
    setFormDirty(false);
  };

  // Wrap setters to auto-mark form as dirty
  const setNewCommandDirty = (updater) => {
    setFormDirty(true);
    setNewCommand(typeof updater === 'function' ? updater : prev => ({ ...prev, ...updater }));
  };
  const setNewIOMappingDirty = (updater) => {
    setFormDirty(true);
    setNewIOMapping(typeof updater === 'function' ? updater : prev => ({ ...prev, ...updater }));
  };

  // Track changes to temporary form states (command form, IO mapping form)
  // These don't go through handleInputChange so we need to detect them separately
  const newCommandRef = React.useRef(null);
  const newIOMappingRef = React.useRef(null);
  const commandFormCardRef = React.useRef(null);
  const ioMappingFormCardRef = React.useRef(null);
  const scrollFormCardIntoView = (ref) => {
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  React.useEffect(() => {
    if (!isEditDialogOpen && !isAddDialogOpen) { newCommandRef.current = null; return; }
    if (newCommandRef.current === null) { newCommandRef.current = newCommand; return; }
    if (newCommandRef.current !== newCommand && (newCommand.button_label || newCommand.command_text)) {
      setFormDirty(true);
    }
    newCommandRef.current = newCommand;
  }, [newCommand, isEditDialogOpen, isAddDialogOpen]);
  React.useEffect(() => {
    if (!isEditDialogOpen && !isAddDialogOpen) { newIOMappingRef.current = null; return; }
    if (newIOMappingRef.current === null) { newIOMappingRef.current = newIOMapping; return; }
    if (newIOMappingRef.current !== newIOMapping && newIOMapping.io_id && newIOMapping.io_id !== '__placeholder' && newIOMapping.io_id !== '__custom') {
      setFormDirty(true);
    }
    newIOMappingRef.current = newIOMapping;
  }, [newIOMapping, isEditDialogOpen, isAddDialogOpen]);

  const handleInputChange = (field, value) => {
    if (isEditDialogOpen || isAddDialogOpen) setFormDirty(true);
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'beacon_purpose' && prev.device_type === 'beacon') {
        updated.asset_type = value === 'personal' ? 'people' : (prev.asset_type === 'people' ? 'equipment' : prev.asset_type);
      }
      // When switching device type, auto-set appropriate asset_type default
      if (field === 'device_type') {
        if (value === 'beacon') {
          if (!beaconAssetTypes.some(t => t.value === prev.asset_type)) {
            updated.asset_type = 'people';
          }
          updated.protocol = 'BLE Beacon';
          updated.engine_hours_source = 'off';
        } else if (value !== 'beacon') {
          if (!assetTypes.some(t => t.value === prev.asset_type)) {
            updated.asset_type = 'car';
          }
          if (prev.protocol === 'BLE Beacon') {
            updated.protocol = '';
          }
          // Auto-set engine hours to off for device types without engines
          if (value === 'personal_tracker' || value === 'asset_tracker') {
            updated.engine_hours_source = 'off';
          }
        }
      }
      // Auto-follow the device icon from the asset type when the icon hasn't been manually
      // chosen. "Auto" = no icon yet, or the icon still matches the previous asset type's default.
      if (updated.asset_type !== prev.asset_type) {
        const wasAuto = !prev.device_icon || prev.device_icon === assetTypeIcon(prev.asset_type);
        if (wasAuto) {
          updated.device_icon = assetTypeIcon(updated.asset_type);
        }
      }
      return updated;
    });
  };

  const openBeaconDiscover = async () => {
    setShowBeaconDiscover(true);
    setDiscoverLoading(true);
    setDiscoverError('');
    try {
      const res = await api.get('/parsing/beacons/discover-unregistered?limit=200');
      setDiscoverCandidates(res.data?.candidates || []);
    } catch (e) {
      setDiscoverError(e?.response?.data?.detail || 'Failed to load recent BLE scans');
      setDiscoverCandidates([]);
    } finally {
      setDiscoverLoading(false);
    }
  };

  // Open the Add Device modal pre-configured as a BLE Beacon, with all identifier fields
  // pre-filled from a candidate selected on the Beacon Management → Auto-detect list. The user
  // only needs to give it a name and Save.
  const openAddBeaconWithCandidate = (cand) => {
    resetForm();
    setAllocatedUserIds([]);
    fetchAllocatableUsers();
    fetchManageableAdmins();
    setIsAddDialogOpen(true);
    setFormData(prev => {
      const u = {
        ...prev,
        device_type: 'beacon',
        protocol: 'BLE Beacon',
        engine_hours_source: 'off',
        beacon_type: cand?.beacon_type || 'vendor_specific',
      };
      if (!beaconAssetTypes.some(t => t.value === prev.asset_type)) u.asset_type = 'people';
      if (cand?.beacon_type === 'ibeacon') {
        u.beacon_uuid = cand.beacon_uuid || '';
        u.beacon_major = cand.beacon_major || '';
        u.beacon_minor = cand.beacon_minor || '';
      } else if (cand?.beacon_type === 'eddystone') {
        u.beacon_namespace = cand.beacon_namespace || '';
        u.beacon_instance_id = cand.beacon_instance_id || '';
      } else {
        u.mac_address = cand?.mac_address || '';
      }
      return u;
    });
    setBeaconIdLocked(false);
    setMacLocked(false);
    setFormDirty(true);
    toast.success('Beacon details pre-filled from scan — review and Save');
  };

  // Open the Add Device modal pre-configured as a BLE Beacon (no specific candidate).
  const openAddBeaconBlank = () => {
    resetForm();
    setAllocatedUserIds([]);
    fetchAllocatableUsers();
    fetchManageableAdmins();
    setFormDirty(false);
    setIsAddDialogOpen(true);
    setFormData(prev => {
      const u = { ...prev, device_type: 'beacon', protocol: 'BLE Beacon', engine_hours_source: 'off' };
      if (!beaconAssetTypes.some(t => t.value === prev.asset_type)) u.asset_type = 'people';
      return u;
    });
  };

  // Consume a cross-tab deep-link action from Beacon Management → "Register": open the Add Device
  // modal pre-configured as a BLE Beacon. With a candidate → pre-fill identifiers; without one →
  // open the in-modal Auto-detect picker.
  useEffect(() => {
    if (!pendingDeviceAction || pendingDeviceAction.action !== 'add-beacon') return;
    if (!canAddDevices) { setPendingDeviceAction(null); return; }
    if (pendingDeviceAction.candidate) {
      openAddBeaconWithCandidate(pendingDeviceAction.candidate);
    } else {
      openAddBeaconBlank();
      if (pendingDeviceAction.openDiscover) openBeaconDiscover();
    }
    setPendingDeviceAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeviceAction]);

  const applyBeaconCandidate = (cand) => {
    setFormDirty(true);
    setFormData(prev => {
      const u = { ...prev, beacon_type: cand.beacon_type };
      if (cand.beacon_type === 'ibeacon') {
        u.beacon_uuid = cand.beacon_uuid || '';
        u.beacon_major = cand.beacon_major || '';
        u.beacon_minor = cand.beacon_minor || '';
      } else if (cand.beacon_type === 'eddystone') {
        u.beacon_namespace = cand.beacon_namespace || '';
        u.beacon_instance_id = cand.beacon_instance_id || '';
      } else if (cand.beacon_type === 'vendor_specific') {
        u.mac_address = cand.mac_address || '';
      }
      return u;
    });
    // Unlock identity fields so the auto-filled values are editable on an existing device.
    setBeaconIdLocked(false);
    setMacLocked(false);
    setShowBeaconDiscover(false);
    toast.success('Beacon fields auto-filled from recent scan');
  };

  const copyBeaconField = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const getDiscoverDetectorOptions = () => {
    const names = new Set();
    discoverCandidates.forEach(c => (c.detected_by || []).forEach(d => { if (d.name) names.add(d.name); }));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  };

  const getFilteredDiscoverCandidates = () => {
    const q = discoverSearch.trim().toLowerCase();
    const minScans = parseInt(discoverMinScans, 10);
    const rssiThreshold = discoverMinRssi === 'any' ? null : parseInt(discoverMinRssi, 10);
    const list = discoverCandidates.filter(c => {
      if (discoverTypeFilter !== 'all' && c.beacon_type !== discoverTypeFilter) return false;
      if (discoverDetectorFilter !== 'all' && !(c.detected_by || []).some(d => d.name === discoverDetectorFilter)) return false;
      if (rssiThreshold !== null && !(typeof c.best_rssi === 'number' && c.best_rssi >= rssiThreshold)) return false;
      if (!isNaN(minScans) && minScans > 0 && (c.scan_count || 0) < minScans) return false;
      if (q) {
        const hay = `${c.identifier || ''} ${(c.detected_by || []).map(d => d.name).join(' ')}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return list.sort((a, b) => {
      if (discoverSort === 'strength') return (b.best_rssi ?? -999) - (a.best_rssi ?? -999);
      if (discoverSort === 'scans') return (b.scan_count || 0) - (a.scan_count || 0);
      return (b.last_seen || '').localeCompare(a.last_seen || '');
    });
  };


  const openEditDialog = async (device) => {
    setSelectedDevice(device);
    // Clear previous device's cached data
    setDeviceStatus(null);
    setBeaconStatus(null);
    setBeaconTelemetry(null);
    // Reset to valid tab if current tab doesn't apply to this device type
    const beaconOnlyTabs = ['beacon_status'];
    const gpsOnlyTabs = ['status', 'commands', 'mapping'];
    let nextActiveTab = activeTab;
    if (device.device_type === 'beacon' && gpsOnlyTabs.includes(activeTab)) {
      nextActiveTab = 'beacon_status';
      setActiveTab(nextActiveTab);
    } else if (device.device_type !== 'beacon' && beaconOnlyTabs.includes(activeTab)) {
      nextActiveTab = 'details';
      setActiveTab(nextActiveTab);
    }
    // Fetch fresh device data from backend (another user may have edited it)
    let freshDevice = device;
    try {
      const res = await api.get(`/admin/devices/${device.id}`);
      freshDevice = res.data;
      setSelectedDevice(freshDevice);
    } catch { /* fallback to cached data */ }
    const d = freshDevice;
    setFormData({
      name: d.name || '',
      device_type: d.device_type || 'gps_tracker',
      imei: d.imei || '',
      serial_number: d.serial_number || '',
      mac_address: d.mac_address || '',
      protocol: d.protocol || 'Teltonika',
      model: d.model || '',
      firmware_version: d.firmware_version || '',
      hardware_version: d.hardware_version || '',
      device_password: d.device_password || '',
      has_video: d.has_video || false,
      num_cameras: d.num_cameras || 0,
      channel_names: d.channel_names || {},
      always_on_streaming: !!d.always_on_streaming,
      sim_iccid: d.sim_iccid || '',
      sim_number: d.sim_number || '',
      sim_provider: d.sim_provider || '',
      installation_date: d.installation_date || '',
      installation_date_display: '',
      installation_type: d.installation_type || '',
      odometer_source: d.odometer_source || 'off',
      engine_hours_source: d.engine_hours_source || 'off',
      speed_source: d.speed_source || 'gps',
      fuel_source: d.fuel_source || 'off',
      max_speed_kmh: d.max_speed_kmh ?? 200,
      server_ip_1: d.server_ip_1 || '',
      server_port_1: d.server_port_1 || '',
      server_ip_2: d.server_ip_2 || '',
      server_port_2: d.server_port_2 || '',
      show_server_2: !!(d.server_ip_2 || d.server_port_2),
      reporting_interval_moving: d.reporting_interval_moving || '',
      reporting_interval_stopped: d.reporting_interval_stopped || '',
      apn: d.apn || '',
      beacon_type: d.beacon_type || '',
      beacon_purpose: d.beacon_purpose || 'asset',
      beacon_uuid: d.beacon_uuid || '',
      beacon_major: d.beacon_major || '',
      beacon_minor: d.beacon_minor || '',
      beacon_eid: d.beacon_eid || '',
      beacon_namespace: d.beacon_namespace || '',
      beacon_instance_id: d.beacon_instance_id || '',
      advertising_interval: d.advertising_interval || '',
      tx_power: d.tx_power || '',
      connected_peripherals: d.connected_peripherals || [],
      backup_device_ids: d.backup_device_ids || [],
      linked_devices: d.linked_devices || [],
      timezone_override_enabled: d.timezone_override_enabled || false,
      timezone_override: d.timezone_override || null,
      offline_threshold_secs: d.offline_threshold_secs ?? 600,
      notes: d.notes || '',
      asset_type: d.asset_type || 'car',
      asset_name: d.asset_name || '',
      asset_make: d.asset_make || '',
      asset_model: d.asset_model || '',
      asset_year: d.asset_year || '',
      registration: d.registration || '',
      vin_number: d.vin_number || '',
      rfid_ibutton_number: d.rfid_ibutton_number || '',
      identity_method: d.identity_method || 'ble',
      fuel_type: d.fuel_type || '',
      serial_no: d.serial_no || '',
      container_number: d.container_number || '',
      container_size: d.container_size || '',
      container_type: d.container_type || '',
      asset_description: d.asset_description || '',
      odometer_reading: d.computed_odometer ?? d.odometer_reading ?? 0,
      engine_hours: d.computed_engine_hours ?? d.engine_hours ?? 0,
      tags: d.tags || [],
      asset_editing_enabled: d.asset_editing_enabled !== undefined ? d.asset_editing_enabled : true,
      allocated_tenant_ids: d.allocated_tenant_ids || [],
      management_admin_ids: d.management_admin_ids || [],
      custom_commands: d.custom_commands || [],
      io_mappings: d.io_mappings || [],
      identity_logbook: normalizeIdentityLogbook(d.identity_logbook),
      device_icon: d.device_icon || 'car',
      device_icon_color: d.device_icon_color || null,
      map_marker_mode: d.map_marker_mode || 'arrow',
      icon_editing_enabled: d.icon_editing_enabled !== undefined ? d.icon_editing_enabled : true,
      is_active: d.is_active !== undefined ? d.is_active : true,
    });
    setTagInput('');
    setShowPeripheralForm(d.connected_peripherals?.length > 0);
    setSimAutoPopulate(true);
    setEditingCommandId(null);
    setEditingPeripheralId(null);
    await Promise.all([fetchAuditLogs(device.id), fetchDeviceAllocation(device.id), fetchAllocatableUsers()]);
    const allLinkableDevices = await fetchLinkableDevices();
    // For any linked device not present in the fetched linkable list (rare edge case — e.g. a
    // device the operator cannot see in Device Management but that IS linked historically), do
    // a lightweight per-device fetch instead of another full /admin/devices?limit=1000 pull.
    const linkedDevIds = (device.linked_devices || []).map(ld => ld.device_id).filter(id => !allLinkableDevices.find(d => d.id === id));
    if (linkedDevIds.length > 0) {
      const cache = {};
      await Promise.all(linkedDevIds.map(async (ldId) => {
        try {
          const res = await api.get(`/admin/devices/${ldId}`);
          if (res?.data) cache[ldId] = { name: res.data.name, imei: res.data.imei, protocol: res.data.protocol };
        } catch { /* linked device inaccessible — skip */ }
      }));
      if (Object.keys(cache).length) {
        setLinkedDeviceCache(prev => ({ ...prev, ...cache }));
      }
    }
    // Pre-fetch data for the currently active tab
    if (nextActiveTab === 'status') {
      fetchDeviceStatus(device.imei);
      fetchCommandHistory(device.imei);
    } else if (nextActiveTab === 'beacon_status') {
      refreshSelectedBeaconStatus(device);
    } else if (nextActiveTab === 'mapping') {
      fetchProtocolIODefaults(device.protocol, device.imei);
      fetchCurrentIOValues(device.imei);
    }
    // Reset dirty tracking
    setFormDirty(false);
    setFormDirty(false);
    setNewCommand(getDefaultCommand());
    setEditingCommandId(null);
    setNewIOMapping({
      io_type: 'Battery', io_id: '', display_name: '', value_type: 'Value',
      formula: '', units: '', decimal_places: 2, logic_mapping: { "1": "ON", "0": "OFF" },
      dictionary: {}, calibration: [], data_source: 'device_io', value_colors: {}, show_telemetry: true, show_fleet_card: true,
      show_map_popup: false, show_icon: true, ignore_ignition_off: false,
      secondary_io_id: null, multi_param_mode: 'lookup', multi_param_separator: null, multi_param_dictionary: {}, multi_param_template: '',
    });
    setEditingMappingId(null);
    setImeiLocked(true);
    setMacLocked(true);
    setBeaconIdLocked(true);
    setTimeout(() => setIsEditDialogOpen(true), 0);
  };
  
  const identityLogbookPayload = () => {
    const il = normalizeIdentityLogbook(formData.identity_logbook);
    if (formData.device_type !== 'beacon' && il.enabled) {
      if (!il.rfid_ibutton_enabled && !il.ble_enabled) {
        return { error: 'Identity Logbooks: enable at least one identity source (RFID / iButton or BLE Beacons)' };
      }
      const maps = formData.io_mappings || [];
      if (!maps.some(m => m.io_type === 'Ignition' && m.io_id)) {
        return { error: 'Identity Logbooks require an IO Mapping with IO Type = Ignition (add it in the IO Mapping tab)' };
      }
      if (il.rfid_ibutton_enabled && !maps.some(m => m.io_type === 'RFID / iButton' && m.io_id)) {
        return { error: 'RFID / iButton source requires an IO Mapping with IO Type = RFID / iButton (add it in the IO Mapping tab)' };
      }
    }
    const tq = il.trip_qualification || {};
    const num = (v, def, min) => { const n = parseFloat(v); return Number.isFinite(n) ? Math.max(min, n) : def; };
    return {
      value: {
        enabled: formData.device_type === 'beacon' ? false : !!il.enabled,
        rfid_ibutton_enabled: !!il.rfid_ibutton_enabled,
        ble_enabled: !!il.ble_enabled,
        use_custom_qualification: !!il.use_custom_qualification,
        trip_qualification: {
          min_ignition_seconds: Math.round(num(tq.min_ignition_seconds, 60, 1)),
          min_distance_meters: num(tq.min_distance_meters, 50, 0),
          movement_speed_kmh: num(tq.movement_speed_kmh, 3, 0),
          consecutive_moving_reports: Math.round(num(tq.consecutive_moving_reports, 2, 1)),
        },
      },
    };
  };

  const handleAddDevice = async () => {
    const isBeacon = formData.device_type === 'beacon';
    if (!formData.name) {
      toast.error('Device name is required');
      return;
    }
    if (!isBeacon && !formData.imei) {
      toast.error('IMEI is required for GPS devices');
      return;
    }
    if (!isBeacon && !formData.protocol) {
      toast.error('Protocol is required');
      return;
    }
    if (isBeacon) {
      const bv = computeBeaconValidation(formData);
      if (!bv.isValid) {
        toast.error(Object.values(bv.errors)[0] || 'Please correct the highlighted beacon fields.');
        return;
      }
    }
    const il = identityLogbookPayload();
    if (il.error) {
      toast.error(il.error);
      return;
    }
    if (rfidOnlyTrailer && !(formData.rfid_ibutton_number || '').trim()) {
      toast.error('RFID / iButton-only trailer requires the Trailer RFID / iButton No.');
      return;
    }
    setSubmitting(true);
    try {
      const { show_server_2, installation_date_display, ...payload } = formData;
      // Include allocation from the allocation tab state
      payload.allocated_tenant_ids = allocatedUserIds;
      payload.primary_operational_account_id = primaryOperationalAccountId || null;
      payload.identity_logbook = il.value;
      // MAC may be entered as plain hex (QR scan) — backend/TCP parser expects colon format.
      if (payload.mac_address) payload.mac_address = _normalizeMacToColon(payload.mac_address);
      // Beacons: the backend derives the canonical identifier (imei) from the
      // beacon type fields — don't force a MAC/serial substitute here.
      // Clean all optional integer fields — convert empty strings to null
      const intFields = ['server_port_1', 'server_port_2', 'reporting_interval_moving', 'reporting_interval_stopped', 'advertising_interval', 'tx_power', 'max_speed_kmh'];
      intFields.forEach(f => { payload[f] = payload[f] !== '' && payload[f] != null ? parseInt(payload[f]) || null : null; });
      // offline_threshold_secs is required — default to 600 if empty
      payload.offline_threshold_secs = (payload.offline_threshold_secs !== '' && payload.offline_threshold_secs != null) ? (parseInt(payload.offline_threshold_secs) || 600) : 600;
      // num_cameras must be int (not optional) — default to 0
      payload.num_cameras = parseInt(payload.num_cameras) || 0;
      // Clean float fields
      payload.odometer_reading = payload.odometer_reading !== '' && payload.odometer_reading != null ? parseFloat(payload.odometer_reading) || null : null;
      payload.engine_hours = payload.engine_hours !== '' && payload.engine_hours != null ? parseFloat(payload.engine_hours) || null : null;
      // Clean enum fields — empty string to null
      if (!payload.beacon_type) payload.beacon_type = null;
      if (!payload.asset_type) payload.asset_type = 'car';
      // Clean any remaining empty strings in the payload that should be null
      Object.keys(payload).forEach(k => { if (payload[k] === '' && !Array.isArray(payload[k])) payload[k] = null; });
      await api.post('/admin/devices', payload);
      toast.success('Device created successfully');
      fetchDevices();
      setFormDirty(false);
      setShowAddUnsavedWarning(false);
      setIsAddDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create device:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => {
        const field = Array.isArray(e.loc) ? e.loc.filter(l => l !== 'body').pop() : null;
        const fieldLabel = field ? field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
        return fieldLabel ? `${fieldLabel}: ${e.msg}` : (e.msg || e);
      }).join(', ') : 'Failed to create device';
      if (msg.includes('device limit')) {
        setDeviceLimitError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleUpdateDevice = async () => {
    if (!selectedDevice) return;
    const il = identityLogbookPayload();
    if (il.error) {
      toast.error(il.error);
      return;
    }
    if (rfidOnlyTrailer && !(formData.rfid_ibutton_number || '').trim()) {
      toast.error('RFID / iButton-only trailer requires the Trailer RFID / iButton No.');
      return;
    }
    
    setSubmitting(true);
    try {
      const { show_server_2, installation_date_display, allocated_tenant_ids, ...payload } = formData;
      // allocated_tenant_ids handled by dedicated allocation endpoint below
      payload.identity_logbook = il.value;
      // MAC may be entered as plain hex (QR scan) — backend/TCP parser expects colon format.
      if (payload.mac_address) payload.mac_address = _normalizeMacToColon(payload.mac_address);
      // Clean all optional integer fields — convert empty strings to null
      const intFields = ['server_port_1', 'server_port_2', 'reporting_interval_moving', 'reporting_interval_stopped', 'advertising_interval', 'tx_power', 'max_speed_kmh'];
      intFields.forEach(f => { payload[f] = payload[f] !== '' && payload[f] != null ? parseInt(payload[f]) || null : null; });
      // offline_threshold_secs is required — default to 600 if empty
      payload.offline_threshold_secs = (payload.offline_threshold_secs !== '' && payload.offline_threshold_secs != null) ? (parseInt(payload.offline_threshold_secs) || 600) : 600;
      // num_cameras must be int (not optional) — default to 0
      payload.num_cameras = parseInt(payload.num_cameras) || 0;
      // Clean float fields — only include odometer/engine hours if the user actually changed the value
      // (prevents accidental reset of accumulated GPS/ignition counters)
      const loadedOdo = selectedDevice.computed_odometer ?? selectedDevice.odometer_reading ?? 0;
      const loadedEh = selectedDevice.computed_engine_hours ?? selectedDevice.engine_hours ?? 0;
      const currentOdo = (payload.odometer_reading !== '' && payload.odometer_reading != null) ? (parseFloat(payload.odometer_reading) || 0) : 0;
      const currentEh = (payload.engine_hours !== '' && payload.engine_hours != null) ? (parseFloat(payload.engine_hours) || 0) : 0;
      if (currentOdo !== loadedOdo) {
        payload.odometer_reading = currentOdo;
      } else {
        delete payload.odometer_reading;
      }
      if (currentEh !== loadedEh) {
        payload.engine_hours = currentEh;
      } else {
        delete payload.engine_hours;
      }
      // Clean enum fields — empty string to null
      if (!payload.beacon_type) payload.beacon_type = null;
      if (!payload.asset_type) payload.asset_type = 'car';
      // Clean any remaining empty strings in the payload that should be null
      Object.keys(payload).forEach(k => { if (payload[k] === '' && !Array.isArray(payload[k])) payload[k] = null; });
      // Update device
      await api.put(`/admin/devices/${selectedDevice.id}`, payload);
      // Only sync allocation if it actually changed (prevents accidental wipes)
      const allocChanged = JSON.stringify([...allocatedUserIds].sort()) !== JSON.stringify([...originalAllocatedUserIds].sort())
        || primaryOperationalAccountId !== originalPrimaryOperationalAccountId;
      if (allocChanged) {
        const removedUsers = originalAllocatedUserIds.filter(id => !allocatedUserIds.includes(id));
        if (removedUsers.length > 0) {
          setRemovedUserCount(removedUsers.length);
          setRemoveAllUsersContext('save');
          setShowRemoveAllUsersConfirm(true);
          return;
        }
        await api.put(`/admin/devices/${selectedDevice.id}/allocation`, {
          allocated_user_ids: allocatedUserIds,
          primary_operational_account_id: primaryOperationalAccountId || null,
        });
      }
      toast.success('Device updated successfully');
      fetchDevices();
      setSavedFormSnapshot(null);
      setShowUnsavedWarning(false);
      setIsEditDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to update device:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => {
        const field = Array.isArray(e.loc) ? e.loc.filter(l => l !== 'body').pop() : null;
        const fieldLabel = field ? field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : null;
        return fieldLabel ? `${fieldLabel}: ${e.msg}` : (e.msg || e);
      }).join(', ') : 'Failed to update device';
      if (msg.includes('device limit')) {
        setDeviceLimitError(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const [purgeOnDelete, setPurgeOnDelete] = useState(true);
  const purgeOnDeleteRef = useRef(true);
  const togglePurgeOnDelete = () => {
    setPurgeOnDelete((prev) => {
      purgeOnDeleteRef.current = !prev;
      return !prev;
    });
  };

  const handleDeleteDevice = async (deviceId) => {
    const device = devices.find(d => d.id === deviceId);
    const assetLabel = [device?.asset_name, device?.unit_number].filter(Boolean).join(' / ');
    setPurgeOnDelete(true);
    purgeOnDeleteRef.current = true;
    // Fetch the maintenance impact so the operator sees what else gets removed BEFORE confirming.
    let impact = null;
    try { impact = (await api.get(`/service/device-impact/${deviceId}`)).data; } catch { /* non-blocking */ }
    const hasImpact = !!(impact && (impact.schedules || impact.history || impact.attachments));
    setPendingAction({
      type: 'delete_device',
      title: 'Delete Device?',
      requireTyped: true,
      message: (
        <>
          Are you sure you want to delete this device? This action cannot be undone.
          <span className="block mt-2">Device Name: <strong className="text-foreground">{device?.name || '—'}</strong></span>
          <span className="block">Asset Name / Unit Number: <strong className="text-foreground">{assetLabel || '—'}</strong></span>
          {hasImpact && (
            <span className="block mt-3 p-2 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs" data-testid="delete-device-maintenance-impact">
              This will also permanently remove this device's maintenance data:
              <span className="block mt-1">
                {impact.schedules > 0 && <>• {impact.schedules} service schedule{impact.schedules === 1 ? '' : 's'}<br /></>}
                {impact.history > 0 && <>• {impact.history} service log{impact.history === 1 ? '' : 's'}<br /></>}
                {impact.attachments > 0 && <>• {impact.attachments} attached file{impact.attachments === 1 ? '' : 's'} (deleted from storage)</>}
              </span>
            </span>
          )}
        </>
      ),
      action: async () => {
        try {
          const res = await api.delete(`/admin/devices/${deviceId}?purge_history=${purgeOnDeleteRef.current}`);
          toast.success(res.data?.message || 'Device deleted successfully');
          // Clear the deleted id from any selection so the "N selected" chip
          // stays accurate without needing a full page reload.
          setSelectedDeviceIds(prev => prev.filter(id => id !== deviceId));
          // If we just emptied the current page and we're past page 1, walk back
          // one page — otherwise the list stays blank until the user clicks Prev.
          if (devices.length === 1 && pageIndex > 0) {
            setPageIndex(pageIndex - 1); // fetchDevices re-runs via the pageIndex effect
          } else {
            fetchDevices();
          }
          if (isEditDialogOpen) {
            setIsEditDialogOpen(false);
            resetForm();
          }
        } catch (error) {
          console.error('Failed to delete device:', error);
          toast.error(error.response?.data?.detail || 'Failed to delete device');
        }
      },
    });
  };
  

  /* ============================================================
   * Export devices — split by kind (GPS vs Beacons) for round-trip
   * safety; plus a merged Audit export with all metadata.
   *
   * Column layout: the first 7 columns are IDENTICAL across both
   * round-trip exports (Name, Serial Number, MAC Address, Protocol,
   * Model, Device Type, Allocated Users) so admins get a consistent
   * mental model when opening either file.
   *
   * Beacon-specific fields (beacon_type, beacon_uuid, beacon_major,
   * beacon_minor, beacon_namespace, beacon_instance_id, beacon_eid)
   * are read from their proper structured fields on the doc — NOT
   * from the `imei` field, which the backend uses as a canonical
   * concatenated identifier for beacons.
   *
   * Every long-numeric column is wrapped as ="…" in CSV and written
   * with Text formatting in XLSX so Excel never truncates IMEI /
   * ICCID / UUID / Major / Minor into scientific notation.
   * ============================================================ */

  // Excel-safe CSV: wrap long-numeric text cells as ="…" so Excel keeps them as text.
  const buildCsvBlob = (headers, rows, textColIndices) => {
    const textSet = new Set(textColIndices || []);
    const csvRow = (arr) => arr.map((v, ci) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return textSet.has(ci) && s ? `="${s}"` : `"${s}"`;
    }).join(',');
    const csv = [headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','), ...rows.map(csvRow)].join('\n');
    return new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  };

  // Excel-safe XLSX: force text-column cells to Text (`t:'s', z:'@'`)
  // so long numbers survive save/reopen. Freezes header row.
  const buildXlsxBlob = (headers, rows, textColIndices, sheetName) => {
    const textSet = new Set(textColIndices || []);
    const aoa = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    // Header cells: bold-ish default; leave as is.
    // Data cells: force text type on protected columns.
    for (let r = 1; r < aoa.length; r += 1) {
      for (let c = 0; c < headers.length; c += 1) {
        if (!textSet.has(c)) continue;
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = ws[addr];
        if (cell) { cell.t = 's'; cell.z = '@'; if (cell.v != null) cell.v = String(cell.v); }
        else { ws[addr] = { t: 's', z: '@', v: '' }; }
      }
    }
    // Reasonable column widths
    ws['!cols'] = headers.map(h => ({ wch: Math.max(String(h).length + 2, 14) }));
    ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: aoa.length - 1, c: headers.length - 1 } });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet1');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  };

  const triggerDownload = (blob, filename) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Fire-and-forget entry into the Import/Export Log (never blocks the action)
  const logDeviceIo = async (payload) => {
    try { const r = await api.post('/admin/device-io-logs', payload); return r.data?.id || null; } catch { return null; }
  };

  // Export the currently filtered Import/Export Log entries as CSV (compliance).
  // Fetches ALL filtered entries from the server (not just the visible page).
  const exportIoLogCsv = async () => {
    let filtered = [];
    try {
      const r = await api.get(`/admin/device-io-logs?skip=0&limit=10000&${buildIoLogFilterParams()}`);
      filtered = r.data?.logs || [];
    } catch {
      toast.error('Failed to export log');
      return;
    }
    const headers = ['When', 'Type', 'Details', 'File Type', 'File Name', 'Rows', 'Created', 'Skipped', 'Failed', 'Undo Status', 'Undo Removed', 'By', 'Email'];
    const detailFor = (l) => l.kind === 'import'
      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
      : ({ gps_devices: 'GPS Devices', ble_beacons: 'BLE Beacons', audit: 'Full Details (Audit)' }[l.export_type] || 'Export');
    const rows = filtered.map(l => [
      l.timestamp ? formatDT(l.timestamp) : '',
      l.kind === 'import' ? 'Import' : 'Export',
      detailFor(l),
      l.file_type || '',
      l.file_name || '',
      l.row_count ?? 0,
      l.kind === 'import' ? (l.created ?? 0) : '',
      l.kind === 'import' ? (l.skipped ?? 0) : '',
      l.kind === 'import' ? (l.failed ?? 0) : '',
      l.undo_status === 'undone' ? 'Undone' : '',
      l.undo_status === 'undone' ? (l.undo_removed ?? 0) : '',
      l.actor_name || '',
      l.actor_email || '',
    ]);
    triggerDownload(buildCsvBlob(headers, rows, []), `import_export_log_${getFileTimestamp()}.csv`);
    toast.success(`Exported ${filtered.length} log entr${filtered.length === 1 ? 'y' : 'ies'}`);
  };

  const fetchAllIoLogs = async () => {
    try {
      const r = await api.get(`/admin/device-io-logs?skip=0&limit=10000&${buildIoLogFilterParams()}`);
      return r.data?.logs || [];
    } catch {
      toast.error('Failed to export log');
      return null;
    }
  };

  const buildIoLogTable = (filtered) => {
    const detailFor = (l) => l.kind === 'import'
      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
      : ({ gps_devices: 'GPS Devices', ble_beacons: 'BLE Beacons', audit: 'Full Details (Audit)' }[l.export_type] || 'Export');
    const columns = [
      { label: 'When', weight: 15 }, { label: 'Type', weight: 7 }, { label: 'Details', weight: 14 },
      { label: 'File', weight: 6 }, { label: 'File Name', weight: 20 }, { label: 'Rows', weight: 5 },
      { label: 'Created', weight: 7 }, { label: 'Skipped', weight: 7 }, { label: 'Failed', weight: 6 },
      { label: 'Undo', weight: 8 }, { label: 'By', weight: 14 },
    ];
    const rows = filtered.map(l => [
      l.timestamp ? formatDT(l.timestamp) : '', l.kind === 'import' ? 'Import' : 'Export', detailFor(l),
      l.file_type || '', l.file_name || '', String(l.row_count ?? 0),
      l.kind === 'import' ? String(l.created ?? 0) : '', l.kind === 'import' ? String(l.skipped ?? 0) : '',
      l.kind === 'import' ? String(l.failed ?? 0) : '',
      l.undo_status === 'undone' ? `Undone (${l.undo_removed ?? 0})` : '',
      l.actor_name || l.actor_email || '',
    ]);
    return { columns, rows };
  };

  const exportIoLogPdf = async () => {
    const filtered = await fetchAllIoLogs();
    if (!filtered) return;
    try {
      const { doc, layout } = newLandscapeDoc();
      const logo = await loadLogoPng();
      const y = addBrandHeader(doc, layout, logo, 'Device Import / Export Log',
        `${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}  ·  ${new Date().toLocaleString()}`);
      const { columns, rows } = buildIoLogTable(filtered);
      drawPdfTable(doc, layout, y, columns, rows);
      addFooters(doc, layout);
      doc.save(`import_export_log_${getFileTimestamp()}.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const exportIoLogHtml = async () => {
    const filtered = await fetchAllIoLogs();
    if (!filtered) return;
    const { columns, rows } = buildIoLogTable(filtered);
    await exportTableHtml('Device Import / Export Log',
      `${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}`,
      columns.map(c => c.label), rows, `import_export_log_${getFileTimestamp()}.html`);
    toast.success('HTML downloaded');
  };

  const openIoLog = () => {
    setIoLogOpen(true);
  };

  // ── Command Delivery Receipts (per-device command log) ──────────
  const CMD_METHOD_LABELS = { gprs: 'GPRS', sms_api: 'SMS (API)', sms_3p: 'SMS (3rd-party)', sms: 'SMS' };
  const cmdMethodLabel = (m) => CMD_METHOD_LABELS[m || 'gprs'] || (m || 'GPRS').toUpperCase();
  const cmdStatusBadgeClass = (s) => (
    s === 'acknowledged' ? 'bg-green-600 text-white' :
    s === 'sent' || s === 'delivered' ? 'bg-blue-600 text-white' :
    s === 'queued' ? 'bg-yellow-500 text-white' :
    s === 'timeout' ? 'bg-orange-500 text-white' :
    s === 'failed' ? 'bg-red-600 text-white' :
    'bg-gray-500 text-white'
  );
  // 10-minute GPRS queue expiry countdown (command_sender queue_expiry=600s)
  const queuedExpiryText = (cmd) => {
    if (!cmd.created_at) return null;
    const remaining = Math.floor((new Date(cmd.created_at).getTime() + 600000 - cmdNowTick) / 1000);
    if (remaining <= 0) return 'expiring…';
    return `expires in ${Math.floor(remaining / 60)}m ${String(remaining % 60).padStart(2, '0')}s`;
  };

  // Legacy device_commands rows stored a user UUID in created_by — hide those
  const cmdSentBy = (c) => {
    const v = c.created_by || c.sent_by || '';
    if (!v || v === 'system' || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(v)) return '';
    return v;
  };

  const buildCmdLogFilterParams = () => {
    const params = new URLSearchParams();
    params.set('device_imei', selectedDevice?.imei || '');
    if (cmdLogStatusFilter !== 'all') params.set('status', cmdLogStatusFilter);
    if (cmdLogMethodFilter !== 'all') params.set('delivery_method', cmdLogMethodFilter);
    if (cmdLogDateRange.from) params.set('from_date', new Date(cmdLogDateRange.from).toISOString());
    if (cmdLogDateRange.to) {
      const end = new Date(cmdLogDateRange.to);
      end.setHours(23, 59, 59, 999);
      params.set('to_date', end.toISOString());
    }
    return params.toString();
  };

  useEffect(() => { setCmdLogPage(1); }, [cmdLogStatusFilter, cmdLogMethodFilter, cmdLogDateRange]);

  useEffect(() => {
    if (!cmdLogOpen || !selectedDevice?.imei) return;
    let cancelled = false;
    (async () => {
      setCmdLogsLoading(true);
      try {
        const skip = (cmdLogPage - 1) * cmdLogPageSize;
        const r = await api.get(`/admin/commands/history?skip=${skip}&limit=${cmdLogPageSize}&${buildCmdLogFilterParams()}`);
        if (cancelled) return;
        setCmdLogs(r.data?.commands || []);
        setCmdLogTotal(r.data?.total || 0);
      } catch {
        if (!cancelled) toast.error('Failed to load command log');
      } finally {
        if (!cancelled) setCmdLogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cmdLogOpen, cmdLogPage, cmdLogPageSize, cmdLogStatusFilter, cmdLogMethodFilter, cmdLogDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetches ALL filtered commands (endpoint caps limit at 500 → page through)
  const fetchAllCmdLogs = async () => {
    try {
      const all = [];
      let skip = 0;
      for (;;) {
        const r = await api.get(`/admin/commands/history?skip=${skip}&limit=500&${buildCmdLogFilterParams()}`);
        const batch = r.data?.commands || [];
        all.push(...batch);
        if (batch.length < 500 || all.length >= (r.data?.total || 0) || all.length >= 10000) break;
        skip += 500;
      }
      return all;
    } catch {
      toast.error('Failed to export command log');
      return null;
    }
  };

  const buildCmdLogTable = (cmds) => {
    const columns = [
      { label: 'When', weight: 12 }, { label: 'Status', weight: 8 }, { label: 'Method', weight: 9 },
      { label: 'Command', weight: 21 }, { label: 'Response / Error', weight: 21 },
      { label: 'Sent', weight: 7 }, { label: 'Reply', weight: 7 }, { label: 'By', weight: 15 },
    ];
    const rows = cmds.map(c => [
      c.created_at ? formatDT(c.created_at, { seconds: true }) : '',
      c.status || '',
      cmdMethodLabel(c.delivery_method),
      c.command || '',
      c.response || c.error || '',
      c.sent_at ? formatTimeOnly(c.sent_at, { seconds: true }) : '',
      c.responded_at ? formatTimeOnly(c.responded_at, { seconds: true }) : '',
      cmdSentBy(c),
    ]);
    return { columns, rows };
  };

  const cmdLogExportTitle = () => `Command Log — ${selectedDevice?.name || selectedDevice?.imei || ''}`;

  const exportCmdLogCsv = async () => {
    const cmds = await fetchAllCmdLogs();
    if (!cmds) return;
    const { columns, rows } = buildCmdLogTable(cmds);
    triggerDownload(buildCsvBlob(columns.map(c => c.label), rows, []), `command_log_${selectedDevice?.imei || 'device'}_${getFileTimestamp()}.csv`);
    toast.success(`Exported ${cmds.length} command(s)`);
  };

  const exportCmdLogPdf = async () => {
    const cmds = await fetchAllCmdLogs();
    if (!cmds) return;
    try {
      const { doc, layout } = newLandscapeDoc();
      const logo = await loadLogoPng();
      const y = addBrandHeader(doc, layout, logo, cmdLogExportTitle(),
        `${cmds.length} command(s)  ·  ${new Date().toLocaleString()}`);
      const { columns, rows } = buildCmdLogTable(cmds);
      drawPdfTable(doc, layout, y, columns, rows);
      addFooters(doc, layout);
      doc.save(`command_log_${selectedDevice?.imei || 'device'}_${getFileTimestamp()}.pdf`);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  const exportCmdLogHtml = async () => {
    const cmds = await fetchAllCmdLogs();
    if (!cmds) return;
    const { columns, rows } = buildCmdLogTable(cmds);
    await exportTableHtml(cmdLogExportTitle(), `${cmds.length} command(s)`,
      columns.map(c => c.label), rows, `command_log_${selectedDevice?.imei || 'device'}_${getFileTimestamp()}.html`);
    toast.success('HTML downloaded');
  };

  const buildIoLogFilterParams = () => {
    const params = new URLSearchParams();
    if (ioLogTypeFilter !== 'all') params.set('kind', ioLogTypeFilter);
    if (ioLogPersonFilter !== 'all') params.set('person', ioLogPersonFilter);
    if (ioLogDateRange.from) params.set('date_from', new Date(ioLogDateRange.from).toISOString());
    if (ioLogDateRange.to) {
      const end = new Date(ioLogDateRange.to);
      end.setHours(23, 59, 59, 999);
      params.set('date_to', end.toISOString());
    }
    return params.toString();
  };

  // Reset to page 1 whenever a filter changes
  useEffect(() => {
    setIoLogPage(1);
  }, [ioLogTypeFilter, ioLogPersonFilter, ioLogDateRange]);

  // Server-side paginated fetch — runs while the modal is open
  useEffect(() => {
    if (!ioLogOpen) return;
    let cancelled = false;
    (async () => {
      setIoLogsLoading(true);
      try {
        const params = buildIoLogFilterParams();
        const skip = (ioLogPage - 1) * ioLogPageSize;
        const r = await api.get(`/admin/device-io-logs?skip=${skip}&limit=${ioLogPageSize}&${params}`);
        if (cancelled) return;
        setIoLogs(r.data?.logs || []);
        setIoLogTotal(r.data?.total || 0);
        setIoLogGrandTotal(r.data?.grand_total || 0);
        setIoLogPersons(r.data?.persons || []);
      } catch {
        if (!cancelled) toast.error('Failed to load Import/Export Log');
      } finally {
        if (!cancelled) setIoLogsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ioLogOpen, ioLogPage, ioLogPageSize, ioLogTypeFilter, ioLogPersonFilter, ioLogDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve current selection into GPS vs Beacon subsets. Requires an
  // explicit selection (use the header checkbox for the current page or
  // the "Select All" button on the selection bar for cross-page selection).
  // When cross-page "Select All" was used, `selectedDeviceIds` holds IDs
  // that aren't in the currently-paginated `devices` state — we fetch the
  // missing docs (one bulk call, filters honoured) so the export contains
  // FULL data for every selected row, not just the current page.
  const partitionForExport = async () => {
    if (selectedDeviceIds.length === 0) {
      toast.error('Select devices to export — use the Select All button on the selection bar to pick every device across all pages');
      return { base: [], gps: [], beacons: [] };
    }
    const inPage = devices.filter(d => selectedDeviceIds.includes(d.id));
    let base;
    if (inPage.length >= selectedDeviceIds.length) {
      // Everything selected is on the current page — no need to refetch.
      base = inPage;
    } else {
      // Cross-page selection — fetch the full filtered set and pick out the selected IDs.
      let url = `/admin/devices?limit=100000&skip=0`;
      if (searchTerm) url += `&search=${searchTerm}`;
      if (protocolFilter && protocolFilter !== '__all__') url += `&protocol=${protocolFilter}`;
      if (modelFilter && modelFilter !== '__all__') url += `&model=${encodeURIComponent(modelFilter)}`;
      if (statusFilter && statusFilter !== '__all__') url += `&status_filter=${statusFilter}`;
      if (isActiveFilter !== 'all') url += `&is_active=${isActiveFilter === 'active'}`;
      try {
        const r = await api.get(url);
        const all = Array.isArray(r.data) ? r.data : [];
        const idSet = new Set(selectedDeviceIds);
        base = all.filter(d => idSet.has(d.id));
      } catch {
        toast.error('Failed to fetch selected devices — export aborted');
        return { base: [], gps: [], beacons: [] };
      }
    }
    const gps = base.filter(d => (d.device_type || 'gps_tracker') !== 'beacon');
    const beacons = base.filter(d => (d.device_type || 'gps_tracker') === 'beacon');
    return { base, gps, beacons };
  };

  const resolveAllocatedUserLabels = (d) => (d.allocated_tenant_ids || [])
    .map(uid => userMap[uid]?.name || userMap[uid]?.email || uid)
    .join('; ');

  const resolveManagedByLabels = (d) => (d.management_admin_ids || [])
    .map(uid => userMap[uid]?.name || userMap[uid]?.email || uid)
    .join('; ');

  // ---- GPS Devices round-trip export ----
  const GPS_HEADERS = [
    'Name', 'Serial Number', 'MAC Address', 'Protocol', 'Model', 'Device Type', 'Allocated Users', 'Managed By',
    'IMEI', 'SIM Provider', 'SIM ICCID', 'SIM Mobile',
    'Asset Name', 'Asset Type', 'Asset Make', 'Asset Model', 'Asset Year',
    'Registration', 'VIN Number', 'Notes',
  ];
  // Indices of columns that Excel would mangle if left as General
  const GPS_TEXT_COLS = [1, 2, 8, 10, 11, 16, 18]; // Serial, MAC, IMEI, ICCID, SIM Mobile, Asset Year, VIN

  const gpsRowFor = (d) => [
    d.name || '',
    d.serial_number || '',
    d.mac_address || '',
    d.protocol || '',
    d.model || '',
    d.device_type || 'gps_tracker',
    resolveAllocatedUserLabels(d),
    resolveManagedByLabels(d),
    d.imei || '',
    d.sim_provider || '',
    d.sim_iccid || '',
    d.sim_number || '',
    d.asset_name || '',
    d.asset_type || '',
    d.asset_make || d.vehicle_make || '',
    d.asset_model || d.vehicle_model || '',
    d.asset_year || d.vehicle_year || '',
    d.registration || d.registration_number || '',
    d.vin_number || '',
    d.notes || '',
  ];

  const exportGpsDevices = async (format) => {
    const { gps, beacons } = await partitionForExport();
    if (gps.length === 0) {
      toast.error(beacons.length > 0
        ? 'No GPS devices in selection — pick BLE Beacons export for beacons'
        : 'No GPS devices to export');
      return;
    }
    const rows = gps.map(gpsRowFor);
    const ts = getFileTimestamp();
    const fname = `gps_devices_export_${ts}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
    if (format === 'xlsx') {
      const blob = buildXlsxBlob(GPS_HEADERS, rows, GPS_TEXT_COLS, 'GPS Devices');
      triggerDownload(blob, fname);
    } else {
      const blob = buildCsvBlob(GPS_HEADERS, rows, GPS_TEXT_COLS);
      triggerDownload(blob, fname);
    }
    logDeviceIo({ kind: 'export', source: 'export_menu', export_type: 'gps_devices', file_type: format === 'xlsx' ? 'xlsx' : 'csv', file_name: fname, row_count: gps.length });
    if (beacons.length > 0) {
      toast.success(`Exported ${gps.length} GPS device(s) — ${beacons.length} beacon(s) skipped, use BLE Beacons export`);
    } else {
      toast.success(`Exported ${gps.length} GPS device(s)`);
    }
  };

  // ---- BLE Beacons round-trip export ----
  const BEACON_HEADERS = [
    'Name', 'Serial Number', 'MAC Address', 'Protocol', 'Model', 'Device Type', 'Allocated Users', 'Managed By',
    'Beacon Type', 'Beacon UUID', 'Beacon Major', 'Beacon Minor',
    'Beacon Namespace', 'Beacon Instance ID', 'Beacon EID',
    'Asset Name', 'Asset Type', 'Notes',
  ];
  // Serial, MAC, UUID, Major, Minor, Namespace, Instance ID, EID
  const BEACON_TEXT_COLS = [1, 2, 9, 10, 11, 12, 13, 14];

  const beaconRowFor = (d) => [
    d.name || '',
    d.serial_number || '',
    d.mac_address || '',
    d.protocol || 'BLE Beacon',
    d.model || '',
    d.device_type || 'beacon',
    resolveAllocatedUserLabels(d),
    resolveManagedByLabels(d),
    d.beacon_type || '',
    d.beacon_uuid || '',
    d.beacon_major || '',
    d.beacon_minor || '',
    d.beacon_namespace || '',
    d.beacon_instance_id || '',
    d.beacon_eid || '',
    d.asset_name || '',
    d.asset_type || '',
    d.notes || '',
  ];

  const exportBeacons = async (format) => {
    const { gps, beacons } = await partitionForExport();
    if (beacons.length === 0) {
      toast.error(gps.length > 0
        ? 'No BLE Beacons in selection — pick GPS Devices export for trackers'
        : 'No BLE Beacons to export');
      return;
    }
    const rows = beacons.map(beaconRowFor);
    const ts = getFileTimestamp();
    const fname = `ble_beacons_export_${ts}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
    if (format === 'xlsx') {
      const blob = buildXlsxBlob(BEACON_HEADERS, rows, BEACON_TEXT_COLS, 'BLE Beacons');
      triggerDownload(blob, fname);
    } else {
      const blob = buildCsvBlob(BEACON_HEADERS, rows, BEACON_TEXT_COLS);
      triggerDownload(blob, fname);
    }
    logDeviceIo({ kind: 'export', source: 'export_menu', export_type: 'ble_beacons', file_type: format === 'xlsx' ? 'xlsx' : 'csv', file_name: fname, row_count: beacons.length });
    if (gps.length > 0) {
      toast.success(`Exported ${beacons.length} beacon(s) — ${gps.length} GPS device(s) skipped, use GPS Devices export`);
    } else {
      toast.success(`Exported ${beacons.length} beacon(s)`);
    }
  };

  // ---- Full Details / Audit export (merged GPS + Beacons) ----
  const AUDIT_HEADERS = [
    // Shared block (same first 7 columns for consistency)
    'Name', 'Serial Number', 'MAC Address', 'Protocol', 'Model', 'Device Type', 'Allocated Users',
    // GPS-specific
    'IMEI', 'SIM Provider', 'SIM ICCID', 'SIM Mobile',
    // Beacon-specific
    'Beacon Type', 'Beacon UUID', 'Beacon Major', 'Beacon Minor', 'Beacon Namespace', 'Beacon Instance ID', 'Beacon EID',
    // Asset details
    'Asset Name', 'Asset Type', 'Asset Make', 'Asset Model', 'Asset Year', 'Registration', 'VIN Number',
    // Config / notes
    'Notes', 'Timezone Override', 'Custom Commands', 'IO Mappings', 'Connected Peripherals',
    // Management
    'Management Admins', 'Allocated User Count',
    // State / timestamps
    'Status', 'Active', 'Last Connection', 'Odometer (km)', 'Engine Hours',
    'Created At', 'Updated At',
  ];
  // Every long identifier column (Serial, MAC, IMEI, ICCID, SIM Mobile,
  // Beacon UUID, Major, Minor, Namespace, Instance, EID, Asset Year, VIN)
  const AUDIT_TEXT_COLS = [1, 2, 7, 9, 10, 12, 13, 14, 15, 16, 17, 22, 24];

  const auditRowFor = (d) => {
    const mgmt = (d.management_admin_ids || []).map(uid => userMap[uid]?.name || userMap[uid]?.email || uid).join('; ');
    const alloc = resolveAllocatedUserLabels(d);
    const isBeacon = (d.device_type || 'gps_tracker') === 'beacon';
    return [
      d.name || '',
      d.serial_number || '',
      d.mac_address || '',
      d.protocol || (isBeacon ? 'BLE Beacon' : ''),
      d.model || '',
      d.device_type || 'gps_tracker',
      alloc,
      isBeacon ? '' : (d.imei || ''),
      isBeacon ? '' : (d.sim_provider || ''),
      isBeacon ? '' : (d.sim_iccid || ''),
      isBeacon ? '' : (d.sim_number || ''),
      isBeacon ? (d.beacon_type || '') : '',
      isBeacon ? (d.beacon_uuid || '') : '',
      isBeacon ? (d.beacon_major || '') : '',
      isBeacon ? (d.beacon_minor || '') : '',
      isBeacon ? (d.beacon_namespace || '') : '',
      isBeacon ? (d.beacon_instance_id || '') : '',
      isBeacon ? (d.beacon_eid || '') : '',
      d.asset_name || '',
      d.asset_type || '',
      d.asset_make || d.vehicle_make || '',
      d.asset_model || d.vehicle_model || '',
      d.asset_year || d.vehicle_year || '',
      d.registration || d.registration_number || '',
      d.vin_number || '',
      d.notes || '',
      d.timezone_override_enabled === false ? 'system-default' : (d.timezone_override || 'auto'),
      (d.custom_commands || []).length,
      (d.io_mappings || []).length,
      (d.connected_peripherals || []).length,
      mgmt,
      (d.allocated_tenant_ids || []).length,
      d.status || '',
      d.is_active ? 'Yes' : 'No',
      d.last_connection || '',
      d.odometer_reading || '',
      d.engine_hours || '',
      d.created_at || '',
      d.updated_at || '',
    ];
  };

  const exportAuditAll = async (format) => {
    const { base } = await partitionForExport();
    if (base.length === 0) { toast.error('No devices to export'); return; }
    const rows = base.map(auditRowFor);
    const ts = getFileTimestamp();
    const fname = `devices_audit_export_${ts}.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
    if (format === 'xlsx') {
      const blob = buildXlsxBlob(AUDIT_HEADERS, rows, AUDIT_TEXT_COLS, 'Devices Audit');
      triggerDownload(blob, fname);
    } else {
      const blob = buildCsvBlob(AUDIT_HEADERS, rows, AUDIT_TEXT_COLS);
      triggerDownload(blob, fname);
    }
    logDeviceIo({ kind: 'export', source: 'export_menu', export_type: 'audit', file_type: format === 'xlsx' ? 'xlsx' : 'csv', file_name: fname, row_count: base.length });
    toast.success(`Exported ${base.length} device(s) — full details for audit`);
  };

  // Import devices from CSV
  // Bulk delete — SUPER ADMIN ONLY (server-enforced too)
  const handleBulkDeleteDevices = async () => {
    setBulkDeleting(true);
    try {
      const r = await api.post('/admin/devices/bulk-delete', { device_ids: selectedDeviceIds, purge_history: bulkPurgeHistory });
      const deleted = r.data?.deleted ?? 0;
      const notFound = r.data?.not_found ?? 0;
      toast.success(`Deleted ${deleted} device(s)${bulkPurgeHistory ? ' + all historical data' : ''}${notFound ? ` — ${notFound} not found` : ''}`);
      setSelectedDeviceIds([]);
      setBulkDeleteOpen(false);
      setBulkDeleteText('');
      fetchDevices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleImportDevices = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isXlsx = /\.xlsx$/i.test(file.name) || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    // Proper CSV line parser (handles quotes, escaped quotes and Excel-safe ="..." wrappers).
    // The prior regex-based tokeniser produced interleaved empty strings, so odd-indexed
    // fields (imei, mac, model, sim_provider, sim_number, asset_type, asset_model, registration,
    // allocated_users) were silently dropped and the sci-notation guard never saw the value.
    const parseCsvLine = (line) => {
      const values = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
          else { inQuotes = !inQuotes; }
        } else if (ch === ',' && !inQuotes) {
          values.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      values.push(current);
      return values.map(v => v.trim().replace(/^=/, '').replace(/^"|"$/g, '').replace(/""/g, '"').trim());
    };
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        let headers, rowsRaw;
        if (isXlsx) {
          const wb = XLSX.read(ev.target.result, { type: 'array', raw: false });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
          if (!aoa.length) { toast.error('XLSX file is empty'); return; }
          headers = (aoa[0] || []).map(h => String(h ?? '').replace(/"/g, '').trim().toLowerCase());
          rowsRaw = aoa.slice(1)
            .map(r => (r || []).map(v => String(v ?? '').trim()))
            .filter(r => r.some(v => v !== ''));
        } else {
          const text = ev.target.result;
          const lines = text.split(/\r?\n/).filter(l => l.trim());
          if (lines.length < 2) { toast.error('CSV file is empty or has no data rows'); return; }
          headers = parseCsvLine(lines[0]).map(h => h.toLowerCase());
          rowsRaw = lines.slice(1).map(parseCsvLine);
        }
        const fieldMap = {
          'name': 'name', 'imei': 'imei', 'serial number': 'serial_number', 'serial_number': 'serial_number',
          'mac address': 'mac_address', 'mac_address': 'mac_address',
          'protocol': 'protocol', 'model': 'model',
          'device type': 'device_type', 'device_type': 'device_type',
          'asset name': 'asset_name', 'asset_name': 'asset_name',
          'asset type': 'asset_type', 'asset_type': 'asset_type',
          'sim provider': 'sim_provider', 'sim_provider': 'sim_provider',
          'sim iccid': 'sim_iccid', 'sim_iccid': 'sim_iccid',
          'sim mobile': 'sim_number', 'sim_number': 'sim_number', 'sim number': 'sim_number',
          'registration': 'registration',
          'make': 'asset_make', 'asset_make': 'asset_make',
          'asset_model': 'asset_model',
          'year': 'asset_year', 'asset_year': 'asset_year',
          'registration number': 'registration', 'registration_number': 'registration',
          'vin': 'vin_number', 'vin number': 'vin_number', 'vin_number': 'vin_number',
          'notes': 'notes',
          // Allocated users — accepted as names/emails, resolved to IDs below
          'allocated users': 'allocated_tenant_ids', 'allocated_users': 'allocated_tenant_ids',
          'allocated_tenant_ids': 'allocated_tenant_ids',
          // Managed by (Device Management List admins) — names/emails, resolved below
          'managed by': 'management_admin_ids', 'managed_by': 'management_admin_ids',
          'management admins': 'management_admin_ids', 'management_admin_ids': 'management_admin_ids',
          // Beacon fields (Quick CSV used to drop these entirely → beacon import silently failed
          // because no identifier could be built server-side)
          'beacon_type': 'beacon_type', 'beacon type': 'beacon_type',
          'beacon_uuid': 'beacon_uuid', 'beacon uuid': 'beacon_uuid',
          'beacon_major': 'beacon_major', 'beacon major': 'beacon_major',
          'beacon_minor': 'beacon_minor', 'beacon minor': 'beacon_minor',
          'beacon_namespace': 'beacon_namespace', 'beacon namespace': 'beacon_namespace',
          'beacon_instance_id': 'beacon_instance_id', 'beacon instance id': 'beacon_instance_id',
          'beacon_eid': 'beacon_eid', 'beacon eid': 'beacon_eid',
        };
        // Detect Excel scientific-notation corruption BEFORE trying to POST.
        // Excel silently truncates 15-digit IMEIs to 6-sig-digit floats like
        // "3.50612E+14" → 350612000000000 (last 9 digits gone). We can't
        // reconstruct the original, so we abort with a helpful message.
        // Note: only check IMEI/ICCID on rows that are actually GPS-style (device_type != 'beacon');
        // beacon rows have no IMEI or ICCID by design.
        const sciRe = /^-?\d+(\.\d+)?[Ee][+-]?\d+$/;
        const corrupted = [];
        rowsRaw.forEach((vals, i) => {
          const rowMap = {};
          headers.forEach((h, idx) => { if (fieldMap[h]) rowMap[fieldMap[h]] = vals[idx]; });
          const isBeaconRow = String(rowMap.device_type || '').toLowerCase().trim() === 'beacon';
          headers.forEach((h, idx) => {
            const key = fieldMap[h];
            const v = vals[idx];
            if ((key === 'imei' || key === 'sim_iccid') && v && sciRe.test(v)) {
              if (isBeaconRow) return; // beacons never have IMEI/ICCID — ignore stray values
              corrupted.push({ row: i + 1, field: key === 'imei' ? 'IMEI' : 'ICCID', value: v });
            }
          });
        });
        if (corrupted.length) {
          const first = corrupted[0];
          toast.error(
            `Row ${first.row}: ${first.field} was corrupted by Excel to "${first.value}" (scientific notation — the real digits are gone). ` +
            `Open the CSV in Google Sheets, or in Excel format the ${first.field} column as Text BEFORE opening the file. ` +
            `${corrupted.length > 1 ? `${corrupted.length - 1} more row(s) affected. ` : ''}Import aborted.`,
            { duration: 12000 }
          );
          return;
        }
        // Resolve "Allocated Users" cells (names or emails, ; or , separated) → user IDs,
        // mirroring the Import Wizard. Unresolved refs are skipped (reported in the toast).
        const userByEmail = new Map();
        const userByName = new Map();
        Object.entries(userMap).forEach(([id, u]) => {
          if (u?.email) userByEmail.set(String(u.email).toLowerCase().trim(), { id, role: u.role });
          if (u?.name) userByName.set(String(u.name).toLowerCase().trim(), { id, role: u.role });
        });
        const unresolvedUserRefs = new Set();
        const unresolvedManagedRefs = new Set();
        let created = 0; let errors = 0;
        let firstError = null;
        for (const values of rowsRaw) {
          const row = {};
          headers.forEach((h, idx) => { if (fieldMap[h] && values[idx]) row[fieldMap[h]] = values[idx]; });
          if (!row.name) continue;
          const isBeacon = String(row.device_type || '').toLowerCase().trim() === 'beacon';
          if (!row.device_type) row.device_type = 'gps_tracker';
          if (!row.protocol) row.protocol = isBeacon ? 'BLE Beacon' : 'Teltonika';
          if (row.mac_address) row.mac_address = _normalizeMacToColon(row.mac_address);
          if (isBeacon) delete row.imei;
          if (row.allocated_tenant_ids) {
            const refs = String(row.allocated_tenant_ids).split(/[;,]/).map(s => s.trim()).filter(Boolean);
            const resolved = [];
            refs.forEach(ref => {
              const u = userByEmail.get(ref.toLowerCase()) || userByName.get(ref.toLowerCase());
              if (u) resolved.push(u.id);
              else unresolvedUserRefs.add(ref);
            });
            row.allocated_tenant_ids = resolved;
          }
          if (row.management_admin_ids) {
            const refs = String(row.management_admin_ids).split(/[;,]/).map(s => s.trim()).filter(Boolean);
            const resolved = [];
            refs.forEach(ref => {
              const u = userByEmail.get(ref.toLowerCase()) || userByName.get(ref.toLowerCase());
              if (u && (u.role === 'admin' || u.role === 'super_admin')) resolved.push(u.id);
              else unresolvedManagedRefs.add(ref);
            });
            row.management_admin_ids = resolved;
          }
          try {
            await api.post('/admin/devices', row);
            created++;
          } catch (err) {
            errors++;
            if (!firstError) firstError = { name: row.name, detail: err?.response?.data?.detail || err.message };
          }
        }
        if (errors && created === 0) {
          toast.error(`Import failed — ${errors} row(s) rejected. First error (${firstError?.name}): ${firstError?.detail}`, { duration: 10000 });
        } else if (errors) {
          toast.warning(`Imported ${created} device(s), ${errors} failed. First error (${firstError?.name}): ${firstError?.detail}`, { duration: 10000 });
        } else {
          toast.success(`Imported ${created} device(s)`);
        }
        if (unresolvedUserRefs.size > 0) {
          toast.warning(
            `${unresolvedUserRefs.size} allocated user(s) not found and skipped: ${[...unresolvedUserRefs].join(', ')}`,
            { duration: 10000 }
          );
        }
        if (unresolvedManagedRefs.size > 0) {
          toast.warning(
            `${unresolvedManagedRefs.size} "Managed By" entr(ies) skipped (not found or not an Admin): ${[...unresolvedManagedRefs].join(', ')}`,
            { duration: 10000 }
          );
        }
        logDeviceIo({ kind: 'import', source: 'quick_csv', file_type: isXlsx ? 'xlsx' : 'csv', file_name: file.name, row_count: rowsRaw.length, created, failed: errors });
        fetchDevices();
      } catch (err) {
        console.error('Quick import failed', err);
        toast.error(`Failed to parse ${isXlsx ? 'XLSX' : 'CSV'} file`);
      }
    };
    if (isXlsx) reader.readAsArrayBuffer(file);
    else reader.readAsText(file);
    e.target.value = '';
  };

  // Clone device
  const handleCloneDevice = (device) => {
    resetForm();
    // Clone allocated users
    const clonedAllocations = device.allocated_tenant_ids || [];
    setFormData(prev => ({
      ...prev,
      name: '',
      imei: '',
      device_type: device.device_type || 'gps_tracker',
      protocol: device.protocol || 'Teltonika',
      model: device.model || '',
      speed_source: device.speed_source || 'gps',
      odometer_source: device.odometer_source || 'off',
      engine_hours_source: device.engine_hours_source || 'off',
      fuel_source: device.fuel_source || 'off',
      server_1_host: device.server_1_host || '',
      server_1_port: device.server_1_port || '',
      show_server_2: device.show_server_2 || false,
      server_2_host: device.server_2_host || '',
      server_2_port: device.server_2_port || '',
      connected_peripherals: device.connected_peripherals || [],
      // Video / dashcam options (e.g. Dashcam Video Tracker types)
      has_video: device.has_video || false,
      num_cameras: device.num_cameras || 0,
      channel_names: device.channel_names ? { ...device.channel_names } : {},
      always_on_streaming: device.always_on_streaming || false,
      allocated_tenant_ids: clonedAllocations,
      custom_commands: (device.custom_commands || []).map(cmd => ({ ...cmd, id: `cmd_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` })),
      io_mappings: (device.io_mappings || []).map(m => ({ ...m, id: `io_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` })),
      identity_logbook: normalizeIdentityLogbook(device.identity_logbook),
      device_icon: device.device_icon || 'car',
      device_icon_color: device.device_icon_color || null,
      map_marker_mode: device.map_marker_mode || 'arrow',
      icon_editing_enabled: device.icon_editing_enabled !== undefined ? device.icon_editing_enabled : true,
    }));
    setAllocatedUserIds(clonedAllocations);
    setPrimaryOperationalAccountId(device.primary_operational_account_id || '');
    fetchAllocatableUsers();
    setFormDirty(false);
    setIsAddDialogOpen(true);
    toast.info(`Cloned from "${device.name}" — enter a new name and IMEI`);
  };


  const handleBulkActivate = async () => {
    if (selectedDeviceIds.length === 0) {
      toast.error('No devices selected');
      return;
    }
    setPendingAction({
      type: 'bulk_activate',
      title: 'Activate Devices',
      message: `Are you sure you want to activate ${selectedDeviceIds.length} device(s)?`,
      variant: 'success',
      action: async () => {
        try {
          await api.post('/admin/devices/bulk-activate', selectedDeviceIds);
          toast.success(`Activated ${selectedDeviceIds.length} device(s)`);
          fetchDevices();
          setSelectedDeviceIds([]);
        } catch (error) {
          console.error('Failed to activate devices:', error);
          toast.error('Failed to activate devices');
        }
      },
    });
  };
  
  const handleBulkDeactivate = async () => {
    if (selectedDeviceIds.length === 0) {
      toast.error('No devices selected');
      return;
    }
    setPendingAction({
      type: 'bulk_deactivate',
      title: 'Deactivate Devices',
      message: `Are you sure you want to deactivate ${selectedDeviceIds.length} device(s)? Deactivated devices will stop connecting to the server.`,
      action: async () => {
        try {
          await api.post('/admin/devices/bulk-deactivate', selectedDeviceIds);
          toast.success(`Deactivated ${selectedDeviceIds.length} device(s)`);
          fetchDevices();
          setSelectedDeviceIds([]);
        } catch (error) {
          console.error('Failed to deactivate devices:', error);
          toast.error('Failed to deactivate devices');
        }
      },
    });
  };
  
  const openBulkAssign = () => {
    if (selectedDeviceIds.length === 0) { toast.error('No devices selected'); return; }
    setBulkAssignSelectedAdmins([]);
    setBulkAssignMode('add');
    setBulkAssignSearch('');
    fetchManageableAdmins();
    setIsBulkAssignOpen(true);
  };

  const toggleBulkAssignAdmin = (adminId) => {
    setBulkAssignSelectedAdmins(prev => prev.includes(adminId) ? prev.filter(x => x !== adminId) : [...prev, adminId]);
  };

  const handleBulkAssignAdmins = async () => {
    setBulkAssignSubmitting(true);
    try {
      const resp = await api.post('/admin/devices/bulk-management-admins', {
        device_ids: selectedDeviceIds,
        admin_ids: bulkAssignSelectedAdmins,
        mode: bulkAssignMode,
      });
      const n = resp.data?.updated ?? 0;
      toast.success(`Updated management list for ${n} device(s)`);
      setIsBulkAssignOpen(false);
      setSelectedDeviceIds([]);
      fetchDevices();
    } catch (error) {
      console.error('Failed to bulk-assign admins:', error);
      toast.error(error.response?.data?.detail || 'Failed to update management list');
    } finally {
      setBulkAssignSubmitting(false);
    }
  };

  const openBulkAlloc = () => {
    if (selectedDeviceIds.length === 0) { toast.error('No devices selected'); return; }
    setBulkAllocSelectedUsers([]);
    setBulkAllocMode('add');
    setBulkAllocSearch('');
    fetchAllocatableUsers();
    setIsBulkAllocOpen(true);
  };

  const toggleBulkAllocUser = (userId) => {
    setBulkAllocSelectedUsers(prev => prev.includes(userId) ? prev.filter(x => x !== userId) : [...prev, userId]);
  };

  const doBulkAllocateUsers = async (confirmRemoveAll = false) => {
    setBulkAllocSubmitting(true);
    try {
      const resp = await api.post('/admin/devices/bulk-allocate-users', {
        device_ids: selectedDeviceIds,
        user_ids: bulkAllocSelectedUsers,
        mode: bulkAllocMode,
        confirm_remove_all: confirmRemoveAll,
      });
      const { updated = 0, unchanged = 0, skipped = [], limit_warnings: limitWarnings = [] } = resp.data || {};
      let msg = `Updated allocation for ${updated} device(s)`;
      if (unchanged > 0) msg += `, ${unchanged} already up to date`;
      toast.success(msg);
      if (skipped.length > 0) {
        toast.warning(`Skipped ${skipped.length} device(s): ${skipped.slice(0, 3).map(s => `${s.name} — ${s.reason}`).join('; ')}${skipped.length > 3 ? '…' : ''}`);
      }
      if (limitWarnings.length > 0) {
        toast.warning(`Users at device limit (not added): ${limitWarnings.join(', ')}`);
      }
      setIsBulkAllocOpen(false);
      setSelectedDeviceIds([]);
      fetchDevices();
    } catch (error) {
      console.error('Failed to bulk-allocate users:', error);
      toast.error(error.response?.data?.detail || 'Failed to update allocation');
    } finally {
      setBulkAllocSubmitting(false);
    }
  };

  const handleBulkAllocateUsers = () => {
    if (bulkAllocMode === 'remove') {
      setPendingAction({
        type: 'bulk_alloc_remove',
        title: 'Remove Users From Devices',
        message: `Remove ${bulkAllocSelectedUsers.length} user(s) from ${selectedDeviceIds.length} device(s)? They will lose operational access to those devices; all other allocated users keep theirs.`,
        action: () => doBulkAllocateUsers(false),
      });
      return;
    }
    if (bulkAllocMode === 'replace' && bulkAllocSelectedUsers.length === 0) {
      setPendingAction({
        type: 'bulk_alloc_clear',
        title: 'Remove All Allocated Users',
        message: `This will remove ALL allocated users from ${selectedDeviceIds.length} device(s) — those users will lose operational access to them. Continue?`,
        action: () => doBulkAllocateUsers(true),
      });
      return;
    }
    doBulkAllocateUsers(false);
  };

  const CLEAR_HISTORY_OPTIONS = [
    { key: 'gps', label: 'GPS records', note: 'Recorded GPS trail & telemetry history used by History, exports and reports' },
    { key: 'live_state', label: 'Live map state', note: 'Removes the device icon from the map and resets Odometer & Engine Hours to 0 — returns when the device next reports' },
    { key: 'events', label: 'Events', note: 'Event Engine occurrences — purple "E" markers and event log rows' },
    { key: 'ble_scans', label: 'Beacon scans', note: 'BLE beacon detections recorded by this device' },
    { key: 'logbook', label: 'Log Book trips', note: 'Closed driver sessions, occupant bindings & trips (an active trip is kept)' },
  ];
  const DEFAULT_CLEAR_TARGETS = { gps: true, live_state: true, events: false, ble_scans: false, logbook: false };
  const [clearHistoryTargets, setClearHistoryTargets] = useState(DEFAULT_CLEAR_TARGETS);
  const clearHistoryTargetsRef = useRef(DEFAULT_CLEAR_TARGETS);
  const toggleClearHistoryTarget = (key) => {
    setClearHistoryTargets((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      clearHistoryTargetsRef.current = next;
      return next;
    });
  };

  const handleClearHistory = async (useRange = false) => {
    if (!selectedDevice) return;
    const rangeText = useRange && historyFromDate && historyToDate
      ? ` from ${formatDT(historyFromDate)} to ${formatDT(historyToDate)}`
      : '';
    setClearHistoryTargets(DEFAULT_CLEAR_TARGETS);
    clearHistoryTargetsRef.current = DEFAULT_CLEAR_TARGETS;
    setPendingAction({
      type: 'clear_history',
      requireTyped: true,
      title: useRange ? 'Clear History (Date Range)' : 'Clear All History',
      message: `Select what to clear${rangeText} for "${selectedDevice.name}". This action cannot be undone.`,
      action: async () => {
        try {
          const selected = Object.entries(clearHistoryTargetsRef.current).filter(([, v]) => v).map(([k]) => k);
          let url = `/admin/devices/${selectedDevice.id}/history?targets=${selected.join(',')}`;
          if (useRange && historyFromDate && historyToDate) {
            const from = new Date(historyFromDate).toISOString();
            const to = new Date(historyToDate).toISOString();
            url += `&start_date=${encodeURIComponent(from)}&end_date=${encodeURIComponent(to)}`;
          }
          const res = await api.delete(url);
          toast.success(res.data.message || 'History cleared');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to clear history');
        }
      },
    });
  };

  const [historyExporting, setHistoryExporting] = useState(null);
  const [historyImporting, setHistoryImporting] = useState(null); // { percent } | null

  const handleImportHistory = () => {
    if (!selectedDevice || historyImporting) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async (ev) => {
        let toastId;
        try {
          const parsed = JSON.parse(ev.target.result);
          const records = parsed.records || (Array.isArray(parsed) ? parsed : []);
          if (!records.length) { toast.error('No records found in file'); return; }
          const BATCH = 2000;
          const total = records.length;
          let imported = 0;
          let skipped = 0;
          setHistoryImporting({ percent: 0 });
          toastId = toast.loading(`Importing 0 of ${total.toLocaleString()} records… (0%)`);
          for (let i = 0; i < total; i += BATCH) {
            const chunk = records.slice(i, i + BATCH);
            const isLast = i + BATCH >= total;
            const res = await api.post(`/admin/devices/${selectedDevice.id}/history/import`, {
              records: chunk,
              log_audit: isLast,
              audit_total: isLast ? total : undefined,
            });
            imported += res.data?.imported ?? 0;
            skipped += res.data?.skipped ?? 0;
            const done = Math.min(i + BATCH, total);
            const percent = Math.round((done / total) * 100);
            setHistoryImporting({ percent });
            toast.loading(`Importing ${done.toLocaleString()} of ${total.toLocaleString()} records… (${percent}%)`, { id: toastId });
          }
          toast.success(`Imported ${imported.toLocaleString()} of ${total.toLocaleString()} records${skipped ? ` — ${skipped.toLocaleString()} duplicates skipped` : ''}`, { id: toastId });
        } catch (err) {
          const msg = err.response?.data?.detail || 'Failed to import history — re-running the import is safe (already-imported records are skipped)';
          if (toastId) toast.error(msg, { id: toastId }); else toast.error(msg);
        } finally {
          setHistoryImporting(null);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleExportHistory = async (format, useRange) => {
    if (!selectedDevice || historyExporting) return;
    setHistoryExporting(format);
    const exportToastId = toast.loading('Preparing export… large histories can take a minute. Keep this browser tab open until the download starts.');
    try {
      let url = `/admin/devices/${selectedDevice.id}/history/export?format=${format}`;
      if (useRange && historyFromDate && historyToDate) {
        const from = new Date(historyFromDate).toISOString();
        const to = new Date(historyToDate).toISOString();
        url += `&start_date=${encodeURIComponent(from)}&end_date=${encodeURIComponent(to)}`;
      }
      const res = await api.get(url, { responseType: 'blob', timeout: 0 });
      const a = document.createElement('a');
      const _ts = getFileTimestamp();
      if (format === 'csv') {
        const blob = new Blob(['\uFEFF', res.data], { type: 'text/csv;charset=utf-8;' });
        a.href = URL.createObjectURL(blob);
        a.download = `${selectedDevice.name}_${selectedDevice.imei || selectedDevice.id}_history_${_ts}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('CSV exported', { id: exportToastId });
      } else {
        const blob = new Blob([res.data], { type: 'application/json' });
        a.href = URL.createObjectURL(blob);
        a.download = `${selectedDevice.name}_${selectedDevice.imei || selectedDevice.id}_history_${_ts}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
        const total = res.headers['x-total-records'];
        toast.success(total ? `Exported ${Number(total).toLocaleString()} records as JSON` : 'JSON exported', { id: exportToastId });
      }
    } catch (error) {
      let msg = 'Failed to export history';
      try {
        const t = await error.response?.data?.text?.();
        if (t) msg = JSON.parse(t)?.detail || msg;
      } catch { /* keep default */ }
      toast.error(msg, { id: exportToastId });
    } finally {
      setHistoryExporting(null);
    }
  };
  
  // Bulk IO Mapping Import
  const handleBulkImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const imported = JSON.parse(ev.target.result);
        if (!Array.isArray(imported)) { toast.error('Invalid format — expected a JSON array'); return; }
        setBulkImportMappings(imported);
        setBulkImportTargetIds([]);
        setBulkImportSearch('');
        setBulkImportMode('merge');
        setIsBulkImportOpen(true);
      } catch { toast.error('Failed to parse JSON file'); }
    };
    reader.readAsText(file);
  };

  const handleBulkImportSubmit = async () => {
    if (bulkImportTargetIds.length === 0) { toast.error('Select at least one device'); return; }
    if (bulkImportMappings.length === 0) { toast.error('No mappings to import'); return; }
    setBulkImporting(true);
    try {
      // Strip internal fields before sending
      const clean = bulkImportMappings.map(({ id, _customUnit, ...rest }) => rest);
      const res = await api.post('/admin/devices/bulk-import-io-mappings', {
        device_ids: bulkImportTargetIds,
        io_mappings: clean,
        mode: bulkImportMode,
      });
      toast.success(res.data.message);
      setIsBulkImportOpen(false);
      setBulkImportMappings([]);
      fetchDevices();
    } catch (error) {
      console.error('Bulk import failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  const handleBulkCmdImportSubmit = async () => {
    if (bulkCmdImportTargetIds.length === 0) { toast.error('Select at least one device'); return; }
    if (bulkCmdImportData.length === 0) { toast.error('No commands to import'); return; }
    setBulkCmdImporting(true);
    try {
      const clean = bulkCmdImportData.map(({ id, ...rest }) => rest);
      const res = await api.post('/admin/devices/bulk-import-commands', {
        device_ids: bulkCmdImportTargetIds,
        commands: clean,
        mode: bulkCmdImportMode,
      });
      toast.success(res.data.message);
      setIsBulkCmdImportOpen(false);
      setBulkCmdImportData([]);
      fetchDevices();
    } catch (error) {
      console.error('Bulk command import failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk command import failed');
    } finally {
      setBulkCmdImporting(false);
    }
  };

  // Command management
  const getDefaultCommand = () => ({
    protocol: formData.protocol || 'Teltonika',
    command_text: '',
    button_label: '',
    button_color: 'blue',
    description: '',
    requires_confirmation: false,
    show_telemetry: true,
    show_fleet_card: true,
    target_device_id: null,
    command_mode: 'single',
    command_sequence: [],
  });

  const handleAddCommand = () => {
    if (!newCommand.button_label) {
      toast.error('Button label is required');
      return false;
    }
    if (newCommand.command_mode === 'sequence') {
      if (!newCommand.command_sequence || newCommand.command_sequence.length < 2) {
        toast.error('A command sequence requires at least 2 steps');
        return false;
      }
      if (newCommand.command_sequence.some(s => !s.command_text)) {
        toast.error('All sequence steps must have a command');
        return false;
      }
    } else {
      if (!newCommand.command_text) {
        toast.error('Command text is required');
        return false;
      }
    }
    // Use target device's protocol if command targets a linked device, otherwise primary's protocol
    let cmdProtocol = formData.protocol;
    if (newCommand.target_device_id) {
      const targetDev = devices.find(d => d.id === newCommand.target_device_id);
      if (targetDev?.protocol) cmdProtocol = targetDev.protocol;
    }
    const cmdWithProtocol = { ...newCommand, protocol: cmdProtocol };
    if (editingCommandId) {
      setFormData(prev => ({
        ...prev,
        custom_commands: prev.custom_commands.map(cmd => cmd.id === editingCommandId ? { ...cmdWithProtocol, id: editingCommandId } : cmd)
      }));
      setEditingCommandId(null);
      toast.success('Command updated');
    } else {
      const command = { id: `cmd_${Date.now()}`, ...cmdWithProtocol };
      setFormData(prev => ({
        ...prev,
        custom_commands: [...prev.custom_commands, command]
      }));
      toast.success('Command added');
    }
    setNewCommand(getDefaultCommand());
    return true;
  };

  const handleEditCommand = (cmd) => {
    setNewCommand({ ...cmd });
    setEditingCommandId(cmd.id);
    scrollFormCardIntoView(commandFormCardRef);
  };

  const handleCancelEditCommand = () => {
    setNewCommand(getDefaultCommand());
    setEditingCommandId(null);
  };

  // Interpolate value using calibration table (X→Y points)
  const interpolateCalibration = (rawValue, calibration) => {
    if (!calibration || calibration.length === 0) return rawValue;
    const val = Number(rawValue);
    if (isNaN(val)) return rawValue;
    
    // Sort points by X
    const points = [...calibration].sort((a, b) => a.x - b.x);
    
    // Below lowest point
    if (val <= points[0].x) return points[0].y;
    // Above highest point
    if (val >= points[points.length - 1].x) return points[points.length - 1].y;
    
    // Find the two points to interpolate between
    for (let i = 0; i < points.length - 1; i++) {
      if (val >= points[i].x && val <= points[i + 1].x) {
        const ratio = (val - points[i].x) / (points[i + 1].x - points[i].x);
        return points[i].y + ratio * (points[i + 1].y - points[i].y);
      }
    }
    return rawValue;
  };

  // Evaluate IO mapping: Calibration → Formula → Decimals → Units
  const evaluateFormula = (rawValue, formula, decimalPlaces, dictionary, calibration) => {
    if (rawValue === undefined || rawValue === null) return '-';
    // Dictionary lookup first
    if (dictionary && Object.keys(dictionary).length > 0) {
      const key = String(rawValue);
      const dictVal = dictionary[key];
      if (dictVal !== undefined && dictVal !== null) {
        return typeof dictVal === 'object' ? JSON.stringify(dictVal) : String(dictVal);
      }
    }
    // Apply calibration interpolation first
    let value = calibration && calibration.length > 0 ? interpolateCalibration(rawValue, calibration) : rawValue;
    // Formula evaluation
    if (formula) {
      try {
        const X = Number(value);
        if (isNaN(X)) return String(value);
        const sanitized = formula.replace(/[^0-9X+\-*/().%\s]/g, '');
        const result = new Function('X', `return ${sanitized}`)(X);
        if (decimalPlaces != null && typeof result === 'number') {
          return result.toFixed(decimalPlaces);
        }
        return String(result);
      } catch { return String(value); }
    }
    // Plain value with decimals
    if (decimalPlaces != null && !isNaN(Number(value))) {
      return Number(value).toFixed(decimalPlaces);
    }
    return String(value);
  };

  
  const handleRemoveCommand = (commandId) => {
    const cmd = formData.custom_commands.find(c => c.id === commandId);
    setPendingDelete({ type: 'command', id: commandId, label: cmd?.button_label || 'this command' });
  };

  const handleRemoveIOMapping = (mappingId) => {
    const mapping = formData.io_mappings.find(m => m.id === mappingId);
    setPendingDelete({ type: 'mapping', id: mappingId, label: mapping?.display_name || 'this mapping' });
  };

  const confirmPendingDelete = () => {
    if (!pendingDelete) return;
    if (pendingDelete.type === 'command') {
      setFormData(prev => ({
        ...prev,
        custom_commands: prev.custom_commands.filter(cmd => cmd.id !== pendingDelete.id)
      }));
      if (editingCommandId === pendingDelete.id) {
        setEditingCommandId(null);
        setNewCommand(getDefaultCommand());
      }
      toast.success('Command removed');
    } else if (pendingDelete.type === 'mapping') {
      setFormData(prev => ({
        ...prev,
        io_mappings: prev.io_mappings.filter(m => m.id !== pendingDelete.id)
      }));
      toast.success('IO mapping removed');
    }
    setPendingDelete(null);
  };

  const handleSendCommand = async (cmd) => {
    if (!selectedDevice?.imei) { toast.error('Device has no IMEI — cannot send command'); return; }
    if (cmd.requires_confirmation) {
      setSendConfirmCmd(cmd);
      return;
    }
    await executeSendCommand(cmd);
  };

  const executeSendCommand = async (cmd) => {
    setSendingCommand(true);
    try {
      // Route command to linked device if target_device_id is set
      let targetImei = selectedDevice.imei;
      let targetName = selectedDevice.name;
      if (cmd.target_device_id) {
        const targetDev = devices.find(d => d.id === cmd.target_device_id);
        if (targetDev?.imei) {
          targetImei = targetDev.imei;
          targetName = targetDev.name;
        } else {
          toast.error('Target device not found or has no IMEI');
          setSendingCommand(false);
          return;
        }
      }
      if (cmd.command_mode === 'sequence' && cmd.command_sequence?.length > 0) {
        // Send as a sequence — all steps dispatched to TCP socket in one message
        await api.post('/parsing/commands/send-sequence', {
          device_imei: targetImei,
          steps: cmd.command_sequence.map(s => ({
            command: s.command_text,
            delay_ms: s.delay_ms || 0,
          })),
          button_label: cmd.button_label,
        });
        toast.success(`Sequence "${cmd.button_label}" queued for ${targetName} (${cmd.command_sequence.length} steps)${cmd.target_device_id ? ' (linked)' : ''}`);
      } else {
        // Single command (existing behavior)
        await api.post('/parsing/commands/send', {
          device_imei: targetImei,
          command: cmd.command_text,
          command_type: 'custom',
        });
        toast.success(`Command "${cmd.button_label}" queued for ${targetName}${cmd.target_device_id ? ' (linked)' : ''}`);
      }
      setSendConfirmCmd(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send command');
    } finally {
      setSendingCommand(false);
    }
  };

  const handleDeleteQueuedCommand = async (commandId) => {
    try {
      await api.delete(`/parsing/commands/${commandId}`);
      toast.success('Queued command deleted');
      if (selectedDevice?.imei) fetchCommandHistory(selectedDevice.imei);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete command');
    }
  };

  // IO Mapping management
  const getDefaultIOMapping = () => ({
    io_type: 'Battery', io_id: '', display_name: '', value_type: 'Value',
    formula: '', units: '', decimal_places: 2, calibration: [],
    logic_mapping: { "1": "ON", "0": "OFF" },
    dictionary: {}, data_source: 'device_io', show_telemetry: true,
    show_fleet_card: true, show_map_popup: false, show_icon: true, ignore_ignition_off: false,
    is_computed: false, computed_logic: null,
  });

  const nextCustomIoId = React.useMemo(() => {
    const used = new Set((formData.io_mappings || []).map(m => String(m.io_id)));
    let n = 1;
    while (used.has(`custom${n}`)) n += 1;
    return `custom${n}`;
  }, [formData.io_mappings]);

  const ioDuplicates = React.useMemo(() => {
    const byId = {};
    (formData.io_mappings || []).forEach(m => {
      const key = String(m.io_id ?? '').trim();
      if (!key) return;
      (byId[key] = byId[key] || []).push(m.display_name || m.io_type || key);
    });
    return Object.fromEntries(Object.entries(byId).filter(([, names]) => names.length > 1));
  }, [formData.io_mappings]);

  const handleAddIOMapping = () => {
    if (newIOMapping.is_computed) {
      if (!newIOMapping.display_name) {
        toast.error('Display name is required');
        return false;
      }
      const hasCond = (newIOMapping.computed_logic?.groups || []).some(g =>
        (g.conditions || []).some(c => String(c.io_id || '').trim() && String(c.value ?? '').trim() !== ''));
      if (!hasCond) {
        toast.error('Add at least one configured condition to the logic formula');
        return false;
      }
    } else if (!newIOMapping.io_id || !newIOMapping.display_name) {
      toast.error('IO Parameter and display name are required');
      return false;
    }
    
    if (editingMappingId) {
      // Update existing
      setFormData(prev => ({
        ...prev,
        io_mappings: prev.io_mappings.map(m => m.id === editingMappingId ? { ...newIOMapping, id: editingMappingId } : m)
      }));
      setEditingMappingId(null);
      toast.success('IO mapping updated');
    } else {
      // Add new
      const mapping = { id: `io_${Date.now()}`, ...newIOMapping };
      if (mapping.is_computed && !mapping.io_id) mapping.io_id = nextCustomIoId;
      setFormData(prev => ({ ...prev, io_mappings: [...prev.io_mappings, mapping] }));
      toast.success('IO mapping added');
    }
    
    setNewIOMapping(getDefaultIOMapping());
    return true;
  };

  const handleEditIOMapping = (mapping) => {
    setNewIOMapping({ ...mapping });
    setEditingMappingId(mapping.id);
    scrollFormCardIntoView(ioMappingFormCardRef);
  };

  const handleCancelEditMapping = () => {
    setNewIOMapping(getDefaultIOMapping());
    setEditingMappingId(null);
  };

  // ─── Unfinished form guard (IO mapping + command forms) ───
  const ioFormDirty = !!editingMappingId || !!newIOMapping.is_computed
    || !!(newIOMapping.io_id && newIOMapping.io_id !== '__placeholder')
    || !!(newIOMapping.display_name || '').trim();
  const commandFormDirty = !!editingCommandId
    || !!(newCommand.button_label || '').trim()
    || !!(newCommand.command_text || '').trim()
    || (newCommand.command_mode === 'sequence' && (newCommand.command_sequence || []).some(s => (s.command_text || '').trim()));

  const guardedSave = (action) => {
    if (ioFormDirty || commandFormDirty) {
      setPendingFormGuard({ action });
      return;
    }
    if (action === 'create') handleAddDevice(); else handleUpdateDevice();
  };

  const handleGuardApply = () => {
    let ok = true;
    if (ioFormDirty) ok = handleAddIOMapping();
    if (ok && commandFormDirty) ok = handleAddCommand();
    const action = pendingFormGuard?.action;
    setPendingFormGuard(null);
    if (!ok) return;
    setSaveAfterApply(action);
  };

  const handleGuardDiscard = () => {
    if (ioFormDirty) handleCancelEditMapping();
    if (commandFormDirty) handleCancelEditCommand();
    const action = pendingFormGuard?.action;
    setPendingFormGuard(null);
    if (action === 'create') handleAddDevice(); else handleUpdateDevice();
  };

  useEffect(() => {
    if (!saveAfterApply) return;
    const action = saveAfterApply;
    setSaveAfterApply(null);
    if (action === 'create') handleAddDevice(); else handleUpdateDevice();
  }, [saveAfterApply]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Peripheral management
  const getDefaultPeripheral = () => ({ peripheral_type: 'can_module', name: '', serial_number: '', connection_type: 'can_bus', program_id: '' });

  const handleAddPeripheral = () => {
    if (!newPeripheral.name) {
      toast.error('Peripheral name is required');
      return;
    }
    if (editingPeripheralId) {
      setFormData(prev => ({
        ...prev,
        connected_peripherals: prev.connected_peripherals.map(p => p.id === editingPeripheralId ? { ...newPeripheral, id: editingPeripheralId } : p)
      }));
      setEditingPeripheralId(null);
      toast.success('Peripheral updated');
    } else {
      const peripheral = { id: `per_${Date.now()}`, ...newPeripheral };
      setFormData(prev => ({
        ...prev,
        connected_peripherals: [...prev.connected_peripherals, peripheral]
      }));
      toast.success('Peripheral added');
    }
    setNewPeripheral(getDefaultPeripheral());
  };

  const handleEditPeripheral = (peripheral) => {
    setNewPeripheral({ ...peripheral });
    setEditingPeripheralId(peripheral.id);
    setShowPeripheralForm(true);
  };

  const handleCancelEditPeripheral = () => {
    setNewPeripheral(getDefaultPeripheral());
    setEditingPeripheralId(null);
  };
  
  const handleRemovePeripheral = (peripheralId) => {
    setFormData(prev => ({
      ...prev,
      connected_peripherals: prev.connected_peripherals.filter(p => p.id !== peripheralId)
    }));
    if (editingPeripheralId === peripheralId) {
      setEditingPeripheralId(null);
      setNewPeripheral(getDefaultPeripheral());
    }
    toast.success('Peripheral removed');
  };
  
  // Tag management
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (!tag) return;
    if (formData.tags.includes(tag)) { toast.error('Tag already exists'); return; }
    setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
    setTagInput('');
  };
  
  const handleRemoveTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };
  
  // Date formatting helpers (DD/MM/YYYY)
  const formatDateForDisplay = (isoDate) => {
    if (!isoDate) return '';
    const parts = isoDate.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return isoDate;
  };
  
  const parseDateFromDisplay = (displayDate) => {
    if (!displayDate) return '';
    const parts = displayDate.replace(/[^\d/]/g, '').split('/');
    if (parts.length === 3 && parts[0].length <= 2 && parts[1].length <= 2 && parts[2].length === 4) {
      return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
    }
    return '';
  };
  
  const handleDateInput = (value) => {
    // Auto-add slashes
    const cleaned = value.replace(/[^\d/]/g, '');
    let formatted = cleaned;
    if (cleaned.length === 2 && !cleaned.includes('/')) formatted = cleaned + '/';
    else if (cleaned.length === 5 && cleaned.charAt(2) === '/' && !cleaned.substring(3).includes('/')) formatted = cleaned + '/';
    
    if (formatted.length <= 10) {
      handleInputChange('installation_date_display', formatted);
      const iso = parseDateFromDisplay(formatted);
      if (iso) handleInputChange('installation_date', iso);
    }
  };
  
  // SIM auto-populate: look up provider + MSISDN from the SIM database by ICCID alone
  const lookupSimNumber = async (iccid) => {
    if (!iccid || iccid.length < 15) return;
    try {
      setSimLookupLoading(true);
      const resp = await api.post('/admin/sim-cards/lookup-by-iccids', [iccid]);
      const sim = resp.data?.results?.[iccid];
      const usedBy = (resp.data?.in_use?.[iccid] || []).filter(d => d.id !== selectedDevice?.id);
      if (sim) {
        if (sim.mobile_number) handleInputChange('sim_number', sim.mobile_number);
        if (sim.provider_name && sim.provider_name !== 'Unknown') handleInputChange('sim_provider', sim.provider_name);
        if (sim.mobile_number) toast.success('SIM provider & mobile auto-populated from the SIM database');
        else toast.info('SIM found in the database but has no mobile number on record');
        const status = String(sim.status || '').trim();
        if (status && !/^(active|activated|enabled)$/i.test(status)) {
          toast.warning(`Warning: this SIM is "${status}" in the SIM database — it may not be able to report data`, { duration: 8000 });
        }
      } else {
        toast.info('ICCID not found in the SIM database');
      }
      if (usedBy.length > 0) {
        toast.warning(`Warning: this SIM is already assigned to device "${usedBy[0].name}"${usedBy.length > 1 ? ` and ${usedBy.length - 1} other(s)` : ''}`, { duration: 8000 });
      }
    } catch (err) {
      toast.error('Failed to look up SIM details');
    } finally {
      setSimLookupLoading(false);
    }
  };

  const simLookupTimerRef = React.useRef(null);
  const handleSimIccidChange = (iccid) => {
    handleInputChange('sim_iccid', iccid);
    if (!simAutoPopulate) return;
    if (simLookupTimerRef.current) clearTimeout(simLookupTimerRef.current);
    simLookupTimerRef.current = setTimeout(() => lookupSimNumber(String(iccid || '').trim()), 600);
  };
  
  const handleSimRefresh = () => {
    lookupSimNumber(String(formData.sim_iccid || '').trim());
  };

  const handleOpenSimDetail = async () => {
    const iccid = deviceStatus?.sim?.iccid || formData.sim_iccid;
    if (!iccid) { toast.error('No SIM ICCID set for this device'); return; }
    setSimDetailLoading(true);
    setSimDetailOpen(true);
    try {
      // Find the SIM card by ICCID
      const simRes = await api.get(`/admin/sim-cards/search-by-iccid/${encodeURIComponent(iccid)}`);
      if (simRes.data?.id) {
        const providerName = simRes.data.provider_name || '';
        const detailRes = await api.post(`/admin/sim-cards/${simRes.data.id}/fetch-details`);
        // Sanitize nested objects
        const sanitized = {};
        for (const [k, v] of Object.entries(detailRes.data || {})) {
          sanitized[k] = (v !== null && typeof v === 'object' && !Array.isArray(v)) ? (v.description || v.name || v.label || JSON.stringify(v)) : v;
        }
        // Preserve provider name from the search result
        if (!sanitized.provider_name) sanitized.provider_name = providerName;
        setSimDetailData(sanitized);
        // Fetch SMS history
        try {
          const smsRes = await api.get(`/admin/sim-cards/${simRes.data.id}/sms/history`);
          setSimSmsHistory((smsRes.data || []).map(m => {
            const s = {};
            for (const [k2, v2] of Object.entries(m)) { s[k2] = (v2 !== null && typeof v2 === 'object' && !Array.isArray(v2)) ? (v2.description || JSON.stringify(v2)) : v2; }
            return s;
          }));
        } catch { setSimSmsHistory([]); }
      } else {
        // Fallback: show basic info from device
        setSimDetailData({
          iccid,
          mobile_number: deviceStatus?.sim?.mobile_number || formData.sim_number || '',
          provider_name: deviceStatus?.sim?.provider || formData.sim_provider || '',
          status: 'Unknown',
        });
      }
    } catch (err) {
      setSimDetailData({
        iccid,
        mobile_number: deviceStatus?.sim?.mobile_number || formData.sim_number || '',
        provider_name: deviceStatus?.sim?.provider || formData.sim_provider || '',
        status: 'Unable to fetch details',
        error: err.response?.data?.detail || 'API error',
      });
    } finally {
      setSimDetailLoading(false);
    }
  };

  const handleSimSendSms = async () => {
    if (!simSmsMessage || !simDetailData?.id) return;
    setSimSmsSending(true);
    try {
      await api.post(`/admin/sim-cards/${simDetailData.id}/sms/send`, { message: simSmsMessage });
      toast.success('SMS sent successfully');
      setSimSmsMessage('');
      // Refresh history
      const smsRes = await api.get(`/admin/sim-cards/${simDetailData.id}/sms/history`);
      setSimSmsHistory((smsRes.data || []).map(m => {
        const s = {};
        for (const [k, v] of Object.entries(m)) { s[k] = (v !== null && typeof v === 'object' && !Array.isArray(v)) ? (v.description || JSON.stringify(v)) : v; }
        return s;
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSimSmsSending(false);
    }
  };


  const handleReplaceDevice = async () => {
    if (!replaceImei.trim() || replaceImei.trim().length < 10) {
      toast.error('Please enter a valid new IMEI number');
      return;
    }
    setReplacing(true);
    try {
      const res = await api.post(`/admin/devices/${selectedDevice.id}/replace`, {
        new_imei: replaceImei.trim(),
        reason: replaceReason.trim(),
        transfer_history: replaceTransferHistory,
      });
      toast.success(`Device replaced. Migrated ${res.data.migrated_records} records, ${res.data.migrated_commands} commands.`);
      setShowReplaceDevice(false);
      setReplaceImei('');
      setReplaceReason('');
      handleInputChange('imei', replaceImei.trim());
      fetchDevices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace device');
    } finally {
      setReplacing(false);
    }
  };

  const handleSimSyncByImei = async () => {
    const imei = formData.imei?.trim();
    if (!imei || imei.length < 10) {
      toast.error('Enter a valid IMEI number first (Device Details tab)');
      return;
    }
    setSimImeiSyncLoading(true);
    try {
      const resp = await api.get(`/admin/sim-cards/search-by-imei/${encodeURIComponent(imei)}`);
      if (resp.data.found && resp.data.results?.length > 0) {
        const sim = resp.data.results[0];
        handleInputChange('sim_provider', sim.provider_name || '');
        handleInputChange('sim_iccid', sim.iccid || '');
        handleInputChange('sim_number', sim.mobile_number || '');
        toast.success(`SIM found: ${sim.provider_name} — ${sim.iccid}`);
      } else {
        toast.info('No SIM card found for this IMEI across all providers');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to search for SIM by IMEI');
    } finally {
      setSimImeiSyncLoading(false);
    }
  };

  // Save a manually-entered SIM (e.g. a non-API provider like ALDI) into the
  // provider's SIM card list so it can be managed in Admin System > Sim Provider.
  const handleAddSimToProvider = async () => {
    const providerName = (formData.sim_provider || '').trim();
    const iccid = (formData.sim_iccid || '').trim();
    if (!providerName || !iccid) {
      toast.error('Select a SIM provider and enter the ICCID first');
      return;
    }
    const provider = simProviders.find((p) => p.name === providerName);
    if (!provider) {
      toast.error(`Provider "${providerName}" not found`);
      return;
    }
    if (provider.provider_type !== 'manual') {
      toast.error(`${providerName} syncs automatically via its API — SIMs can't be added manually`);
      return;
    }
    setSimAddToProviderLoading(true);
    try {
      await api.post(`/admin/sim-providers/${provider.id}/sim-cards`, {
        provider_id: provider.id,
        iccid,
        mobile_number: (formData.sim_number || '').trim() || null,
        imei: (formData.imei || '').trim() || null,
        status: 'active',
      });
      toast.success(`SIM ${iccid} added to ${providerName} — manage it in Admin System → Sim Provider`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (String(detail).toLowerCase().includes('already exists')) {
        toast.info('This SIM is already in the provider list');
      } else {
        toast.error(detail || 'Failed to add SIM to provider list');
      }
    } finally {
      setSimAddToProviderLoading(false);
    }
  };
  
  // Scroll tab content to top on tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Auto-fetch data when switching to status/beacon tabs
    if (tab === 'status' && selectedDevice?.imei) {
      fetchDeviceStatus(selectedDevice.imei);
    }
    if (tab === 'beacon_status' && selectedDevice) {
      refreshSelectedBeaconStatus(selectedDevice);
    }
    if (tab === 'mapping' && selectedDevice) {
      fetchProtocolIODefaults(selectedDevice.protocol, selectedDevice.imei);
      fetchCurrentIOValues(selectedDevice.imei);
    }
    if (tab === 'management_list') {
      fetchManageableAdmins();
    }
    setTimeout(() => {
      const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) scrollArea.scrollTop = 0;
    }, 50);
  };
  
  // Bulk selection — header checkbox toggles ONLY the current-page rows.
  // Cross-page "Select All" lives on the selection-bar button.
  const handleSelectAll = (checked) => {
    const pageIds = filteredDevices.map(d => d.id);
    setSelectedDeviceIds((prev) => checked
      ? Array.from(new Set([...prev, ...pageIds]))
      : prev.filter(id => !pageIds.includes(id)));
  };
  
  const handleSelectDevice = (deviceId, checked) => {
    if (checked) {
      setSelectedDeviceIds(prev => [...prev, deviceId]);
    } else {
      setSelectedDeviceIds(prev => prev.filter(id => id !== deviceId));
    }
  };
  
  const formatTimestamp = (timestamp) => {
    return formatUserDateTime(timestamp, userDateFmt, userTimeFmt, userTimezone);
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      online: { color: 'bg-green-600', label: 'Online', icon: Wifi },
      offline: { color: 'bg-gray-500', label: 'Offline', icon: WifiOff },
      moving: { color: 'bg-blue-600', label: 'Moving', icon: Activity },
      stopped: { color: 'bg-red-600', label: 'Stopped', icon: Circle },
      idle: { color: 'bg-yellow-600', label: 'Idle', icon: Circle },
    };
    
    const config = statusConfig[status] || statusConfig.offline;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getDeviceStatusBadge = (device) => {
    if (device?.device_type === 'beacon') {
      const detected = (device.beacon_scan_count || 0) > 0;
      return (
        <Badge className={`${detected ? 'bg-cyan-600' : 'bg-slate-500'} text-white`} data-testid={`beacon-list-status-${device.id}`}>
          <Bluetooth className="w-3 h-3 mr-1" />
          {detected ? 'Detected' : 'Not Detected'}
        </Badge>
      );
    }
    return getStatusBadge(device?.status);
  };

  const renderBeaconBatteryStatus = (scan) => {
    if (scan?.low_battery === true) {
      return <Badge className="bg-red-100 text-red-700" data-testid="beacon-scan-low-battery-badge">Low</Badge>;
    }
    if (scan?.low_battery === false) {
      return <Badge className="bg-green-100 text-green-700" data-testid="beacon-scan-battery-ok-badge">OK</Badge>;
    }
    if (scan?.battery_voltage_mv != null) {
      return <span className="font-mono" data-testid="beacon-scan-battery-voltage">{scan.battery_voltage_mv} mV</span>;
    }
    if (scan?.battery != null) {
      return <span data-testid="beacon-scan-battery-percent">{scan.battery}%</span>;
    }
    return <span className="text-muted-foreground">-</span>;
  };
  
  const filteredDevices = [...devices]
    .filter(d => {
      if (managementFilter === 'unassigned') return (d.management_admin_ids || []).length === 0;
      if (managementFilter === 'assigned') return (d.management_admin_ids || []).length > 0;
      return true;
    });
    // NOTE: sorting is now done server-side via `sort` + `sort_dir` query params on
    // /admin/devices — this ensures the sorted view spans the FULL result set instead of
    // only the currently-loaded page. The client trusts the backend order and preserves it.

  const linkedDeviceSearchNeedle = linkedDeviceSearchTerm.trim().toLowerCase();
  // Server-side type-ahead now returns pre-filtered results, so we only need to hide the
  // currently-edited device + devices that are already linked. The `linkedDeviceSearchNeedle`
  // itself is applied server-side via `searchLinkableDevices(term)` — we keep the local
  // needle only as a hint for the empty-state UI.
  const filteredLinkableDevices = linkableDevices
    .filter(d => d.id !== selectedDevice?.id && !formData.linked_devices.some(ld => ld.device_id === d.id));

  // Filter the protocol IO-element list for the "IO Parameter" dropdown search.
  // Matches the IO id (e.g. AVL id) OR the default name. Order is preserved
  // (protocol-defined order is meaningful for IO/AVL ids).
  const ioParamSearchNeedle = ioParamSearchTerm.trim().toLowerCase();
  const filteredProtocolIODefaults = protocolIODefaults.filter(d => {
    if (!ioParamSearchNeedle) return true;
    return [d.io_id, d.default_name]
      .some(value => String(value ?? '').toLowerCase().includes(ioParamSearchNeedle));
  });

  // Secondary IO parameter list — same search, but excludes the primary IO id.
  const secondaryIoParamSearchNeedle = secondaryIoParamSearchTerm.trim().toLowerCase();
  const filteredSecondaryProtocolIODefaults = protocolIODefaults.filter(d => {
    if (d.io_id === newIOMapping.io_id) return false;
    if (!secondaryIoParamSearchNeedle) return true;
    return [d.io_id, d.default_name]
      .some(value => String(value ?? '').toLowerCase().includes(secondaryIoParamSearchNeedle));
  });

  const handleDeviceSort = (field) => {
    if (deviceSortField === field) {
      setDeviceSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setDeviceSortField(field);
      setDeviceSortDir('asc');
    }
    // Batch pageIndex reset with the sort-state updates so React does ONE render
    // (avoids the previous double /admin/devices call when sorting while on page ≥2).
    setPageIndex(0);
  };

  const allocatedOperationalAccounts = allocatedUserIds
    .map(id => allocatableUsers.find(user => user.id === id))
    .filter(user => ['super_admin', 'admin', 'manager', 'end_user'].includes(user?.role))
    .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', undefined, { sensitivity: 'base' }));
  const primaryOperationalAccountMissing = formData.device_type !== 'beacon'
    && allocatedOperationalAccounts.length > 1
    && !allocatedOperationalAccounts.some(user => user.id === primaryOperationalAccountId);
  
  // Shared form tabs renderer for both Add and Edit dialogs
  const renderFormTabs = (isEdit = false) => (
    <>
      {/* Tab 1: Device Details */}
      {activeTab === 'details' && (
        <div className="space-y-5">
          {isEdit && (
            <div className="flex items-center justify-between p-4 rounded-lg border-2 border-slate-200 dark:border-slate-700" data-testid="device-status-card">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${formData.is_active ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                  {formData.is_active ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-sm">Device Status</p>
                  <p className="text-xs text-muted-foreground">
                    {formData.is_active ? 'Device can connect to the server' : 'Device is blocked from connecting'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm font-medium ${formData.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {formData.is_active ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setStatusTogglePrompt({ next: v })}
                  data-testid="device-status-switch"
                />
              </div>
            </div>
          )}
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Device Name *</Label>
              <Input
                data-testid="device-name-input"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="FMB920-001"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">Internal name for the hardware device</p>
            </div>
            <div className="space-y-2">
              <Label>Device Type</Label>
              <Select value={formData.device_type} onValueChange={(v) => handleInputChange('device_type', v)}>
                <SelectTrigger data-testid="device-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {deviceTypes.map(type => (
                    <SelectItem key={type.value} value={type.value} data-testid={`device-type-option-${type.value}`}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Video (for Dashcam Video Tracker) */}
          {formData.device_type === 'dashcam' && (
            <div className="p-3 border rounded-lg bg-muted/50 space-y-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.has_video}
                    onCheckedChange={(v) => handleInputChange('has_video', v)}
                    data-testid="details-has-video"
                  />
                  <Label>Device has video</Label>
                </div>
                {formData.has_video && (
                  <div className="flex items-center gap-2">
                    <Label>Number of Cameras:</Label>
                    <Select value={String(formData.num_cameras)} onValueChange={(v) => handleInputChange('num_cameras', parseInt(v))}>
                      <SelectTrigger className="h-8 w-20" data-testid="details-num-cameras">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 17 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {formData.has_video && formData.num_cameras > 0 && (
                <>
                  {/* Channel name inputs (custom labels per camera) */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Channel Names (optional — shown in Live Videos)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {Array.from({ length: formData.num_cameras }, (_, i) => {
                        const ch = i + 1;
                        return (
                          <div key={ch} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-12 shrink-0">CH{ch}</span>
                            <Input
                              type="text"
                              placeholder={`e.g. Driver Facing`}
                              value={formData.channel_names?.[String(ch)] || ''}
                              onChange={(e) => {
                                const next = { ...(formData.channel_names || {}) };
                                if (e.target.value) next[String(ch)] = e.target.value;
                                else delete next[String(ch)];
                                handleInputChange('channel_names', next);
                              }}
                              className="h-8 text-xs"
                              data-testid={`channel-name-${ch}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Always-on streaming (admin bypass for auto-timeout) */}
                  <div className="flex items-start gap-3 pt-2 border-t">
                    <Switch
                      checked={!!formData.always_on_streaming}
                      onCheckedChange={(v) => handleInputChange('always_on_streaming', v)}
                      className="mt-0.5"
                      data-testid="details-always-on"
                    />
                    <div className="flex-1">
                      <Label className="text-sm font-medium">Always-on Streaming</Label>
                      <p className="text-xs text-muted-foreground">
                        Bypass the auto-timeout for this device. Live streams won't auto-stop — use for critical fleet vehicles. <span className="text-amber-600 font-medium">Warning: higher bandwidth usage.</span>
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Identification */}
          <div className={`grid gap-4 items-start grid-cols-1 ${formData.device_type === 'beacon' ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
            {formData.device_type !== 'beacon' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between min-h-[20px]">
                  <div className="flex items-center gap-1">
                    <Label>IMEI *</Label>
                    {formData.imei && (
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(formData.imei); toast.success('IMEI copied'); }}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="details-imei-copy-btn"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {selectedDevice && (
                    <button
                      type="button"
                      onClick={() => setImeiLocked(!imeiLocked)}
                      className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-md transition-colors ${imeiLocked ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60' : 'text-green-600 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-950/60'}`}
                      data-testid="imei-lock-toggle"
                    >
                      <Lock className="w-3 h-3" />
                      {imeiLocked ? 'Locked' : 'Unlocked'}
                    </button>
                  )}
                </div>
                <Input
                  data-testid="device-imei-input"
                  value={formData.imei}
                  onChange={(e) => handleInputChange('imei', e.target.value)}
                  placeholder="861234567890123"
                  className="font-mono"
                  autoComplete="off"
                  disabled={!!selectedDevice && imeiLocked}
                />
                {selectedDevice && formData.imei && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs text-amber-600 hover:text-amber-700 p-0"
                    onClick={() => { setReplaceImei(''); setReplaceReason(''); setReplaceTransferHistory(true); setShowReplaceDevice(true); }}
                    data-testid="replace-device-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Replace Device
                  </Button>
                )}
              </div>
            )}
            <div className="space-y-2">
              <div className="min-h-[20px] flex items-center">
                <Label>Serial Number</Label>
              </div>
              <Input
                value={formData.serial_number}
                onChange={(e) => handleInputChange('serial_number', e.target.value)}
                placeholder="SN123456"
                autoComplete="off"
              />
              {formData.device_type === 'beacon' && formData.beacon_type === 'vendor_specific' && !formData.mac_address && (
                <p className="text-xs text-muted-foreground">* Required if MAC Address is empty</p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between min-h-[20px]">
                <div className="flex items-center gap-1">
                  <Label>MAC Address {formData.device_type === 'beacon' && formData.beacon_type === 'vendor_specific' ? '*' : ''}</Label>
                  {formData.mac_address && (
                    <button type="button" onClick={() => copyBeaconField(formData.mac_address, 'MAC Address')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="mac-address-copy-btn">
                      <Copy className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {selectedDevice && formData.device_type === 'beacon' && formData.beacon_type === 'vendor_specific' && (
                  <button
                    type="button"
                    onClick={() => setMacLocked(!macLocked)}
                    className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-md transition-colors ${macLocked ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60' : 'text-green-600 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-950/60'}`}
                    data-testid="mac-lock-toggle"
                  >
                    <Lock className="w-3 h-3" />
                    {macLocked ? 'Locked' : 'Unlocked'}
                  </button>
                )}
              </div>
              <Input
                value={formData.mac_address}
                onChange={(e) => handleInputChange('mac_address', e.target.value)}
                placeholder="7CD9F408E3EB"
                className={`font-mono ${beaconValidation.errors.mac ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                autoComplete="off"
                disabled={!!selectedDevice && formData.device_type === 'beacon' && formData.beacon_type === 'vendor_specific' && macLocked}
                data-testid="mac-address-input"
              />
              <p className="text-xs text-muted-foreground">Paste plain hex from a QR scan (e.g. <span className="font-mono">7CD9F408E3EB</span>) or colon format — we format it automatically.</p>
              {beaconValidation.errors.mac && (
                <p className="text-xs text-red-600" data-testid="mac-address-error">{beaconValidation.errors.mac}</p>
              )}
              {formData.device_type === 'beacon' && formData.beacon_type === 'vendor_specific' && !formData.serial_number && (
                <p className="text-xs text-muted-foreground">* Required if Serial Number is empty</p>
              )}
            </div>
          </div>
          
          {/* Protocol & Model */}
          {/* Protocol, Model, Password - hidden for beacons */}
          {formData.device_type !== 'beacon' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Protocol *</Label>
              <Select value={formData.protocol || '__none'} onValueChange={(v) => { if (v !== '__none') handleInputChange('protocol', v); }}>
                <SelectTrigger data-testid="protocol-select">
                  <SelectValue placeholder="Select Protocol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none" disabled className="text-muted-foreground">Select Protocol</SelectItem>
                  {protocols.map(protocol => (
                    <SelectItem key={protocol} value={protocol}>{protocol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Input
                value={formData.model}
                onChange={(e) => handleInputChange('model', e.target.value)}
                placeholder="FMB920"
                autoComplete="off"
                name="device_model_field"
              />
            </div>
            <div className="space-y-2">
              <Label>Device PIN / Password</Label>
              <Input
                value={formData.device_password}
                onChange={(e) => handleInputChange('device_password', e.target.value)}
                placeholder="Command PIN / Password"
                type="text"
                autoComplete="new-password"
                name="device_gprs_password"
              />
            </div>
          </div>
          )}
          
          {/* Firmware & Hardware - hidden for beacons */}
          {formData.device_type !== 'beacon' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Firmware Version</Label>
              <Input
                value={formData.firmware_version}
                onChange={(e) => handleInputChange('firmware_version', e.target.value)}
                placeholder="03.27.07.Rev.00"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Hardware Version</Label>
              <Input
                value={formData.hardware_version}
                onChange={(e) => handleInputChange('hardware_version', e.target.value)}
                placeholder="Rev.3"
                autoComplete="off"
              />
            </div>
          </div>
          )}
          
          {/* Installation */}
          {formData.device_type !== 'beacon' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Installation Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!formData.installation_date ? 'text-muted-foreground' : ''}`}
                      data-testid="installation-date-btn"
                    >
                      <CalendarDays className="mr-2 h-4 w-4" />
                      {formData.installation_date 
                        ? formatDateForDisplay(formData.installation_date) 
                        : 'DD/MM/YYYY'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.installation_date ? new Date(formData.installation_date + 'T00:00:00') : undefined}
                      onSelect={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          handleInputChange('installation_date', `${year}-${month}-${day}`);
                        } else {
                          handleInputChange('installation_date', '');
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><Wrench className="w-4 h-4" /> Installation Type</Label>
                <Select value={formData.installation_type || ''} onValueChange={(v) => handleInputChange('installation_type', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {installationTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          {/* SIM Card */}
          {formData.device_type !== 'beacon' && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Radio className="w-4 h-4" /> SIM Card</h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => {
                      handleInputChange('sim_provider', '');
                      handleInputChange('sim_iccid', '');
                      handleInputChange('sim_number', '');
                    }}
                    disabled={!formData.sim_provider && !formData.sim_iccid && !formData.sim_number}
                    title="Clear SIM Provider, ICCID and Mobile Number"
                    data-testid="sim-reset-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1" />
                    Reset
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSimSyncByImei}
                    disabled={simImeiSyncLoading || !formData.imei}
                    title="Search all SIM providers by IMEI to auto-fill SIM details"
                    data-testid="sim-sync-imei-btn"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${simImeiSyncLoading ? 'animate-spin' : ''}`} />
                    Sync by IMEI
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>SIM Provider</Label>
                  <Select 
                    value={formData.sim_provider || ''} 
                    onValueChange={(v) => handleInputChange('sim_provider', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select SIM provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...simProviders].sort((a, b) => a.name.localeCompare(b.name)).map(provider => (
                        <SelectItem key={provider.id} value={provider.name}>{provider.name}</SelectItem>
                      ))}
                      {simProviders.length === 0 && (
                        <SelectItem value="__none__" disabled>No providers available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>SIM ICCID</Label>
                  <Input
                    value={formData.sim_iccid}
                    onChange={(e) => handleSimIccidChange(e.target.value)}
                    placeholder="89012345678901234567"
                    className="font-mono"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>SIM Mobile Number</Label>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">Auto</span>
                      <Switch
                        checked={simAutoPopulate}
                        onCheckedChange={setSimAutoPopulate}
                        className="scale-75"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        value={formData.sim_number}
                        onChange={(e) => handleInputChange('sim_number', e.target.value)}
                        placeholder="+61412345678"
                        autoComplete="off"
                        disabled={simLookupLoading}
                      />
                      {simLookupLoading && (
                        <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-3 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleSimRefresh}
                      disabled={simLookupLoading || !formData.sim_iccid}
                      title="Fetch provider & mobile from the SIM database by ICCID"
                      data-testid="sim-refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${simLookupLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </div>
              {/* Manual SIM → provider list (strictly Manual (No API) providers — API providers sync automatically) */}
              {formData.sim_provider && formData.sim_iccid && simProviders.find((p) => p.name === formData.sim_provider)?.provider_type === 'manual' && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-dashed px-3 py-2" data-testid="sim-add-to-provider-row">
                  <p className="text-xs text-muted-foreground flex-1 min-w-[200px]">
                    Not in the <span className="font-medium">{formData.sim_provider}</span> SIM list yet? Save it so it can be managed under Admin System → Sim Provider.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs shrink-0"
                    onClick={handleAddSimToProvider}
                    disabled={simAddToProviderLoading}
                    data-testid="sim-add-to-provider-btn"
                  >
                    {simAddToProviderLoading
                      ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      : <Plus className="w-3.5 h-3.5 mr-1" />}
                    Add to {formData.sim_provider} list
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Speed, Odometer & Engine Hours Source */}
          {formData.device_type !== 'beacon' && (
            <div className="space-y-3 pt-3 border-t">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Gauge className="w-4 h-4" /> {(formData.device_type === 'personal_tracker' || formData.device_type === 'asset_tracker') ? 'Speed & Odometer Source' : 'Speed, Odometer, Engine Hours & Fuel Source'}</h3>
              <div className={`grid gap-4 grid-cols-1 ${(formData.device_type === 'personal_tracker' || formData.device_type === 'asset_tracker') ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
                <div className="space-y-2">
                  <Label>Speed Source</Label>
                  <Select value={formData.speed_source || 'gps'} onValueChange={(v) => handleInputChange('speed_source', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gps">GPS (from GPS module)</SelectItem>
                      <SelectItem value="sensor">Sensor (CAN Bus / IO Mapping)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {(formData.speed_source || 'gps') === 'gps' && 'Speed from GPS module — may drop in tunnels'}
                    {formData.speed_source === 'sensor' && 'Speed from CAN Bus sensor (IO Mapping) — works in tunnels'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Odometer Source</Label>
                  <Select value={formData.odometer_source} onValueChange={(v) => handleInputChange('odometer_source', v)}>
                    <SelectTrigger data-testid="odometer-source-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">OFF</SelectItem>
                      <SelectItem value="gps">GPS (calculated from GPS data)</SelectItem>
                      <SelectItem value="sensor">Sensor (CAN Bus / IO Mapping)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {formData.odometer_source === 'gps' && 'Odometer will be editable in Asset Type tab'}
                    {formData.odometer_source === 'sensor' && 'Odometer is read-only from CAN Bus sensor (IO Mapping)'}
                    {formData.odometer_source === 'off' && 'Odometer tracking is disabled'}
                  </p>
                </div>
                {formData.device_type !== 'personal_tracker' && formData.device_type !== 'asset_tracker' && (
                  <div className="space-y-2">
                    <Label>Engine Hours Source</Label>
                    <Select value={formData.engine_hours_source} onValueChange={(v) => handleInputChange('engine_hours_source', v)}>
                      <SelectTrigger data-testid="engine-hours-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">OFF</SelectItem>
                        <SelectItem value="ignition">Ignition (counter when ignition ON)</SelectItem>
                        <SelectItem value="sensor">Sensor (CAN Bus / IO Mapping)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {formData.engine_hours_source === 'ignition' && 'Engine hours will be editable in Asset Type tab'}
                      {formData.engine_hours_source === 'sensor' && 'Engine hours is read-only from CAN Bus sensor (IO Mapping)'}
                      {formData.engine_hours_source === 'off' && 'Engine hours tracking is disabled'}
                    </p>
                  </div>
                )}
                {formData.device_type !== 'personal_tracker' && formData.device_type !== 'asset_tracker' && (
                  <div className="space-y-2">
                    <Label>Fuel Source</Label>
                    <Select value={formData.fuel_source || 'off'} onValueChange={(v) => handleInputChange('fuel_source', v)}>
                      <SelectTrigger data-testid="fuel-source-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">OFF</SelectItem>
                        <SelectItem value="sensor">Sensor (CAN Bus / IO Mapping)</SelectItem>
                        <SelectItem value="estimate">Estimate (reference consumption × distance)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {(formData.fuel_source || 'off') === 'off' && 'Fuel tracking is disabled (manual fill-ups still allowed)'}
                      {formData.fuel_source === 'sensor' && 'Reads the mapped Fuel Level / EV Battery Level IO Type'}
                      {formData.fuel_source === 'estimate' && 'Estimated from reference L/100km (or kWh/100km) × GPS distance — configure in Fleet > Edit'}
                    </p>
                    {formData.fuel_source === 'sensor' && !(formData.io_mappings || []).some(m => m.io_type === 'Fuel Level' || m.io_type === 'EV Battery Level') && (
                      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="fuel-source-mapping-warning">
                        No Fuel Level / EV Battery Level IO mapping configured — add one in the IO Mapping tab for sensor readings to work.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/* Max Speed (GPS odometer glitch filter) — only shown when odometer is GPS */}
              {formData.odometer_source === 'gps' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-2">
                  <div className="space-y-2">
                    <Label>Max Speed (km/h)</Label>
                    <Input
                      data-testid="max-speed-kmh-input"
                      type="number"
                      value={formData.max_speed_kmh ?? 200}
                      onChange={(e) => handleInputChange('max_speed_kmh', e.target.value ? parseInt(e.target.value) : 200)}
                      placeholder="200"
                      min={50}
                      max={500}
                    />
                    <p className="text-xs text-muted-foreground">
                      Maximum realistic speed for this vehicle. GPS jumps exceeding this are filtered from odometer.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Server & Connectivity Settings */}
          {formData.device_type !== 'beacon' && (
            <div className="space-y-3 pt-3 border-t">
              <h3 className="font-semibold text-sm flex items-center gap-2"><Server className="w-4 h-4" /> Server & Connectivity</h3>
              <div className="space-y-2">
                <Label>APN</Label>
                <Input
                  value={formData.apn}
                  onChange={(e) => handleInputChange('apn', e.target.value)}
                  placeholder="telstra.internet"
                  autoComplete="off"
                />
              </div>
              
              {/* Server Setting 1 */}
              <div className="p-3 border rounded-lg space-y-3">
                <p className="font-medium text-sm">Server Setting 1</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Server IP / Domain</Label>
                    <Input
                      value={formData.server_ip_1}
                      onChange={(e) => handleInputChange('server_ip_1', e.target.value)}
                      placeholder="gps.example.com"
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Server Port</Label>
                    <Input
                      type="number"
                      value={formData.server_port_1}
                      onChange={(e) => handleInputChange('server_port_1', e.target.value)}
                      placeholder="5001"
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>
              
              {/* Server Setting 2 */}
              {formData.show_server_2 ? (
                <div className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-sm">Server Setting 2</p>
                    <Button variant="ghost" size="sm" onClick={() => {
                      handleInputChange('show_server_2', false);
                      handleInputChange('server_ip_2', '');
                      handleInputChange('server_port_2', '');
                    }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Server IP / Domain 2</Label>
                      <Input
                        value={formData.server_ip_2}
                        onChange={(e) => handleInputChange('server_ip_2', e.target.value)}
                        placeholder="backup.example.com"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Server Port 2</Label>
                      <Input
                        type="number"
                        value={formData.server_port_2}
                        onChange={(e) => handleInputChange('server_port_2', e.target.value)}
                        placeholder="5002"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => handleInputChange('show_server_2', true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Second Server
                </Button>
              )}
              
              {/* Reporting Intervals */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Reporting Interval (Ignition On / Moving)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.reporting_interval_moving}
                      onChange={(e) => handleInputChange('reporting_interval_moving', e.target.value)}
                      placeholder="10"
                      autoComplete="off"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">seconds</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reporting Interval (Ignition Off / Stopped)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={formData.reporting_interval_stopped}
                      onChange={(e) => handleInputChange('reporting_interval_stopped', e.target.value)}
                      placeholder="300"
                      autoComplete="off"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">seconds</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Beacon Configuration — hidden for RFID/iButton-only trailers (no beacon hardware) */}
          {formData.device_type === 'beacon' && !rfidOnlyTrailer && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Bluetooth className="w-4 h-4" /> Beacon Configuration</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={openBeaconDiscover}
                  data-testid="beacon-autodetect-btn"
                >
                  <Signal className="w-3.5 h-3.5 mr-1.5" />
                  Auto-detect from recent scans
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Beacon Purpose</Label>
                  <Select value={formData.beacon_purpose || 'asset'} onValueChange={(v) => handleInputChange('beacon_purpose', v)}>
                    <SelectTrigger data-testid="beacon-purpose-select">
                      <SelectValue placeholder="Select purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      {beaconPurposes.map(type => (
                        <SelectItem key={type.value} value={type.value} data-testid={`beacon-purpose-option-${type.value}`}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Beacon Type</Label>
                  <Select value={formData.beacon_type || ''} onValueChange={(v) => handleInputChange('beacon_type', v)}>
                    <SelectTrigger data-testid="beacon-type-select">
                      <SelectValue placeholder="Select beacon type" />
                    </SelectTrigger>
                    <SelectContent>
                      {beaconTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Advertising Interval (ms)</Label>
                  <Input
                    type="number"
                    value={formData.advertising_interval}
                    onChange={(e) => handleInputChange('advertising_interval', e.target.value)}
                    placeholder="1000"
                    min="100"
                    max="10000"
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-2">
                  <Label>TX Power (dBm)</Label>
                  <Input
                    type="number"
                    value={formData.tx_power}
                    onChange={(e) => handleInputChange('tx_power', e.target.value)}
                    placeholder="2"
                    autoComplete="off"
                  />
                </div>
              </div>
              
              {formData.beacon_type === 'ibeacon' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 sm:col-span-3">
                    <div className="flex items-center justify-between min-h-[20px]">
                      <div className="flex items-center gap-1">
                        <Label>UUID</Label>
                        {formData.beacon_uuid && (
                          <button type="button" onClick={() => copyBeaconField(formData.beacon_uuid, 'UUID')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beacon-uuid-copy-btn">
                            <Copy className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {selectedDevice && (
                        <button
                          type="button"
                          onClick={() => setBeaconIdLocked(!beaconIdLocked)}
                          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-md transition-colors ${beaconIdLocked ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60' : 'text-green-600 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-950/60'}`}
                          data-testid="beacon-id-lock-toggle"
                        >
                          <Lock className="w-3 h-3" />
                          {beaconIdLocked ? 'Locked' : 'Unlocked'}
                        </button>
                      )}
                    </div>
                    <Input
                      value={formData.beacon_uuid}
                      onChange={(e) => handleInputChange('beacon_uuid', e.target.value)}
                      placeholder="FDA50693-A4E2-4FB1-AFCF-C6EB07647825"
                      className={`font-mono ${beaconValidation.errors.uuid ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      autoComplete="off"
                      disabled={!!selectedDevice && beaconIdLocked}
                      data-testid="beacon-uuid-input"
                    />
                    {beaconValidation.errors.uuid && (
                      <p className="text-xs text-red-600" data-testid="beacon-uuid-error">{beaconValidation.errors.uuid}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 min-h-[20px]">
                      <Label>Major (0-65535)</Label>
                      {formData.beacon_major !== '' && formData.beacon_major != null && (
                        <button type="button" onClick={() => copyBeaconField(String(formData.beacon_major), 'Major')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beacon-major-copy-btn">
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <Input
                      value={formData.beacon_major}
                      onChange={(e) => handleInputChange('beacon_major', e.target.value)}
                      placeholder="10001"
                      autoComplete="off"
                      disabled={!!selectedDevice && beaconIdLocked}
                      className={beaconValidation.errors.major ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      data-testid="beacon-major-input"
                    />
                    {beaconValidation.errors.major && (
                      <p className="text-xs text-red-600" data-testid="beacon-major-error">{beaconValidation.errors.major}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 min-h-[20px]">
                      <Label>Minor (0-65535)</Label>
                      {formData.beacon_minor !== '' && formData.beacon_minor != null && (
                        <button type="button" onClick={() => copyBeaconField(String(formData.beacon_minor), 'Minor')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beacon-minor-copy-btn">
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <Input
                      value={formData.beacon_minor}
                      onChange={(e) => handleInputChange('beacon_minor', e.target.value)}
                      placeholder="19641"
                      autoComplete="off"
                      disabled={!!selectedDevice && beaconIdLocked}
                      className={beaconValidation.errors.minor ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      data-testid="beacon-minor-input"
                    />
                    {beaconValidation.errors.minor && (
                      <p className="text-xs text-red-600" data-testid="beacon-minor-error">{beaconValidation.errors.minor}</p>
                    )}
                  </div>
                  <div className="sm:col-span-3 space-y-1">
                    <p className="text-xs text-muted-foreground" data-testid="beacon-ibeacon-helper">
                      Matched to scans by <span className="font-mono">UUID : Major : Minor</span>. UUID = 32 hex characters; Major/Minor are decimal 0–65535. The MAC Address field is optional and stored for your reference only — it is not used to match iBeacons.
                    </p>
                    {beaconValidation.identifier && (
                      <p className="text-xs" data-testid="beacon-identifier-preview">
                        Identifier preview: <span className="font-mono font-medium text-green-700 dark:text-green-400">{beaconValidation.identifier}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {formData.beacon_type === 'eddystone' && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between min-h-[28px]">
                      <div className="flex items-center gap-1">
                        <Label>Namespace (10 bytes hex)</Label>
                        {formData.beacon_namespace && (
                          <button type="button" onClick={() => copyBeaconField(formData.beacon_namespace, 'Namespace')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beacon-namespace-copy-btn">
                            <Copy className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      {selectedDevice && (
                        <button
                          type="button"
                          onClick={() => setBeaconIdLocked(!beaconIdLocked)}
                          className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-md transition-colors ${beaconIdLocked ? 'text-amber-600 bg-amber-50 dark:bg-amber-950/40 hover:bg-amber-100 dark:hover:bg-amber-950/60' : 'text-green-600 bg-green-50 dark:bg-green-950/40 hover:bg-green-100 dark:hover:bg-green-950/60'}`}
                          data-testid="beacon-id-lock-toggle"
                        >
                          <Lock className="w-3 h-3" />
                          {beaconIdLocked ? 'Locked' : 'Unlocked'}
                        </button>
                      )}
                    </div>
                    <Input
                      value={formData.beacon_namespace}
                      onChange={(e) => handleInputChange('beacon_namespace', e.target.value)}
                      placeholder="EDD1EBEAC04E12345678"
                      className={`font-mono ${beaconValidation.errors.namespace ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      autoComplete="off"
                      disabled={!!selectedDevice && beaconIdLocked}
                      data-testid="beacon-namespace-input"
                    />
                    {beaconValidation.errors.namespace && (
                      <p className="text-xs text-red-600" data-testid="beacon-namespace-error">{beaconValidation.errors.namespace}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1 min-h-[20px]">
                      <Label>Instance ID (6 bytes hex)</Label>
                      {formData.beacon_instance_id && (
                        <button type="button" onClick={() => copyBeaconField(formData.beacon_instance_id, 'Instance ID')} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="beacon-instance-copy-btn">
                          <Copy className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <Input
                      value={formData.beacon_instance_id}
                      onChange={(e) => handleInputChange('beacon_instance_id', e.target.value)}
                      placeholder="000000000001"
                      className={`font-mono ${beaconValidation.errors.instance ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      autoComplete="off"
                      disabled={!!selectedDevice && beaconIdLocked}
                      data-testid="beacon-instance-input"
                    />
                    {beaconValidation.errors.instance && (
                      <p className="text-xs text-red-600" data-testid="beacon-instance-error">{beaconValidation.errors.instance}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground" data-testid="beacon-eddystone-helper">
                      Matched to scans by <span className="font-mono">Namespace : Instance</span>. Namespace = 20 hex characters; Instance = 12 hex characters. The MAC Address field is optional and stored for your reference only — it is not used to match Eddystone beacons.
                    </p>
                    {beaconValidation.identifier && (
                      <p className="text-xs" data-testid="beacon-identifier-preview">
                        Identifier preview: <span className="font-mono font-medium text-green-700 dark:text-green-400">{beaconValidation.identifier}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {formData.beacon_type === 'vendor_specific' && (
                <div className="space-y-2">
                  <Label>Vendor EID</Label>
                  <Input
                    value={formData.beacon_eid}
                    onChange={(e) => handleInputChange('beacon_eid', e.target.value)}
                    placeholder="Enter vendor-specific EID"
                    className="font-mono"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">The device MAC Address above is used as the BLE identifier. This field is for vendor-specific Extended ID only.</p>
                </div>
              )}
            </div>
          )}
          
          {/* Connected Peripherals */}
          {formData.device_type !== 'beacon' && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm flex items-center gap-2"><Plug className="w-4 h-4" /> Connected Peripherals</h3>
                {!showPeripheralForm && (
                  <Button variant="outline" size="sm" onClick={() => setShowPeripheralForm(true)} data-testid="add-peripheral-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Peripheral
                  </Button>
                )}
              </div>
              
              {showPeripheralForm && (
                <div className="p-3 border rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Peripheral Type</Label>
                      <Select value={newPeripheral.peripheral_type} onValueChange={(v) => setNewPeripheral({...newPeripheral, peripheral_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {peripheralTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Connection</Label>
                      <Select value={newPeripheral.connection_type} onValueChange={(v) => setNewPeripheral({...newPeripheral, connection_type: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {peripheralConnections.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className={`grid gap-3 grid-cols-1 ${(newPeripheral.peripheral_type === 'can_module' || newPeripheral.peripheral_type === 'can_control') ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={newPeripheral.name}
                        onChange={(e) => setNewPeripheral({...newPeripheral, name: e.target.value})}
                        placeholder="Cab Temp Sensor"
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Serial / ID</Label>
                      <Input
                        value={newPeripheral.serial_number}
                        onChange={(e) => setNewPeripheral({...newPeripheral, serial_number: e.target.value})}
                        placeholder="Optional"
                        autoComplete="off"
                      />
                    </div>
                    {(newPeripheral.peripheral_type === 'can_module' || newPeripheral.peripheral_type === 'can_control') && (
                      <div className="space-y-2">
                        <Label>Program ID Number</Label>
                        <Input
                          value={newPeripheral.program_id || ''}
                          onChange={(e) => setNewPeripheral({...newPeripheral, program_id: e.target.value})}
                          placeholder="e.g. 1234"
                          autoComplete="off"
                          data-testid="peripheral-program-id"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleAddPeripheral} size="sm">
                      {editingPeripheralId ? <><Check className="w-4 h-4 mr-2" />Update</> : <><Plus className="w-4 h-4 mr-2" />Add</>}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setShowPeripheralForm(false); handleCancelEditPeripheral(); }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              
              {formData.connected_peripherals.length > 0 && (
                <div className="space-y-2">
                  {formData.connected_peripherals.map(p => (
                    <div key={p.id} className={`flex items-center justify-between p-2 border rounded-lg ${editingPeripheralId === p.id ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/30' : ''}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <Badge variant="outline">{peripheralTypes.find(t => t.value === p.peripheral_type)?.label || p.peripheral_type}</Badge>
                        <span className="font-medium text-sm">{p.name}</span>
                        {p.serial_number && <span className="text-xs text-muted-foreground font-mono">{p.serial_number}</span>}
                        <Badge variant="secondary" className="text-xs">{peripheralConnections.find(c => c.value === p.connection_type)?.label || p.connection_type}</Badge>
                        {p.program_id && <Badge variant="secondary" className="text-xs font-mono">PID: {p.program_id}</Badge>}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => handleEditPeripheral(p)} title="Edit" data-testid={`edit-peripheral-${p.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleRemovePeripheral(p.id)} title="Delete" data-testid={`remove-peripheral-${p.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Linked Devices */}
          <div className="space-y-3 pt-3 border-t">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Link2 className="w-4 h-4" /> Linked Devices</h3>
            <p className="text-xs text-muted-foreground">Link a secondary device for GPS gap filling, data merging, or video integration. Both devices stay independent — the linked device enriches this primary device.</p>

            {/* Add Linked Device */}
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Search & Link a Device</Label>
                <Popover modal={true} open={linkedDeviceSearchOpen} onOpenChange={(open) => {
                  setLinkedDeviceSearchOpen(open);
                  // Fresh top-25 fetch is triggered by the debounced useEffect below when
                  // `linkedDeviceSearchOpen` flips to true — no manual call here (avoids the
                  // duplicate /admin/devices request that fired previously).
                  if (!open) setLinkedDeviceSearchTerm('');
                }}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      data-testid="linked-device-search-trigger"
                      className="w-full h-8 justify-between text-xs font-normal text-muted-foreground"
                    >
                      <span className="flex items-center gap-2"><Search className="w-3 h-3" /> Search by name, IMEI or protocol...</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 z-[10000]" align="start">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Type device name, IMEI or protocol..."
                        data-testid="linked-device-search-input"
                        value={linkedDeviceSearchTerm}
                        onValueChange={setLinkedDeviceSearchTerm}
                      />
                      <CommandList>
                        <CommandGroup>
                          {linkableDevicesLoading && (
                            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="linked-device-search-loading">Searching…</div>
                          )}
                          {!linkableDevicesLoading && filteredLinkableDevices.length === 0 && (
                            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="linked-device-search-empty">
                              {linkedDeviceSearchNeedle ? 'No devices match your search.' : 'No devices available to link.'}
                            </div>
                          )}
                          {filteredLinkableDevices
                            .map(d => (
                              <CommandItem
                                key={d.id}
                                value={`${d.name || ''} ${d.imei || ''} ${d.protocol || ''} ${d.model || ''} ${d.asset_name || ''}`}
                                data-testid={`linked-device-option-${d.id}`}
                                onSelect={() => {
                                  handleInputChange('linked_devices', [...formData.linked_devices, {
                                    device_id: d.id,
                                    device_name: d.name || d.id,
                                    device_imei: d.imei || '',
                                    device_protocol: d.protocol || '',
                                    role: 'backup',
                                    gps_source: false,
                                    io_merge_rules: [],
                                    gap_fill_enabled: true,
                                    gap_fill_minutes: 5,
                                    command_mirror_ids: [],
                                  }]);
                                  toast.success(`Linked ${d.name || d.id}`);
                                  setLinkedDeviceSearchOpen(false);
                                }}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{d.name}</span>
                                  <span className="text-xs text-muted-foreground">{d.imei ? `IMEI: ${d.imei}` : ''}{d.protocol ? ` — ${d.protocol}` : ''}</span>
                                </div>
                              </CommandItem>
                            ))
                          }
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Linked Devices List */}
            {formData.linked_devices.length > 0 && (
              <div className="space-y-3">
                {formData.linked_devices.map((ld, idx) => {
                  const linkedDev = linkableDevices.find(d => d.id === ld.device_id) || devices.find(d => d.id === ld.device_id) || linkedDeviceCache[ld.device_id];
                  return (
                    <div key={ld.device_id} className="p-3 border rounded-lg bg-slate-50 dark:bg-slate-800/50 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{linkedDev?.name || ld.device_name || ld.device_id}</p>
                          <p className="text-xs text-muted-foreground font-mono">{linkedDev?.imei || ld.device_imei || '-'} — {linkedDev?.protocol || ld.device_protocol || 'Unknown'}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => {
                          handleInputChange('linked_devices', formData.linked_devices.filter((_, i) => i !== idx));
                        }}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </Button>
                      </div>

                      {/* Role */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Role</Label>
                          <Select value={ld.role} onValueChange={(v) => {
                            const updated = [...formData.linked_devices];
                            updated[idx] = { ...updated[idx], role: v };
                            handleInputChange('linked_devices', updated);
                          }}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="backup">Backup (Gap Fill)</SelectItem>
                              <SelectItem value="gps_source">GPS Source</SelectItem>
                              <SelectItem value="io_source">IO Data Source</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Gap Fill Threshold (minutes)</Label>
                          <Input
                            type="number"
                            min={1}
                            max={60}
                            value={ld.gap_fill_minutes}
                            onChange={(e) => {
                              const updated = [...formData.linked_devices];
                              updated[idx] = { ...updated[idx], gap_fill_minutes: parseInt(e.target.value) || 5 };
                              handleInputChange('linked_devices', updated);
                            }}
                            className="h-7 text-xs"
                          />
                        </div>
                      </div>

                      {/* Toggles */}
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-xs" title="ALWAYS use this device's GPS position for the primary — overrides primary's own GPS">
                          <Switch className="scale-75" checked={ld.gps_source} onCheckedChange={(v) => {
                            const updated = [...formData.linked_devices];
                            updated[idx] = { ...updated[idx], gps_source: v };
                            handleInputChange('linked_devices', updated);
                          }} />
                          Use as GPS Source (always)
                        </label>
                        <label className="flex items-center gap-2 text-xs" title="Only use this device's GPS when the primary has no data for the threshold period">
                          <Switch className="scale-75" checked={ld.gap_fill_enabled} onCheckedChange={(v) => {
                            const updated = [...formData.linked_devices];
                            updated[idx] = { ...updated[idx], gap_fill_enabled: v };
                            handleInputChange('linked_devices', updated);
                          }} />
                          Gap Fill (backup only)
                        </label>
                      </div>
                      {ld.gps_source && ld.gap_fill_enabled && (
                        <p className="text-xs text-amber-600">GPS Source is ON — this overrides Gap Fill. The primary will always use this device's GPS.</p>
                      )}

                      {/* IO Merge Rules */}
                      {(ld.role === 'io_source') && (
                        <div className="space-y-2">
                          <Label className="text-xs">IO Elements to Merge</Label>
                          <p className="text-xs text-muted-foreground">Select which IO elements from this device should be passed to the primary device.</p>
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Select value="" onValueChange={(ioId) => {
                                if (!ioId || ioId === '_none' || ld.io_merge_rules.some(r => r.io_id === ioId)) return;
                                const linkedDev2 = linkableDevices.find(d => d.id === ld.device_id) || devices.find(d => d.id === ld.device_id);
                                const linkedMappings = linkedDev2?.io_mappings || [];
                                const mapping = linkedMappings.find(m => m.io_id === ioId);
                                const updated = [...formData.linked_devices];
                                updated[idx] = { ...updated[idx], io_merge_rules: [...ld.io_merge_rules, { io_id: ioId, io_name: mapping?.display_name || ioId, enabled: true }] };
                                handleInputChange('linked_devices', updated);
                              }}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select IO element to add..." /></SelectTrigger>
                                <SelectContent>
                                  {(() => {
                                    const linkedDev2 = linkableDevices.find(d => d.id === ld.device_id) || devices.find(d => d.id === ld.device_id);
                                    const linkedMappings = (linkedDev2?.io_mappings || []).filter(m => !ld.io_merge_rules.some(r => r.io_id === m.io_id));
                                    if (linkedMappings.length === 0) return <SelectItem value="_none" disabled>No IO mappings configured on the linked device</SelectItem>;
                                    return linkedMappings.map(m => (
                                      <SelectItem key={m.io_id} value={m.io_id}>{m.display_name || m.io_id} ({m.io_type}) — {m.io_id}</SelectItem>
                                    ));
                                  })()}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {ld.io_merge_rules.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {ld.io_merge_rules.map((rule, ri) => (
                                <Badge key={rule.io_id} variant="outline" className="text-xs gap-1 pr-1">
                                  {rule.io_name}
                                  <button className="ml-1 hover:text-red-500" onClick={() => {
                                    const updated = [...formData.linked_devices];
                                    updated[idx] = { ...updated[idx], io_merge_rules: ld.io_merge_rules.filter((_, i) => i !== ri) };
                                    handleInputChange('linked_devices', updated);
                                  }}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Commands to Mirror from Linked Device */}
                      {(() => {
                        const linkedDev3 = linkableDevices.find(d => d.id === ld.device_id) || devices.find(d => d.id === ld.device_id);
                        const linkedCmds = linkedDev3?.custom_commands || [];
                        if (linkedCmds.length === 0) return null;
                        const mirrorIds = ld.command_mirror_ids || [];
                        return (
                          <div className="space-y-2">
                            <Label className="text-xs">Commands from Linked Device</Label>
                            <p className="text-xs text-muted-foreground">Select commands to show on this device's Telemetry Panel. They route to the linked device automatically.</p>
                            <div className="flex items-end gap-2">
                              <div className="flex-1">
                                <Select value="" onValueChange={(cmdId) => {
                                  if (!cmdId || cmdId === '_none' || mirrorIds.includes(cmdId)) return;
                                  const updated = [...formData.linked_devices];
                                  updated[idx] = { ...updated[idx], command_mirror_ids: [...mirrorIds, cmdId] };
                                  handleInputChange('linked_devices', updated);
                                }}>
                                  <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select command to add..." /></SelectTrigger>
                                  <SelectContent>
                                    {linkedCmds.filter(c => !mirrorIds.includes(c.id)).length === 0
                                      ? <SelectItem value="_none" disabled>All commands already added</SelectItem>
                                      : linkedCmds.filter(c => !mirrorIds.includes(c.id)).map(c => (
                                        <SelectItem key={c.id} value={c.id}>{c.button_label} — {c.command_mode === 'sequence' ? `${c.command_sequence?.length || 0} steps` : c.command_text}</SelectItem>
                                      ))
                                    }
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            {mirrorIds.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {mirrorIds.map(cmdId => {
                                  const cmd = linkedCmds.find(c => c.id === cmdId);
                                  return (
                                    <Badge key={cmdId} variant="outline" className={`text-xs gap-1 pr-1 ${cmd ? '' : 'opacity-50'}`}>
                                      {cmd?.button_label || cmdId}
                                      <button className="ml-1 hover:text-red-500" onClick={() => {
                                        const updated = [...formData.linked_devices];
                                        updated[idx] = { ...updated[idx], command_mirror_ids: mirrorIds.filter(id => id !== cmdId) };
                                        handleInputChange('linked_devices', updated);
                                      }}>
                                        <X className="w-3 h-3" />
                                      </button>
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Offline Threshold */}
          <div className="space-y-2 pt-3 border-t">
            <Label className="flex items-center gap-2"><Clock className="w-4 h-4" /> Offline Threshold</Label>
            <div className="flex items-center gap-3">
              <Input
                data-testid="offline-threshold-input"
                type="number"
                value={formData.offline_threshold_secs ?? ''}
                onChange={(e) => handleInputChange('offline_threshold_secs', e.target.value === '' ? '' : parseInt(e.target.value))}
                onBlur={() => { if (!formData.offline_threshold_secs) handleInputChange('offline_threshold_secs', 600); }}
                placeholder="600"
                min={60}
                max={86400}
                className="w-32"
              />
              <span className="text-sm text-muted-foreground">seconds</span>
              {formData.offline_threshold_secs && (
                <span className="text-xs text-muted-foreground ml-2">
                  ({Math.floor(formData.offline_threshold_secs / 60)}m {formData.offline_threshold_secs % 60}s)
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Time without data before the device is marked offline. Default is 600 seconds (10 minutes). Devices that report less frequently should have a higher threshold.
            </p>
          </div>

          {/* Notes */}
          <div className="space-y-2 pt-3 border-t">
            <Label className="flex items-center gap-2"><StickyNote className="w-4 h-4" /> Admin Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Internal notes about this device..."
              rows={3}
            />
          </div>

          {/* Identity & Logbooks (GPS trackers only — not beacons) */}
          {formData.device_type !== 'beacon' && (
            <IdentityLogbookSettingsSection
              value={formData.identity_logbook}
              onChange={(v) => handleInputChange('identity_logbook', v)}
              ioMappings={formData.io_mappings}
            />
          )}
        </div>
      )}
      
      {/* Tab 2: Asset Type */}
      {activeTab === 'asset' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Asset Name / Unit Number</Label>
            <Input
              data-testid="asset-name-input"
              value={formData.asset_name}
              onChange={(e) => handleInputChange('asset_name', e.target.value)}
              placeholder={formData.device_type === 'beacon' ? 'John Smith, Trailer T-01, Tool Box A' : 'Truck 14, Excavator #3'}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              {formData.device_type === 'beacon'
                ? 'The person, asset, or object this beacon is attached to'
                : 'The real-world name for the vehicle or asset this device is attached to'}
            </p>
          </div>
          
          <div className="space-y-2">
            <Label>Asset Type</Label>
            <Select value={formData.asset_type} onValueChange={(v) => handleInputChange('asset_type', v)}>
              <SelectTrigger data-testid="asset-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(formData.device_type === 'beacon' && formData.beacon_purpose === 'personal'
                  ? beaconAssetTypes.filter(type => type.value === 'people')
                  : formData.device_type === 'beacon'
                    ? beaconAssetTypes.filter(type => type.value !== 'people')
                    : assetTypes
                ).map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.asset_type === 'trailer' && formData.device_type === 'beacon' && (
            <div className="space-y-2">
              <Label>Identity Method</Label>
              <Select value={formData.identity_method || 'ble'} onValueChange={(v) => handleInputChange('identity_method', v)}>
                <SelectTrigger data-testid="trailer-identity-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ble" data-testid="identity-method-option-ble">BLE Beacon</SelectItem>
                  <SelectItem value="rfid" data-testid="identity-method-option-rfid">RFID / iButton only</SelectItem>
                  <SelectItem value="both" data-testid="identity-method-option-both">BLE Beacon + RFID / iButton</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                RFID / iButton only skips the beacon hardware fields — the trailer binds when its tag is scanned on the vehicle&apos;s reader.
              </p>
            </div>
          )}

          {formData.asset_type === 'trailer' && (formData.device_type !== 'beacon' || (formData.identity_method || 'ble') !== 'ble') && (
            <div className="space-y-2">
              <Label>Trailer RFID / iButton No.{rfidOnlyTrailer ? ' *' : ''}</Label>
              <Input
                data-testid="asset-rfid-ibutton-input"
                value={formData.rfid_ibutton_number || ''}
                onChange={(e) => handleInputChange('rfid_ibutton_number', e.target.value)}
                placeholder="CF00070029784301 or trailer RFID number"
                className="font-mono"
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                Used by GPS tracker IO mappings set to RFID / iButton to bind this trailer automatically.
                {rfidOnlyTrailer ? ' Required — it is this trailer\u2019s identifier.' : ''}
              </p>
            </div>
          )}
          
          {/* Context-specific asset fields - dynamic based on asset_type */}
          {formData.device_type !== 'beacon' && (() => {
            const vehicleTypes = ['car', 'suv', 'van', 'ute', 'truck', 'bus', 'trailer', 'motorcycle', 'machine', 'excavator', 'scooter', 'caravan', 'boat', 'jetski'];
            const at = formData.asset_type;
            
            if (vehicleTypes.includes(at)) {
              return (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Make</Label>
                      <Input value={formData.asset_make} onChange={(e) => handleInputChange('asset_make', e.target.value)} placeholder="Toyota" autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Input value={formData.asset_model} onChange={(e) => handleInputChange('asset_model', e.target.value)} placeholder="Hilux" autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label>Year</Label>
                      <Input value={formData.asset_year} onChange={(e) => handleInputChange('asset_year', e.target.value)} placeholder="2024" autoComplete="off" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Registration Number</Label>
                      <Input value={formData.registration} onChange={(e) => handleInputChange('registration', e.target.value)} placeholder="ABC123" autoComplete="off" />
                    </div>
                    <div className="space-y-2">
                      <Label>VIN Number</Label>
                      <Input value={formData.vin_number} onChange={(e) => handleInputChange('vin_number', e.target.value)} placeholder="1HGBH41JXMN109186" className="font-mono" autoComplete="off" />
                    </div>
                  </div>
                </>
              );
            }
            
            if (at === 'ebike') {
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Make</Label>
                    <Input value={formData.asset_make} onChange={(e) => handleInputChange('asset_make', e.target.value)} placeholder="Specialized" autoComplete="off" />
                  </div>
                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Input value={formData.asset_model} onChange={(e) => handleInputChange('asset_model', e.target.value)} placeholder="Turbo Levo" autoComplete="off" />
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Input value={formData.asset_year} onChange={(e) => handleInputChange('asset_year', e.target.value)} placeholder="2024" autoComplete="off" />
                  </div>
                  <div className="sm:col-span-3 space-y-2">
                    <Label>Serial Number</Label>
                    <Input value={formData.serial_no} onChange={(e) => handleInputChange('serial_no', e.target.value)} placeholder="WSBC123456789" className="font-mono" autoComplete="off" />
                  </div>
                </div>
              );
            }
            
            if (at === 'container') {
              return (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Container Number</Label>
                    <Input value={formData.container_number} onChange={(e) => handleInputChange('container_number', e.target.value)} placeholder="MSCU1234567" className="font-mono" autoComplete="off" />
                  </div>
                  <div className="space-y-2">
                    <Label>Size</Label>
                    <Select value={formData.container_size || ''} onValueChange={(v) => handleInputChange('container_size', v)}>
                      <SelectTrigger data-testid="container-size-select"><SelectValue placeholder="Select size" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="20ft">20ft</SelectItem>
                        <SelectItem value="40ft">40ft</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.container_type || ''} onValueChange={(v) => handleInputChange('container_type', v)}>
                      <SelectTrigger data-testid="container-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dry">Dry</SelectItem>
                        <SelectItem value="reefer">Reefer</SelectItem>
                        <SelectItem value="open_top">Open Top</SelectItem>
                        <SelectItem value="flat_rack">Flat Rack</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            }
            
            if (at === 'personnel') {
              return (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={formData.asset_make} onChange={(e) => handleInputChange('asset_make', e.target.value)} placeholder="Person's name" autoComplete="off" />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={formData.asset_description} onChange={(e) => handleInputChange('asset_description', e.target.value)} placeholder="Additional details..." rows={3} />
                  </div>
                </div>
              );
            }
            
            if (at === 'other') {
              return (
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={formData.asset_description} onChange={(e) => handleInputChange('asset_description', e.target.value)} placeholder="Describe this asset..." rows={3} />
                </div>
              );
            }
            
            return null;
          })()}
          
          {/* Fuel Type - only for vehicle types */}
          {formData.device_type !== 'beacon' && ['car', 'suv', 'van', 'ute', 'truck', 'bus', 'trailer', 'motorcycle', 'machine', 'excavator', 'scooter', 'caravan', 'boat', 'jetski'].includes(formData.asset_type) && (
            <div className="space-y-2">
              <Label>Fuel Type</Label>
              <Select value={formData.fuel_type || ''} onValueChange={(v) => handleInputChange('fuel_type', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select fuel type" />
                </SelectTrigger>
                <SelectContent>
                  {fuelTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
              
          {/* Odometer & Engine Hours - only for vehicle/machine types */}
          {formData.device_type !== 'beacon' && ['car', 'suv', 'van', 'ute', 'truck', 'bus', 'trailer', 'motorcycle', 'machine', 'excavator', 'scooter', 'caravan', 'boat', 'jetski', 'ebike'].includes(formData.asset_type) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Odometer Reading (km)</Label>
                  <Input
                    type="number"
                    data-testid="odometer-reading-input"
                    value={formData.odometer_reading}
                    onChange={(e) => handleInputChange('odometer_reading', e.target.value)}
                    placeholder={formData.odometer_source === 'off' ? 'Tracking disabled' : '0'}
                    disabled={formData.odometer_source === 'off' || formData.odometer_source === 'sensor'}
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.odometer_source === 'off' && 'Enable odometer source in Device Details to use'}
                    {formData.odometer_source === 'gps' && 'Editable — calculated from GPS data'}
                    {formData.odometer_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Engine Hours</Label>
                  <Input
                    type="number"
                    data-testid="engine-hours-input"
                    value={formData.engine_hours}
                    onChange={(e) => handleInputChange('engine_hours', e.target.value)}
                    placeholder={formData.engine_hours_source === 'off' ? 'Tracking disabled' : '0'}
                    disabled={formData.engine_hours_source === 'off' || formData.engine_hours_source === 'sensor'}
                    step="0.1"
                    autoComplete="off"
                  />
                  <p className="text-xs text-muted-foreground">
                    {formData.engine_hours_source === 'off' && 'Enable engine hours source in Device Details to use'}
                    {formData.engine_hours_source === 'ignition' && 'Editable — counted when ignition is ON'}
                    {formData.engine_hours_source === 'sensor' && 'Read-only — sourced from CAN Bus sensor'}
                  </p>
                </div>
              </div>
          )}
          
          {/* Timezone Override */}
          <div className="p-3 border rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm flex items-center gap-2"><Clock className="w-4 h-4" /> Timezone Override</p>
                <p className="text-xs text-muted-foreground">Override all timezone settings for this device. Useful when a device operates in a different state or country.</p>
              </div>
              <Switch
                checked={formData.timezone_override_enabled || false}
                onCheckedChange={(v) => {
                  handleInputChange('timezone_override_enabled', v);
                  // When enabling, initialize timezone_override if it's null
                  if (v && !formData.timezone_override) {
                    handleInputChange('timezone_override', 'Australia/Sydney');
                  }
                  // When disabling, clear timezone_override
                  if (!v) {
                    handleInputChange('timezone_override', null);
                  }
                }}
              />
            </div>
            {formData.timezone_override_enabled && (
              <div className="space-y-2">
                <TimezoneSelector
                  value={formData.timezone_override || 'Australia/Sydney'}
                  onChange={(v) => handleInputChange('timezone_override', v)}
                  includeAuto={true}
                />
                <div className="p-2 bg-muted rounded-lg flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Current time at device:</span>
                  <span className="font-mono font-medium">
                    {formData.timezone_override === 'auto' ? (
                      <span className="text-amber-600">Auto — timezone detected from GPS location</span>
                    ) : (() => {
                      try {
                        const tz = formData.timezone_override || 'Australia/Sydney';
                        const now = new Date();
                        const time = now.toLocaleString('en-AU', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, weekday: 'short', day: '2-digit', month: 'short' });
                        const offset = now.toLocaleString('en-AU', { timeZone: tz, timeZoneName: 'shortOffset' }).split(' ').pop();
                        return `${time} (${offset})`;
                      } catch { return '-'; }
                    })()}
                  </span>
                </div>
                {formData.timezone_override === 'auto' && selectedDevice?.timezone_auto && (
                  <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-sm">
                    <span className="text-muted-foreground">Detected timezone: </span>
                    <span className="font-mono font-medium text-blue-600">{selectedDevice.timezone_auto}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Allow End User Editing */}
          <div className="p-3 border rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">Allow End User Editing</p>
                <p className="text-xs text-muted-foreground">
                  {formData.device_type === 'beacon'
                    ? 'Enable this to allow end users to edit asset name and type'
                    : 'Enable this to allow end users to edit asset details (name, make, model, year, registration, VIN, fuel type, odometer, engine hours)'}
                </p>
              </div>
              <Switch
                checked={formData.asset_editing_enabled}
                onCheckedChange={(v) => handleInputChange('asset_editing_enabled', v)}
              />
            </div>
          </div>
        </div>
      )}
      {activeTab === 'allocation' && (
        <div className="space-y-4">
          {/* Info banner for beacons */}
          {formData.device_type === 'beacon' && (
            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200">
              <p className="text-sm">
                <strong>Beacon Auto-Allocation:</strong> When a GPS tracker detects this beacon via BLE, 
                the server will automatically associate it with the tracker using the strongest RSSI signal.
              </p>
            </div>
          )}
          
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Allocate this {formData.device_type === 'beacon' ? 'beacon' : 'device'} to accounts so it appears in their device list.
              Select the accounts that should have access to this {formData.device_type === 'beacon' ? 'beacon' : 'device'}.
            </p>
          </div>

          {formData.device_type !== 'beacon' && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-3" data-testid="primary-operational-account-section">
              <div>
                <Label htmlFor="primary-operational-account">Primary Operational Account</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Used only when trip evidence cannot identify which allocated account owns the Driver review.
                </p>
              </div>
              <Select
                value={primaryOperationalAccountId || '__none'}
                onValueChange={(value) => {
                  setPrimaryOperationalAccountId(value === '__none' ? '' : value);
                  setFormDirty(true);
                }}
                disabled={allocatedOperationalAccounts.length === 0}
              >
                <SelectTrigger id="primary-operational-account" className="bg-background" data-testid="primary-operational-account-select">
                  <SelectValue placeholder="Select an allocated account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none" data-testid="primary-operational-account-option-none">Select an allocated account</SelectItem>
                  {allocatedOperationalAccounts.map(user => (
                    <SelectItem key={user.id} value={user.id} data-testid={`primary-operational-account-option-${user.id}`}>
                      {user.full_name || user.email} · {user.role === 'end_user' ? 'User' : user.role.replace(/_/g, ' ')}{user.company ? ` · ${user.company}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {allocatedOperationalAccounts.length === 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="primary-operational-account-empty">
                  Allocate an account to enable evidence-free Driver reviews.
                </p>
              )}
              {primaryOperationalAccountMissing && (
                <p className="text-xs text-amber-700 dark:text-amber-300" data-testid="primary-operational-account-hint">
                  No primary account set — Driver reviews that cannot be matched from trip evidence will need manual assignment.
                </p>
              )}
            </div>
          )}
          
          {/* Search and save */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="allocation-search"
                placeholder="Search users by name, email or company..."
                value={allocationSearch}
                onChange={(e) => setAllocationSearch(e.target.value)}
                className="h-8 pl-10"
              />
            </div>
            {isEdit && (
              <Button
                data-testid="save-allocation-btn"
                onClick={() => handleSaveAllocation(selectedDevice?.id)}
                disabled={allocationSaving}
                className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              >
                {allocationSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Save Allocation
              </Button>
            )}
          </div>
          
          {/* Allocation count */}
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="secondary" data-testid="allocation-count">
              {allocatedUserIds.length} user{allocatedUserIds.length !== 1 ? 's' : ''} allocated
            </Badge>
            {allocatedUserIds.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setAllocatedUserIds([]); setPrimaryOperationalAccountId(''); }}
                className="text-xs text-muted-foreground h-6"
                data-testid="allocation-clear-all-button"
              >
                Clear all
              </Button>
            )}
          </div>
          
          {/* Column legend */}
          <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground px-2">
            <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" /> Devices</span>
            <span className="flex items-center gap-1"><Radio className="w-3 h-3" /> Beacons</span>
          </div>
          
          {/* User list */}
          {allocationLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          ) : (
            <ScrollArea className="h-[600px] border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
              <div className="p-2 space-y-1">
                {[...allocatableUsers]
                  .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', undefined, { sensitivity: 'base' }))
                  .filter(u => {
                    if (!allocationSearch) return true;
                    const s = allocationSearch.toLowerCase();
                    return (
                      u.full_name?.toLowerCase().includes(s) ||
                      u.email?.toLowerCase().includes(s) ||
                      u.company?.toLowerCase().includes(s)
                    );
                  })
                  .map(u => {
                    const isAllocated = allocatedUserIds.includes(u.id);
                    const wasAllocated = originalAllocatedUserIds.includes(u.id);
                    const pendingChange = (isAllocated && !wasAllocated) ? 1 : (!isAllocated && wasAllocated) ? -1 : 0;
                    const isBeaconDevice = formData.device_type === 'beacon';
                    // Use correct count/limit based on whether this is a beacon or device
                    const devCount = u.device_count || 0;
                    const bcnCount = u.beacon_count || 0;
                    const displayDevCount = isBeaconDevice ? devCount : Math.max(0, devCount + pendingChange);
                    const displayBcnCount = isBeaconDevice ? Math.max(0, bcnCount + pendingChange) : bcnCount;
                    const maxDev = u.max_devices ?? 0;
                    const maxBcn = u.max_beacons ?? 0;
                    const activeMax = isBeaconDevice ? maxBcn : maxDev;
                    const activeCount = isBeaconDevice ? displayBcnCount : displayDevCount;
                    const isAtLimit = activeMax !== -1 && activeCount >= activeMax;
                    const isBlocked = activeMax === 0;
                    return (
                      <div
                        key={u.id}
                        data-testid={`allocation-user-${u.id}`}
                        className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                          isBlocked && !isAllocated
                            ? 'opacity-50 cursor-not-allowed border border-transparent'
                            : isAllocated 
                              ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 cursor-pointer' 
                              : isAtLimit
                                ? 'opacity-60 cursor-not-allowed border border-transparent'
                                : 'hover:bg-muted border border-transparent cursor-pointer'
                        }`}
                        onClick={() => {
                          // Block allocation if at limit (but always allow de-allocation)
                          if (!isAllocated && isAtLimit) {
                            return;
                          }
                          handleToggleUserAllocation(u.id);
                        }}
                      >
                        <Checkbox
                          checked={isAllocated}
                          disabled={!isAllocated && isAtLimit}
                          data-testid={`allocation-user-checkbox-${u.id}`}
                          onClick={(e) => e.stopPropagation()}
                          onCheckedChange={() => {
                            if (!isAllocated && isAtLimit) return;
                            handleToggleUserAllocation(u.id);
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{u.full_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {u.company && (
                            <span className="text-xs text-muted-foreground hidden sm:inline">{u.company}</span>
                          )}
                          <Badge variant="outline" className="text-xs capitalize">
                            {u.role === 'end_user' ? 'User' : u.role}
                          </Badge>
                          <Badge variant="secondary" className={`text-xs ${maxDev !== -1 && displayDevCount >= maxDev ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : ''}`}>
                            {maxDev === -1 ? `${displayDevCount}/∞` : `${displayDevCount}/${maxDev}`}
                          </Badge>
                          <Badge variant="secondary" className={`text-xs ${maxBcn !== -1 && displayBcnCount >= maxBcn ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : ''}`}>
                            {maxBcn === -1 ? `${displayBcnCount}/∞` : `${displayBcnCount}/${maxBcn}`}
                          </Badge>
                          {isAtLimit && (
                            <span className="text-xs text-red-600 font-medium">{isBlocked ? 'BLOCKED' : 'FULL'}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                {allocatableUsers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No accounts are available for allocation.
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
      
      {activeTab === 'management_list' && (
        <div className="space-y-4" data-testid="management-list-tab">
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              Choose which Admins can <strong>view and manage</strong> this {formData.device_type === 'beacon' ? 'beacon' : 'device'} in
              their <strong>Device Management</strong> list. This is separate from Allocation — it does not affect Live Tracking or
              any operational page. Super Admins and Admins with "View / Manage All Devices" always see every device.
            </p>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              data-testid="management-list-search"
              placeholder="Search admins by name or email..."
              value={managementListSearch}
              onChange={(e) => setManagementListSearch(e.target.value)}
              className="h-8 pl-10"
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Badge variant="secondary" data-testid="management-list-count">
              {(formData.management_admin_ids || []).length} admin{(formData.management_admin_ids || []).length !== 1 ? 's' : ''} selected
            </Badge>
            {(formData.management_admin_ids || []).length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleInputChange('management_admin_ids', [])}
                className="text-xs text-muted-foreground h-6"
                data-testid="management-list-clear"
              >
                Clear all
              </Button>
            )}
          </div>

          <ScrollArea className="h-[300px] border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="p-2 space-y-1">
              {manageableAdmins
                .filter((a) => {
                  if (!managementListSearch) return true;
                  const s = managementListSearch.toLowerCase();
                  return a.full_name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s);
                })
                .map((a) => {
                  const isSelected = (formData.management_admin_ids || []).includes(a.id);
                  return (
                    <div
                      key={a.id}
                      data-testid={`management-admin-${a.id}`}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800'
                          : 'hover:bg-muted border border-transparent'
                      }`}
                      onClick={() => handleToggleManagementAdmin(a.id)}
                    >
                      <Checkbox checked={isSelected} onClick={(e) => e.stopPropagation()} onCheckedChange={() => handleToggleManagementAdmin(a.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{a.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                      </div>
                      <Badge variant="outline" className="text-xs">Admin</Badge>
                    </div>
                  );
                })}
              {manageableAdmins.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No Admins available.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
      
      {/* Tab 4: Custom Commands */}
      {activeTab === 'commands' && (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm">
              Define custom command buttons that appear in Live Tracking and Fleet pages.
              Commands are sent to the device via the existing TCP connection ({formData.protocol || 'protocol not set'}).
            </p>
            <div className="flex flex-wrap gap-2 shrink-0 sm:ml-4">
              <CommandsHelpPanel />
              <Button variant="outline" size="sm" className="text-xs h-7" data-testid="cmd-export-btn" onClick={() => {
                const toExport = selectedCommandIds.length > 0
                  ? formData.custom_commands.filter(c => selectedCommandIds.includes(c.id))
                  : formData.custom_commands;
                const clean = toExport.map(({ id, ...rest }) => rest);
                const exportData = { protocol: formData.protocol, commands: clean };
                const blob = new Blob([JSON.stringify(exportData, null, 2)], {type: 'application/json'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const _tsc = getFileTimestamp();
                a.download = `commands_${selectedDevice?.name || 'device'}_${_tsc}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${clean.length} command(s)${selectedCommandIds.length > 0 ? ' (selected)' : ''}`);
                setSelectedCommandIds([]);
              }} disabled={formData.custom_commands.length === 0}>
                <Upload className="w-3 h-3 mr-1" />
                {selectedCommandIds.length > 0 ? `Export (${selectedCommandIds.length})` : 'Export'}
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-7" data-testid="cmd-import-btn" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    try {
                      const parsed = JSON.parse(ev.target.result);
                      const cmds = Array.isArray(parsed) ? parsed : parsed.commands;
                      if (!Array.isArray(cmds)) { toast.error('Invalid format'); return; }
                      if (parsed.protocol && parsed.protocol !== formData.protocol) {
                        toast.error(`Cannot import — this file was exported from a ${parsed.protocol} device but this device uses ${formData.protocol}.`);
                        return;
                      }
                      const withNewIds = cmds.map(c => {
                        const { id, ...rest } = c;
                        return { ...rest, protocol: formData.protocol, id: `cmd_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` };
                      });
                      setPendingImport({ type: 'commands', items: withNewIds, count: withNewIds.length });
                    } catch { toast.error('Failed to parse JSON file'); }
                  };
                  reader.readAsText(file);
                };
                input.click();
              }}>
                <Download className="w-3 h-3 mr-1" />
                Import
              </Button>
            </div>
          </div>
          
          {/* Add/Edit Command Form */}
          <Card ref={commandFormCardRef}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{editingCommandId ? 'Edit Command' : 'Add New Command'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Row 1: Protocol (read-only) + Button Label + Button Colour */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">Protocol</Label>
                  <Input className="h-9 text-sm bg-muted" value={formData.protocol || 'Not set'} disabled />
                  <p className="text-[10px] text-muted-foreground">Set in Device Details tab</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">Button Label * <CmdHelpTip topic="button_label" /></Label>
                  <Input
                    className="h-9 text-sm"
                    value={newCommand.button_label}
                    onChange={(e) => setNewCommand({...newCommand, button_label: e.target.value})}
                    placeholder="Lock Vehicle"
                    data-testid="cmd-button-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">Button Colour <CmdHelpTip topic="button_color" /></Label>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {[
                      { value: 'blue', bg: 'bg-blue-600' },
                      { value: 'red', bg: 'bg-red-600' },
                      { value: 'green', bg: 'bg-green-600' },
                      { value: 'yellow', bg: 'bg-yellow-500' },
                      { value: 'orange', bg: 'bg-orange-500' },
                      { value: 'purple', bg: 'bg-purple-600' },
                      { value: 'gray', bg: 'bg-gray-500' },
                      { value: 'teal', bg: 'bg-teal-600' },
                      { value: 'pink', bg: 'bg-pink-600' },
                      { value: 'indigo', bg: 'bg-indigo-600' },
                      { value: 'cyan', bg: 'bg-cyan-600' },
                      { value: 'lime', bg: 'bg-lime-600' },
                      { value: 'rose', bg: 'bg-rose-600' },
                    ].map(c => (
                      <button
                        key={c.value}
                        type="button"
                        className={`w-7 h-7 shrink-0 rounded-md ${c.bg} transition-all ${newCommand.button_color === c.value ? 'ring-2 ring-offset-2 ring-black dark:ring-white scale-110' : 'opacity-60 hover:opacity-100'}`}
                        onClick={() => setNewCommand({...newCommand, button_color: c.value})}
                        data-testid={`cmd-color-${c.value}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Row 2: Command Mode Toggle */}
              <div className="flex items-center gap-3 pt-1">
                <Label className="text-xs flex items-center gap-1.5">Command Mode: <CmdHelpTip topic="command_mode" /></Label>
                <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${newCommand.command_mode !== 'sequence' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setNewCommand({...newCommand, command_mode: 'single'})}
                    data-testid="cmd-mode-single"
                  >
                    Single Command
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${newCommand.command_mode === 'sequence' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setNewCommand({...newCommand, command_mode: 'sequence', command_sequence: newCommand.command_sequence?.length > 0 ? newCommand.command_sequence : [{command_text: '', delay_ms: 0}, {command_text: '', delay_ms: 0}]})}
                    data-testid="cmd-mode-sequence"
                  >
                    Command Sequence
                  </button>
                </div>
                {newCommand.command_mode === 'sequence' && (
                  <span className="text-[10px] text-muted-foreground">Sends multiple commands in order with 1 button press</span>
                )}
              </div>

              {/* Single Command Mode */}
              {newCommand.command_mode !== 'sequence' && (
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">Command Text * <CmdHelpTip topic="command_text" /></Label>
                <Input
                  className="h-9 text-sm font-mono"
                  value={newCommand.command_text}
                  onChange={(e) => setNewCommand({...newCommand, command_text: e.target.value})}
                  placeholder={formData.protocol === 'Meitrack' ? 'C01,0,10000' : 'setdigout 1'}
                  data-testid="cmd-command-text"
                />
                <p className="text-[10px] text-muted-foreground">
                  {formData.protocol === 'Teltonika' && 'Teltonika GPRS: setdigout, cpureset, getinfo, getver, getgps, getio, setparam, getparam'}
                  {formData.protocol === 'Meitrack' && 'C01,Speed,ABCDE (outputs) | A10 (locate) | F09 (restart) | E91 (firmware)'}
                  {formData.protocol === 'Digital Matter' && 'Digital Matter: Commands sent via DM Cloud OTA — not supported over TCP'}
                  {!['Teltonika', 'Meitrack', 'Digital Matter'].includes(formData.protocol) && 'Enter the raw command text for your device protocol'}
                </p>
              </div>
              )}

              {/* Command Sequence Mode */}
              {newCommand.command_mode === 'sequence' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5">Sequence Steps ({newCommand.command_sequence?.length || 0}) <CmdHelpTip topic="sequence_steps" /></Label>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 text-[10px]"
                    onClick={() => setNewCommand({...newCommand, command_sequence: [...(newCommand.command_sequence || []), {command_text: '', delay_ms: 0}]})}
                    data-testid="cmd-seq-add-step"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Step
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {(newCommand.command_sequence || []).map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2 group" data-testid={`cmd-seq-step-${idx}`}>
                      <span className="text-[10px] text-muted-foreground w-5 text-right shrink-0">{idx + 1}.</span>
                      <Input
                        className="h-8 text-xs font-mono flex-1"
                        value={step.command_text}
                        onChange={(e) => {
                          const updated = [...newCommand.command_sequence];
                          updated[idx] = {...updated[idx], command_text: e.target.value};
                          setNewCommand({...newCommand, command_sequence: updated});
                        }}
                        placeholder={formData.protocol === 'Meitrack' ? `Step ${idx + 1} command (e.g. C01,0,12222)` : `Step ${idx + 1} command (e.g. setdigout 1)`}
                        data-testid={`cmd-seq-step-${idx}-text`}
                      />
                      <div className="flex items-center gap-1 shrink-0">
                        <NumberInput
                          className="h-8 text-xs w-20 text-right"
                          min="0"
                          value={step.delay_ms || ''}
                          onValueChange={(n) => {
                            const updated = [...newCommand.command_sequence];
                            updated[idx] = {...updated[idx], delay_ms: n};
                            setNewCommand({...newCommand, command_sequence: updated});
                          }}
                          placeholder="0"
                          data-testid={`cmd-seq-step-${idx}-delay`}
                        />
                        <span className="text-[10px] text-muted-foreground w-6">ms</span>
                      </div>
                      {idx > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 shrink-0"
                          onClick={() => {
                            const updated = [...newCommand.command_sequence];
                            [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
                            setNewCommand({...newCommand, command_sequence: updated});
                          }}
                          title="Move up"
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 15l-6-6-6 6"/></svg>
                        </Button>
                      ) : (
                        <div className="w-6 h-6 shrink-0" />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 shrink-0"
                        onClick={() => {
                          const updated = newCommand.command_sequence.filter((_, i) => i !== idx);
                          setNewCommand({...newCommand, command_sequence: updated});
                        }}
                        disabled={newCommand.command_sequence.length <= 2}
                        data-testid={`cmd-seq-step-${idx}-remove`}
                      >
                        <X className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground">
                  Commands are sent sequentially on the TCP socket. Delay (ms) is applied after each step before the next. Use 0 for instant chaining.
                </p>
              </div>
              )}

              {/* Row 3: Description */}
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">Description (optional tooltip) <CmdHelpTip topic="description" /></Label>
                <Input
                  className="h-9 text-sm"
                  value={newCommand.description || ''}
                  onChange={(e) => setNewCommand({...newCommand, description: e.target.value})}
                  placeholder="Locks the vehicle immobiliser relay"
                  data-testid="cmd-description"
                />
              </div>

              {/* Row 4: Toggles */}
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <label className="flex items-center gap-2 text-xs"><Switch checked={newCommand.show_telemetry} onCheckedChange={(v) => setNewCommand({...newCommand, show_telemetry: v})} className="scale-75" /> Telemetry Panel</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={newCommand.show_fleet_card} onCheckedChange={(v) => setNewCommand({...newCommand, show_fleet_card: v})} className="scale-75" /> Fleet Card</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={newCommand.requires_confirmation} onCheckedChange={(v) => setNewCommand({...newCommand, requires_confirmation: v})} className="scale-75" /> Require Confirmation</label>
                <CmdHelpTip topic="cmd_toggles" />
              </div>

              {/* Row 5: Target Device (for linked devices) */}
              {formData.linked_devices?.length > 0 && (
                <div className="space-y-1 pt-2 border-t">
                  <Label className="text-xs flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Send Command To <CmdHelpTip topic="target_device" /></Label>
                  <Select value={newCommand.target_device_id || '_self'} onValueChange={(v) => setNewCommand({...newCommand, target_device_id: v === '_self' ? null : v})}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="This device (default)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_self">This device ({selectedDevice?.name})</SelectItem>
                      {formData.linked_devices.map(ld => {
                        const dev = devices.find(d => d.id === ld.device_id);
                        return (
                          <SelectItem key={ld.device_id} value={ld.device_id}>
                            {dev?.name || ld.device_name || ld.device_id} ({dev?.protocol || 'Unknown'})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Route this command to a linked device instead of the primary.</p>
                </div>
              )}

              {/* Live Preview */}
              {newCommand.button_label && (
                <div className="p-3 bg-muted rounded-lg space-y-1">
                  <span className="text-[10px] text-muted-foreground">Button Preview:</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className={`px-3 py-1.5 rounded-md text-white text-xs font-medium ${
                        { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-gray-500', teal: 'bg-teal-600', pink: 'bg-pink-600', indigo: 'bg-indigo-600', cyan: 'bg-cyan-600', lime: 'bg-lime-600', rose: 'bg-rose-600' }[newCommand.button_color] || 'bg-blue-600'
                      }`}
                    >
                      {newCommand.button_label}
                    </button>
                    {newCommand.command_mode === 'sequence' && <Badge variant="outline" className="text-[10px]">{newCommand.command_sequence?.length || 0} steps</Badge>}
                    {newCommand.requires_confirmation && <span className="text-[10px] text-amber-600">Requires confirmation</span>}
                    {newCommand.description && <span className="text-[10px] text-muted-foreground italic">"{newCommand.description}"</span>}
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-2">
                <Button onClick={handleAddCommand} size="sm" data-testid="cmd-add-btn">
                  {editingCommandId ? <><Check className="w-4 h-4 mr-1" /> Update</> : <><Plus className="w-4 h-4 mr-1" /> Add Command</>}
                </Button>
                {editingCommandId && (
                  <Button onClick={handleCancelEditCommand} size="sm" variant="outline">Cancel</Button>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Configured Commands Table */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Configured Commands ({formData.custom_commands.length})</Label>
              {selectedCommandIds.length > 0 && (
                <span className="text-xs text-muted-foreground">{selectedCommandIds.length} selected for export</span>
              )}
            </div>
            {formData.custom_commands.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center border rounded-lg">
                No commands configured. Add your first command above.
              </p>
            ) : (
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleCommandDragEnd} modifiers={[restrictToVerticalAxis]}>
                <SortableContext items={formData.custom_commands.map(c => c.id)} strategy={verticalListSortingStrategy}>
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="px-1 w-6"></TableHead>
                    <TableHead className="px-2 w-8">
                      <Checkbox
                        checked={selectedCommandIds.length === formData.custom_commands.length && formData.custom_commands.length > 0}
                        onCheckedChange={(checked) => setSelectedCommandIds(checked ? formData.custom_commands.map(c => c.id) : [])}
                      />
                    </TableHead>
                    <TableHead className="px-2 w-8"></TableHead>
                    <TableHead className="px-2">Label</TableHead>
                    <TableHead className="px-2">Command</TableHead>
                    <TableHead className="px-2">Protocol</TableHead>
                    <TableHead className="px-2 text-center">Visible</TableHead>
                    <TableHead className="px-2 text-center">Confirm</TableHead>
                    <TableHead className="px-2 w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.custom_commands.map(cmd => (
                    <SortableTableRow key={cmd.id} id={cmd.id} className={`text-xs ${editingCommandId === cmd.id ? 'bg-blue-50 dark:bg-blue-950/30' : ''} ${selectedCommandIds.includes(cmd.id) ? 'bg-blue-50 dark:bg-blue-950/30' : ''}`}>
                      <TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedCommandIds.includes(cmd.id)}
                          onCheckedChange={(checked) => setSelectedCommandIds(prev => checked ? [...prev, cmd.id] : prev.filter(id => id !== cmd.id))}
                        />
                      </TableCell>
                      <TableCell className="px-2">
                        <div className={`w-4 h-4 rounded-sm ${
                          { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-gray-500', teal: 'bg-teal-600', pink: 'bg-pink-600', indigo: 'bg-indigo-600', cyan: 'bg-cyan-600', lime: 'bg-lime-600', rose: 'bg-rose-600' }[cmd.button_color] || 'bg-blue-600'
                        }`} />
                      </TableCell>
                      <TableCell className="px-2">
                        <div className="font-medium">{cmd.button_label}</div>
                        {cmd.description && <div className="text-[10px] text-muted-foreground">{cmd.description}</div>}
                        {cmd.target_device_id && (
                          <div className="text-[10px] text-blue-600 flex items-center gap-0.5">
                            <Link2 className="w-2.5 h-2.5" />
                            {devices.find(d => d.id === cmd.target_device_id)?.name || 'Linked device'}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="px-2 font-mono text-[10px]">
                        {cmd.command_mode === 'sequence' && cmd.command_sequence?.length > 0 ? (
                          <div className="space-y-0.5">
                            {cmd.command_sequence.map((s, i) => (
                              <div key={i} className="flex items-center gap-1">
                                <span className="text-muted-foreground">{i + 1}.</span>
                                <span>{s.command_text}</span>
                                {s.delay_ms > 0 && i < cmd.command_sequence.length - 1 && (
                                  <span className="text-muted-foreground ml-1">({s.delay_ms}ms)</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : cmd.command_text}
                      </TableCell>
                      <TableCell className="px-2"><Badge variant="outline" className="text-[10px]">{cmd.protocol}</Badge></TableCell>
                      <TableCell className="px-2 text-center">
                        <div className="flex gap-1 justify-center">
                          {cmd.show_telemetry && <Badge variant="outline" className="text-[8px] px-1">T</Badge>}
                          {cmd.show_fleet_card && <Badge variant="outline" className="text-[8px] px-1">F</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="px-2 text-center">
                        {cmd.requires_confirmation && <Badge variant="secondary" className="text-[8px] px-1">Yes</Badge>}
                      </TableCell>
                      <TableCell className="px-2">
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => handleSendCommand(cmd)} data-testid={`cmd-send-${cmd.id}`} title="Send Command"><Send className="w-3 h-3 text-blue-600" /></Button>
                          <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditCommand(cmd)} data-testid={`cmd-edit-${cmd.id}`} title="Edit"><Pencil className="w-3 h-3" /></Button>
                          <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => handleRemoveCommand(cmd.id)} data-testid={`cmd-remove-${cmd.id}`} title="Delete"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </SortableTableRow>
                  ))}
                </TableBody>
              </Table>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}
      
      {/* Tab 5: IO Mapping */}
      {activeTab === 'mapping' && (
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm">
              Map IO elements from the device to display names, formulas, and visibility settings.
              Protocol defaults are pre-loaded — customize per device.
            </p>
            <div className="flex flex-wrap gap-2 shrink-0 sm:ml-4">
              <IOMappingHelpPanel />
              <Button variant="outline" size="sm" className="text-xs h-7" data-testid="io-export-btn" onClick={() => {
                const toExport = selectedMappingIds.length > 0
                  ? formData.io_mappings.filter(m => selectedMappingIds.includes(m.id))
                  : formData.io_mappings;
                const clean = toExport.map(({ id, _customUnit, data_source, ...rest }) => rest);
                const exportData = { protocol: formData.protocol, mappings: clean };
                const blob = new Blob([JSON.stringify(exportData, null, 2)], {type: 'application/json'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const _tsm = getFileTimestamp();
                a.download = `io_mappings_${selectedDevice?.name || 'device'}_${_tsm}.json`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success(`Exported ${clean.length} mapping(s)${selectedMappingIds.length > 0 ? ' (selected)' : ''}`);
                setSelectedMappingIds([]);
              }} disabled={formData.io_mappings.length === 0}>
                <Upload className="w-3 h-3 mr-1" />
                {selectedMappingIds.length > 0 ? `Export (${selectedMappingIds.length})` : 'Export'}
              </Button>
              <Button variant="outline" size="sm" className="text-xs h-7" data-testid="io-import-btn" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    try {
                      const parsed = JSON.parse(ev.target.result);
                      const mappings = Array.isArray(parsed) ? parsed : parsed.mappings;
                      if (!Array.isArray(mappings)) { toast.error('Invalid format'); return; }
                      if (parsed.protocol && parsed.protocol !== formData.protocol) {
                        toast.error(`Cannot import — this file was exported from a ${parsed.protocol} device but this device uses ${formData.protocol}.`);
                        return;
                      }
                      const withNewIds = mappings.map(m => {
                        const { id, _customUnit, data_source, ...rest } = m;
                        return { ...rest, id: `io_${Date.now()}_${Math.random().toString(36).slice(2, 6)}` };
                      });
                      setPendingImport({ type: 'mappings', items: withNewIds, count: withNewIds.length });
                    } catch { toast.error('Failed to parse JSON file'); }
                  };
                  reader.readAsText(file);
                };
                input.click();
              }}>
                <Download className="w-3 h-3 mr-1" />
                Import
              </Button>
            </div>
          </div>
          
          {/* Add/Edit IO Mapping Form */}
          <Card ref={ioMappingFormCardRef}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{editingMappingId ? 'Edit IO Mapping' : 'Add New IO Mapping'}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30">
                <div>
                  <p className="text-xs font-medium flex items-center gap-1.5">Computed Logic Parameter <IOHelpTip topic="computed_toggle" /></p>
                  <p className="text-[10px] text-muted-foreground">Virtual IO calculated from other IO values with AND/OR logic — behaves like a real IO everywhere (telemetry, events, timers)</p>
                </div>
                <Switch
                  checked={!!newIOMapping.is_computed}
                  disabled={!!editingMappingId}
                  onCheckedChange={(v) => setNewIOMapping(prev => ({
                    ...prev,
                    is_computed: !!v,
                    ...(v
                      ? { io_id: '', io_type: 'Computed', value_type: 'Logic', logic_mapping: { '1': 'YES', '0': 'NO' }, computed_logic: prev.computed_logic || { logic: 'AND', groups: [{ logic: 'AND', conditions: [{ io_id: '', operator: 'eq', value: '' }] }] } }
                      : { io_type: 'Battery', value_type: 'Value', computed_logic: null }),
                  }))}
                  data-testid="computed-logic-toggle"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* IO Parameter — searchable protocol dropdown */}
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">{newIOMapping.is_computed ? 'Virtual IO ID' : 'IO Parameter'} <IOHelpTip topic={newIOMapping.is_computed ? 'virtual_io_id' : 'io_parameter'} /></Label>
                  {newIOMapping.is_computed ? (
                    <div className="h-8 px-3 flex items-center rounded-md border bg-muted/50 text-sm font-mono" data-testid="computed-io-id-display">
                      {newIOMapping.io_id || nextCustomIoId}
                    </div>
                  ) : (
                  <Popover modal={true} open={ioParamSearchOpen} onOpenChange={(open) => {
                    setIoParamSearchOpen(open);
                    if (!open) setIoParamSearchTerm('');
                  }}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        data-testid="io-param-select-trigger"
                        className="w-full h-8 justify-between text-sm font-normal bg-white dark:bg-[#0D1425]"
                      >
                        <span className="truncate">
                          {newIOMapping.io_id === '__custom'
                            ? 'Custom ID...'
                            : (newIOMapping.io_id && newIOMapping.io_id !== '__placeholder')
                              ? (() => {
                                  const sel = protocolIODefaults.find(d => d.io_id === newIOMapping.io_id);
                                  return sel ? `${sel.io_id} — ${sel.default_name}` : newIOMapping.io_id;
                                })()
                              : <span className="text-muted-foreground">Select IO element</span>}
                        </span>
                        <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0 z-[10000]" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Search IO parameter or ID..."
                          data-testid="io-param-search-input"
                          value={ioParamSearchTerm}
                          onValueChange={setIoParamSearchTerm}
                        />
                        <CommandList>
                          {protocolIODefaults.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="io-param-loading">Loading IO elements...</div>
                          ) : (
                            <>
                              {filteredProtocolIODefaults.length === 0 && (
                                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="io-param-search-empty">No IO parameters found.</div>
                              )}
                              <CommandGroup>
                                {filteredProtocolIODefaults.map(d => (
                                  <CommandItem
                                    key={d.io_id}
                                    value={`${d.io_id} ${d.default_name}`}
                                    data-testid={`io-param-option-${d.io_id}`}
                                    onSelect={() => {
                                      setNewIOMapping(prev => ({
                                        ...prev, io_id: d.io_id,
                                        display_name: d.default_name || prev.display_name || '',
                                      }));
                                      setIoParamSearchTerm('');
                                      setIoParamSearchOpen(false);
                                    }}
                                  >
                                    <span className="font-mono text-xs mr-2">{d.io_id}</span>
                                    <span className="truncate">{d.default_name}</span>
                                  </CommandItem>
                                ))}
                                <CommandItem
                                  value="__custom custom id"
                                  data-testid="io-param-option-custom"
                                  onSelect={() => {
                                    setNewIOMapping(prev => ({...prev, io_id: '__custom'}));
                                    setIoParamSearchTerm('');
                                    setIoParamSearchOpen(false);
                                  }}
                                >
                                  Custom ID...
                                </CommandItem>
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  )}
                  {!newIOMapping.is_computed && newIOMapping.io_id === '__custom' && (
                    <Input className="h-8 text-sm font-mono mt-1" placeholder="Custom IO ID" value="" onChange={(e) => setNewIOMapping(prev => ({...prev, io_id: e.target.value || '__custom'}))} />
                  )}
                  {(() => {
                    const chosen = String(newIOMapping.io_id ?? '').trim();
                    if (!chosen || chosen === '__placeholder' || chosen === '__custom') return null;
                    const others = (formData.io_mappings || []).filter(m => String(m.io_id ?? '').trim() === chosen && m.id !== editingMappingId);
                    if (!others.length) return null;
                    return (
                      <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1 flex items-start gap-1" data-testid="io-duplicate-inline-warning">
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>IO {chosen} is already mapped as {others.map(m => `"${m.display_name}"`).join(', ')} — saving creates a duplicate (rules/displays use the first match).</span>
                      </p>
                    );
                  })()}
                </div>
                {/* Display Name */}
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">Display Name <IOHelpTip topic="display_name" /></Label>
                  <Input className="h-8 text-sm" value={newIOMapping.display_name} onChange={(e) => setNewIOMapping(prev => ({...prev, display_name: e.target.value}))} placeholder="Battery Voltage" />
                </div>
                {/* IO Type */}
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">IO Type <IOHelpTip topic="io_type" /></Label>
                  <Select value={newIOMapping.io_type || 'Battery'} onValueChange={(v) => setNewIOMapping(prev => ({...prev, io_type: v}))}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ioTypes.map(t => (
                        <SelectItem key={t} value={t}>
                          {ioTypeIcons[t] && <svg className="w-3 h-3 inline mr-1" viewBox="0 0 24 24" fill="currentColor"><path d={ioTypeIcons[t]}/></svg>}
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Value Type */}
                <div className="space-y-1">
                  <Label className="text-xs flex items-center gap-1.5">Value Type <IOHelpTip topic="value_type" /></Label>
                  <Select value={newIOMapping.value_type || 'Value'} disabled={!!newIOMapping.is_computed} onValueChange={(v) => setNewIOMapping(prev => ({
                    ...prev, value_type: v,
                    decimal_places: v === 'Value' ? (prev.decimal_places ?? 2) : null,
                  }))}>
                    <SelectTrigger className="h-8 text-sm" data-testid="io-value-type-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{valueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {/* Units (for Value type) */}
                {(newIOMapping.value_type === 'Value' || newIOMapping.value_type === 'Percentage') && (
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1.5">Units <IOHelpTip topic="units" /></Label>
                    {(() => {
                      const isStandard = unitOptions.includes(newIOMapping.units) && newIOMapping.units !== 'Custom';
                      const isNone = !newIOMapping.units || newIOMapping.units === 'None';
                      const isCustom = newIOMapping._customUnit || (!isStandard && !isNone && newIOMapping.units);
                      const selectValue = isNone ? 'None' : isCustom ? 'Custom' : newIOMapping.units;
                      return (<>
                        <Select value={selectValue} onValueChange={(v) => {
                          if (v === 'None') setNewIOMapping(prev => ({...prev, units: '', _customUnit: false}));
                          else if (v === 'Custom') setNewIOMapping(prev => ({...prev, units: '', _customUnit: true}));
                          else setNewIOMapping(prev => ({...prev, units: v, _customUnit: false}));
                        }}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>{unitOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                        {(isCustom || selectValue === 'Custom') && (
                          <Input className="h-8 text-sm mt-1" placeholder="Enter custom unit" value={newIOMapping.units || ''} onChange={(e) => setNewIOMapping(prev => ({...prev, units: e.target.value, _customUnit: true}))} />
                        )}
                      </>);
                    })()}
                  </div>
                )}
              </div>

              {/* Value type specific fields */}
              {newIOMapping.value_type === 'Value' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1.5">Formula (X = raw value) <IOHelpTip topic="formula" /></Label>
                    <Input className="h-9 text-sm font-mono" value={newIOMapping.formula} onChange={(e) => setNewIOMapping(prev => ({...prev, formula: e.target.value}))} placeholder="X / 1000" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1.5">Decimal Places <IOHelpTip topic="decimal_places" /></Label>
                    <Select value={String(newIOMapping.decimal_places ?? 2)} onValueChange={(v) => setNewIOMapping(prev => ({...prev, decimal_places: parseInt(v)}))}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{[0,1,2,3,4].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {newIOMapping.value_type === 'Logic' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1.5">If "1" (ON text) <IOHelpTip topic="logic_text" /></Label>
                      <div className="flex gap-1">
                        <Input className="h-9 text-sm flex-1" value={newIOMapping.logic_mapping?.["1"] || ''} onChange={(e) => setNewIOMapping(prev => ({...prev, logic_mapping: {...(prev.logic_mapping || {}), "1": e.target.value}}))} placeholder="Ignition ON" />
                        <Select value={newIOMapping.value_colors?.[newIOMapping.logic_mapping?.["1"]] || ''} onValueChange={(v) => {
                          const text = newIOMapping.logic_mapping?.["1"];
                          if (!text) return;
                          setNewIOMapping(prev => ({...prev, value_colors: {...(prev.value_colors || {}), [text]: v === '_none' ? '' : v}}));
                        }}>
                          <SelectTrigger className="h-8 w-20 px-2">
                            {(() => { const c = newIOMapping.value_colors?.[newIOMapping.logic_mapping?.["1"]]; const opt = valueColorOptions.find(o => o.value === c); return opt?.bg ? <span className={`w-3 h-3 rounded-full ${opt.bg}`}></span> : <span className="text-xs text-muted-foreground">Color</span>; })()}
                          </SelectTrigger>
                          <SelectContent>
                            {valueColorOptions.map(c => (
                              <SelectItem key={c.value || '_none'} value={c.value || '_none'}>
                                <span className="flex items-center gap-2">{c.bg && <span className={`w-2.5 h-2.5 rounded-full ${c.bg}`}></span>}{c.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1.5">If "0" (OFF text)</Label>
                      <div className="flex gap-1">
                        <Input className="h-9 text-sm flex-1" value={newIOMapping.logic_mapping?.["0"] || ''} onChange={(e) => setNewIOMapping(prev => ({...prev, logic_mapping: {...(prev.logic_mapping || {}), "0": e.target.value}}))} placeholder="Ignition OFF" />
                        <Select value={newIOMapping.value_colors?.[newIOMapping.logic_mapping?.["0"]] || ''} onValueChange={(v) => {
                          const text = newIOMapping.logic_mapping?.["0"];
                          if (!text) return;
                          setNewIOMapping(prev => ({...prev, value_colors: {...(prev.value_colors || {}), [text]: v === '_none' ? '' : v}}));
                        }}>
                          <SelectTrigger className="h-8 w-20 px-2">
                            {(() => { const c = newIOMapping.value_colors?.[newIOMapping.logic_mapping?.["0"]]; const opt = valueColorOptions.find(o => o.value === c); return opt?.bg ? <span className={`w-3 h-3 rounded-full ${opt.bg}`}></span> : <span className="text-xs text-muted-foreground">Color</span>; })()}
                          </SelectTrigger>
                          <SelectContent>
                            {valueColorOptions.map(c => (
                              <SelectItem key={c.value || '_none'} value={c.value || '_none'}>
                                <span className="flex items-center gap-2">{c.bg && <span className={`w-2.5 h-2.5 rounded-full ${c.bg}`}></span>}{c.label}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {newIOMapping.value_type === 'String' && (
                <div className="space-y-2">
                  <Label className="text-xs flex items-center gap-1.5">Dictionary (Value → Text) <IOHelpTip topic="dictionary" /></Label>
                  <div className="flex gap-2">
                    <Input className="h-8 text-sm font-mono w-24" placeholder="Value" value={newDictKey} onChange={(e) => setNewDictKey(e.target.value)} />
                    <span className="text-sm self-center">=</span>
                    <Input className="h-8 text-sm flex-1" placeholder="Display text" value={newDictValue} onChange={(e) => setNewDictValue(e.target.value)} />
                    <Button size="sm" variant="outline" className="h-8" onClick={() => {
                      if (newDictKey && newDictValue) {
                        setNewIOMapping(prev => ({...prev, dictionary: {...(prev.dictionary || {}), [newDictKey]: newDictValue}}));
                        setNewDictKey(''); setNewDictValue('');
                      }
                    }}><Plus className="w-3 h-3" /></Button>
                  </div>
                  {Object.entries(newIOMapping.dictionary || {}).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 text-xs bg-muted px-2 py-1 rounded">
                      <span className="font-mono">{k}</span> = <span className={getValueColorClass(typeof v === 'string' ? v : String(v), newIOMapping.value_colors) || ''}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      <Select value={newIOMapping.value_colors?.[v] || ''} onValueChange={(col) => {
                        setNewIOMapping(prev => ({...prev, value_colors: {...(prev.value_colors || {}), [v]: col === '_none' ? '' : col}}));
                      }}>
                        <SelectTrigger className="h-8 h-5 w-14 px-1 ml-auto">
                          {(() => { const c = newIOMapping.value_colors?.[v]; const opt = valueColorOptions.find(o => o.value === c); return opt?.bg ? <span className={`w-2 h-2 rounded-full ${opt.bg}`}></span> : <span className="text-[9px] text-muted-foreground">Color</span>; })()}
                        </SelectTrigger>
                        <SelectContent>
                          {valueColorOptions.map(c => (
                            <SelectItem key={c.value || '_none'} value={c.value || '_none'}>
                              <span className="flex items-center gap-1.5 text-xs">{c.bg && <span className={`w-2 h-2 rounded-full ${c.bg}`}></span>}{c.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => {
                        const d = {...newIOMapping.dictionary}; delete d[k];
                        const vc = {...(newIOMapping.value_colors || {})}; delete vc[v];
                        setNewIOMapping(prev => ({...prev, dictionary: d, value_colors: vc}));
                      }}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Multi-Parameter (combine 2 IO elements) */}
              {newIOMapping.value_type === 'Multi-Parameter' && (
                <div className="space-y-3 p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200">
                  <Label className="text-xs font-semibold flex items-center gap-1.5">Multi-Parameter Configuration <IOHelpTip topic="multi_parameter" /></Label>
                  <p className="text-xs text-muted-foreground">Combine two IO elements for lookup or concatenation.</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Secondary IO Parameter</Label>
                      <Popover modal={true} open={secondaryIoParamSearchOpen} onOpenChange={(open) => {
                        setSecondaryIoParamSearchOpen(open);
                        if (!open) setSecondaryIoParamSearchTerm('');
                      }}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            data-testid="secondary-io-param-select-trigger"
                            className="w-full h-8 justify-between text-xs font-normal bg-white dark:bg-[#0D1425]"
                          >
                            <span className="truncate">
                              {newIOMapping.secondary_io_id
                                ? (() => {
                                    const sel = protocolIODefaults.find(d => d.io_id === newIOMapping.secondary_io_id);
                                    return sel ? `${sel.io_id} — ${sel.default_name}` : newIOMapping.secondary_io_id;
                                  })()
                                : <span className="text-muted-foreground">Select second IO element</span>}
                            </span>
                            <ChevronsUpDown className="w-3.5 h-3.5 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0 z-[10000]" align="start">
                          <Command shouldFilter={false}>
                            <CommandInput
                              placeholder="Search IO parameter or ID..."
                              data-testid="secondary-io-param-search-input"
                              value={secondaryIoParamSearchTerm}
                              onValueChange={setSecondaryIoParamSearchTerm}
                            />
                            <CommandList>
                              {protocolIODefaults.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="secondary-io-param-loading">Loading IO elements...</div>
                              ) : filteredSecondaryProtocolIODefaults.length === 0 ? (
                                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="secondary-io-param-search-empty">No IO parameters found.</div>
                              ) : (
                                <CommandGroup>
                                  {filteredSecondaryProtocolIODefaults.map(d => (
                                    <CommandItem
                                      key={d.io_id}
                                      value={`${d.io_id} ${d.default_name}`}
                                      data-testid={`secondary-io-param-option-${d.io_id}`}
                                      onSelect={() => {
                                        setNewIOMapping(prev => ({...prev, secondary_io_id: d.io_id}));
                                        setSecondaryIoParamSearchTerm('');
                                        setSecondaryIoParamSearchOpen(false);
                                      }}
                                    >
                                      <span className="font-mono text-xs mr-2">{d.io_id}</span>
                                      <span className="truncate">{d.default_name}</span>
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Mode</Label>
                      <Select value={newIOMapping.multi_param_mode || 'lookup'} onValueChange={(v) => setNewIOMapping(prev => ({...prev, multi_param_mode: v}))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="lookup">Lookup (Dictionary)</SelectItem>
                          <SelectItem value="concatenate">Concatenate (Join)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Lookup mode: dictionary with composite keys */}
                  {(newIOMapping.multi_param_mode || 'lookup') === 'lookup' && (
                    <div className="space-y-2">
                      <Label className="text-xs">Lookup Dictionary</Label>
                      <p className="text-xs text-muted-foreground">
                        Key format: <span className="font-mono">[{newIOMapping.io_id || 'param1'}]:[{newIOMapping.secondary_io_id || 'param2'}]</span> → Display text
                      </p>
                      <div className="flex gap-2">
                        <Input className="h-8 text-xs font-mono w-20" placeholder={newIOMapping.io_id || 'val1'} id="mp-key1" />
                        <span className="text-xs self-center font-mono">:</span>
                        <Input className="h-8 text-xs font-mono w-20" placeholder={newIOMapping.secondary_io_id || 'val2'} id="mp-key2" />
                        <span className="text-xs self-center">=</span>
                        <Input className="h-8 text-xs flex-1" placeholder="Display text (e.g. Telstra AU)" value={newMultiDictDisplay} onChange={(e) => setNewMultiDictDisplay(e.target.value)} />
                        <Button size="sm" variant="outline" className="h-8" onClick={() => {
                          const k1 = document.getElementById('mp-key1')?.value?.trim();
                          const k2 = document.getElementById('mp-key2')?.value?.trim();
                          if (k1 && k2 && newMultiDictDisplay) {
                            const key = `${k1}:${k2}`;
                            setNewIOMapping(prev => ({...prev, multi_param_dictionary: {...(prev.multi_param_dictionary || {}), [key]: newMultiDictDisplay}}));
                            document.getElementById('mp-key1').value = '';
                            document.getElementById('mp-key2').value = '';
                            setNewMultiDictDisplay('');
                          } else {
                            toast.error('Fill in both key values and display text');
                          }
                        }}><Plus className="w-3 h-3" /></Button>
                      </div>
                      {Object.keys(newIOMapping.multi_param_dictionary || {}).length > 0 ? (
                        Object.entries(newIOMapping.multi_param_dictionary || {}).map(([k, v]) => (
                          <div key={k} className="flex items-center gap-2 text-xs bg-muted px-2 py-1 rounded">
                            <span className="font-mono">{k}</span> = <span className={`font-semibold ${getValueColorClass(v, newIOMapping.value_colors)}`}>{v}</span>
                            <Select value={newIOMapping.value_colors?.[v] || ''} onValueChange={(col) => {
                              setNewIOMapping(prev => ({...prev, value_colors: {...(prev.value_colors || {}), [v]: col === '_none' ? '' : col}}));
                            }}>
                              <SelectTrigger className="h-8 h-5 w-14 px-1 ml-auto">
                                {(() => { const c = newIOMapping.value_colors?.[v]; const opt = valueColorOptions.find(o => o.value === c); return opt?.bg ? <span className={`w-2 h-2 rounded-full ${opt.bg}`}></span> : <span className="text-[9px] text-muted-foreground">Color</span>; })()}
                              </SelectTrigger>
                              <SelectContent>
                                {valueColorOptions.map(c => (
                                  <SelectItem key={c.value || '_none'} value={c.value || '_none'}>
                                    <span className="flex items-center gap-1.5 text-xs">{c.bg && <span className={`w-2 h-2 rounded-full ${c.bg}`}></span>}{c.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="ghost" className="h-5 w-5 p-0" onClick={() => {
                              const d = {...(newIOMapping.multi_param_dictionary || {})}; delete d[k];
                              const vc = {...(newIOMapping.value_colors || {})}; delete vc[v];
                              setNewIOMapping(prev => ({...prev, multi_param_dictionary: d, value_colors: vc}));
                            }}><X className="w-3 h-3 text-red-500" /></Button>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-amber-600">No entries yet — add dictionary entries before saving this mapping.</p>
                      )}
                    </div>
                  )}

                  {/* Concatenate mode: template */}
                  {newIOMapping.multi_param_mode === 'concatenate' && (
                    <div className="space-y-2">
                      <Label className="text-xs">Output Template</Label>
                      <p className="text-xs text-muted-foreground">
                        Use <span className="font-mono">[{newIOMapping.io_id || 'param1'}]</span> and <span className="font-mono">[{newIOMapping.secondary_io_id || 'param2'}]</span> as placeholders.
                        Leave empty to simply join values together.
                      </p>
                      <Input
                        className="h-8 text-xs font-mono"
                        value={newIOMapping.multi_param_template || ''}
                        onChange={(e) => setNewIOMapping(prev => ({...prev, multi_param_template: e.target.value}))}
                        placeholder={`[${newIOMapping.io_id || 'param1'}][${newIOMapping.secondary_io_id || 'param2'}]`}
                      />
                    </div>
                  )}

                  {/* Live preview */}
                  {newIOMapping.io_id && newIOMapping.secondary_io_id && currentIOValues && (
                    <div className="p-2 bg-muted rounded text-xs">
                      <span className="text-muted-foreground">Preview: </span>
                      <span className="font-mono">{safeIOVal(currentIOValues[newIOMapping.io_id]) ?? '?'}</span>
                      <span className="text-muted-foreground"> + </span>
                      <span className="font-mono">{safeIOVal(currentIOValues[newIOMapping.secondary_io_id]) ?? '?'}</span>
                      {(newIOMapping.multi_param_mode || 'lookup') === 'lookup' && (
                        <span> → <strong>
                          {(newIOMapping.multi_param_dictionary || {})[`${currentIOValues[newIOMapping.io_id] ?? ''}:${currentIOValues[newIOMapping.secondary_io_id] ?? ''}`] || '(no match)'}
                        </strong></span>
                      )}
                      {newIOMapping.multi_param_mode === 'concatenate' && (
                        <span> → <strong>
                          {newIOMapping.multi_param_template
                            ? newIOMapping.multi_param_template
                                .replace(`[${newIOMapping.io_id}]`, String(currentIOValues[newIOMapping.io_id] ?? ''))
                                .replace(`[${newIOMapping.secondary_io_id}]`, String(currentIOValues[newIOMapping.secondary_io_id] ?? ''))
                            : `${currentIOValues[newIOMapping.io_id] ?? ''}${currentIOValues[newIOMapping.secondary_io_id] ?? ''}`
                          }
                        </strong></span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Calibration Table (Value type only) */}
              {newIOMapping.value_type === 'Value' && (
                <div className="space-y-2 pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold flex items-center gap-1.5">Calibration Table (X→Y interpolation) <IOHelpTip topic="calibration" /></Label>
                    <span className="text-[10px] text-muted-foreground">Raw→Calibrated, applied before formula</span>
                  </div>
                  {(newIOMapping.calibration || []).length > 0 && (
                    <div className="grid grid-cols-[1fr_1fr_40px] gap-1 text-xs">
                      <span className="font-semibold text-muted-foreground px-1">X (Raw)</span>
                      <span className="font-semibold text-muted-foreground px-1">Y (Calibrated)</span>
                      <span></span>
                      {(newIOMapping.calibration || []).map((pt, i) => (
                        <React.Fragment key={i}>
                          <Input className="h-7 text-xs font-mono" type="number" value={pt.x} onChange={(e) => {
                            const cal = [...(newIOMapping.calibration || [])];
                            cal[i] = {...cal[i], x: parseFloat(e.target.value) || 0};
                            setNewIOMapping(prev => ({...prev, calibration: cal}));
                          }} />
                          <Input className="h-7 text-xs font-mono" type="number" value={pt.y} onChange={(e) => {
                            const cal = [...(newIOMapping.calibration || [])];
                            cal[i] = {...cal[i], y: parseFloat(e.target.value) || 0};
                            setNewIOMapping(prev => ({...prev, calibration: cal}));
                          }} />
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                            setNewIOMapping(prev => ({...prev, calibration: (prev.calibration || []).filter((_, j) => j !== i)}));
                          }}><X className="w-3 h-3 text-red-500" /></Button>
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => {
                    setNewIOMapping(prev => ({...prev, calibration: [...(prev.calibration || []), {x: 0, y: 0}]}));
                  }}>
                    <Plus className="w-3 h-3 mr-1" /> Add Point
                  </Button>
                  {(newIOMapping.calibration || []).length > 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      {(newIOMapping.calibration || []).length} point(s). Values between points are linearly interpolated.
                    </p>
                  )}
                </div>
              )}

              {/* Default text colour (for Value/Percentage types, or as fallback) */}
              {(newIOMapping.value_type === 'Value' || newIOMapping.value_type === 'Percentage' || newIOMapping.value_type === 'Multi-Parameter' && newIOMapping.multi_param_mode === 'concatenate') && (
                <div className="space-y-1">
                  <Label className="text-xs">Output Text Colour</Label>
                  <div className="flex gap-1.5">
                    {valueColorOptions.map(c => (
                      <button
                        key={c.value || '_none'}
                        type="button"
                        onClick={() => setNewIOMapping(prev => ({...prev, value_colors: c.value ? {...(prev.value_colors || {}), '_default': c.value} : {}}))}
                        className={`w-6 h-6 rounded-full border-2 transition-all ${
                          (newIOMapping.value_colors?.['_default'] || '') === c.value
                            ? 'border-blue-500 scale-110'
                            : 'border-muted hover:border-foreground/30'
                        } ${c.bg || 'bg-muted'}`}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Visibility toggles */}
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <label className="flex items-center gap-2 text-xs"><Switch checked={newIOMapping.show_telemetry} onCheckedChange={(v) => setNewIOMapping(prev => ({...prev, show_telemetry: v}))} className="scale-75" /> Telemetry Panel</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={newIOMapping.show_fleet_card} onCheckedChange={(v) => setNewIOMapping(prev => ({...prev, show_fleet_card: v}))} className="scale-75" /> Fleet Card</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={newIOMapping.show_icon} onCheckedChange={(v) => setNewIOMapping(prev => ({...prev, show_icon: v}))} className="scale-75" /> Show Icon</label>
                <label className="flex items-center gap-2 text-xs"><Switch checked={newIOMapping.ignore_ignition_off} onCheckedChange={(v) => setNewIOMapping(prev => ({...prev, ignore_ignition_off: v}))} className="scale-75" /> Hide if Ignition OFF</label>
                <IOHelpTip topic="visibility" />
              </div>

              {/* Live Preview */}
              {newIOMapping.io_id && newIOMapping.io_id !== '__placeholder' && currentIOValues[newIOMapping.io_id] !== undefined && (
                <div className="p-2 bg-muted rounded-lg flex items-center gap-3 text-sm">
                  <span className="text-muted-foreground">Live Preview:</span>
                  {newIOMapping.value_type === 'Multi-Parameter' && newIOMapping.secondary_io_id ? (
                    <>
                      <span className="font-mono text-xs">Raw: {safeIOVal(currentIOValues[newIOMapping.io_id])} + {safeIOVal(currentIOValues[newIOMapping.secondary_io_id])}</span>
                      <span>→</span>
                      <span className={`font-semibold ${(() => {
                        const v1 = safeIOVal(currentIOValues[newIOMapping.io_id]);
                        const v2 = safeIOVal(currentIOValues[newIOMapping.secondary_io_id]);
                        if ((newIOMapping.multi_param_mode || 'lookup') === 'lookup') {
                          const sep = newIOMapping.multi_param_separator != null ? newIOMapping.multi_param_separator : ':';
                          const result = (newIOMapping.multi_param_dictionary || {})[`${v1}${sep}${v2}`];
                          return result ? getValueColorClass(result, newIOMapping.value_colors) : '';
                        }
                        return getValueColorClass('_default', newIOMapping.value_colors);
                      })()}`}>
                        {(() => {
                          const v1 = safeIOVal(currentIOValues[newIOMapping.io_id]);
                          const v2 = safeIOVal(currentIOValues[newIOMapping.secondary_io_id]);
                          if (v1 == null || v2 == null) return '-';
                          if ((newIOMapping.multi_param_mode || 'lookup') === 'lookup') {
                            const sep = newIOMapping.multi_param_separator != null ? newIOMapping.multi_param_separator : ':';
                            const key = `${v1}${sep}${v2}`;
                            return (newIOMapping.multi_param_dictionary || {})[key] || `(no match: ${key})`;
                          }
                          return newIOMapping.multi_param_template
                            ? newIOMapping.multi_param_template.replace(`[${newIOMapping.io_id}]`, String(v1)).replace(`[${newIOMapping.secondary_io_id}]`, String(v2))
                            : `${v1}${newIOMapping.multi_param_separator != null ? newIOMapping.multi_param_separator : ''}${v2}`;
                        })()}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-xs">Raw: {safeIOVal(currentIOValues[newIOMapping.io_id])}</span>
                      <span>→</span>
                      <span className={`font-semibold ${(() => {
                        if (newIOMapping.value_type === 'Logic') {
                          const logicVal = newIOMapping.logic_mapping?.[String(safeIOVal(currentIOValues[newIOMapping.io_id]))] || '';
                          return getValueColorClass(logicVal, newIOMapping.value_colors);
                        }
                        return getValueColorClass('_default', newIOMapping.value_colors);
                      })()}`}>
                        {newIOMapping.value_type === 'Logic'
                          ? (newIOMapping.logic_mapping?.[String(safeIOVal(currentIOValues[newIOMapping.io_id]))] || String(safeIOVal(currentIOValues[newIOMapping.io_id])))
                          : newIOMapping.value_type === 'Percentage'
                            ? `${evaluateFormula(safeIOVal(currentIOValues[newIOMapping.io_id]), newIOMapping.formula, null, newIOMapping.dictionary, newIOMapping.calibration)}%`
                            : `${evaluateFormula(safeIOVal(currentIOValues[newIOMapping.io_id]), newIOMapping.formula, newIOMapping.decimal_places, newIOMapping.dictionary, newIOMapping.calibration)} ${newIOMapping.units || ''}`
                        }
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Buttons */}
              {newIOMapping.is_computed && (
                <ComputedLogicBuilder
                  value={newIOMapping.computed_logic}
                  onChange={(cl) => setNewIOMapping(prev => ({ ...prev, computed_logic: cl }))}
                  physicalMappings={(formData.io_mappings || []).filter(m => !m.is_computed && m.id !== editingMappingId)}
                  ioOptions={protocolIODefaults}
                  currentValues={currentIOValues}
                  labels={newIOMapping.logic_mapping}
                />
              )}
              <div className="flex gap-2">
                <Button onClick={handleAddIOMapping} size="sm">
                  {editingMappingId ? <><Check className="w-4 h-4 mr-1" /> Update</> : <><Plus className="w-4 h-4 mr-1" /> Add Mapping</>}
                </Button>
                {editingMappingId && (
                  <Button onClick={handleCancelEditMapping} size="sm" variant="outline">Cancel</Button>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Configured IO Mappings List */}
          <div className="space-y-2">
            {Object.keys(ioDuplicates).length > 0 && (
              <div className="p-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400 space-y-0.5" data-testid="io-duplicate-warning-banner">
                <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Duplicate IO mappings — rules and displays resolve to the FIRST match:</p>
                {Object.entries(ioDuplicates).map(([ioId, names]) => (
                  <p key={ioId} className="pl-5">IO {ioId} has {names.length} mappings: {names.join(', ')}</p>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label>Configured Mappings ({formData.io_mappings.length})</Label>
              {selectedMappingIds.length > 0 && (
                <span className="text-xs text-muted-foreground">{selectedMappingIds.length} selected for export</span>
              )}
            </div>
            {formData.io_mappings.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center border rounded-lg">
                No IO mappings configured. Add your first mapping above.
              </p>
            ) : (
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleMappingDragEnd} modifiers={[restrictToVerticalAxis]}>
                <SortableContext items={formData.io_mappings.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <Table>
                <TableHeader>
                  <TableRow className="text-xs">
                    <TableHead className="px-1 w-6"></TableHead>
                    <TableHead className="px-2 w-8">
                      <Checkbox
                        checked={selectedMappingIds.length === formData.io_mappings.length && formData.io_mappings.length > 0}
                        onCheckedChange={(checked) => {
                          setSelectedMappingIds(checked ? formData.io_mappings.map(m => m.id) : []);
                        }}
                        data-testid="io-select-all-checkbox"
                      />
                    </TableHead>
                    <TableHead className="px-2 w-8"></TableHead>
                    <TableHead className="px-2">Name</TableHead>
                    <TableHead className="px-2">IO ID</TableHead>
                    <TableHead className="px-2">Type</TableHead>
                    <TableHead className="px-2">Value Type</TableHead>
                    <TableHead className="px-2">Formula/Display</TableHead>
                    <TableHead className="px-2 text-center">Visible</TableHead>
                    <TableHead className="px-2">Current</TableHead>
                    <TableHead className="px-2 w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.io_mappings.map(mapping => {
                    const rawVal = safeIOVal(currentIOValues[mapping.io_id]);
                    let displayVal;
                    if (mapping.value_type === 'Multi-Parameter') {
                      const val1 = safeIOVal(currentIOValues[mapping.io_id]);
                      const val2 = safeIOVal(currentIOValues[mapping.secondary_io_id]);
                      if (val1 != null && val2 != null) {
                        if ((mapping.multi_param_mode || 'lookup') === 'lookup') {
                          const sep = mapping.multi_param_separator != null ? mapping.multi_param_separator : ':';
                          const key = `${val1}${sep}${val2}`;
                          displayVal = (mapping.multi_param_dictionary || {})[key] || key;
                        } else {
                          displayVal = mapping.multi_param_template
                            ? mapping.multi_param_template.replace(`[${mapping.io_id}]`, String(val1)).replace(`[${mapping.secondary_io_id}]`, String(val2))
                            : `${val1}${mapping.multi_param_separator != null ? mapping.multi_param_separator : ''}${val2}`;
                        }
                      } else {
                        displayVal = String(val1 ?? '-');
                      }
                    } else if (mapping.value_type === 'Logic') {
                      const logicResult = mapping.logic_mapping?.[String(rawVal)];
                      displayVal = (logicResult && typeof logicResult !== 'object') ? String(logicResult) : String(rawVal ?? '-');
                    } else {
                      displayVal = `${evaluateFormula(rawVal, mapping.formula, mapping.decimal_places, mapping.dictionary, mapping.calibration)} ${mapping.units || ''}`;
                    }
                    return (
                      <SortableTableRow key={mapping.id} id={mapping.id} className={`text-xs ${selectedMappingIds.includes(mapping.id) ? 'bg-blue-50 dark:bg-blue-950/30' : (ioDuplicates[String(mapping.io_id ?? '').trim()] ? 'bg-amber-50 dark:bg-amber-950/20' : '')}`}>
                        <TableCell className="px-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedMappingIds.includes(mapping.id)}
                            onCheckedChange={(checked) => {
                              setSelectedMappingIds(prev => checked ? [...prev, mapping.id] : prev.filter(id => id !== mapping.id));
                            }}
                            data-testid={`io-select-${mapping.io_id}`}
                          />
                        </TableCell>
                        <TableCell className="px-2">
                          {mapping.show_icon && ioTypeIcons[mapping.io_type] && (
                            <svg className="w-4 h-4 text-muted-foreground" viewBox="0 0 24 24" fill="currentColor"><path d={ioTypeIcons[mapping.io_type]}/></svg>
                          )}
                        </TableCell>
                        <TableCell className="px-2 font-medium">{mapping.display_name}</TableCell>
                        <TableCell className="px-2 font-mono text-[10px]">
                          <span className="inline-flex items-center gap-1">
                            {mapping.io_id}
                            {ioDuplicates[String(mapping.io_id ?? '').trim()] && (
                              <span title={`Duplicate — IO ${mapping.io_id} is also mapped as: ${ioDuplicates[String(mapping.io_id ?? '').trim()].filter(n => n !== (mapping.display_name || mapping.io_type)).join(', ') || 'another mapping'}`} data-testid={`io-duplicate-flag-${mapping.id}`}>
                                <AlertTriangle className="w-3 h-3 text-amber-500" />
                              </span>
                            )}
                          </span>
                        </TableCell>
                        <TableCell className="px-2"><Badge variant="outline" className="text-[10px]">{mapping.io_type}</Badge></TableCell>
                        <TableCell className="px-2"><Badge variant="secondary" className="text-[10px]">{mapping.value_type}</Badge></TableCell>
                        <TableCell className="px-2 font-mono text-[10px]">{
                          mapping.value_type === 'Multi-Parameter'
                            ? `[${mapping.io_id}]+[${mapping.secondary_io_id || '?'}] ${(mapping.multi_param_mode || 'lookup') === 'lookup' ? `(${Object.keys(mapping.multi_param_dictionary || {}).length} entries)` : (mapping.multi_param_template || 'join')}`
                            : mapping.is_computed ? `Computed logic (${(mapping.computed_logic?.groups || []).reduce((a, g) => a + (g.conditions || []).filter(c => String(c.io_id || '').trim()).length, 0)} cond)`
                            : mapping.formula || (mapping.value_type === 'Logic' ? `1="${String(mapping.logic_mapping?.["1"] ?? '')}" 0="${String(mapping.logic_mapping?.["0"] ?? '')}"` : (mapping.value_type === 'Value' && mapping.dictionary && Object.keys(mapping.dictionary).length > 0 ? `(${Object.keys(mapping.dictionary).length} entries)` : '-'))
                        }</TableCell>
                        <TableCell className="px-2 text-center">
                          <div className="flex gap-1 justify-center">
                            {mapping.show_telemetry && <Badge variant="outline" className="text-[8px] px-1">T</Badge>}
                            {mapping.show_fleet_card && <Badge variant="outline" className="text-[8px] px-1">F</Badge>}
                            {mapping.ignore_ignition_off && <Badge variant="outline" className="text-[8px] px-1 text-amber-600 border-amber-300">Ign</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className={`px-2 font-semibold text-xs ${rawVal !== undefined ? getValueColorClass(typeof displayVal === 'string' ? displayVal.trim() : '', mapping.value_colors) : ''}`}>{rawVal !== undefined ? (typeof displayVal === 'object' ? JSON.stringify(displayVal) : String(displayVal ?? '-')) : '-'}</TableCell>
                        <TableCell className="px-2">
                          <div className="flex gap-1">
                            <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditIOMapping(mapping)} title="Edit"><Pencil className="w-3 h-3" /></Button>
                            <Button variant="outline" size="sm" className="h-6 w-6 p-0" onClick={() => handleRemoveIOMapping(mapping.id)} title="Delete"><Trash2 className="w-3 h-3 text-red-500" /></Button>
                          </div>
                        </TableCell>
                      </SortableTableRow>
                    );
                  })}
                </TableBody>
              </Table>
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      )}
      
      {/* Tab: Device Status (Edit only) */}
      {activeTab === 'status' && (
        <div className="min-w-0 space-y-4 overflow-hidden" data-testid="device-status-tab-content">
          {statusLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          ) : deviceStatus ? (
            <>
              {/* Connection & Device Info */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Card className="min-w-0 overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Server className="w-4 h-4" /> Device Info
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3"><span className="text-muted-foreground">{formData.device_type === 'beacon' ? 'MAC Address' : 'IMEI'}</span><span className="min-w-0 font-mono flex items-center justify-end gap-1 break-all text-right">{deviceStatus.device?.imei}<button type="button" onClick={() => { navigator.clipboard.writeText(deviceStatus.device?.imei || ''); toast.success('IMEI copied'); }} className="shrink-0 text-muted-foreground hover:text-foreground transition-colors" data-testid="status-imei-copy-btn"><Copy className="w-3 h-3" /></button></span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3"><span className="text-muted-foreground">Protocol</span><span className="min-w-0 break-words text-right">{deviceStatus.device?.protocol}</span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3"><span className="text-muted-foreground">Status</span>
                      <Badge className={deviceStatus.device?.status === 'online' || deviceStatus.device?.status === 'moving' ? 'bg-green-600' : deviceStatus.device?.status === 'idle' ? 'bg-amber-500' : 'bg-slate-500'}>{deviceStatus.device?.status}</Badge>
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3"><span className="text-muted-foreground">Last Seen</span><span className="min-w-0 break-words text-right">{deviceStatus.device?.last_seen ? formatTimestamp(deviceStatus.device.last_seen) : 'Never'}</span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3"><span className="text-muted-foreground">Total Records</span><span className="min-w-0 break-words text-right">{deviceStatus.stats?.total_records || 0}</span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-start gap-3"><span className="text-muted-foreground">Codec</span><span className="min-w-0 font-mono text-xs break-all text-right">{deviceStatus.stats?.codec || '-'}</span></div>
                  </CardContent>
                </Card>

                {formData.device_type !== 'beacon' && (
                <Card className="min-w-0 overflow-hidden">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Plug className="w-4 h-4" /> SIM Card
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] items-start gap-3"><span className="text-muted-foreground">ICCID</span><span className="min-w-0 font-mono text-xs break-all text-right">{deviceStatus.sim?.iccid || 'Not set'}</span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] items-start gap-3"><span className="text-muted-foreground">Mobile Number</span><span className="min-w-0 break-all text-right">{deviceStatus.sim?.mobile_number || 'Not set'}</span></div>
                    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] items-start gap-3"><span className="text-muted-foreground">Provider</span><span className="min-w-0 break-words text-right">{deviceStatus.sim?.provider || 'Not set'}</span></div>
                    {deviceStatus.sim?.iccid && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="min-w-[10rem] flex-1 text-xs"
                          onClick={handleOpenSimDetail}
                          data-testid="view-sim-detail-btn"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          View SIM Details
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            navigator.clipboard.writeText(deviceStatus.sim.iccid);
                            toast.success(`ICCID copied: ${deviceStatus.sim.iccid}`);
                          }}
                        >
                          Copy ICCID
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                )}
              </div>

              {/* GPS Position */}
              <Card className="min-w-0 overflow-hidden" data-testid="latest-io-raw-data-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Radio className="w-4 h-4" /> Last GPS Position
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {deviceStatus.gps?.last_gps_record ? (
                    <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                      <div className="min-w-0"><span className="text-muted-foreground block text-xs">Latitude</span><span className="font-mono break-all">{deviceStatus.gps.last_gps_record.latitude?.toFixed(7)}</span></div>
                      <div className="min-w-0"><span className="text-muted-foreground block text-xs">Longitude</span><span className="font-mono break-all">{deviceStatus.gps.last_gps_record.longitude?.toFixed(7)}</span></div>
                      <div><span className="text-muted-foreground block text-xs">Altitude</span><span>{deviceStatus.gps.last_gps_record.altitude}m</span></div>
                      <div><span className="text-muted-foreground block text-xs">Speed</span><span>{formatSpeed(deviceStatus.gps.last_gps_record.speed)}</span></div>
                      <div><span className="text-muted-foreground block text-xs">Heading</span><span>{deviceStatus.gps.last_gps_record.angle}°</span></div>
                      <div><span className="text-muted-foreground block text-xs">Satellites</span><span>{deviceStatus.gps.last_gps_record.satellites}</span></div>
                      <div className="min-w-0 sm:col-span-2 lg:col-span-3"><span className="text-muted-foreground block text-xs">GPS Time</span><span className="text-xs break-words">{deviceStatus.gps.last_gps_record.timestamp ? formatTimestamp(deviceStatus.gps.last_gps_record.timestamp) : '-'}</span></div>
                      <div className="sm:col-span-2 lg:col-span-3">
                        <a
                          href={`https://www.google.com/maps?q=${deviceStatus.gps.last_gps_record.latitude},${deviceStatus.gps.last_gps_record.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#2563eb] hover:underline"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View on Google Maps
                        </a>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No GPS data received yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Connection History — GPS devices only */}
              {formData.device_type !== 'beacon' && (
              <Card className="min-w-0 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Wifi className="w-4 h-4" /> Connection History
                    {deviceStatus.connection?.is_connected && <Badge className="bg-green-600 text-xs">Connected</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="w-48 px-3">Connected At</TableHead>
                        <TableHead className="w-64 px-3">IP Address</TableHead>
                        <TableHead className="w-24 px-3">Records</TableHead>
                        <TableHead className="w-32 px-3">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(deviceStatus.connection?.history || []).map((c, i) => (
                        <TableRow key={i} className="text-xs">
                          <TableCell className="px-3 break-words">{formatTimestamp(c.connected_at)}</TableCell>
                          <TableCell className="px-3 font-mono break-all">{c.remote_ip}:{c.remote_port}</TableCell>
                          <TableCell className="px-3">{c.total_records}</TableCell>
                          <TableCell className="px-3">
                            <Badge variant="outline" className={`text-[10px] ${c.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>{c.status}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              )}

              {/* Raw IO Elements — GPS devices only */}
              {formData.device_type !== 'beacon' && (
              <Card className="min-w-0 overflow-hidden" data-testid="identity-scan-diagnostics-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <KeyRound className="w-4 h-4" /> RFID / iButton Diagnostics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {deviceStatus.identity_scan?.configured ? (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          data-testid="identity-scan-status-badge"
                          className={deviceStatus.identity_scan.status === 'matched' ? 'bg-green-600' : deviceStatus.identity_scan.status === 'unmatched' ? 'bg-red-600' : 'bg-amber-500'}
                        >
                          {deviceStatus.identity_scan.status === 'matched' ? 'Matched' : deviceStatus.identity_scan.status === 'unmatched' ? 'Unmatched' : 'Waiting for scan'}
                        </Badge>
                        <span className="text-muted-foreground" data-testid="identity-scan-message">{deviceStatus.identity_scan.message}</span>
                      </div>
                      <div className="overflow-x-auto border rounded-md">
                        <Table className="table-fixed">
                          <TableHeader>
                            <TableRow className="text-xs">
                              <TableHead className="w-24 px-3">IO ID</TableHead>
                              <TableHead className="w-44 px-3">Name</TableHead>
                              <TableHead className="w-56 px-3">Parsed Credential</TableHead>
                              <TableHead className="w-40 px-3">Resolved As</TableHead>
                              <TableHead className="px-3">Resolved Name</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(deviceStatus.identity_scan.mappings || []).map((m, idx) => {
                              const ent = m.resolved_entity || {};
                              return (
                                <TableRow key={`${m.io_id}-${idx}`} className="text-xs" data-testid={`identity-scan-row-${idx}`}>
                                  <TableCell className="px-3 font-mono break-all">{m.io_id}</TableCell>
                                  <TableCell className="px-3 break-words">{m.display_name || '-'}</TableCell>
                                  <TableCell className="px-3 font-mono break-all" data-testid={`identity-scan-credential-${idx}`}>{m.credential_identifier || '-'}</TableCell>
                                  <TableCell className="px-3 capitalize" data-testid={`identity-scan-type-${idx}`}>{ent.person_type || '-'}</TableCell>
                                  <TableCell className="px-3 break-words" data-testid={`identity-scan-name-${idx}`}>{ent.person_name || '-'}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                      {deviceStatus.identity_scan.last_record_timestamp && (
                        <p className="text-xs text-muted-foreground" data-testid="identity-scan-last-record">
                          Last checked packet: {formatTimestamp(deviceStatus.identity_scan.last_record_timestamp)}
                        </p>
                      )}
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                            Recent RFID / iButton scans
                            <span className="ml-2 font-normal text-[11px] text-muted-foreground" data-testid="rfid-history-window-note">
                              New scans are saved automatically; "Rebuild history" imports up to the last 90 days.
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <div className="relative">
                              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                              <input
                                type="text"
                                value={rfidEventsSearch}
                                onChange={(e) => { setRfidEventsSearch(e.target.value); setRfidEventsPage(1); }}
                                placeholder="Search credential…"
                                className="h-7 w-44 rounded-md border border-input bg-background pl-6 pr-6 text-[11px]"
                                title="Search parsed credential"
                                data-testid="rfid-events-search"
                              />
                              {rfidEventsSearch && (
                                <button type="button" className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                  onClick={() => { setRfidEventsSearch(''); setRfidEventsPage(1); }} title="Clear search" data-testid="rfid-events-search-clear">
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            <select
                              value={rfidEventsMatch}
                              onChange={(e) => { setRfidEventsMatch(e.target.value); setRfidEventsPage(1); }}
                              className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                              title="Filter by match status"
                              data-testid="rfid-events-match-filter"
                            >
                              <option value="all">All scans</option>
                              <option value="matched">Matched</option>
                              <option value="unmatched">Unmatched</option>
                            </select>
                            <input
                              type="date"
                              value={rfidEventsStart}
                              onChange={(e) => { setRfidEventsStart(e.target.value); setRfidEventsPage(1); }}
                              className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                              title="From date"
                              data-testid="rfid-events-start-date"
                            />
                            <span className="text-[11px] text-muted-foreground">to</span>
                            <input
                              type="date"
                              value={rfidEventsEnd}
                              onChange={(e) => { setRfidEventsEnd(e.target.value); setRfidEventsPage(1); }}
                              className="h-7 rounded-md border border-input bg-background px-2 text-[11px]"
                              title="To date"
                              data-testid="rfid-events-end-date"
                            />
                            {(rfidEventsStart || rfidEventsEnd) && (
                              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setRfidEventsStart(''); setRfidEventsEnd(''); setRfidEventsPage(1); }} data-testid="rfid-events-clear-dates">
                                Clear
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => { fetchRfidScanEvents(selectedDevice?.imei); fetchDeviceStatus(selectedDevice?.imei); }}
                              disabled={rfidEventsLoading || statusLoading}
                              data-testid="identity-scan-refresh-btn"
                            >
                              <RefreshCw className={`w-3 h-3 mr-1 ${(rfidEventsLoading || statusLoading) ? 'animate-spin' : ''}`} />
                              Refresh
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="h-7 text-xs" data-testid="rfid-events-export-btn">
                                  <Upload className="w-3 h-3 mr-1" /> Export
                                  <ChevronDown className="w-3 h-3 ml-1 opacity-70" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs">Export Scan History</DropdownMenuLabel>
                                <DropdownMenuItem onSelect={handleExportRfidCsv} data-testid="rfid-events-export-csv">
                                  <FileText className="w-4 h-4 mr-2" />
                                  Scans (.csv)
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={handleExportRfidXlsx} data-testid="rfid-events-export-xlsx">
                                  <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                                  Scans Excel-Safe (.xlsx)
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setRfidBackfillConfirmOpen(true)}
                              disabled={rfidBackfillRunning}
                              data-testid="rfid-events-backfill-btn"
                            >
                              {rfidBackfillRunning ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <History className="w-3 h-3 mr-1" />}
                              {rfidBackfillRunning ? 'Rebuilding…' : 'Rebuild history'}
                            </Button>
                          </div>
                        </div>
                        {rfidBackfillInfo?.coverage_start && (
                          <p className="text-[11px] text-muted-foreground" data-testid="rfid-backfill-coverage-note">
                            Last rebuild ({formatUserDateTime(rfidBackfillInfo.finished_at, userDateFmt, userTimeFmt, userTimezone)}) covered{' '}
                            {formatUserDateTime(rfidBackfillInfo.coverage_start, userDateFmt, userTimeFmt, userTimezone)} →{' '}
                            {formatUserDateTime(rfidBackfillInfo.coverage_end, userDateFmt, userTimeFmt, userTimezone)}
                            {' '}({(rfidBackfillInfo.packets_scanned || 0).toLocaleString()} packets, {rfidBackfillInfo.events_built} scans).
                            {rfidBackfillInfo.capped && (
                              <span className="text-amber-600 dark:text-amber-400 font-medium" data-testid="rfid-backfill-capped-warning">
                                {' '}Packet cap reached — older scans before this range were not imported.
                              </span>
                            )}
                          </p>
                        )}
                        <div className="overflow-x-auto border rounded-md">
                          <Table className="table-fixed">
                            <TableHeader>
                              <TableRow className="text-xs">
                                <TableHead className="w-44 px-3">Time</TableHead>
                                <TableHead className="w-20 px-3">IO</TableHead>
                                <TableHead className="w-56 px-3">Parsed Credential</TableHead>
                                <TableHead className="w-36 px-3">Type</TableHead>
                                <TableHead className="px-3">Name</TableHead>
                                <TableHead className="w-24 px-3">Packets</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {rfidEvents.map((scan, idx) => {
                                const ent = scan.resolved_entity || {};
                                return (
                                  <TableRow key={scan.id || `${scan.io_id}-${scan.timestamp}-${idx}`} className="text-xs" data-testid={`identity-recent-scan-row-${idx}`}>
                                    <TableCell className="px-3">{formatUserDateTime(scan.timestamp, userDateFmt, userTimeFmt, userTimezone, { showSeconds: true })}</TableCell>
                                    <TableCell className="px-3 font-mono">{scan.io_id}</TableCell>
                                    <TableCell className="px-3 font-mono break-all" data-testid={`identity-recent-scan-credential-${idx}`}>{scan.credential_identifier || '-'}</TableCell>
                                    <TableCell className="px-3 capitalize" data-testid={`identity-recent-scan-type-${idx}`}>{ent.person_type || (scan.matched ? 'matched' : 'unmatched')}</TableCell>
                                    <TableCell className="px-3 break-words" data-testid={`identity-recent-scan-name-${idx}`}>{ent.person_name || '-'}</TableCell>
                                    <TableCell className="px-3" data-testid={`identity-recent-scan-repeat-count-${idx}`}>{scan.repeat_count > 1 ? `${scan.repeat_count} packets` : '1 packet'}</TableCell>
                                  </TableRow>
                                );
                              })}
                              {rfidEvents.length === 0 && (
                                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-4 text-xs">
                                  {rfidEventsLoading ? 'Loading scans…' : 'No saved RFID / iButton scans yet — use "Rebuild history" to import scans from stored packets.'}
                                </TableCell></TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </div>
                        <PaginationFooter
                          page={rfidEventsPage}
                          setPage={setRfidEventsPage}
                          totalItems={rfidEventsTotal}
                          itemsPerPage={rfidEventsPageSize}
                          setItemsPerPage={setRfidEventsPageSize}
                          testIdPrefix="rfid-scan-events"
                          scrollOnChange={false}
                          disabled={rfidEventsLoading}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground" data-testid="identity-scan-not-configured">
                      Map AVL 78 or AVL 207 as <span className="font-medium text-foreground">RFID / iButton</span> to see scan resolution here.
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              <AlertDialog open={rfidBackfillConfirmOpen} onOpenChange={setRfidBackfillConfirmOpen}>
                <AlertDialogContent data-testid="rfid-backfill-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Rebuild scan history?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This scans the last 90 days of stored raw packets for this device and rebuilds the
                      RFID / iButton scan history from scratch (existing saved scans for this device are
                      replaced). It runs in the background and may take a minute for busy devices.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="rfid-backfill-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => startRfidBackfill(selectedDevice?.imei)} data-testid="rfid-backfill-confirm">
                      Rebuild history
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Raw IO Elements — GPS devices only */}
              {formData.device_type !== 'beacon' && (
              <Card className="min-w-0 overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Activity className="w-4 h-4" /> Latest IO Elements ({ioViewMode === 'raw' ? 'Raw Data' : 'Merged State'})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-md border overflow-hidden">
                        <button
                          type="button"
                          className={`px-2 py-1 text-xs ${ioViewMode === 'raw' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                          onClick={() => setIoViewMode('raw')}
                          data-testid="io-view-raw-btn"
                        >
                          Raw packet
                        </button>
                        <button
                          type="button"
                          className={`px-2 py-1 text-xs ${ioViewMode === 'merged' ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                          onClick={() => setIoViewMode('merged')}
                          data-testid="io-view-merged-btn"
                        >
                          Merged state
                        </button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => fetchLatestIoElements(selectedDevice?.imei)}
                        disabled={latestIoLoading}
                        data-testid="latest-io-refresh-btn"
                      >
                        <RefreshCw className={`w-3 h-3 mr-1 ${latestIoLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="io-view-mode-hint">
                    {ioViewMode === 'raw'
                      ? 'Raw packet: only the IO elements contained in the most recent packet (a beacon-only packet shows just the beacon IO).'
                      : 'Merged state: the running IO state built from all packets — this is what ignition, events and telemetry use.'}
                  </p>
                </CardHeader>
                <CardContent className="overflow-x-auto p-0">
                  <Table className="table-fixed">
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="w-40 px-3">IO ID</TableHead>
                        <TableHead className="w-64 px-3">Mapped Name</TableHead>
                        <TableHead className="px-3">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Flatten nested objects (DM devices have {analog: {key:val}, digital: {key:val}})
                        const rawData = (ioViewMode === 'merged'
                          ? deviceStatus.io_elements?.last_cached
                          : deviceStatus.io_elements?.latest_raw) || {};
                        const ioMap = deviceStatus.io_elements?.io_map || {};
                        const rows = [];
                        for (const [key, value] of Object.entries(rawData)) {
                          if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                            // Expand nested object into individual rows with parent prefix
                            for (const [childKey, childVal] of Object.entries(value)) {
                              const mn = ioMap[childKey];
                              rows.push({ key: `${key}.${childKey}`, mappedName: (mn && typeof mn !== 'object') ? mn : '', value: childVal });
                            }
                          } else {
                            const mn = ioMap[key];
                            rows.push({ key, mappedName: (mn && typeof mn !== 'object') ? mn : '', value });
                          }
                        }
                        if (rows.length === 0) {
                          return (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center py-4 text-sm text-muted-foreground">No IO data received yet</TableCell>
                            </TableRow>
                          );
                        }
                        return rows.map(({ key, mappedName, value }) => {
                          const displayValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-');
                          return (
                            <TableRow key={key} className="text-xs">
                              <TableCell className="px-3 font-mono break-all">{key}</TableCell>
                              <TableCell className="px-3 text-muted-foreground break-words">{typeof mappedName === 'string' ? mappedName : '-'}</TableCell>
                              <TableCell className="px-3 font-mono break-all">{displayValue}</TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              )}

              {/* Raw Packet Viewer */}
              <RawPacketViewer imei={selectedDevice?.imei} api={api} />

              {/* Command History */}
              <Card className="min-w-0 overflow-hidden">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <CommandIcon className="w-4 h-4" /> Command History
                    </CardTitle>
                    <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setCmdLogPage(1); setCmdLogOpen(true); }} data-testid="command-history-view-all-btn">
                      <History className="w-3 h-3 mr-1" />
                      View all
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => fetchCommandHistory(selectedDevice?.imei)} disabled={commandHistoryLoading} data-testid="command-history-refresh-btn">
                      <RefreshCw className={`w-3 h-3 mr-1 ${commandHistoryLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {commandHistoryLoading ? (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>
                  ) : commandHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No commands sent to this device yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto overflow-x-hidden pr-1">
                      {commandHistory.map((cmd, i) => (
                        <div key={cmd.id || i} className="min-w-0 overflow-hidden p-2.5 border rounded-lg text-sm space-y-1.5">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex flex-1 flex-wrap items-center gap-2">
                              <Badge className={`text-[10px] ${
                                cmd.status === 'acknowledged' ? 'bg-green-600 text-white' :
                                cmd.status === 'sent' ? 'bg-blue-600 text-white' :
                                cmd.status === 'queued' ? 'bg-yellow-500 text-white' :
                                cmd.status === 'timeout' ? 'bg-orange-500 text-white' :
                                cmd.status === 'failed' ? 'bg-red-600 text-white' :
                                'bg-gray-500 text-white'
                              }`}>{cmd.status}</Badge>
                              <Badge variant="outline" className="text-[10px]" data-testid={`cmd-method-badge-${cmd.id || i}`}>{cmdMethodLabel(cmd.delivery_method)}</Badge>
                              <span className="min-w-0 font-mono text-xs break-all">{cmd.command}</span>
                              {cmd.status === 'queued' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                                  onClick={() => handleDeleteQueuedCommand(cmd.id)}
                                  data-testid={`delete-queued-cmd-${cmd.id}`}
                                >
                                  <Trash2 className="w-3 h-3 mr-0.5" />
                                  Delete
                                </Button>
                              )}
                            </div>
                            <span className="shrink-0 text-[10px] text-muted-foreground">
                              {cmd.created_at ? formatDT(cmd.created_at, { seconds: true }) : '-'}
                            </span>
                          </div>
                          {cmd.response && (
                            <div className="min-w-0 overflow-hidden p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded text-xs">
                              <span className="text-[10px] text-green-700 dark:text-green-400 font-semibold">Response:</span>
                              <pre className="max-w-full overflow-x-auto font-mono text-xs mt-0.5 whitespace-pre-wrap break-all" dangerouslySetInnerHTML={{ __html: (cmd.response || '').replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>') }} />
                            </div>
                          )}
                          {cmd.error && (
                            <div className="p-2 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded text-xs">
                              <span className="text-[10px] text-red-700 dark:text-red-400 font-semibold">Error:</span>
                              <span className="ml-1 break-all">{cmd.error}</span>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                            {cmd.status === 'queued' && <span className="text-amber-600 dark:text-amber-400 font-medium" data-testid={`cmd-expiry-${cmd.id || i}`}>{queuedExpiryText(cmd)}</span>}
                            {cmd.sent_at && <span>Sent: {formatTimeOnly(cmd.sent_at, { seconds: true })}</span>}
                            {cmd.responded_at && <span>Reply: {formatTimeOnly(cmd.responded_at, { seconds: true })}</span>}
                            {cmd.created_by && cmdSentBy(cmd) && <span data-testid={`cmd-sent-by-${cmd.id || i}`}>By: {cmdSentBy(cmd)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => { fetchDeviceStatus(selectedDevice?.imei); fetchCommandHistory(selectedDevice?.imei); }} disabled={statusLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${statusLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Signal className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No status data available. The device may not have connected yet.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab: Beacon RSSI Status (beacon devices only) */}
      {activeTab === 'beacon_status' && formData.device_type === 'beacon' && (
        <div className="space-y-4">
          {beaconStatusLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          ) : beaconStatus ? (
            <>
              <Card data-testid="beacon-telemetry-panel">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Signal className="w-4 h-4" /> Beacon Telemetry
                    </CardTitle>
                    {beaconTelemetryLoading && <Loader2 className="w-4 h-4 animate-spin text-[#2563eb]" data-testid="beacon-telemetry-loading" />}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {beaconTelemetry ? (
                    <>
                      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-purpose">
                          <span className="block text-xs text-muted-foreground">Purpose</span>
                          <span className="mt-1 block font-medium">{beaconTelemetry.beacon_device?.beacon_purpose === 'personal' ? 'Personal ID Beacon' : 'Asset Beacon'}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-last-seen">
                          <span className="block text-xs text-muted-foreground">Last Seen</span>
                          <span className="mt-1 block font-medium">{beaconTelemetry.telemetry?.last_seen_at ? formatTimestamp(beaconTelemetry.telemetry.last_seen_at) : 'Not detected'}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-detected-by">
                          <span className="block text-xs text-muted-foreground">Last Seen By GPS Device</span>
                          <span className="mt-1 block font-medium break-words">{beaconTelemetry.telemetry?.last_seen_by?.device_name || '-'}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-confidence">
                          <span className="block text-xs text-muted-foreground">Confidence</span>
                          <Badge variant="outline" className="mt-1 capitalize">{beaconTelemetry.telemetry?.confidence || 'low'}</Badge>
                        </div>
                      </div>
                      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-rssi">
                          <span className="block text-xs text-muted-foreground">RSSI</span>
                          <span className="mt-1 block font-mono">{beaconTelemetry.telemetry?.last_rssi_dbm != null ? `${beaconTelemetry.telemetry.last_rssi_dbm} dBm` : '-'}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-battery">
                          <span className="block text-xs text-muted-foreground">Battery</span>
                          <span className="mt-1 block">{renderBeaconBatteryStatus(beaconTelemetry.telemetry || {})}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-protocol">
                          <span className="block text-xs text-muted-foreground">Protocol</span>
                          <span className="mt-1 block capitalize">{beaconTelemetry.telemetry?.protocol || beaconTelemetry.beacon_device?.beacon_type || '-'}</span>
                        </div>
                        <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-total-scans">
                          <span className="block text-xs text-muted-foreground">Total Scans</span>
                          <span className="mt-1 block font-medium">{beaconTelemetry.telemetry?.total_scans || 0}</span>
                        </div>
                      </div>
                      <div className="rounded-md border border-border p-3" data-testid="beacon-telemetry-location">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <span className="block text-xs text-muted-foreground">Last Seen Location</span>
                            <span className="mt-1 block font-mono text-xs">
                              {beaconTelemetry.telemetry?.last_location
                                ? `${Number(beaconTelemetry.telemetry.last_location.latitude).toFixed(5)}, ${Number(beaconTelemetry.telemetry.last_location.longitude).toFixed(5)}`
                                : 'No receiver GPS location available'}
                            </span>
                            {beaconTelemetry.telemetry?.last_location?.source_label && (
                              <span className="mt-1 block text-xs text-muted-foreground">{beaconTelemetry.telemetry.last_location.source_label}</span>
                            )}
                          </div>
                          {beaconTelemetry.telemetry?.last_location && (
                            <a
                              href={`https://www.google.com/maps?q=${beaconTelemetry.telemetry.last_location.latitude},${beaconTelemetry.telemetry.last_location.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#2563eb] hover:underline"
                              data-testid="beacon-telemetry-map-link"
                            >
                              <ExternalLink className="w-3 h-3" /> Map
                            </a>
                          )}
                        </div>
                      </div>
                      {(beaconTelemetry.hints || []).length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs dark:border-amber-900 dark:bg-amber-950/30" data-testid="beacon-telemetry-hints">
                          {(beaconTelemetry.hints || []).map((hint, i) => <p key={i}>{hint}</p>)}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="py-4 text-center text-muted-foreground" data-testid="beacon-telemetry-empty">
                      {beaconTelemetryLoading ? 'Loading telemetry…' : 'Telemetry unavailable for this beacon'}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Beacon Info */}
              <Card data-testid="beacon-rssi-summary-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Bluetooth className="w-4 h-4" /> Beacon Detection Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Beacon ID</span><span className="font-mono" data-testid="beacon-summary-id">{beaconStatus.beacon_id}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Total Scans</span><span data-testid="beacon-summary-total-scans">{beaconStatus.total_scans}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Detected By</span><span data-testid="beacon-summary-detected-by-count">{beaconStatus.detected_by?.length || 0} device(s)</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Battery Alerts</span><span data-testid="beacon-summary-low-battery-count">{(beaconStatus.recent_scans || []).filter(scan => scan.low_battery === true).length}</span></div>
                </CardContent>
              </Card>

              {/* RSSI Trend Sparkline */}
              <Card data-testid="beacon-rssi-trend-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" /> RSSI Trend
                    <span className="ml-auto text-[11px] font-normal text-muted-foreground">
                      Last {beaconStatus.rssi_trend?.length || 0} reading(s){(() => { const n = new Set((beaconStatus.rssi_trend || []).map((r) => r.device_imei)).size; return n > 1 ? ` across ${Math.min(n, 3)} device(s)` : ''; })()} · stronger signal is closer to 0&nbsp;dBm
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-1">
                  <RssiSparkline data={beaconStatus.rssi_trend} formatTime={formatTimestamp} height={110} />
                </CardContent>
              </Card>

              {/* Detecting Devices */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Signal className="w-4 h-4" /> Detected By GPS Devices
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(beaconStatus.detected_by || []).length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="px-3">GPS Device</TableHead>
                          <TableHead className="px-3">IMEI</TableHead>
                          <TableHead className="px-3">Last RSSI</TableHead>
                          <TableHead className="px-3">Last Seen</TableHead>
                          <TableHead className="px-3">Scans</TableHead>
                          <TableHead className="px-3">Last Detected Location</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {beaconStatus.detected_by.map((det, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className="px-3 font-medium">{det.device_name || det.device_imei}</TableCell>
                            <TableCell className="px-3 font-mono">{det.device_imei}</TableCell>
                            <TableCell className="px-3">
                              <Badge variant="outline" className={`text-[10px] ${
                                det.last_rssi > -50 ? 'bg-green-100 text-green-700' :
                                det.last_rssi > -70 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {det.last_rssi} dBm
                              </Badge>
                            </TableCell>
                            <TableCell className="px-3">{det.last_seen ? formatTimestamp(det.last_seen) : '-'}</TableCell>
                            <TableCell className="px-3">{det.scan_count}</TableCell>
                            <TableCell className="px-3">
                              {det.latitude != null && det.longitude != null ? (
                                <div className="flex flex-col gap-0.5" data-testid={`beacon-detected-location-${i}`}>
                                  <span className="font-mono text-[11px]">{Number(det.latitude).toFixed(5)}, {Number(det.longitude).toFixed(5)}</span>
                                  <a href={`https://www.google.com/maps?q=${det.latitude},${det.longitude}`} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" />
                                    Map
                                  </a>
                                  {det.location_source === 'device_current' && (
                                    <span className="text-[10px] text-muted-foreground">approx — device's current position</span>
                                  )}
                                </div>
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Bluetooth className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No GPS devices have detected this beacon yet</p>
                      <p className="text-xs mt-1">Beacons are detected by nearby GPS trackers via BLE scanning</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent Scans */}
              {(beaconStatus.recent_scans || []).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <History className="w-4 h-4" /> Recent Scan History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="text-xs">
                          <TableHead className="px-3">Time</TableHead>
                          <TableHead className="px-3">Device Name</TableHead>
                          <TableHead className="px-3">IMEI</TableHead>
                          <TableHead className="px-3">RSSI</TableHead>
                          <TableHead className="px-3">Battery</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {beaconStatus.recent_scans.map((scan, i) => (
                          <TableRow key={i} className="text-xs">
                            <TableCell className="px-3">{formatTimestamp(scan.timestamp)}</TableCell>
                            <TableCell className="px-3 font-medium" data-testid={`beacon-recent-scan-device-name-${i}`}>{scan.device_name || scan.device_imei}</TableCell>
                            <TableCell className="px-3 font-mono">{scan.device_imei}</TableCell>
                            <TableCell className="px-3">
                              <Badge variant="outline" className={`text-[10px] ${
                                scan.rssi > -50 ? 'bg-green-100 text-green-700' :
                                scan.rssi > -70 ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {scan.rssi} dBm
                              </Badge>
                            </TableCell>
                            <TableCell className="px-3">{renderBeaconBatteryStatus(scan)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Refresh */}
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => refreshSelectedBeaconStatus()}
                disabled={beaconStatusLoading || beaconTelemetryLoading}
                  data-testid="beacon-rssi-refresh-btn"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${beaconStatusLoading || beaconTelemetryLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Bluetooth className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No beacon detection data available yet.</p>
              <p className="text-xs mt-1">BLE beacons are detected by nearby GPS devices via Bluetooth Low Energy scanning.</p>
            </div>
          )}
        </div>
      )}

      {/* Tab 6: Device Icon */}
      {activeTab === 'icon' && (
        <div className="space-y-3">
          {/* Sticky: Current Icon + Color + Allow Editing + Search */}
          <div className="sticky -top-4 z-10 bg-background pt-4 pb-3 space-y-3 border-b">
            {/* Map Marker Mode Toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">Use Icon on Map</p>
                <p className="text-xs text-muted-foreground">
                  {formData.map_marker_mode === 'icon'
                    ? 'This device shows its icon on the map'
                    : 'This device shows a directional arrow on the map'}
                </p>
              </div>
              <Switch
                data-testid="map-marker-mode-toggle"
                checked={formData.map_marker_mode === 'icon'}
                onCheckedChange={(checked) => handleInputChange('map_marker_mode', checked ? 'icon' : 'arrow')}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4 p-3 border rounded-lg">
              <DeviceIconDisplay iconName={formData.device_icon} className="w-12 h-12" customIcons={customIcons} iconColor={formData.device_icon_color} />
              <div className="flex-1 min-w-0">
                <p className="font-medium capitalize text-sm">{formData.device_icon?.startsWith('custom_') ? (customIcons.find(c => c.id === formData.device_icon)?.name || 'Custom Icon') : formatIconLabel(formData.device_icon)}</p>
                <p className="text-xs text-muted-foreground">Selected icon{formData.device_icon_color ? ` \u00b7 ${formData.device_icon_color}` : ''}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">User Editing</span>
                  <Switch
                    checked={formData.icon_editing_enabled}
                    onCheckedChange={(v) => handleInputChange('icon_editing_enabled', v)}
                  />
                </div>
                <Button onClick={() => guardedSave('update')} disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>
            </div>
            <IconColorPicker
              selectedColor={formData.device_icon_color}
              onChange={(color) => handleInputChange('device_icon_color', color)}
              iconName={formData.device_icon}
            />
            <IconSearchInput value={iconPickerSearch} onChange={setIconPickerSearch} />
          </div>

          <DeviceIconPicker
            selectedIcon={formData.device_icon}
            onSelect={(icon) => {
              handleInputChange('device_icon', icon);
              if (isCustomIcon(icon)) handleInputChange('device_icon_color', null);
            }}
            customIcons={customIcons}
            externalSearch={iconPickerSearch}
            onExternalSearchChange={setIconPickerSearch}
            middleSlot={
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-sm">Custom Icon Library</p>
                    <p className="text-xs text-muted-foreground">Upload custom icons (SVG or PNG, max 256x256, max 200KB)</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {customIcons.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => { setIconLibrarySearch(''); setShowIconLibrary(true); }} data-testid="manage-icon-library-btn">
                        <ImageIcon className="w-4 h-4 mr-1" /> Library ({customIcons.length})
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setShowIconUpload(true)} data-testid="upload-custom-icon-btn">
                      <Upload className="w-4 h-4 mr-1" /> Upload Icon
                    </Button>
                    <label className="inline-flex">
                      <input type="file" multiple accept=".svg,.png,image/svg+xml,image/png" onChange={handleBulkFilesSelected} className="hidden" data-testid="bulk-upload-input" />
                      <Button variant="outline" size="sm" asChild>
                        <span><Upload className="w-4 h-4 mr-1" /> Bulk Upload</span>
                      </Button>
                    </label>
                  </div>
                </div>
              </div>
            }
          />
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6" data-testid="device-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="hidden lg:flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#2563eb]/10">
            <HardDrive className="w-6 h-6 text-[#2563eb]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Device Management</h1>
            <p className="text-sm text-muted-foreground">Manage GPS devices, beacons, and hardware configuration</p>
          </div>
        </div>
        {/* Actions — 2×2 full-width grid on phones, inline row on ≥sm. */}
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <input type="file" accept=".csv,.xlsx" onChange={handleImportDevices} className="hidden" id="device-csv-import" />
          {canManageDeviceList && (
          <Button variant="outline" size="sm" onClick={openSimReport} data-testid="sim-health-report-button">
            <Signal className="w-4 h-4 mr-2" />
            SIM Health Report
          </Button>
          )}
          {canManageDeviceList && (
          <Button variant="outline" size="sm" onClick={openIoLog} data-testid="device-io-log-button">
            <History className="w-4 h-4 mr-2" />
            Import / Export Log
          </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="import-devices-dropdown-trigger">
                <Download className="w-4 h-4 mr-2" />
                Import
                <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs">Import options</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => setIsDeviceImportWizardOpen(true)}
                data-testid="import-devices-open-wizard"
              >
                <Sparkles className="w-4 h-4 mr-2 text-[#2563eb]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">Import Wizard</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">Preview, edit &amp; validate before import</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => document.getElementById('device-csv-import').click()}
                data-testid="import-devices-quick-csv"
              >
                <Download className="w-4 h-4 mr-2" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-tight">Quick CSV Import</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">Legacy one-shot loader</p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">GPS Devices</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadDeviceImportTemplate('device', true); }}
                data-testid="import-devices-template-device-example"
              >
                <FileText className="w-4 h-4 mr-2" />
                Device Example Row (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadDeviceImportTemplateXlsx('device', true); }}
                data-testid="import-devices-template-device-xlsx-example"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                Device Excel-Safe Example (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Beacons</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadDeviceImportTemplate('beacon', true); }}
                data-testid="import-devices-template-beacon-example"
              >
                <FileText className="w-4 h-4 mr-2" />
                Beacon Example Row (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => { e.preventDefault(); downloadDeviceImportTemplateXlsx('beacon', true); }}
                data-testid="import-devices-template-beacon-xlsx-example"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                Beacon Excel-Safe Example (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-devices-dropdown-trigger" disabled={selectedDeviceIds.length === 0}>
                <Upload className="w-4 h-4 mr-2" />
                {selectedDeviceIds.length > 0 ? `Export (${selectedDeviceIds.length})` : 'Export'}
                <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-xs">Export for Re-import</DropdownMenuLabel>
              <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-0.5 pb-0.5">GPS Devices</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => exportGpsDevices('csv')}
                data-testid="export-gps-csv"
              >
                <FileText className="w-4 h-4 mr-2" />
                GPS Devices (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => exportGpsDevices('xlsx')}
                data-testid="export-gps-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                GPS Devices Excel-Safe (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Beacons</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => exportBeacons('csv')}
                data-testid="export-beacons-csv"
              >
                <FileText className="w-4 h-4 mr-2" />
                BLE Beacons (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => exportBeacons('xlsx')}
                data-testid="export-beacons-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                BLE Beacons Excel-Safe (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs pt-1">Export Full Details (Audit)</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => exportAuditAll('csv')}
                data-testid="export-audit-csv"
              >
                <FileText className="w-4 h-4 mr-2" />
                All Devices — Audit (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => exportAuditAll('xlsx')}
                data-testid="export-audit-xlsx"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                All Devices — Audit Excel-Safe (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {currentUser?.role === 'super_admin' && !isMobileOrWebview && (
          <Button
            variant="destructive"
            size="sm"
            disabled={selectedDeviceIds.length === 0}
            onClick={() => { setBulkDeleteText(''); setBulkImpact(null); setBulkDeleteOpen(true); api.post('/service/device-impact', { device_ids: selectedDeviceIds }).then(r => setBulkImpact(r.data)).catch(() => {}); }}
            data-testid="bulk-delete-devices-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {selectedDeviceIds.length > 0 ? `Delete (${selectedDeviceIds.length})` : 'Delete'}
          </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchDevices}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          {canAddDevices && (
          <Button 
            size="sm"
            className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[160px] justify-center" 
            onClick={() => { resetForm(); setAllocatedUserIds([]); fetchAllocatableUsers(); fetchManageableAdmins(); setFormDirty(false); setIsAddDialogOpen(true); }}
            data-testid="add-device-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Device
          </Button>
          )}
        </div>
      </div>

      {/* Import Undo banner — shown after a successful device import run (30-minute window) */}
      {lastImportRun && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 px-4 py-3"
          data-testid="device-import-undo-banner"
        >
          <Undo2 className="w-5 h-5 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-[220px]">
            <p className="text-sm font-medium text-foreground" data-testid="device-import-undo-banner-summary">
              Import completed — {lastImportRun.summary?.created ?? lastImportRun.created_count ?? 0} device(s) created
            </p>
            <p className="text-xs text-muted-foreground" data-testid="device-import-undo-banner-expiry">
              Undo available until {lastImportRun.expires_at ? new Date(lastImportRun.expires_at).toLocaleTimeString() : '—'} — removes the devices created by this import and frees any user allocations
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleUndoDeviceImport}
            disabled={undoingImport}
            className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-950"
            data-testid="device-import-undo-button"
          >
            {undoingImport ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
            {undoingImport ? 'Undoing…' : 'Undo import'}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setLastImportRun(null)}
            disabled={undoingImport}
            data-testid="device-import-undo-dismiss"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Name, IMEI, Protocol, Model, Serial, MAC, ICCID, Registration..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-8 pl-10 pr-8 dark:bg-[#0D1425]"
            data-testid="device-management-search-input"
          />
          {searchTerm && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchTerm('')}
              data-testid="device-management-search-clear-button"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        <Select value={protocolFilter} onValueChange={(v) => setProtocolFilter(v === '__all__' ? '__all__' : v)}>
          <SelectTrigger className="h-8 w-[160px] dark:bg-[#0D1425]">
            <SelectValue>
              {protocolFilter === '__all__' ? <span className="text-muted-foreground">All Protocols</span> : protocolFilter}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Protocols</SelectItem>
                  {protocols.map(protocol => (
                    <SelectItem key={protocol} value={protocol}>{protocol}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

        <Select value={modelFilter} onValueChange={(v) => setModelFilter(v === '__all__' ? '__all__' : v)}>
          <SelectTrigger className="h-8 w-[150px] dark:bg-[#0D1425]" data-testid="model-filter">
            <SelectValue>
              {modelFilter === '__all__' ? <span className="text-muted-foreground">All Models</span> : modelFilter}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Models</SelectItem>
            {deviceModels.map(m => (
              <SelectItem key={m} value={m}>{m}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v === '__all__' ? '__all__' : v)}>
          <SelectTrigger className="h-8 w-[140px] dark:bg-[#0D1425]">
            <SelectValue>
              {statusFilter === '__all__' ? <span className="text-muted-foreground">All Status</span> : statusFilter?.charAt(0).toUpperCase() + statusFilter?.slice(1)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Status</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="moving">Moving</SelectItem>
                  <SelectItem value="stopped">Stopped</SelectItem>
                  <SelectItem value="idle">Idle</SelectItem>
                </SelectContent>
              </Select>
        
        <Select value={isActiveFilter} onValueChange={(v) => setIsActiveFilter(v === '__all__' ? 'all' : v)}>
          <SelectTrigger className="h-8 w-[180px] dark:bg-[#0D1425]">
            <SelectValue>
              {isActiveFilter === 'all' ? <span className="text-muted-foreground">All (Active/Inactive)</span> : isActiveFilter === 'active' ? 'Active Only' : 'Inactive Only'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All (Active/Inactive)</SelectItem>
            <SelectItem value="active">Active Only</SelectItem>
            <SelectItem value="inactive">Inactive Only</SelectItem>
          </SelectContent>
        </Select>

        <Select value={simHealthFilter} onValueChange={setSimHealthFilter}>
          <SelectTrigger className="h-8 w-[150px] dark:bg-[#0D1425]" data-testid="sim-health-filter">
            <SelectValue>
              {simHealthFilter === 'all'
                ? <span className="text-muted-foreground">All SIMs</span>
                : ({ dead_sim: 'Dead SIM', healthy_sim: 'Healthy SIM', no_sim: 'No SIM linked', duplicate_sim: 'Duplicate SIMs' }[simHealthFilter])}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All SIMs</SelectItem>
            <SelectItem value="dead_sim" data-testid="sim-health-filter-dead">Dead SIM</SelectItem>
            <SelectItem value="healthy_sim" data-testid="sim-health-filter-healthy">Healthy SIM</SelectItem>
            <SelectItem value="no_sim" data-testid="sim-health-filter-nosim">No SIM linked</SelectItem>
            <SelectItem value="duplicate_sim" data-testid="sim-health-filter-duplicate">Duplicate SIMs</SelectItem>
          </SelectContent>
        </Select>

        {canManageDeviceList && (
        <Select value={managementFilter} onValueChange={setManagementFilter}>
          <SelectTrigger className="h-8 w-[180px] dark:bg-[#0D1425]" data-testid="management-filter">
            <SelectValue>
              {managementFilter === 'all' ? <span className="text-muted-foreground">All (Management List)</span> : managementFilter === 'unassigned' ? 'Unassigned only' : 'Assigned only'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All (Management List)</SelectItem>
            <SelectItem value="unassigned" data-testid="management-filter-unassigned">Unassigned only</SelectItem>
            <SelectItem value="assigned">Assigned only</SelectItem>
          </SelectContent>
        </Select>
        )}

        {/* Clear Filters */}
        {ioDupAudit.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 hover:text-amber-700"
            onClick={openDupAudit}
            data-testid="dup-mapping-audit-btn"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            {ioDupAudit.length} device{ioDupAudit.length > 1 ? 's' : ''} with duplicate IO mappings
          </Button>
        )}

        {simDupAudit.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 border-orange-500/50 text-orange-600 dark:text-orange-400 hover:bg-orange-500/10 hover:text-orange-700"
            onClick={() => setSimHealthFilter('duplicate_sim')}
            title="Show only devices sharing a SIM ICCID"
            data-testid="dup-sim-audit-btn"
          >
            <Copy className="w-3.5 h-3.5" />
            {simDupAudit.reduce((a, g) => a + (g.devices?.length || 0), 0)} devices with duplicate SIM ICCID
          </Button>
        )}

        {(searchTerm || protocolFilter !== '__all__' || modelFilter !== '__all__' || statusFilter !== '__all__' || isActiveFilter !== 'all' || managementFilter !== 'all' || simHealthFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchTerm('');
              setProtocolFilter('__all__');
              setModelFilter('__all__');
              setStatusFilter('__all__');
              setIsActiveFilter('all');
              setManagementFilter('all');
              setSimHealthFilter('all');
            }}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
          
      {/* Bulk Actions — always-visible selection bar (app-wide standard).
          Bulk action buttons live inline with the selection bar so they never
          push the table down when they appear. */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground px-1 min-h-6 -my-1.5" data-testid="device-mgmt-selection-bar">
        <span data-testid="device-selection-count">{selectedDeviceIds.length} selected</span>
        {selectedDeviceIds.length > 0 && (<>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs"
          disabled={selectAllAcrossLoading}
          onClick={async () => {
            if (selectedDeviceIds.length >= totalCount) { setSelectedDeviceIds([]); return; }
            try {
              setSelectAllAcrossLoading(true);
              let url = `/admin/devices?skip=0&limit=${Math.max(totalCount, 1)}`;
              if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
              if (protocolFilter && protocolFilter !== '__all__') url += `&protocol=${protocolFilter}`;
              if (statusFilter && statusFilter !== '__all__') url += `&status_filter=${statusFilter}`;
              if (isActiveFilter !== 'all') url += `&is_active=${isActiveFilter === 'active'}`;
              const res = await api.get(url);
              const allIds = (Array.isArray(res.data) ? res.data : []).map(d => d.id).filter(Boolean);
              setSelectedDeviceIds(allIds);
            } catch (err) {
              toast.error(err?.response?.data?.detail || 'Failed to select all devices');
            } finally {
              setSelectAllAcrossLoading(false);
            }
          }}
          data-testid="device-select-all-toggle"
        >
          {selectAllAcrossLoading ? 'Selecting…' : (selectedDeviceIds.length >= totalCount ? 'Deselect All' : 'Select All')}
        </Button>
        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedDeviceIds([])} data-testid="device-selection-clear">Clear</Button>
        {/* Bulk action buttons — inline (no separate blue card) */}
        <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
        {canManageDeviceList && (
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid="bulk-assign-admins-btn" onClick={openBulkAssign}>
          <ListChecks className="w-3.5 h-3.5 mr-1" />
          Assign to Admin(s)
        </Button>
        )}
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid="bulk-allocate-users-btn" onClick={openBulkAlloc}>
          <Users className="w-3.5 h-3.5 mr-1" />
          Allocate to User(s)
        </Button>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid="bulk-import-io-btn" onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              try {
                const parsed = JSON.parse(ev.target.result);
                const mappings = Array.isArray(parsed) ? parsed : parsed.mappings;
                if (!Array.isArray(mappings)) { toast.error('Invalid format — expected mappings array'); return; }
                setBulkImportMappings(mappings);
                const fileProtocol = parsed.protocol || '';
                setBulkImportProtocol(fileProtocol);
                setBulkImportTargetIds(fileProtocol
                  ? selectedDeviceIds.filter(id => { const d = devices.find(dev => dev.id === id); return d && d.protocol === fileProtocol; })
                  : [...selectedDeviceIds]
                );
                setBulkImportSearch('');
                setBulkImportMode('merge');
                setIsBulkImportOpen(true);
              } catch { toast.error('Failed to parse JSON file'); }
            };
            reader.readAsText(file);
          };
          input.click();
        }}>
          <Download className="w-3.5 h-3.5 mr-1" />
          Import IO Mappings
        </Button>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" data-testid="bulk-import-cmd-btn" onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.json';
          input.onchange = (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              try {
                const parsed = JSON.parse(ev.target.result);
                const cmds = Array.isArray(parsed) ? parsed : parsed.commands;
                if (!Array.isArray(cmds)) { toast.error('Invalid format — expected commands array'); return; }
                setBulkCmdImportData(cmds);
                const fileProtocol = parsed.protocol || '';
                setBulkCmdImportProtocol(fileProtocol);
                setBulkCmdImportTargetIds(fileProtocol
                  ? selectedDeviceIds.filter(id => { const d = devices.find(dev => dev.id === id); return d && d.protocol === fileProtocol; })
                  : [...selectedDeviceIds]
                );
                setBulkCmdImportSearch('');
                setBulkCmdImportMode('merge');
                setIsBulkCmdImportOpen(true);
              } catch { toast.error('Failed to parse JSON file'); }
            };
            reader.readAsText(file);
          };
          input.click();
        }}>
          <Download className="w-3.5 h-3.5 mr-1" />
          Import Commands
        </Button>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={handleBulkActivate}>
          <Check className="w-3.5 h-3.5 mr-1" />
          Activate
        </Button>
        <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={handleBulkDeactivate}>
          <X className="w-3.5 h-3.5 mr-1" />
          Deactivate
        </Button>
        </>)}
      </div>

      <Dialog open={isBulkAssignOpen} onOpenChange={setIsBulkAssignOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-assign-dialog">
          <DialogHeader>
            <DialogTitle>Assign {selectedDeviceIds.length} device(s) to Admin(s)</DialogTitle>
            <DialogDescription>
              Choose which Admins can view &amp; manage the selected devices in their Device Management list. This does not affect allocation or operational pages.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Button size="sm" variant={bulkAssignMode === 'add' ? 'default' : 'outline'} onClick={() => setBulkAssignMode('add')} data-testid="bulk-assign-mode-add">Add to existing</Button>
            <Button size="sm" variant={bulkAssignMode === 'replace' ? 'default' : 'outline'} onClick={() => setBulkAssignMode('replace')} data-testid="bulk-assign-mode-replace">Replace</Button>
            {bulkAssignMode === 'replace' && bulkAssignSelectedAdmins.length === 0 && (
              <span className="text-xs text-amber-600">Replace with none = clears all admins</span>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="bulk-assign-search" placeholder="Search admins by name or email..." value={bulkAssignSearch} onChange={(e) => setBulkAssignSearch(e.target.value)} className="h-8 pl-10" />
          </div>

          <ScrollArea className="h-[260px] border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="p-2 space-y-1">
              {manageableAdmins
                .filter(a => {
                  if (!bulkAssignSearch) return true;
                  const s = bulkAssignSearch.toLowerCase();
                  return a.full_name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s);
                })
                .map(a => {
                  const sel = bulkAssignSelectedAdmins.includes(a.id);
                  return (
                    <div
                      key={a.id}
                      data-testid={`bulk-assign-admin-${a.id}`}
                      className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${sel ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800' : 'hover:bg-muted border border-transparent'}`}
                      onClick={() => toggleBulkAssignAdmin(a.id)}
                    >
                      <Checkbox checked={sel} onClick={(e) => e.stopPropagation()} onCheckedChange={() => toggleBulkAssignAdmin(a.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{a.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                      </div>
                    </div>
                  );
                })}
              {manageableAdmins.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">No Admins available.</div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsBulkAssignOpen(false)}>Cancel</Button>
            <Button size="sm"
              onClick={handleBulkAssignAdmins}
              disabled={bulkAssignSubmitting || (bulkAssignMode === 'add' && bulkAssignSelectedAdmins.length === 0)}
              data-testid="bulk-assign-apply"
              className="bg-[#2563eb] hover:bg-[#1d4ed8]"
            >
              {bulkAssignSubmitting ? 'Applying...' : (bulkAssignMode === 'replace' ? 'Replace admins' : 'Add admins')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isBulkAllocOpen} onOpenChange={setIsBulkAllocOpen}>
        <DialogContent className="max-w-lg" data-testid="bulk-allocate-dialog">
          <DialogHeader>
            <DialogTitle>Allocate {selectedDeviceIds.length} device(s) to User(s)</DialogTitle>
            <DialogDescription>
              Choose which users get operational access (Live Tracking, Fleet, History) to the selected devices. Allocation is silent — no notifications are sent.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <Button size="sm" variant={bulkAllocMode === 'add' ? 'default' : 'outline'} onClick={() => setBulkAllocMode('add')} data-testid="bulk-allocate-mode-add">Add to existing</Button>
            <Button size="sm" variant={bulkAllocMode === 'replace' ? 'default' : 'outline'} onClick={() => setBulkAllocMode('replace')} data-testid="bulk-allocate-mode-replace">Replace</Button>
            <Button size="sm" variant={bulkAllocMode === 'remove' ? 'destructive' : 'outline'} onClick={() => setBulkAllocMode('remove')} data-testid="bulk-allocate-mode-remove">Remove</Button>
            {bulkAllocMode === 'replace' && bulkAllocSelectedUsers.length === 0 && (
              <span className="text-xs text-amber-600">Replace with none = removes all users</span>
            )}
            {bulkAllocMode === 'remove' && (
              <span className="text-xs text-amber-600">Strips the picked users; everyone else keeps access</span>
            )}
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="bulk-allocate-search" placeholder="Search users by name, email or company..." value={bulkAllocSearch} onChange={(e) => setBulkAllocSearch(e.target.value)} className="h-8 pl-10" />
          </div>

          <ScrollArea className="h-[260px] border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
            <div className="p-2 space-y-1">
              {[...allocatableUsers]
                .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '', undefined, { sensitivity: 'base' }))
                .filter(u => {
                  if (!bulkAllocSearch) return true;
                  const s = bulkAllocSearch.toLowerCase();
                  return u.full_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s) || u.company?.toLowerCase().includes(s);
                })
                .map(u => {
                  const sel = bulkAllocSelectedUsers.includes(u.id);
                  const maxDev = u.max_devices ?? 0;
                  const maxBcn = u.max_beacons ?? 0;
                  const devCount = u.device_count || 0;
                  const bcnCount = u.beacon_count || 0;
                  const devFull = maxDev !== -1 && devCount >= maxDev;
                  const bcnFull = maxBcn !== -1 && bcnCount >= maxBcn;
                  const fullyBlocked = devFull && bcnFull && bulkAllocMode !== 'remove';
                  return (
                    <div
                      key={u.id}
                      data-testid={`bulk-allocate-user-${u.id}`}
                      className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${fullyBlocked && !sel ? 'opacity-50 cursor-not-allowed border border-transparent' : sel ? 'bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 cursor-pointer' : 'hover:bg-muted border border-transparent cursor-pointer'}`}
                      onClick={() => { if (fullyBlocked && !sel) return; toggleBulkAllocUser(u.id); }}
                    >
                      <Checkbox checked={sel} disabled={fullyBlocked && !sel} onClick={(e) => e.stopPropagation()} onCheckedChange={() => { if (fullyBlocked && !sel) return; toggleBulkAllocUser(u.id); }} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{u.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{u.email}{u.company ? ` · ${u.company}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {u.role && <Badge variant="outline" className="text-[10px] capitalize">{u.role === 'end_user' ? 'User' : u.role.replace(/_/g, ' ')}</Badge>}
                        <Badge variant="secondary" className={`text-[10px] ${devFull ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : ''}`}>
                          {maxDev === -1 ? `${devCount}/∞` : `${devCount}/${maxDev}`}
                        </Badge>
                        <Badge variant="secondary" className={`text-[10px] ${bcnFull ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : ''}`}>
                          {maxBcn === -1 ? `${bcnCount}/∞` : `${bcnCount}/${maxBcn}`}
                        </Badge>
                        {fullyBlocked && (
                          <span className="text-[10px] text-red-600 font-medium">{maxDev === 0 && maxBcn === 0 ? 'BLOCKED' : 'FULL'}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              {allocatableUsers.length === 0 && (
                <div className="text-center py-8 text-muted-foreground text-sm">No users available for allocation.</div>
              )}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setIsBulkAllocOpen(false)}>Cancel</Button>
            <Button size="sm"
              onClick={handleBulkAllocateUsers}
              disabled={bulkAllocSubmitting || (bulkAllocMode !== 'replace' && bulkAllocSelectedUsers.length === 0)}
              data-testid="bulk-allocate-apply"
              className={bulkAllocMode === 'remove' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#2563eb] hover:bg-[#1d4ed8]'}
            >
              {bulkAllocSubmitting ? 'Applying...' : (bulkAllocMode === 'remove' ? 'Remove users' : bulkAllocMode === 'replace' ? 'Replace users' : 'Add users')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle data-testid="device-management-list-title">
            Devices ({totalCount > filteredDevices.length ? `${filteredDevices.length} shown of ${totalCount}` : filteredDevices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {!initialLoadDone ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
              <SyncedTableScroll minWidth="1800px" testId="device-management-table-scroll">
              <Table className="table-fixed [&_td]:py-1.5" wrapperClassName="overflow-visible">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 sticky top-0 left-0 bg-slate-100 dark:bg-slate-900 z-30" data-testid="device-mgmt-header-checkbox">
                      <Checkbox
                        checked={filteredDevices.length > 0 && filteredDevices.every(d => selectedDeviceIds.includes(d.id))}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="w-16 sticky top-0 left-12 bg-slate-100 dark:bg-slate-900 z-30" data-testid="device-mgmt-header-icon">Icon</TableHead>
                    <SortableTableHead className="w-56 sticky top-0 left-28 bg-slate-100 dark:bg-slate-900 z-30 border-r border-border" field="name" currentSort={deviceSortField} currentDir={deviceSortDir} onSort={handleDeviceSort}>Device</SortableTableHead>
                    <TableHead className="w-44 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Users</TableHead>
                    <TableHead className="w-32 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Type</TableHead>
                    <SortableTableHead className="w-36 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="imei" currentSort={deviceSortField} currentDir={deviceSortDir} onSort={handleDeviceSort}>IMEI</SortableTableHead>
                    <SortableTableHead className="w-28 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="protocol" currentSort={deviceSortField} currentDir={deviceSortDir} onSort={handleDeviceSort}>Protocol</SortableTableHead>
                    <TableHead className="w-28 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Asset Type</TableHead>
                    <SortableTableHead className="w-28 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="status" currentSort={deviceSortField} currentDir={deviceSortDir} onSort={handleDeviceSort}>Status</SortableTableHead>
                    <SortableTableHead className="w-40 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20" field="last_connection" currentSort={deviceSortField} currentDir={deviceSortDir} onSort={handleDeviceSort}>Last Connection</SortableTableHead>
                    <TableHead className="w-20 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Active</TableHead>
                    <TableHead className="w-28 sticky top-0 bg-slate-100 dark:bg-slate-900 z-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDevices.map(device => (
                    <TableRow
                      key={device.id}
                      className="group cursor-pointer hover:bg-muted/50"
                      data-testid={device.device_type === 'gps_tracker' ? `gps-tracker-row-${device.id}` : `device-management-row-${device.id}`}
                      data-device-type={device.device_type || ''}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()} className="sticky left-0 sticky-cell z-10">
                        <Checkbox
                          data-testid={`device-row-checkbox-${device.id}`}
                          checked={selectedDeviceIds.includes(device.id)}
                          onCheckedChange={(checked) => handleSelectDevice(device.id, checked)}
                        />
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)} className="sticky left-12 sticky-cell z-10">
                        <DeviceIconDisplay iconName={device.device_icon || device.asset_type || 'device'} className="w-8 h-8" customIcons={customIcons} iconColor={device.device_icon_color} />
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)} className="sticky left-28 sticky-cell z-10 border-r border-border">
                        <div>
                          <div className="font-medium">{device.name}</div>
                          {device.asset_name && (
                            <div className="text-xs text-muted-foreground">{device.asset_name}</div>
                          )}
                          {device.sim_status && !['active', 'activated', 'enabled'].includes(device.sim_status) && (
                            <div className="mt-0.5">
                              <Badge variant="outline" className="text-[10px] gap-1 text-red-600 border-red-300 dark:text-red-400 dark:border-red-800" data-testid={`dead-sim-badge-${device.id}`}>
                                <AlertTriangle className="w-2.5 h-2.5" />
                                SIM: {device.sim_status.replace(/_/g, ' ')}
                              </Badge>
                            </div>
                          )}
                          {canManageDeviceList && (
                            <div className="mt-0.5">
                              {(device.management_admin_ids || []).length > 0 ? (
                                <Badge variant="secondary" className="text-[10px] gap-1" data-testid={`managed-by-${device.id}`} data-managed-state="assigned">
                                  <ListChecks className="w-2.5 h-2.5" />
                                  Managed by {device.management_admin_ids.length} admin{device.management_admin_ids.length !== 1 ? 's' : ''}
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] gap-1 text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-700" data-testid={`managed-by-${device.id}`} data-managed-state="unassigned">
                                  <ListChecks className="w-2.5 h-2.5" />
                                  No admins assigned
                                </Badge>
                              )}
                            </div>
                          )}
                          {device.linked_devices?.length > 0 && (
                            <div className="flex items-center gap-1 mt-0.5">
                              {device.linked_devices.map(ld => {
                                const linkedDev = devices.find(d => d.id === ld.device_id);
                                const roleIcon = ld.gps_source ? 'M' : ld.role === 'io_source' ? 'M' : 'B';
                                const roleCls = roleIcon === 'M' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300';
                                return (
                                  <span key={ld.device_id} className="inline-flex items-center gap-0.5 text-[10px]">
                                    <span className={`font-bold px-1 rounded text-[9px] ${roleCls}`}>{roleIcon}</span>
                                    <Link2 className="w-2.5 h-2.5 text-muted-foreground" />
                                    <span className="text-muted-foreground">{linkedDev?.name || 'Unknown'}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(device.allocated_tenant_ids || [])
                            .map(uid => ({ uid, ...userMap[uid] }))
                            .filter(u => u.name)
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((u) => {
                              const canClick = currentUser?.role === 'super_admin' ||
                                (currentUser?.role === 'admin' && !['super_admin', 'admin'].includes(u.role));
                              return (
                                <Badge
                                  key={u.uid}
                                  variant="outline"
                                  className={`text-[10px] ${canClick ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950 hover:border-blue-300' : ''}`}
                                  onClick={canClick ? (e) => {
                                    e.stopPropagation();
                                    window.localStorage.setItem('_openUserFromDevice', u.uid);
                                    const buttons = document.querySelectorAll('button');
                                    for (const btn of buttons) {
                                      if (btn.textContent?.trim().includes('User Management')) {
                                        btn.click();
                                        break;
                                      }
                                    }
                                  } : (e) => e.stopPropagation()}
                                >
                                  {u.name || u.email?.split('@')[0]}
                                </Badge>
                              );
                            })
                          }
                          {(!device.allocated_tenant_ids || device.allocated_tenant_ids.length === 0) && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)}>
                        {(() => {
                          const typeColors = {
                            gps_tracker: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                            obd_tracker: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
                            personal_tracker: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                            asset_tracker: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
                            dashcam: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
                            beacon: 'bg-cyan-100 text-cyan-900 dark:bg-cyan-900 dark:text-cyan-200',
                          };
                          const cls = typeColors[device.device_type] || 'bg-gray-100 text-gray-800';
                          return <Badge className={`text-xs ${cls}`}>{deviceTypes.find(t => t.value === device.device_type)?.label || device.device_type || 'GPS Tracker'}</Badge>;
                        })()}
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)} className="w-44 max-w-[11rem]">
                        <span className="font-mono text-sm block max-w-[160px] truncate" title={device.imei} data-testid={`device-imei-${device.id}`}>{device.imei}</span>
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)}>
                        {(() => {
                          const protocolColors = {
                            'Teltonika': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
                            'Meitrack': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                            'Digital Matter': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
                            'BLE Beacon': 'bg-cyan-100 text-cyan-900 dark:bg-cyan-900 dark:text-cyan-200',
                            'Concox': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
                            'CalAmp': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                          };
                          const proto = device.device_type === 'beacon' ? 'BLE Beacon' : (device.protocol || 'Unknown');
                          const cls = protocolColors[proto] || 'bg-gray-100 text-gray-800';
                          return <Badge className={`text-xs ${cls}`}>{proto}</Badge>;
                        })()}
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)}>
                        <span className="capitalize">{device.asset_type}</span>
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)}>
                        {getDeviceStatusBadge(device)}
                      </TableCell>
                      <TableCell onClick={() => openEditDialog(device)}>
                        {formatTimestamp(device.device_type === 'beacon' ? (device.beacon_last_seen || device.last_seen || device.last_connection) : (device.last_seen || device.last_connection))}
                      </TableCell>
                      <TableCell>
                        {device.is_active ? (
                          <Badge className="bg-green-600 text-white cursor-pointer hover:bg-green-700" title="Click to open the Deactivation Wizard" onClick={(e) => { e.stopPropagation(); setDeactivateWizardDevice(device); }}>Active</Badge>
                        ) : (
                          <Badge className="bg-gray-500 text-white cursor-pointer hover:bg-gray-600" title="Click to open the Reactivation Wizard" onClick={(e) => { e.stopPropagation(); setReactivateWizardDevice(device); }}>Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {!isManager && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCloneDevice(device)}
                            title="Clone"
                            className="h-8 w-8 p-0"
                          >
                            <Copy className="w-4 h-4 text-blue-500" />
                          </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(device)}
                            title="Edit"
                            className="h-8 w-8 p-0"
                            data-testid={device.device_type === 'gps_tracker' ? `gps-tracker-edit-button-${device.id}` : `device-edit-button-${device.id}`}
                            data-device-type={device.device_type || ''}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => device.is_active ? setDeactivateWizardDevice(device) : setReactivateWizardDevice(device)}
                            title={device.is_active ? 'Deactivate (opens Deactivation Wizard)' : 'Reactivate (opens Reactivation Wizard)'}
                            className="h-8 w-8 p-0"
                            data-testid={`device-power-btn-${device.id}`}
                          >
                            {device.is_active ? <PowerOff className="w-4 h-4 text-amber-600" /> : <Power className="w-4 h-4 text-green-600" />}
                          </Button>
                          {!isManager && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteDevice(device.id)}
                            title="Delete"
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredDevices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-12 text-muted-foreground">
                        No devices found. Click "Add Device" to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              </SyncedTableScroll>
          )}

          {/* Server-side pagination — uses the shared PaginationFooter so the
              UI matches every other paginated list in the app. */}
          <PaginationFooter
            page={pageIndex}
            setPage={setPageIndex}
            totalItems={totalCount}
            itemsPerPage={pageSize}
            setItemsPerPage={setPageSize}
            testIdPrefix="device-mgmt"
            zeroIndexed
            disabled={loading}
            alwaysShow
          />
        </CardContent>
      </Card>
      
      {/* Add Device Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={(open) => { if (!open) handleAddDialogClose(); }}>
        <DialogContent className="w-[min(96vw,80rem)] max-w-none h-[85vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Add New Device</DialogTitle>
            <DialogDescription className="sr-only">Configure a new GPS device or beacon</DialogDescription>
          </DialogHeader>
          
          {/* Tabs */}
          {/* Mobile: dropdown tab picker (same pattern as main app nav) */}
          <MobileTabPicker
            tabs={[
              { value: 'details', label: 'Device Details', icon: <HardDrive className="w-4 h-4" /> },
              { value: 'asset', label: 'Asset Type', icon: <Truck className="w-4 h-4" /> },
              { value: 'allocation', label: 'Allocation', icon: <Users className="w-4 h-4" /> },
              ...(canManageDeviceList ? [{ value: 'management_list', label: 'Management List', icon: <ListChecks className="w-4 h-4" /> }] : []),
              ...(formData.device_type !== 'beacon' ? [
                { value: 'commands', label: 'Commands', icon: <CommandIcon className="w-4 h-4" /> },
                { value: 'mapping', label: 'IO Mapping', icon: <Activity className="w-4 h-4" /> },
              ] : []),
              { value: 'icon', label: 'Icon', icon: <ImageIcon className="w-4 h-4" /> },
            ]}
            value={activeTab}
            onChange={handleTabChange}
            testId="add-device-tab-picker"
          />
          <div className="hidden sm:flex items-center gap-2 px-6 border-b overflow-x-auto shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('details')}
              className={activeTab === 'details' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <HardDrive className="w-4 h-4 mr-2" />
              Device Details
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('asset')}
              className={activeTab === 'asset' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <Truck className="w-4 h-4 mr-2" />
              Asset Type
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('allocation')}
              className={activeTab === 'allocation' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="tab-allocation-add"
            >
              <Users className="w-4 h-4 mr-2" />
              Allocation
            </Button>
            {canManageDeviceList && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('management_list')}
              className={activeTab === 'management_list' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="tab-management-list"
            >
              <ListChecks className="w-4 h-4 mr-2" />
              Management List
            </Button>
            )}
            {formData.device_type !== 'beacon' && (<>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('commands')}
              className={activeTab === 'commands' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <CommandIcon className="w-4 h-4 mr-2" />
              Commands
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('mapping')}
              className={activeTab === 'mapping' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <Activity className="w-4 h-4 mr-2" />
              IO Mapping
            </Button>
            </>)}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('icon')}
              className={activeTab === 'icon' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Icon
            </Button>
          </div>
          
          {/* Tab Content */}
          <ScrollArea className="flex-1 min-w-0 px-6 py-4 min-h-0 overflow-hidden [&_[data-radix-scroll-area-viewport]>div]:!block">
            {renderFormTabs(false)}
          </ScrollArea>
          
          {/* Footer */}
          <div className="flex justify-between items-center px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => handleAddDialogClose()}>
              Cancel
            </Button>
            <Button onClick={() => guardedSave('create')} disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Device
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Device Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { if (!open) handleEditDialogClose(); }}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>Edit Device: {selectedDevice?.name}</DialogTitle>
            <DialogDescription className="sr-only">Edit device configuration and settings</DialogDescription>
          </DialogHeader>
          
          {/* Tabs */}
          {/* Mobile: dropdown tab picker (same pattern as main app nav) */}
          <MobileTabPicker
            tabs={[
              { value: 'details', label: 'Device Details', icon: <HardDrive className="w-4 h-4" /> },
              { value: 'asset', label: 'Asset Type', icon: <Truck className="w-4 h-4" /> },
              ...(!isManager ? [{ value: 'allocation', label: 'Allocation', icon: <Users className="w-4 h-4" /> }] : []),
              ...(canManageDeviceList ? [{ value: 'management_list', label: 'Management List', icon: <ListChecks className="w-4 h-4" /> }] : []),
              ...(formData.device_type !== 'beacon' ? [{ value: 'status', label: 'Status', icon: <Signal className="w-4 h-4" /> }] : []),
              ...(formData.device_type === 'beacon' ? [{ value: 'beacon_status', label: 'Beacon RSSI', icon: <Bluetooth className="w-4 h-4" /> }] : []),
              ...((!isManager && formData.device_type !== 'beacon') ? [
                { value: 'commands', label: 'Commands', icon: <CommandIcon className="w-4 h-4" /> },
                { value: 'mapping', label: 'IO Mapping', icon: <Activity className="w-4 h-4" /> },
              ] : []),
              { value: 'icon', label: 'Icon', icon: <ImageIcon className="w-4 h-4" /> },
              ...(!isManager ? [
                { value: 'history', label: 'History', icon: <History className="w-4 h-4" /> },
                { value: 'audit', label: 'Audit', icon: <PackageCheck className="w-4 h-4" /> },
                { value: 'removal', label: 'Removal', icon: <AlertTriangle className="w-4 h-4" /> },
              ] : []),
            ]}
            value={activeTab}
            onChange={(v) => {
              // Preserve the per-tab fetch side effects the strip buttons trigger.
              if (v === 'status') { handleTabChange('status'); fetchDeviceStatus(selectedDevice?.imei); fetchCommandHistory(selectedDevice?.imei); }
              else if (v === 'beacon_status') { handleTabChange('beacon_status'); refreshSelectedBeaconStatus(); }
              else handleTabChange(v);
            }}
            testId="edit-device-tab-picker"
          />
          <div className="hidden sm:flex items-center gap-2 px-6 border-b overflow-x-auto shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('details')}
              className={activeTab === 'details' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <HardDrive className="w-4 h-4 mr-2" />
              Device Details
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('asset')}
              className={activeTab === 'asset' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <Truck className="w-4 h-4 mr-2" />
              Asset Type
            </Button>
            {!isManager && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('allocation')}
              className={activeTab === 'allocation' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="tab-allocation-edit"
            >
              <Users className="w-4 h-4 mr-2" />
              Allocation
            </Button>
            )}
            {canManageDeviceList && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('management_list')}
              className={activeTab === 'management_list' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="tab-management-list-edit"
            >
              <ListChecks className="w-4 h-4 mr-2" />
              Management List
            </Button>
            )}
            {formData.device_type !== 'beacon' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { handleTabChange('status'); fetchDeviceStatus(selectedDevice?.imei); fetchCommandHistory(selectedDevice?.imei); }}
              className={activeTab === 'status' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="device-status-tab-btn"
            >
              <Signal className="w-4 h-4 mr-2" />
              Status
            </Button>
            )}
            {formData.device_type === 'beacon' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { handleTabChange('beacon_status'); refreshSelectedBeaconStatus(); }}
              className={activeTab === 'beacon_status' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
              data-testid="beacon-rssi-tab-btn"
            >
              <Bluetooth className="w-4 h-4 mr-2" />
              Beacon RSSI
            </Button>
            )}
            {!isManager && formData.device_type !== 'beacon' && (<>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('commands')}
              className={activeTab === 'commands' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <CommandIcon className="w-4 h-4 mr-2" />
              Commands
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('mapping')}
              className={activeTab === 'mapping' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <Activity className="w-4 h-4 mr-2" />
              IO Mapping
            </Button>
            </>)}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('icon')}
              className={activeTab === 'icon' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Icon
            </Button>
            {!isManager && (<>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('history')}
              className={activeTab === 'history' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <History className="w-4 h-4 mr-2" />
              History
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('audit')}
              className={activeTab === 'audit' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <PackageCheck className="w-4 h-4 mr-2" />
              Audit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleTabChange('removal')}
              className={activeTab === 'removal' ? 'border-b-2 border-[#2563eb] rounded-none' : 'rounded-none'}
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Removal
            </Button>
            </>)}
          </div>
          
          {/* Tab Content (Same as Add Dialog for tabs 1-6) */}
          <ScrollArea className="flex-1 px-6 py-4 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
            {renderFormTabs(true)}

            {activeTab === 'history' && (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200">
                  <p className="text-sm">
                    <strong>Warning:</strong> Clearing device history is permanent and cannot be undone. 
                    Export your data first if you need a backup.
                  </p>
                </div>

                {/* Date Range Picker */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Date Range</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">From (local time)</Label>
                        <Input
                          type="datetime-local"
                          value={historyFromDate}
                          onChange={(e) => setHistoryFromDate(e.target.value)}
                          className="h-8 text-xs"
                          data-testid="history-from-date"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">To (local time)</Label>
                        <Input
                          type="datetime-local"
                          value={historyToDate}
                          onChange={(e) => setHistoryToDate(e.target.value)}
                          className="h-8 text-xs"
                          data-testid="history-to-date"
                        />
                      </div>
                    </div>
                    {historyFromDate && historyToDate && (
                      <p className="text-xs text-muted-foreground">
                        UTC: {new Date(historyFromDate).toISOString()} — {new Date(historyToDate).toISOString()}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Export Section */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Export History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" size="sm" disabled={!!historyExporting} onClick={() => handleExportHistory('json', false)} data-testid="export-all-json">
                        {historyExporting === 'json' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                        {historyExporting === 'json' ? 'Exporting…' : 'Export (JSON)'}
                      </Button>
                      <Button variant="outline" size="sm" disabled={!!historyExporting} onClick={() => handleExportHistory('csv', false)} data-testid="export-all-csv">
                        {historyExporting === 'csv' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                        {historyExporting === 'csv' ? 'Exporting…' : 'Export (CSV)'}
                      </Button>
                      {historyFromDate && historyToDate && (
                        <>
                          <Button variant="outline" size="sm" disabled={!!historyExporting} onClick={() => handleExportHistory('json', true)}>
                            <Upload className="w-3.5 h-3.5 mr-1" />
                            Export Range (JSON)
                          </Button>
                          <Button variant="outline" size="sm" disabled={!!historyExporting} onClick={() => handleExportHistory('csv', true)}>
                            <Upload className="w-3.5 h-3.5 mr-1" />
                            Export Range (CSV)
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Import History (Recovery) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Import History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-2">Restore GPS history from a previously exported JSON file. Already-imported records are skipped, so re-running an interrupted import is safe.</p>
                    <Button variant="outline" size="sm" disabled={!!historyImporting} onClick={handleImportHistory} data-testid="import-history-btn">
                      {historyImporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                      {historyImporting ? `Importing… ${historyImporting.percent}%` : 'Import from JSON'}
                    </Button>
                  </CardContent>
                </Card>

                {/* Clear History Section */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-red-600">
                      <Trash2 className="w-4 h-4" />
                      Clear History
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="destructive" size="sm" onClick={() => handleClearHistory(false)} data-testid="clear-all-history">
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Clear All History
                      </Button>
                      {historyFromDate && historyToDate && (
                        <Button variant="destructive" size="sm" onClick={() => handleClearHistory(true)} data-testid="clear-range-history">
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Clear Date Range Only
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {historyFromDate && historyToDate
                        ? `Date range selected: ${formatDT(historyFromDate)} — ${formatDT(historyToDate)}`
                        : 'Select a date range above to enable range-specific clear and export.'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Tab 8: Audit Logs */}
            {activeTab === 'audit' && (
              <div className="flex flex-col h-[calc(85vh-220px)]">
                <div className="flex flex-wrap items-start justify-between gap-2 flex-shrink-0">
                  <div>
                    <h3 className="font-semibold">Audit Trail</h3>
                    <p className="text-sm text-muted-foreground">
                      Complete history of changes and actions performed on this device
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <div className="relative w-44">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search action, actor, details..."
                        value={auditSearch}
                        onChange={e => { setAuditSearch(e.target.value); fetchAuditLogs(selectedDevice?.id, e.target.value, auditDateRange); }}
                        className="w-full h-8 pl-8 pr-7 text-xs"
                        data-testid="device-audit-search"
                      />
                      {auditSearch && <button onClick={() => { setAuditSearch(''); fetchAuditLogs(selectedDevice?.id, '', auditDateRange); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>}
                    </div>
                    {/* Date range — Event Log "All Time" preset filter (shared component). */}
                    <DateRangePresetFilter
                      value={auditDateRange}
                      onChange={(range) => { setAuditDateRange(range); fetchAuditLogs(selectedDevice?.id, auditSearch, range); }}
                      testIdPrefix="device-audit-date"
                      resetSignal={auditDateResetKey}
                    />
                    <Button variant="outline" size="sm" className="h-8" onClick={handleExportDeviceAudit} data-testid="export-device-audit-btn">
                      <Upload className="w-3.5 h-3.5 mr-1.5" />
                      <span className="text-xs">{auditSearch || auditDateRange.from ? `Export (${auditLogs.length})` : 'Export'}</span>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8"
                      onClick={() => { const cleared = { from: undefined, to: undefined }; setAuditDateRange(cleared); setAuditDateResetKey(k => k + 1); fetchAuditLogs(selectedDevice?.id, auditSearch, cleared); }}
                      disabled={auditLogsLoading}
                      data-testid="device-audit-refresh"
                    >
                      {auditLogsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
                
                {auditLogsLoading ? (
                  <div className="flex items-center justify-center flex-1">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : auditLogs.length === 0 ? (
                  <div className="text-center flex-1 flex flex-col items-center justify-center text-muted-foreground">
                    <History className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm">No audit logs found</p>
                    <p className="text-xs">Activity will be recorded here</p>
                  </div>
                ) : (
                  <ScrollArea className="flex-1 min-h-0 mt-4 [&_[data-radix-scroll-area-viewport]>div]:!block">
                    <div className="space-y-2">
                      {auditLogs.map(log => {
                        const actionIcon = (a) => {
                          if (a === 'device_created') return <Plus className="w-4 h-4" />;
                          if (a === 'device_deleted') return <Trash2 className="w-4 h-4" />;
                          if (a === 'device_activated') return <Check className="w-4 h-4" />;
                          if (a === 'device_deactivated') return <Lock className="w-4 h-4" />;
                          if (a === 'device_allocated') return <Users className="w-4 h-4" />;
                          if (a === 'device_history_cleared') return <Trash2 className="w-4 h-4" />;
                          if (a === 'command_sent') return <Send className="w-4 h-4" />;
                          if (a === 'device_deactivation_wizard' || a === 'device_reactivation_wizard') return <Wrench className="w-4 h-4" />;
                          return <Pencil className="w-4 h-4" />;
                        };
                        const actionColor = (a) => {
                          if (a === 'device_created') return 'bg-green-100 text-green-600';
                          if (a === 'device_deleted' || a === 'device_history_cleared') return 'bg-red-100 text-red-600';
                          if (a === 'device_activated') return 'bg-green-100 text-green-600';
                          if (a === 'device_deactivated') return 'bg-red-100 text-red-600';
                          if (a === 'device_allocated') return 'bg-purple-100 text-purple-600';
                          if (a === 'command_sent') return 'bg-blue-100 text-blue-600';
                          if (a === 'device_deactivation_wizard' || a === 'device_reactivation_wizard') return 'bg-orange-100 text-orange-600';
                          return 'bg-slate-100 text-slate-600';
                        };
                        const actionLabel = (a) => {
                          const map = {
                            'device_created': 'Device Created',
                            'device_updated': 'Device Updated',
                            'device_deleted': 'Device Deleted',
                            'device_activated': 'Device Activated',
                            'device_deactivated': 'Device Deactivated',
                            'device_allocated': 'Device Allocated',
                            'device_history_cleared': 'History Cleared',
                            'driver_assignment_changed': 'Driver Assignment',
                            'command_sent': 'Command Sent',
                            'device_deactivation_wizard': 'Deactivation Wizard',
                            'device_reactivation_wizard': 'Reactivation Wizard',
                          };
                          return map[a] || a?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || a;
                        };

                        // Format changes
                        const formatChanges = (changes) => {
                          if (!changes) return null;
                          const entries = Object.entries(changes).filter(([k]) => k !== 'updated_at');
                          if (entries.length === 0) return null;
                          return entries.map(([field, change]) => {
                            if (!change && change !== false && change !== 0) return null;
                            const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                            if (field === 'tenant_id') return null; // internal scoping key (driver assignment entries)
                            if (field === 'driver_assignment') {
                              const fmt = (s) => (!s || s.mode !== 'manual' ? 'Auto' : `Manual — ${s.person_name || s.person_id || '—'}${s.person_type ? ` (${s.person_type === 'driver' ? 'Driver' : 'Staff'})` : ''}`);
                              return { field: 'Driver Assignment', old: fmt(change.before), new: fmt(change.after) };
                            }
                            if (field === 'allocated_tenant_ids') {
                              const oldNames = (change.old || []).map(id => userMap[id]?.name || id.slice(0, 8));
                              const newNames = (change.new || []).map(id => userMap[id]?.name || id.slice(0, 8));
                              const added = newNames.filter(n => !oldNames.includes(n));
                              const removed = oldNames.filter(n => !newNames.includes(n));
                              return { field: 'Allocated Users', added, removed };
                            }
                            if (field === 'primary_operational_account_id') {
                              // Legacy entries stored raw user IDs (sometimes unchanged) — resolve
                              // to names and hide no-op rows.
                              if (change?.old === change?.new) return null;
                              const resolve = (id) => (id ? (userMap[id]?.name || String(id).slice(0, 8)) : null);
                              return { field: 'Primary Operational Account', old: resolve(change?.old), new: resolve(change?.new) };
                            }
                            if (field === 'manual_assigned_person' && change && typeof change === 'object' && !('old' in change || 'new' in change)) {
                              const pt = change.person_type ? ` (${change.person_type === 'driver' ? 'Driver' : 'Staff'})` : '';
                              return { field: 'Manual Assigned Person', value: `${change.person_name || change.person_id || '—'}${pt}` };
                            }
                            if (typeof change !== 'object' || change === null || !('old' in change || 'new' in change)) {
                              const val = (change && typeof change === 'object') ? JSON.stringify(change) : String(change);
                              return { field: fieldLabel, value: val };
                            }
                            return { field: fieldLabel, old: change.old, new: change.new };
                          }).filter(Boolean);
                        };

                        const changes = formatChanges(log.changes);
                        return (
                          <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${actionColor(log.action)}`}>
                              {actionIcon(log.action)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <p className="font-medium text-sm">{actionLabel(log.action)}</p>
                                <Badge variant="outline" className="text-xs flex-shrink-0">{formatTimestamp(log.timestamp || log.created_at)}</Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">{log.details || '-'}</p>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-2 text-xs text-muted-foreground">
                                <span className="break-all">By: {log.actor_email || 'System'}</span>
                                {log.ip_address && <span>IP: {log.ip_address}</span>}
                              </div>
                              {changes && changes.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {changes.map((c, idx) => (
                                    <div key={idx} className="text-xs p-1.5 bg-muted rounded flex flex-wrap gap-x-2">
                                      <span className="font-medium">{c.field}:</span>
                                      {c.added ? (
                                        <>
                                          {c.added.length > 0 && <span className="text-green-600">+ {c.added.join(', ')}</span>}
                                          {c.removed.length > 0 && <span className="text-red-600">- {c.removed.join(', ')}</span>}
                                        </>
                                      ) : c.value != null ? (
                                        <span className="font-mono">{c.value}</span>
                                      ) : (
                                        <>
                                          <span className="text-muted-foreground line-through">{c.old != null ? (Array.isArray(c.old) ? `${c.old.length} item(s)` : String(c.old)) : 'empty'}</span>
                                          <span>&rarr;</span>
                                          <span className="font-mono">{c.new != null ? (Array.isArray(c.new) ? `${c.new.length} item(s)` : String(c.new)) : 'empty'}</span>
                                        </>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                )}
              </div>
            )}
            
            {/* Tab 9: Device Removal */}
            {activeTab === 'removal' && (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200">
                  <p className="text-sm">
                    <strong>Danger Zone:</strong> Deleting a device is permanent and cannot be undone. 
                    All associated data, history, and configuration will be lost.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">Before deleting this device:</h3>
                    <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                      <li>All GPS tracking history will be permanently deleted</li>
                      <li>Custom commands and IO mappings will be lost</li>
                      <li>Device will be removed from all tenant allocations</li>
                      <li>This action cannot be undone</li>
                    </ul>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button 
                      variant="destructive" 
                      onClick={() => handleDeleteDevice(selectedDevice?.id)}
                      className="w-full"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Device Permanently
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </ScrollArea>

          
          {/* Footer */}
          <div className="flex justify-between items-center px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => handleEditDialogClose()}>
              Cancel
            </Button>
            <Button onClick={() => guardedSave('update')} disabled={submitting} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unfinished mapping/command form guard */}
      <Dialog open={!!pendingFormGuard} onOpenChange={(open) => { if (!open) setPendingFormGuard(null); }}>
        <DialogContent className="max-w-md" data-testid="form-guard-dialog">
          <DialogHeader>
            <DialogTitle>Unfinished {[ioFormDirty && 'IO mapping', commandFormDirty && 'command'].filter(Boolean).join(' and ')}</DialogTitle>
            <DialogDescription>
              {ioFormDirty && commandFormDirty
                ? 'You started an IO mapping and a command but did not press Add/Update. What should happen to them before saving?'
                : ioFormDirty
                  ? `You ${editingMappingId ? 'are editing an IO mapping' : 'started a new IO mapping'} but did not press ${editingMappingId ? 'Update' : 'Add Mapping'}. What should happen to it before saving?`
                  : `You ${editingCommandId ? 'are editing a command' : 'started a new command'} but did not press ${editingCommandId ? 'Update' : 'Add Command'}. What should happen to it before saving?`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setPendingFormGuard(null)} data-testid="form-guard-go-back">
              Go back
            </Button>
            <Button variant="outline" className="text-destructive hover:text-destructive" onClick={handleGuardDiscard} data-testid="form-guard-discard">
              Discard &amp; Save
            </Button>
            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleGuardApply} data-testid="form-guard-apply">
              <Check className="w-4 h-4 mr-1.5" /> Apply &amp; Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning Dialog (Edit) */}
      <Dialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowUnsavedWarning(false)} data-testid="unsaved-stay-btn">
              Stay
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleEditDialogClose(true)} data-testid="unsaved-leave-btn">
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning Dialog (Add) */}
      <Dialog open={showAddUnsavedWarning} onOpenChange={setShowAddUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowAddUnsavedWarning(false)}>
              Stay
            </Button>
            <Button size="sm" variant="destructive" onClick={() => handleAddDialogClose(true)}>
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (Commands & IO Mappings) */}
      <Dialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete {pendingDelete?.type === 'command' ? 'Command' : 'IO Mapping'}?
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{pendingDelete?.label}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setPendingDelete(null)} data-testid="delete-cancel-btn">
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={confirmPendingDelete} data-testid="delete-confirm-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Confirmation Dialog (Commands & IO Mappings) */}
      <Dialog open={!!pendingImport} onOpenChange={(open) => { if (!open) setPendingImport(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Import {pendingImport?.count} {pendingImport?.type === 'commands' ? 'Command(s)' : 'IO Mapping(s)'}
            </DialogTitle>
            <DialogDescription>
              How would you like to import? <strong>Merge</strong> adds to your existing {pendingImport?.type}, while <strong>Replace</strong> overwrites all current {pendingImport?.type}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button size="sm" variant="outline" onClick={() => setPendingImport(null)} data-testid="import-cancel-btn">
              Cancel
            </Button>
            <Button size="sm"
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
              onClick={() => {
                if (pendingImport?.type === 'commands') {
                  setFormData(prev => ({...prev, custom_commands: pendingImport.items}));
                } else {
                  setFormData(prev => ({...prev, io_mappings: pendingImport.items}));
                }
                toast.success(`Replaced with ${pendingImport.count} ${pendingImport.type}`);
                setPendingImport(null);
              }}
              data-testid="import-replace-btn"
            >
              Replace All
            </Button>
            <Button size="sm"
              className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              onClick={() => {
                if (pendingImport?.type === 'commands') {
                  setFormData(prev => ({...prev, custom_commands: [...prev.custom_commands, ...pendingImport.items]}));
                } else {
                  setFormData(prev => ({...prev, io_mappings: [...prev.io_mappings, ...pendingImport.items]}));
                }
                toast.success(`Merged ${pendingImport.count} ${pendingImport.type}`);
                setPendingImport(null);
              }}
              data-testid="import-merge-btn"
            >
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog (Delete Device, Bulk Delete, Clear History, Activate/Deactivate) */}
      <Dialog open={!!pendingAction} onOpenChange={(open) => { if (!open) { setPendingAction(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${pendingAction?.variant === 'success' ? 'text-green-500' : pendingAction?.variant === 'warning' ? 'text-amber-500' : 'text-red-500'}`} />
              {pendingAction?.title}
            </DialogTitle>
            <DialogDescription>
              {pendingAction?.message}
            </DialogDescription>
          </DialogHeader>
          {pendingAction?.type === 'delete_device' && (
            <label className="flex items-start gap-2 cursor-pointer" data-testid="delete-device-purge-label">
              <Checkbox
                checked={purgeOnDelete}
                onCheckedChange={togglePurgeOnDelete}
                className="mt-0.5"
                data-testid="delete-device-purge-checkbox"
              />
              <span className="text-sm leading-tight">
                <span className="font-medium">Also permanently delete all historical data</span>
                <span className="block text-xs text-muted-foreground">GPS history, events, beacon scans & log book records — recommended, so a future device re-added with the same IMEI doesn't inherit this device's old data</span>
              </span>
            </label>
          )}
          {pendingAction?.type === 'clear_history' && (
            <div className="space-y-2" data-testid="clear-history-target-list">
              {CLEAR_HISTORY_OPTIONS.map((opt) => (
                <label key={opt.key} className="flex items-start gap-2 cursor-pointer" data-testid={`clear-history-target-${opt.key}`}>
                  <Checkbox
                    checked={!!clearHistoryTargets[opt.key]}
                    onCheckedChange={() => toggleClearHistoryTarget(opt.key)}
                    className="mt-0.5"
                    data-testid={`clear-history-target-checkbox-${opt.key}`}
                  />
                  <span className="text-sm leading-tight">
                    <span className="font-medium">{opt.label}</span>
                    <span className="block text-xs text-muted-foreground">{opt.note}</span>
                  </span>
                </label>
              ))}
              {!Object.values(clearHistoryTargets).some(Boolean) && (
                <p className="text-xs text-red-500" data-testid="clear-history-no-target-warning">Select at least one item to clear.</p>
              )}
            </div>
          )}
          {pendingAction?.requireTyped && (
            <div className="space-y-3">
              <p className="text-sm">To confirm, type the following exactly:</p>
              <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">DELETE</code>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation text..."
                className="font-mono"
                autoFocus
                data-testid="device-delete-confirm-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => { setPendingAction(null); setDeleteConfirmText(''); }} data-testid="action-cancel-btn">
              Cancel
            </Button>
            <Button size="sm"
              variant={pendingAction?.variant === 'success' ? 'default' : 'destructive'}
              className={pendingAction?.variant === 'success' ? 'bg-green-600 hover:bg-green-700' : ''}
              disabled={(pendingAction?.requireTyped && deleteConfirmText !== 'DELETE') || (pendingAction?.type === 'clear_history' && !Object.values(clearHistoryTargets).some(Boolean))}
              onClick={async () => { await pendingAction?.action(); setPendingAction(null); setDeleteConfirmText(''); }}
              data-testid="action-confirm-btn"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove All Users Confirmation */}
      <Dialog open={showRemoveAllUsersConfirm} onOpenChange={(open) => { if (!open) { setShowRemoveAllUsersConfirm(false); setRemoveAllUsersContext(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Remove Allocation?
            </DialogTitle>
            <DialogDescription>
              This will remove {removedUserCount} user{removedUserCount !== 1 ? 's' : ''} from <strong>{selectedDevice?.name || 'this device'}</strong>. They will no longer have access to this {formData.device_type === 'beacon' ? 'beacon' : 'device'}. Continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowRemoveAllUsersConfirm(false); setRemoveAllUsersContext(null); }}>
              Cancel
            </Button>
            <Button size="sm"
              variant="destructive"
              onClick={handleConfirmRemoveAllUsers}
              data-testid="confirm-remove-all-users-btn"
            >
              Remove &amp; Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIM Card Detail Overlay */}
      <Dialog open={simDetailOpen} onOpenChange={setSimDetailOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radio className="w-5 h-5" />
              SIM Card Details
            </DialogTitle>
            <DialogDescription>
              {simDetailData?.iccid || 'Loading...'}
            </DialogDescription>
          </DialogHeader>
          {simDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : simDetailData ? (
            <div className="space-y-4">
              {/* Status */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground block">Status</span>
                  <Badge className={`text-xs ${
                    simDetailData.status?.toUpperCase() === 'ACTIVATED' || simDetailData.status?.toUpperCase() === 'ACTIVE' ? 'bg-green-600 text-white' :
                    simDetailData.status?.toUpperCase() === 'DEACTIVATED' ? 'bg-red-600 text-white' :
                    'bg-gray-500 text-white'
                  }`}>{simDetailData.status || '-'}</Badge>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">Provider</span>
                  <span className="font-medium">{simDetailData.provider_name || simDetailData.provider || '-'}</span>
                </div>
              </div>

              {/* Key Info */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between border-b pb-1">
                  <span className="text-muted-foreground">ICCID</span>
                  <span className="font-mono text-xs">{simDetailData.iccid || '-'}</span>
                </div>
                <div className="flex justify-between border-b pb-1">
                  <span className="text-muted-foreground">Mobile Number</span>
                  <span>{simDetailData.mobile_number || '-'}</span>
                </div>
                {simDetailData.imei && (
                  <div className="flex justify-between border-b pb-1">
                    <span className="text-muted-foreground">IMEI (SIM)</span>
                    <span className="font-mono text-xs">{simDetailData.imei}</span>
                  </div>
                )}
                {simDetailData.imsi && (
                  <div className="flex justify-between border-b pb-1">
                    <span className="text-muted-foreground">IMSI</span>
                    <span className="font-mono text-xs">{simDetailData.imsi}</span>
                  </div>
                )}
                {simDetailData.apn && (
                  <div className="flex justify-between border-b pb-1">
                    <span className="text-muted-foreground">APN</span>
                    <span className="font-mono text-xs">{simDetailData.apn}</span>
                  </div>
                )}
                {simDetailData.ip_address && (
                  <div className="flex justify-between border-b pb-1">
                    <span className="text-muted-foreground">IP Address</span>
                    <span className="font-mono text-xs">{simDetailData.ip_address}</span>
                  </div>
                )}
              </div>

              {/* Data Usage */}
              {(simDetailData.data_used_mb != null || simDetailData.data_limit_mb != null) && (
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-xs font-medium block mb-1">Data Usage</span>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground">Used</span>
                      <span className="block font-mono">{simDetailData.data_used_mb != null ? `${simDetailData.data_used_mb.toFixed(2)} MB` : '-'}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground">Limit</span>
                      <span className="block font-mono">{simDetailData.data_limit_mb ? `${simDetailData.data_limit_mb} MB` : 'Unlimited'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Dates */}
              {(simDetailData.activated_at || simDetailData.expires_at) && (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {simDetailData.activated_at && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Activated</span>
                      <span>{formatDateOnly(simDetailData.activated_at)}</span>
                    </div>
                  )}
                  {simDetailData.expires_at && (
                    <div>
                      <span className="text-xs text-muted-foreground block">Expires</span>
                      <span>{formatDateOnly(simDetailData.expires_at)}</span>
                    </div>
                  )}
                </div>
              )}

              {simDetailData.error && (
                <p className="text-xs text-amber-600">{simDetailData.error}</p>
              )}

              {/* Send SMS */}
              {simDetailData.id && (
                <div className="space-y-3 pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Send SMS</h4>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={async () => {
                        try {
                          const smsRes = await api.get(`/admin/sim-cards/${simDetailData.id}/sms/history`);
                          setSimSmsHistory((smsRes.data || []).map(m => {
                            const s = {};
                            for (const [k, v] of Object.entries(m)) { s[k] = (v !== null && typeof v === 'object' && !Array.isArray(v)) ? (v.description || JSON.stringify(v)) : v; }
                            return s;
                          }));
                          toast.success('SMS history refreshed');
                        } catch { toast.error('Failed to refresh SMS history'); }
                      }}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Refresh
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={simSmsMessage}
                      onChange={(e) => setSimSmsMessage(e.target.value)}
                      placeholder="Type message to send to this SIM..."
                      className="flex-1 h-9 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleSimSendSms()}
                      maxLength={320}
                    />
                    <Button
                      size="sm"
                      className="bg-[#2563eb] hover:bg-[#1d4ed8] h-9"
                      onClick={handleSimSendSms}
                      disabled={simSmsSending || !simSmsMessage}
                    >
                      {simSmsSending ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Send'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">{simSmsMessage.length}/320 characters</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400">Message is sent as raw text. Include any required PIN/password formatting (e.g. "0000,command" for Meitrack or "[space][space]command" for Teltonika).</p>

                  {/* SMS History */}
                  {simSmsHistory.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-xs font-medium text-muted-foreground">Recent Messages</h4>
                      {simSmsHistory.slice(0, 5).map((msg, idx) => (
                        <div key={msg.id || idx} className={`p-2 rounded-lg text-xs ${
                          msg.direction === 'incoming'
                            ? 'bg-green-50 dark:bg-green-950 border border-green-200 mr-8'
                            : 'bg-blue-50 dark:bg-blue-950 border border-blue-200 ml-8'
                        }`}>
                          <p dangerouslySetInnerHTML={{ __html: (msg.message || msg.text || '-').replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>') }} />
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {msg.direction === 'incoming' ? 'From device' : 'Sent'} — {msg.sent_at || msg.created_at || msg.timestamp ? formatDT(msg.sent_at || msg.created_at || msg.timestamp, { seconds: true }) : '-'}
                            {msg.status && <span className="ml-2">[{msg.status}]</span>}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">No SIM data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Replace Device Dialog */}
      
      {/* Device Limit Error Dialog */}
      <Dialog open={!!deviceLimitError} onOpenChange={(open) => { if (!open) setDeviceLimitError(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Device Limit Reached
            </DialogTitle>
            <DialogDescription>
              {deviceLimitError}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button size="sm" onClick={() => setDeviceLimitError(null)}>OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace Device Dialog */}
      {/* Auto-detect unregistered beacons from recent BLE scans */}
      <Dialog open={showBeaconDiscover} onOpenChange={setShowBeaconDiscover}>
        <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Signal className="w-5 h-5 text-blue-500" />
              Auto-Detect Beacons from Recent Scans
            </DialogTitle>
            <DialogDescription>
              Beacons recently detected by your GPS trackers that are <strong>not yet registered</strong>. Pick one to auto-fill the identifier fields — no need to type long hex strings by hand.
            </DialogDescription>
          </DialogHeader>

          {/* Filter bar */}
          <div className="space-y-2 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
              <Input
                value={discoverSearch}
                onChange={(e) => setDiscoverSearch(e.target.value)}
                placeholder="Search identifier or detecting device…"
                className="pl-8 h-8"
                data-testid="beacon-discover-search"
              />
              {discoverSearch && (
                <button type="button" onClick={() => setDiscoverSearch('')} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground" data-testid="beacon-discover-search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <Select value={discoverTypeFilter} onValueChange={setDiscoverTypeFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="ibeacon">iBeacon</SelectItem>
                  <SelectItem value="eddystone">Eddystone</SelectItem>
                  <SelectItem value="vendor_specific">Vendor / MAC</SelectItem>
                </SelectContent>
              </Select>
              <Select value={discoverDetectorFilter} onValueChange={setDiscoverDetectorFilter}>
                <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-detector-filter"><SelectValue placeholder="Detected by" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any device</SelectItem>
                  {getDiscoverDetectorOptions().map(name => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={discoverMinRssi} onValueChange={setDiscoverMinRssi}>
                <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-rssi-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any signal</SelectItem>
                  <SelectItem value="-70">Strong (≥ -70 dBm)</SelectItem>
                  <SelectItem value="-85">Medium (≥ -85 dBm)</SelectItem>
                  <SelectItem value="-95">Weak (≥ -95 dBm)</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                min="1"
                value={discoverMinScans}
                onChange={(e) => setDiscoverMinScans(e.target.value)}
                placeholder="Min scans"
                className="h-9 text-xs"
                data-testid="beacon-discover-minscans-filter"
              />
              <Select value={discoverSort} onValueChange={setDiscoverSort}>
                <SelectTrigger className="h-8 text-xs" data-testid="beacon-discover-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most recent</SelectItem>
                  <SelectItem value="strength">Strongest signal</SelectItem>
                  <SelectItem value="scans">Most scans</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between shrink-0 pt-1">
            <p className="text-xs text-muted-foreground" data-testid="beacon-discover-count">
              {discoverLoading
                ? 'Scanning…'
                : `${getFilteredDiscoverCandidates().length} of ${discoverCandidates.length} shown`}
            </p>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={openBeaconDiscover} disabled={discoverLoading} data-testid="beacon-discover-refresh-btn">
              <RefreshCw className={`w-3 h-3 mr-1 ${discoverLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          {discoverError && (
            <p className="text-sm text-red-600 shrink-0" data-testid="beacon-discover-error">{discoverError}</p>
          )}

          {/* Scrollable candidate list (native overflow — reliable max-height scrolling) */}
          <div className="flex-1 min-h-0 overflow-y-auto -mr-2 pr-2">
            <div className="space-y-2" data-testid="beacon-discover-list">
              {discoverLoading ? (
                <div className="flex items-center justify-center py-10 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading recent scans…
                </div>
              ) : discoverCandidates.length === 0 ? (
                <div className="text-center py-10 text-sm text-muted-foreground" data-testid="beacon-discover-empty">
                  No unregistered beacons detected recently. Make sure a GPS tracker has scanned the beacon, then refresh.
                </div>
              ) : (() => {
                const filtered = getFilteredDiscoverCandidates();
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-10 text-sm text-muted-foreground" data-testid="beacon-discover-no-match">
                      No beacons match your filters.
                    </div>
                  );
                }
                return filtered.map((c, idx) => (
                  <div
                    key={c.identifier || idx}
                    className="flex items-center justify-between gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors"
                    data-testid={`beacon-discover-row-${idx}`}
                  >
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="secondary" className="text-[10px] uppercase">
                          {c.beacon_type === 'ibeacon' ? 'iBeacon' : c.beacon_type === 'eddystone' ? 'Eddystone' : 'Vendor / MAC'}
                        </Badge>
                        {typeof c.best_rssi === 'number' && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Signal className="w-3 h-3" /> {c.best_rssi} dBm
                          </span>
                        )}
                        {c.low_battery && (
                          <Badge variant="destructive" className="text-[10px]">Low battery</Badge>
                        )}
                      </div>
                      <p className="font-mono text-xs font-medium text-foreground break-all">{c.identifier}</p>
                      <p className="text-[11px] text-muted-foreground">
                        Seen {c.last_seen ? formatDT(c.last_seen, { seconds: true }) : '—'}
                        {c.detected_by && c.detected_by.length > 0 && (
                          <> · by {c.detected_by.map(d => d.name).join(', ')}</>
                        )}
                        {typeof c.scan_count === 'number' && <> · {c.scan_count} scan{c.scan_count === 1 ? '' : 's'}</>}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 text-xs shrink-0"
                      onClick={() => applyBeaconCandidate(c)}
                      data-testid={`beacon-discover-use-${idx}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Use this
                    </Button>
                  </div>
                ));
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showReplaceDevice} onOpenChange={setShowReplaceDevice}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-amber-500" />
              Replace Device
            </DialogTitle>
            <DialogDescription>
              Swap the hardware for <strong>{selectedDevice?.name}</strong>. All history, settings, and allocations will be preserved under the new IMEI.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 text-sm">
              <p><strong>Current IMEI:</strong> <span className="font-mono">{selectedDevice?.imei || formData.imei}</span></p>
            </div>
            <div className="space-y-2">
              <Label>New IMEI *</Label>
              <Input
                value={replaceImei}
                onChange={(e) => setReplaceImei(e.target.value)}
                placeholder="Enter new device IMEI"
                className="font-mono"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={replaceReason}
                onChange={(e) => setReplaceReason(e.target.value)}
                placeholder="e.g. Device failed, Upgrade, RMA"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={replaceTransferHistory} onCheckedChange={setReplaceTransferHistory} />
              Transfer GPS history to new IMEI
            </label>
            {!replaceTransferHistory && (
              <p className="text-xs text-red-600">Warning: GPS history will stay under the old IMEI and won't appear for this device.</p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowReplaceDevice(false)}>Cancel</Button>
            <Button size="sm"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleReplaceDevice}
              disabled={replacing || !replaceImei.trim()}
            >
              {replacing ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Replace Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk IO Mapping Import Dialog */}
      <Dialog open={isBulkImportOpen} onOpenChange={setIsBulkImportOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import IO Mappings to Devices</DialogTitle>
            <DialogDescription>
              Apply {bulkImportMappings.length} mapping(s) to selected devices.
              {bulkImportProtocol && <> Exported from <Badge variant="outline" className="ml-1">{bulkImportProtocol}</Badge> devices.</>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            {/* Mappings Preview */}
            <div>
              <Label className="text-sm font-semibold">Mappings to Import ({bulkImportMappings.length})</Label>
              <div className="mt-1 max-h-32 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs">
                      <TableHead className="px-2">Name</TableHead>
                      <TableHead className="px-2">IO ID</TableHead>
                      <TableHead className="px-2">Type</TableHead>
                      <TableHead className="px-2">Value Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkImportMappings.map((m, i) => (
                      <TableRow key={i} className="text-xs">
                        <TableCell className="px-2 font-medium">{m.display_name}</TableCell>
                        <TableCell className="px-2 font-mono text-[10px]">{m.io_id}</TableCell>
                        <TableCell className="px-2"><Badge variant="outline" className="text-[10px]">{m.io_type}</Badge></TableCell>
                        <TableCell className="px-2"><Badge variant="secondary" className="text-[10px]">{m.value_type}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mode Selection */}
            <div>
              <Label className="text-sm font-semibold">Import Mode</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkMode" value="merge" checked={bulkImportMode === 'merge'} onChange={() => setBulkImportMode('merge')} className="accent-blue-600" />
                  Merge (add to existing mappings)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkMode" value="replace" checked={bulkImportMode === 'replace'} onChange={() => setBulkImportMode('replace')} className="accent-blue-600" />
                  Replace (overwrite all mappings)
                </label>
              </div>
            </div>

            {/* Device Selector */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Target Devices ({bulkImportTargetIds.length} selected)</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setBulkImportTargetIds(
                    devices.filter(d => d.device_type !== 'beacon' && (!bulkImportProtocol || d.protocol === bulkImportProtocol)).map(d => d.id)
                  )}>
                    {bulkImportProtocol ? `Select All ${bulkImportProtocol}` : 'Select All GPS'}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setBulkImportTargetIds([])}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8 h-8 text-sm"
                  placeholder="Search devices..."
                  value={bulkImportSearch}
                  onChange={(e) => setBulkImportSearch(e.target.value)}
                  data-testid="bulk-import-search"
                />
              </div>
              <ScrollArea className="h-48 mt-2 border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
                <div className="p-2 space-y-1">
                  {devices
                    .filter(d => d.device_type !== 'beacon')
                    .filter(d => !bulkImportSearch || d.name.toLowerCase().includes(bulkImportSearch.toLowerCase()) || (d.imei || '').includes(bulkImportSearch))
                    .map(d => {
                      const protocolMatch = !bulkImportProtocol || d.protocol === bulkImportProtocol;
                      return (
                        <label key={d.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${protocolMatch ? 'hover:bg-muted' : 'opacity-40'}`}>
                          <Checkbox
                            checked={bulkImportTargetIds.includes(d.id)}
                            onCheckedChange={(checked) => setBulkImportTargetIds(prev => checked ? [...prev, d.id] : prev.filter(id => id !== d.id))}
                            disabled={!protocolMatch}
                          />
                          <span className="font-medium">{d.name}</span>
                          <Badge variant="outline" className="text-[10px] ml-1">{d.protocol}</Badge>
                          {!protocolMatch && <span className="text-[10px] text-red-500">Different protocol</span>}
                          <span className="text-xs text-muted-foreground font-mono ml-auto">{d.imei || d.mac_address || ''}</span>
                        </label>
                      );
                    })}
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsBulkImportOpen(false)}>Cancel</Button>
            <Button
              onClick={handleBulkImportSubmit}
              disabled={bulkImporting || bulkImportTargetIds.length === 0}
              className="bg-[#2563eb] hover:bg-[#1d4ed8]"
              data-testid="bulk-import-submit-btn"
            >
              {bulkImporting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Import to {bulkImportTargetIds.length} Device(s)</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Command Import Dialog */}
      <Dialog open={isBulkCmdImportOpen} onOpenChange={setIsBulkCmdImportOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Import Commands to Devices</DialogTitle>
            <DialogDescription>
              Apply {bulkCmdImportData.length} command(s) to selected devices.
              {bulkCmdImportProtocol && <> Exported from <Badge variant="outline" className="ml-1">{bulkCmdImportProtocol}</Badge> devices.</>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 pr-1">
            {/* Commands Preview */}
            <div>
              <Label className="text-sm font-semibold">Commands to Import ({bulkCmdImportData.length})</Label>
              <div className="mt-1 max-h-32 overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs">
                      <TableHead className="px-2 w-8"></TableHead>
                      <TableHead className="px-2">Label</TableHead>
                      <TableHead className="px-2">Command</TableHead>
                      <TableHead className="px-2">Colour</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bulkCmdImportData.map((c, i) => (
                      <TableRow key={i} className="text-xs">
                        <TableCell className="px-2">
                          <div className={`w-3 h-3 rounded-sm ${
                            { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-gray-500', teal: 'bg-teal-600', pink: 'bg-pink-600', indigo: 'bg-indigo-600', cyan: 'bg-cyan-600', lime: 'bg-lime-600', rose: 'bg-rose-600' }[c.button_color] || 'bg-blue-600'
                          }`} />
                        </TableCell>
                        <TableCell className="px-2 font-medium">{c.button_label}</TableCell>
                        <TableCell className="px-2 font-mono text-[10px]">{c.command_text}</TableCell>
                        <TableCell className="px-2 capitalize text-[10px]">{c.button_color || 'blue'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Mode Selection */}
            <div>
              <Label className="text-sm font-semibold">Import Mode</Label>
              <div className="flex gap-4 mt-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkCmdMode" value="merge" checked={bulkCmdImportMode === 'merge'} onChange={() => setBulkCmdImportMode('merge')} className="accent-blue-600" />
                  Merge (add to existing commands)
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="bulkCmdMode" value="replace" checked={bulkCmdImportMode === 'replace'} onChange={() => setBulkCmdImportMode('replace')} className="accent-blue-600" />
                  Replace (overwrite all commands)
                </label>
              </div>
            </div>

            {/* Device Selector — filtered by matching protocol */}
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Target Devices ({bulkCmdImportTargetIds.length} selected)</Label>
                <div className="flex gap-2">
                  {bulkCmdImportProtocol && (
                    <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setBulkCmdImportTargetIds(devices.filter(d => d.device_type !== 'beacon' && d.protocol === bulkCmdImportProtocol).map(d => d.id))}>
                      Select All {bulkCmdImportProtocol}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="text-xs h-6" onClick={() => setBulkCmdImportTargetIds([])}>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="relative mt-1">
                <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-8 h-8 text-sm" placeholder="Search devices..." value={bulkCmdImportSearch} onChange={(e) => setBulkCmdImportSearch(e.target.value)} />
              </div>
              <ScrollArea className="h-48 mt-2 border rounded-lg [&_[data-radix-scroll-area-viewport]>div]:!block">
                <div className="p-2 space-y-1">
                  {devices
                    .filter(d => d.device_type !== 'beacon')
                    .filter(d => !bulkCmdImportSearch || d.name.toLowerCase().includes(bulkCmdImportSearch.toLowerCase()) || (d.imei || '').includes(bulkCmdImportSearch))
                    .map(d => {
                      const protocolMatch = !bulkCmdImportProtocol || d.protocol === bulkCmdImportProtocol;
                      return (
                        <label key={d.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${protocolMatch ? 'hover:bg-muted' : 'opacity-40'}`}>
                          <Checkbox
                            checked={bulkCmdImportTargetIds.includes(d.id)}
                            onCheckedChange={(checked) => setBulkCmdImportTargetIds(prev => checked ? [...prev, d.id] : prev.filter(id => id !== d.id))}
                            disabled={!protocolMatch}
                          />
                          <span className="font-medium">{d.name}</span>
                          <Badge variant="outline" className="text-[10px] ml-1">{d.protocol}</Badge>
                          {!protocolMatch && <span className="text-[10px] text-red-500 ml-auto">Different protocol</span>}
                          <span className="text-xs text-muted-foreground font-mono ml-auto">{d.imei || ''}</span>
                        </label>
                      );
                    })}
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsBulkCmdImportOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkCmdImportSubmit} disabled={bulkCmdImporting || bulkCmdImportTargetIds.length === 0} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              {bulkCmdImporting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Import to {bulkCmdImportTargetIds.length} Device(s)</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Command Confirmation — uses Dialog to work with Radix focus trap */}
      <Dialog open={!!sendConfirmCmd} onOpenChange={(open) => { if (!open) setSendConfirmCmd(null); }}>
        <DialogContent className="max-w-md">
          {sendConfirmCmd && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${
                    { blue: 'bg-blue-600', red: 'bg-red-600', green: 'bg-green-600', yellow: 'bg-yellow-500', orange: 'bg-orange-500', purple: 'bg-purple-600', gray: 'bg-gray-500', teal: 'bg-teal-600', pink: 'bg-pink-600', indigo: 'bg-indigo-600', cyan: 'bg-cyan-600', lime: 'bg-lime-600', rose: 'bg-rose-600' }[sendConfirmCmd.button_color] || 'bg-blue-600'
                  }`}>
                    <Send className="w-5 h-5" />
                  </div>
                  <div>
                    <DialogTitle>Send Command?</DialogTitle>
                    <DialogDescription>This will be sent to {selectedDevice?.name}</DialogDescription>
                  </div>
                </div>
              </DialogHeader>
              <div className="space-y-2 p-3 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Button</span>
                  <span className="font-medium">{sendConfirmCmd.button_label}</span>
                </div>
                {sendConfirmCmd.command_mode === 'sequence' && sendConfirmCmd.command_sequence?.length > 0 ? (
                  <div className="space-y-1">
                    <span className="text-muted-foreground text-sm">Sequence ({sendConfirmCmd.command_sequence.length} steps)</span>
                    {sendConfirmCmd.command_sequence.map((s, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs font-mono pl-2">
                        <span className="text-muted-foreground">{i + 1}.</span>
                        <span>{s.command_text}</span>
                        {s.delay_ms > 0 && i < sendConfirmCmd.command_sequence.length - 1 && (
                          <span className="text-muted-foreground">→ {s.delay_ms}ms delay</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Command</span>
                    <span className="font-mono text-xs">{sendConfirmCmd.command_text}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Device IMEI</span>
                  <span className="font-mono text-xs">{selectedDevice?.imei}</span>
                </div>
                {sendConfirmCmd.description && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Description</span>
                    <span className="text-xs">{sendConfirmCmd.description}</span>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => setSendConfirmCmd(null)} disabled={sendingCommand}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={() => executeSendCommand(sendConfirmCmd)}
                  disabled={sendingCommand}
                  className={`text-white ${
                    { blue: 'bg-blue-600 hover:bg-blue-700', red: 'bg-red-600 hover:bg-red-700', green: 'bg-green-600 hover:bg-green-700', yellow: 'bg-yellow-500 hover:bg-yellow-600', orange: 'bg-orange-500 hover:bg-orange-600', purple: 'bg-purple-600 hover:bg-purple-700', gray: 'bg-gray-500 hover:bg-gray-600', teal: 'bg-teal-600 hover:bg-teal-700', pink: 'bg-pink-600 hover:bg-pink-700', indigo: 'bg-indigo-600 hover:bg-indigo-700', cyan: 'bg-cyan-600 hover:bg-cyan-700', lime: 'bg-lime-600 hover:bg-lime-700', rose: 'bg-rose-600 hover:bg-rose-700' }[sendConfirmCmd.button_color] || 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  data-testid="cmd-confirm-send-btn"
                >
                  {sendingCommand ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send Command</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom Icon Upload Dialog */}
      <Dialog open={showIconUpload} onOpenChange={(open) => { if (!open) { setShowIconUpload(false); setIconUploadFile(null); setIconUploadName(''); } }}>
        <DialogContent className="max-w-md" data-testid="icon-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Custom Icon</DialogTitle>
            <DialogDescription>Upload a PNG or SVG icon to the custom library. PNG max 256x256, max 200KB.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Icon Name</Label>
              <Input
                value={iconUploadName}
                onChange={e => setIconUploadName(e.target.value)}
                placeholder="e.g. Company Logo, Forklift, Drone"
                maxLength={50}
                data-testid="icon-upload-name"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon File</Label>
              <Input
                type="file"
                accept=".svg,.png,image/svg+xml,image/png"
                onChange={e => setIconUploadFile(e.target.files?.[0] || null)}
                data-testid="icon-upload-file"
              />
              {iconUploadFile && (
                <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                  <img
                    src={URL.createObjectURL(iconUploadFile)}
                    alt="Preview"
                    className="w-12 h-12 object-contain border rounded"
                  />
                  <div className="text-xs">
                    <p className="font-medium">{iconUploadFile.name}</p>
                    <p className="text-muted-foreground">{(iconUploadFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowIconUpload(false); setIconUploadFile(null); setIconUploadName(''); }}>Cancel</Button>
            <Button size="sm" onClick={handleIconUpload} disabled={iconUploading || !iconUploadFile || !iconUploadName.trim()} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="icon-upload-submit">
              {iconUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom Icon Library Dialog */}
      <Dialog open={showIconLibrary} onOpenChange={setShowIconLibrary}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="icon-library-dialog">
          <DialogHeader>
            <DialogTitle>Custom Icon Library</DialogTitle>
            <DialogDescription>{customIcons.length} custom icon{customIcons.length !== 1 ? 's' : ''} uploaded</DialogDescription>
          </DialogHeader>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={iconLibrarySearch}
              onChange={e => setIconLibrarySearch(e.target.value)}
              placeholder="Search icons..."
              className="h-8 pl-9 pr-9"
              data-testid="icon-library-search"
              autoComplete="off"
            />
            {iconLibrarySearch && (
              <button onClick={() => setIconLibrarySearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="icon-library-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {customIconsLoading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : customIcons.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ImageIcon className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No custom icons uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customIcons.filter(ci => !iconLibrarySearch || ci.name.toLowerCase().includes(iconLibrarySearch.toLowerCase())).map(ci => (
                <div key={ci.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors" data-testid={`library-icon-${ci.id}`}>
                  <div className="w-12 h-12 shrink-0">
                    <img src={ci.full_url} alt={ci.name} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {iconEditingId === ci.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={iconEditingName}
                          onChange={e => setIconEditingName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleIconRename(ci.id); if (e.key === 'Escape') setIconEditingId(null); }}
                          className="h-7 text-sm"
                          maxLength={50}
                          autoFocus
                          autoComplete="off"
                          data-testid={`rename-input-${ci.id}`}
                        />
                        <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7 text-green-600 hover:text-green-700" onClick={() => handleIconRename(ci.id)} disabled={!iconEditingName.trim()} data-testid={`rename-save-${ci.id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="shrink-0 h-7 w-7" onClick={() => setIconEditingId(null)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <p className="font-medium text-sm truncate">{ci.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {ci.content_type === 'image/svg+xml' ? 'SVG' : 'PNG'} &middot; Uploaded by {ci.uploaded_by_name}
                        </p>
                      </>
                    )}
                  </div>
                  {iconEditingId !== ci.id && (
                    <>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-8 w-8"
                        onClick={() => { setIconEditingId(ci.id); setIconEditingName(ci.name); }}
                        title="Edit"
                        data-testid={`edit-icon-name-${ci.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="shrink-0 h-8 w-8 text-red-500 hover:text-red-700"
                        onClick={() => handleIconDelete(ci.id, ci.name)}
                        disabled={iconDeleting === ci.id}
                        title="Delete"
                        data-testid={`delete-icon-${ci.id}`}
                      >
                        {iconDeleting === ci.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </>
                  )}
                </div>
              ))}
              {iconLibrarySearch && customIcons.filter(ci => ci.name.toLowerCase().includes(iconLibrarySearch.toLowerCase())).length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">No icons matching "{iconLibrarySearch}"</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowIconLibrary(false)}>Close</Button>
            <Button size="sm" onClick={() => { setShowIconLibrary(false); setShowIconUpload(true); }} className="bg-[#2563eb] hover:bg-[#1d4ed8]">
              <Upload className="w-4 h-4 mr-2" /> Upload New Icon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onOpenChange={(open) => { if (!open) { setShowBulkUpload(false); setBulkUploadFiles([]); setBulkUploadProgress(0); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Icons</DialogTitle>
            <DialogDescription>{bulkUploadFiles.length} file{bulkUploadFiles.length !== 1 ? 's' : ''} selected — rename before uploading</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {bulkUploadFiles.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 border rounded-lg">
                <img src={item.preview} alt="" className="w-10 h-10 object-contain border rounded shrink-0" />
                <div className="flex-1 min-w-0">
                  <Input
                    value={item.name}
                    onChange={e => {
                      const updated = [...bulkUploadFiles];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setBulkUploadFiles(updated);
                    }}
                    placeholder="Icon name"
                    className="h-8 text-sm"
                    maxLength={50}
                    data-testid={`bulk-name-${idx}`}
                    autoComplete="off"
                  />
                </div>
                <div className="text-xs text-muted-foreground shrink-0 w-16 text-right">
                  {(item.file.size / 1024).toFixed(0)} KB
                </div>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={() => setBulkUploadFiles(prev => prev.filter((_, i) => i !== idx))}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
          {bulkUploading && (
            <div className="space-y-1">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-[#2563eb] transition-all rounded-full" style={{ width: `${(bulkUploadProgress / bulkUploadFiles.length) * 100}%` }} />
              </div>
              <p className="text-xs text-muted-foreground text-center">{bulkUploadProgress} / {bulkUploadFiles.length}</p>
            </div>
          )}
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowBulkUpload(false); setBulkUploadFiles([]); }} disabled={bulkUploading}>Cancel</Button>
            <Button size="sm" onClick={handleBulkUpload} disabled={bulkUploading || bulkUploadFiles.length === 0 || bulkUploadFiles.some(f => !f.name.trim())} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="bulk-upload-submit">
              {bulkUploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload All ({bulkUploadFiles.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate IO Mapping Audit */}
      <Dialog open={dupAuditOpen} onOpenChange={setDupAuditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Duplicate IO Mappings</DialogTitle>
            <DialogDescription>These devices have 2+ mappings on the same IO ID. Rules and displays resolve to the first match — remove or consolidate the duplicates.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto space-y-2" data-testid="dup-mapping-audit-list">
            {ioDupAudit.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No duplicate mappings found.</p>
            ) : ioDupAudit.map(d => (
              <div key={d.id} className="p-2.5 rounded-md border flex items-start justify-between gap-3" data-testid={`dup-audit-row-${d.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.name || d.imei}</p>
                  <p className="text-[10px] text-muted-foreground font-mono">{d.imei}</p>
                  <div className="mt-1 space-y-0.5">
                    {d.duplicates.map(dup => (
                      <p key={dup.io_id} className="text-xs text-amber-700 dark:text-amber-400">IO {dup.io_id}: {dup.names.join(', ')}</p>
                    ))}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0" data-testid={`dup-audit-edit-${d.id}`}
                  onClick={async () => {
                    setDupAuditOpen(false);
                    await openEditDialog({ id: d.id, name: d.name, imei: d.imei, device_type: d.device_type || 'gps_tracker' });
                    setActiveTab('mapping');
                  }}>
                  Fix mappings
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Device Deactivation Wizard */}
      <DeviceDeactivationWizard
        open={!!deactivateWizardDevice}
        onClose={() => setDeactivateWizardDevice(null)}
        device={deactivateWizardDevice}
        api={api}
        onComplete={fetchDevices}
      />

      {/* Device Reactivation Wizard */}
      <DeviceReactivationWizard
        open={!!reactivateWizardDevice}
        onClose={() => setReactivateWizardDevice(null)}
        device={reactivateWizardDevice}
        api={api}
        users={Object.entries(userMap).map(([id, u]) => ({ id, ...u }))}
        onComplete={fetchDevices}
      />

      {/* Bulk Delete confirmation — SUPER ADMIN ONLY */}
      <Dialog open={bulkDeleteOpen} onOpenChange={(o) => { setBulkDeleteOpen(o); if (!o) setBulkDeleteText(''); }}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-devices-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete {selectedDeviceIds.length} device(s)?
            </DialogTitle>
            <DialogDescription>
              This permanently deletes the selected devices, frees their user allocation slots and removes them from event rules and sub-account allocations. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {bulkImpact && (bulkImpact.schedules > 0 || bulkImpact.history > 0 || bulkImpact.attachments > 0) && (
              <div className="p-2 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs" data-testid="bulk-delete-maintenance-impact">
                This will also permanently remove maintenance data across {bulkImpact.devices_with_data || 0} of the selected device(s):
                <span className="block mt-1">
                  {bulkImpact.schedules > 0 && <>• {bulkImpact.schedules} service schedule{bulkImpact.schedules === 1 ? '' : 's'}<br /></>}
                  {bulkImpact.history > 0 && <>• {bulkImpact.history} service log{bulkImpact.history === 1 ? '' : 's'}<br /></>}
                  {bulkImpact.attachments > 0 && <>• {bulkImpact.attachments} attached file{bulkImpact.attachments === 1 ? '' : 's'} (deleted from storage)</>}
                </span>
              </div>
            )}
            <label className="flex items-start gap-2 cursor-pointer" data-testid="bulk-delete-purge-label">
              <Checkbox
                checked={bulkPurgeHistory}
                onCheckedChange={() => setBulkPurgeHistory((p) => !p)}
                className="mt-0.5"
                data-testid="bulk-delete-purge-checkbox"
              />
              <span className="text-sm leading-tight">
                <span className="font-medium">Also permanently delete all historical data</span>
                <span className="block text-xs text-muted-foreground">GPS history, events, beacon scans & log book records for each deleted device</span>
              </span>
            </label>
            <Label htmlFor="bulk-delete-devices-input">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input
              id="bulk-delete-devices-input"
              value={bulkDeleteText}
              onChange={e => setBulkDeleteText(e.target.value)}
              placeholder="DELETE"
              data-testid="bulk-delete-devices-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)} data-testid="bulk-delete-devices-cancel">Cancel</Button>
            <Button
              variant="destructive"
              disabled={bulkDeleteText !== 'DELETE' || bulkDeleting}
              onClick={handleBulkDeleteDevices}
              data-testid="bulk-delete-devices-confirm"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status-switch wizard prompt (Device edit dialog) */}
      <Dialog open={!!statusTogglePrompt} onOpenChange={(o) => { if (!o) setStatusTogglePrompt(null); }}>
        <DialogContent className="max-w-xl" data-testid="device-status-wizard-prompt">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Power className={`w-5 h-5 ${statusTogglePrompt?.next ? 'text-green-500' : 'text-red-500'}`} />
              {statusTogglePrompt?.next ? 'Reactivate' : 'Deactivate'} this device?
            </DialogTitle>
            <DialogDescription>
              The {statusTogglePrompt?.next ? 'Reactivation' : 'Deactivation'} Wizard walks you through GPRS commands, SIM handling, user allocations and billing. Opening it will close this edit dialog. Alternatively, change the status flag only — remember to Save afterwards.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => setStatusTogglePrompt(null)} data-testid="device-status-prompt-cancel">Cancel</Button>
            <Button
              variant="outline"
              onClick={() => { handleInputChange('is_active', statusTogglePrompt.next); setStatusTogglePrompt(null); }}
              data-testid="device-status-prompt-toggle-only"
            >
              Change status only
            </Button>
            <Button
              className={statusTogglePrompt?.next ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={() => {
                const next = statusTogglePrompt.next;
                const dev = selectedDevice;
                setStatusTogglePrompt(null);
                handleEditDialogClose(true);
                if (next) setReactivateWizardDevice(dev); else setDeactivateWizardDevice(dev);
              }}
              data-testid="device-status-prompt-open-wizard"
            >
              Open {statusTogglePrompt?.next ? 'Reactivation' : 'Deactivation'} Wizard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Import/Export Log */}
      <Dialog open={ioLogOpen} onOpenChange={setIoLogOpen}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="device-io-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Import / Export Log
            </DialogTitle>
            <DialogDescription>
              Every device import and export — who ran it, row counts and undo status
            </DialogDescription>
          </DialogHeader>
          {ioLogGrandTotal === 0 && !ioLogsLoading ? (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground" data-testid="device-io-log-empty">
              <History className="w-8 h-8 mb-2 opacity-30" />
              <p className="text-sm">No imports or exports recorded yet</p>
              <p className="text-xs">Run an import or export and it will appear here</p>
            </div>
          ) : (
            <>
            <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="device-io-log-filters">
              <Select value={ioLogTypeFilter} onValueChange={setIoLogTypeFilter}>
                <SelectTrigger className="h-8 w-[130px] text-xs" data-testid="device-io-log-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="import">Imports</SelectItem>
                  <SelectItem value="export">Exports</SelectItem>
                </SelectContent>
              </Select>
              <Select value={ioLogPersonFilter} onValueChange={setIoLogPersonFilter}>
                <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="device-io-log-person-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All people</SelectItem>
                  {ioLogPersons.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              <DateRangePresetFilter
                value={ioLogDateRange}
                onChange={setIoLogDateRange}
                testIdPrefix="device-io-log-date"
                resetSignal={ioLogDateResetKey}
              />
              {(ioLogTypeFilter !== 'all' || ioLogPersonFilter !== 'all' || ioLogDateRange.from || ioLogDateRange.to) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => { setIoLogTypeFilter('all'); setIoLogPersonFilter('all'); setIoLogDateRange({ from: undefined, to: undefined }); setIoLogDateResetKey(k => k + 1); }}
                  data-testid="device-io-log-clear-filters"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Clear
                </Button>
              )}
              <span className="text-xs text-muted-foreground ml-auto" data-testid="device-io-log-count">
                {ioLogTotal} of {ioLogGrandTotal} entr{ioLogGrandTotal === 1 ? 'y' : 'ies'}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    disabled={ioLogTotal === 0}
                    data-testid="device-io-log-export-menu"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportIoLogCsv} data-testid="device-io-log-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportIoLogHtml} data-testid="device-io-log-export-html">HTML</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportIoLogPdf} data-testid="device-io-log-export-pdf">PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {ioLogsLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : ioLogTotal === 0 ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm" data-testid="device-io-log-no-match">
                No entries match the current filters
              </div>
            ) : (
            <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs">Details</TableHead>
                    <TableHead className="text-xs">File</TableHead>
                    <TableHead className="text-xs text-right">Rows</TableHead>
                    <TableHead className="text-xs">Result</TableHead>
                    <TableHead className="text-xs">Undo</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ioLogs.map(l => {
                    const detail = l.kind === 'import'
                      ? (l.source === 'wizard' ? 'Import Wizard' : 'Quick CSV Import')
                      : ({ gps_devices: 'GPS Devices', ble_beacons: 'BLE Beacons', audit: 'Full Details (Audit)' }[l.export_type] || 'Export');
                    return (
                      <TableRow key={l.id} data-testid={`device-io-log-row-${l.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{l.timestamp ? formatDT(l.timestamp) : '—'}</TableCell>
                        <TableCell>
                          <Badge className={l.kind === 'import'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300'}>
                            {l.kind === 'import' ? 'Import' : 'Export'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{detail}{l.file_type ? ` (.${l.file_type})` : ''}</TableCell>
                        <TableCell className="text-xs max-w-[180px] truncate" title={l.file_name || ''}>{l.file_name || '—'}</TableCell>
                        <TableCell className="text-xs text-right">{l.row_count ?? 0}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {l.kind === 'import'
                            ? `${l.created ?? 0} created / ${l.skipped ?? 0} skipped / ${l.failed ?? 0} failed`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {l.undo_status === 'undone'
                            ? <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">Undone ({l.undo_removed ?? 0} removed)</Badge>
                            : '—'}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{l.actor_name || l.actor_email || '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
            )}
            <PaginationFooter
              page={ioLogPage}
              setPage={setIoLogPage}
              totalItems={ioLogTotal}
              itemsPerPage={ioLogPageSize}
              setItemsPerPage={setIoLogPageSize}
              testIdPrefix="device-io-log"
              scrollOnChange={false}
              disabled={ioLogsLoading}
              alwaysShow
            />
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Command Delivery Receipts — per-device command log */}
      <Dialog open={cmdLogOpen} onOpenChange={setCmdLogOpen}>
        <DialogContent className="max-w-6xl h-[85vh] flex flex-col" data-testid="device-cmd-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CommandIcon className="w-5 h-5" />
              Command Log — {selectedDevice?.name || selectedDevice?.imei || ''}
            </DialogTitle>
            <DialogDescription>
              Every command sent to this device with its delivery result — GPRS and SMS
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 pb-1" data-testid="device-cmd-log-filters">
            <Select value={cmdLogStatusFilter} onValueChange={setCmdLogStatusFilter}>
              <SelectTrigger className="h-8 w-[145px] text-xs" data-testid="device-cmd-log-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="timeout">Timeout</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="no_response">No response</SelectItem>
              </SelectContent>
            </Select>
            <Select value={cmdLogMethodFilter} onValueChange={setCmdLogMethodFilter}>
              <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="device-cmd-log-method-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All methods</SelectItem>
                <SelectItem value="gprs">GPRS</SelectItem>
                <SelectItem value="sms_api">SMS (API)</SelectItem>
                <SelectItem value="sms_3p">SMS (3rd-party)</SelectItem>
              </SelectContent>
            </Select>
            <DateRangePresetFilter
              value={cmdLogDateRange}
              onChange={setCmdLogDateRange}
              testIdPrefix="device-cmd-log-date"
              resetSignal={cmdLogDateResetKey}
            />
            {(cmdLogStatusFilter !== 'all' || cmdLogMethodFilter !== 'all' || cmdLogDateRange.from || cmdLogDateRange.to) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => { setCmdLogStatusFilter('all'); setCmdLogMethodFilter('all'); setCmdLogDateRange({ from: undefined, to: undefined }); setCmdLogDateResetKey(k => k + 1); }}
                data-testid="device-cmd-log-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
            <span className="text-xs text-muted-foreground ml-auto" data-testid="device-cmd-log-count">
              {cmdLogTotal} command{cmdLogTotal === 1 ? '' : 's'}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs" disabled={cmdLogTotal === 0} data-testid="device-cmd-log-export-menu">
                  <Upload className="w-3.5 h-3.5 mr-1" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportCmdLogCsv} data-testid="device-cmd-log-export-csv">CSV</DropdownMenuItem>
                <DropdownMenuItem onClick={exportCmdLogHtml} data-testid="device-cmd-log-export-html">HTML</DropdownMenuItem>
                <DropdownMenuItem onClick={exportCmdLogPdf} data-testid="device-cmd-log-export-pdf">PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {cmdLogsLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : cmdLogs.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm" data-testid="device-cmd-log-no-match">
              No commands match the current filters
            </div>
          ) : (
            <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Method</TableHead>
                    <TableHead className="text-xs">Command</TableHead>
                    <TableHead className="text-xs">Response / Error</TableHead>
                    <TableHead className="text-xs">Sent</TableHead>
                    <TableHead className="text-xs">Reply</TableHead>
                    <TableHead className="text-xs">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cmdLogs.map((c, i) => (
                    <TableRow key={c.id || i} data-testid={`device-cmd-log-row-${c.id || i}`}>
                      <TableCell className="text-xs whitespace-nowrap">{c.created_at ? formatDT(c.created_at, { seconds: true }) : '—'}</TableCell>
                      <TableCell><Badge className={`text-[10px] ${cmdStatusBadgeClass(c.status)}`}>{c.status || '—'}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] whitespace-nowrap">{cmdMethodLabel(c.delivery_method)}</Badge></TableCell>
                      <TableCell className="text-xs font-mono max-w-[220px] truncate" title={c.command || ''}>{c.command || '—'}</TableCell>
                      <TableCell className={`text-xs max-w-[220px] truncate ${c.error ? 'text-red-600 dark:text-red-400' : ''}`} title={c.response || c.error || ''}>{c.response || c.error || '—'}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{c.sent_at ? formatTimeOnly(c.sent_at, { seconds: true }) : '—'}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{c.responded_at ? formatTimeOnly(c.responded_at, { seconds: true }) : '—'}</TableCell>
                      <TableCell className="text-xs whitespace-nowrap max-w-[150px] truncate" title={cmdSentBy(c)}>{cmdSentBy(c) || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
          <PaginationFooter
            page={cmdLogPage}
            setPage={setCmdLogPage}
            totalItems={cmdLogTotal}
            itemsPerPage={cmdLogPageSize}
            setItemsPerPage={setCmdLogPageSize}
            testIdPrefix="device-cmd-log"
            scrollOnChange={false}
            disabled={cmdLogsLoading}
            alwaysShow
          />
        </DialogContent>
      </Dialog>

      {/* SIM Health Report */}
      <Dialog open={simReportOpen} onOpenChange={(o) => { setSimReportOpen(o); if (!o) { setSimReportTab('offline'); setSimReportPage(1); } }}>
        <DialogContent className="max-w-6xl h-[80vh] flex flex-col" data-testid="sim-health-report-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Signal className="w-5 h-5 text-[#2563eb]" />
              SIM Health Report
            </DialogTitle>
            <DialogDescription>
              Cross-checks the SIM database against your fleet to catch faulty trackers and money leaks.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'offline', label: 'Active SIM · Offline Tracker', count: simReportCounts?.active_sim_offline },
              { key: 'dead', label: 'Dead SIM · Active Device', count: simReportCounts?.dead_sim_active_devices },
              { key: 'orphaned', label: 'Orphaned Active SIMs', count: simReportCounts?.orphaned_active_sims },
              { key: 'duplicates', label: 'Duplicate SIMs', count: simReportCounts?.duplicate_sim_devices },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => { setSimReportTab(t.key); setSimReportPage(1); }}
                data-testid={`sim-report-tab-${t.key}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${simReportTab === t.key ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-card text-muted-foreground border-border hover:text-foreground'}`}
              >
                {t.label}{typeof t.count === 'number' ? ` (${t.count})` : ''}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              {simReportTab === 'offline' && (
                <Select value={simReportDays} onValueChange={(v) => { setSimReportDays(v); setSimReportPage(1); }}>
                  <SelectTrigger className="h-8 w-[170px]" data-testid="sim-report-days-filter">
                    <SelectValue>Offline &gt; {simReportDays} day{simReportDays !== '1' ? 's' : ''}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Offline &gt; 1 day</SelectItem>
                    <SelectItem value="3">Offline &gt; 3 days</SelectItem>
                    <SelectItem value="7">Offline &gt; 7 days</SelectItem>
                    <SelectItem value="30">Offline &gt; 30 days</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" disabled={simReportLoading} data-testid="sim-report-export-menu">
                    <Upload className="w-4 h-4 mr-1.5" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={exportSimReportCsv} data-testid="sim-report-export-csv">CSV</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportSimReportHtml} data-testid="sim-report-export-html">HTML</DropdownMenuItem>
                  <DropdownMenuItem onClick={exportSimReportPdf} data-testid="sim-report-export-pdf">PDF</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {simReportLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
              {simReportTab === 'offline' && (
                simReportRows.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground" data-testid="sim-report-offline-empty">
                    No active SIMs on offline trackers — all good!
                  </div>
                ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Device</TableHead>
                      <TableHead className="text-xs">IMEI</TableHead>
                      <TableHead className="text-xs">Protocol</TableHead>
                      <TableHead className="text-xs">Last Connection</TableHead>
                      <TableHead className="text-xs">Offline For</TableHead>
                      <TableHead className="text-xs">ICCID</TableHead>
                      <TableHead className="text-xs">Provider</TableHead>
                      <TableHead className="text-xs">Mobile</TableHead>
                      <TableHead className="text-xs">SIM Session</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simReportRows.map(r => {
                      const sev = r.offline_days === null || r.offline_days > 7 ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400';
                      return (
                        <TableRow key={r.device_id} data-testid={`sim-report-offline-row-${r.device_id}`}>
                          <TableCell className="text-xs font-medium">{r.device_name || '—'}</TableCell>
                          <TableCell className="text-xs font-mono">{r.imei || '—'}</TableCell>
                          <TableCell className="text-xs">{r.protocol || '—'}</TableCell>
                          <TableCell className="text-xs whitespace-nowrap">{r.last_connection ? formatDT(r.last_connection) : 'Never connected'}</TableCell>
                          <TableCell className={`text-xs font-semibold whitespace-nowrap ${sev}`}>
                            {r.offline_days === null ? 'Never connected' : `${r.offline_days} day${r.offline_days !== 1 ? 's' : ''}`}
                          </TableCell>
                          <TableCell className="text-xs font-mono">{r.iccid}</TableCell>
                          <TableCell className="text-xs">{r.provider}</TableCell>
                          <TableCell className="text-xs">{r.mobile_number || '—'}</TableCell>
                          <TableCell className="text-xs">
                            {r.session_status === 'in_session'
                              ? <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" title="SIM has a live session while the tracker is offline — likely a frozen tracker">In session ⚠</Badge>
                              : (r.session_status ? <Badge variant="outline" className="text-[10px]">{r.session_status.replace('_', ' ')}</Badge> : '—')}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                )
              )}
              {simReportTab === 'dead' && (
                simReportRows.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground" data-testid="sim-report-dead-empty">
                    No dead SIMs on active devices — all good!
                  </div>
                ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Device</TableHead>
                      <TableHead className="text-xs">IMEI</TableHead>
                      <TableHead className="text-xs">Protocol</TableHead>
                      <TableHead className="text-xs">Device Status</TableHead>
                      <TableHead className="text-xs">ICCID</TableHead>
                      <TableHead className="text-xs">Provider</TableHead>
                      <TableHead className="text-xs">Mobile</TableHead>
                      <TableHead className="text-xs">SIM Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simReportRows.map(r => (
                      <TableRow key={r.device_id} data-testid={`sim-report-dead-row-${r.device_id}`}>
                        <TableCell className="text-xs font-medium">{r.device_name || '—'}</TableCell>
                        <TableCell className="text-xs font-mono">{r.imei || '—'}</TableCell>
                        <TableCell className="text-xs">{r.protocol || '—'}</TableCell>
                        <TableCell className="text-xs capitalize">{r.device_status}</TableCell>
                        <TableCell className="text-xs font-mono">{r.iccid}</TableCell>
                        <TableCell className="text-xs">{r.provider}</TableCell>
                        <TableCell className="text-xs">{r.mobile_number || '—'}</TableCell>
                        <TableCell>
                          <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 capitalize">{(r.sim_status || '').replace(/_/g, ' ')}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )
              )}
              {simReportTab === 'duplicates' && (
                simReportRows.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground" data-testid="sim-report-duplicates-empty">
                    No devices share a SIM ICCID — all good!
                  </div>
                ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Device</TableHead>
                      <TableHead className="text-xs">IMEI</TableHead>
                      <TableHead className="text-xs">Protocol</TableHead>
                      <TableHead className="text-xs">Device Status</TableHead>
                      <TableHead className="text-xs">ICCID</TableHead>
                      <TableHead className="text-xs">Provider</TableHead>
                      <TableHead className="text-xs">SIM Status</TableHead>
                      <TableHead className="text-xs">Shared With</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simReportRows.map(r => (
                      <TableRow key={r.device_id} data-testid={`sim-report-duplicates-row-${r.device_id}`}>
                        <TableCell className="text-xs font-medium">{r.device_name || '—'}</TableCell>
                        <TableCell className="text-xs font-mono">{r.imei || '—'}</TableCell>
                        <TableCell className="text-xs">{r.protocol || '—'}</TableCell>
                        <TableCell className="text-xs capitalize">{r.device_status}</TableCell>
                        <TableCell className="text-xs font-mono">{r.iccid}</TableCell>
                        <TableCell className="text-xs">{r.provider}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] capitalize">{(r.sim_status || '').replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-orange-600 dark:text-orange-400">{(r.shared_with || []).join(', ') || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )
              )}
              {simReportTab === 'orphaned' && (
                simReportRows.length === 0 ? (
                  <div className="py-16 text-center text-sm text-muted-foreground" data-testid="sim-report-orphaned-empty">
                    No orphaned active SIMs — all good!
                  </div>
                ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">ICCID</TableHead>
                      <TableHead className="text-xs">Provider</TableHead>
                      <TableHead className="text-xs">Mobile</TableHead>
                      <TableHead className="text-xs">Plan</TableHead>
                      <TableHead className="text-xs">SIM Status</TableHead>
                      <TableHead className="text-xs">Session</TableHead>
                      <TableHead className="text-xs">Last Activity</TableHead>
                      <TableHead className="text-xs text-right">Cost</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simReportRows.map(r => (
                      <TableRow key={r.iccid} data-testid={`sim-report-orphaned-row-${r.iccid}`}>
                        <TableCell className="text-xs font-mono">{r.iccid}</TableCell>
                        <TableCell className="text-xs">{r.provider}</TableCell>
                        <TableCell className="text-xs">{r.mobile_number || '—'}</TableCell>
                        <TableCell className="text-xs">{r.plan_name || '—'}</TableCell>
                        <TableCell>
                          <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 capitalize">{(r.sim_status || '').replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.session_status ? r.session_status.replace('_', ' ') : '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{r.sim_last_activity ? formatDT(r.sim_last_activity) : '—'}</TableCell>
                        <TableCell className="text-xs text-right">{r.cost != null ? `$${Number(r.cost).toFixed(2)}` : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                )
              )}
            </ScrollArea>
          )}
          <PaginationFooter
            page={simReportPage}
            setPage={setSimReportPage}
            totalItems={simReportTotal}
            itemsPerPage={simReportPageSize}
            setItemsPerPage={setSimReportPageSize}
            testIdPrefix="sim-health-report"
            scrollOnChange={false}
            disabled={simReportLoading}
            alwaysShow
          />
        </DialogContent>
      </Dialog>

      <DeviceImportWizard
        open={isDeviceImportWizardOpen}
        onOpenChange={setIsDeviceImportWizardOpen}
        devices={devices}
        users={Object.entries(userMap).map(([id, u]) => ({ id, ...u }))}
        simProviders={simProviders}
        api={api}
        onCompleted={(r) => {
          fetchDevices();
          if (r?.run?.id) setLastImportRun(r.run);
          if (r?.undo) setLastImportRun(null);
        }}
      />
    </div>
  );
};

export default DeviceManagement;
export { DeviceManagement };
